import React, { useEffect, useState, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import DataTable from "datatables.net-bs5";
import $ from "jquery";
import "select2";
import "select2/dist/css/select2.min.css";
import "datatables.net-bs5/css/dataTables.bootstrap5.min.css";
import "lightbox2/dist/css/lightbox.min.css";
import "lightbox2";
import "./RaiseComplaint.css";
import "./ServiceRequestQueryHistory.css";
import "./RaiseComplaintChecklist.css";
import { complaintAPI, masterDataAPI, workflowAPI, getApiBaseUrl, plantMasterAPI, rdcMasterAPI, userAPI } from "../services/api";
import { getUser } from "../utils/auth";
import {
  readComplaintsListState,
  markComplaintsListPendingRestore,
} from "../utils/complaintsListState";
import {
  readBcmServiceRequestListState,
  markBcmServiceRequestListPendingRestore,
} from "../utils/bcmServiceRequestListState";
import {
  readAdminServiceRequestListState,
  markAdminServiceRequestListPendingRestore,
  ADMIN_SERVICE_REQUESTS_FROM_LIST,
} from "../utils/adminServiceRequestListState";
import Swal from "sweetalert2";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import QuestionAnswerIcon from "@mui/icons-material/QuestionAnswer";
import ReplyIcon from "@mui/icons-material/Reply";
import CancelIcon from "@mui/icons-material/Cancel";
import SendIcon from "@mui/icons-material/Send";
import CheckIcon from "@mui/icons-material/Check";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonIcon from "@mui/icons-material/Person";
import PrintIcon from "@mui/icons-material/Print";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useSidebar } from "../contexts/SidebarContext";
import { hasUserType, isBcmUser, isChUserType, isDosAdminUser, isRcaUserType, isSeUserType, isZonalHeadUser, isZonalProductSpecialistUserType } from "../utils/auth";
import { escapeHtml, escapeHtmlAttr } from "../utils/xss";
import { htmlFromPrintResponse, openTrustedPrintHtml } from "../utils/printHtml";
import InvoiceUpload from "../components/InvoiceUpload";
import UploadedFileList from "../components/UploadedFileList";
import SrCategoryTabbedChecklistReadOnly from "../components/SrCategoryTabbedChecklistReadOnly";
import { getValueCaseInsensitive, looksLikeNumericChecklistAnswer } from "../utils/checklistSrCategoryUtils";
import { materialRowShowsComponentDetailsIcon, matchesSturdoBusinessVertical } from "../utils/componentDetailsBusinessVertical";
import CustomMultiSelect from "../components/common/CustomMultiSelect";
import { startViewServiceRequestPageLog } from "../utils/viewServiceRequestPageLog";
import { getApiErrorMessage } from "../utils/apiErrorMessage";

/** SR type code required for next-approver routing on Approve/Reject modal. */
const SE_APPROVE_ROUTING_SR_TYPE_CODE = "SRTYPE-002";
/** Installation SR type — Plant QC Assign flow (WIP + Domestic). */
const INSTALLATION_SR_TYPE_CODE = "SRTYPE-003";

function normalizeSeApproveRoutingCode(v) {
  return String(v == null ? "" : v)
    .trim()
    .toUpperCase();
}

/** View SR qty: integer (truncate), no round-up-to-integer rule. */
function formatViewSrQty(v) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return "0";
  return String(Math.floor(n));
}

/** View SR monetary values: 2 decimal places, no round-up-to-integer rule. */
function formatViewSrMoney(v) {
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return "0.00";
  return n.toFixed(2);
}

function toViewSrQtyInt(v) {
  return Math.max(0, Math.floor(Number(v) || 0));
}

function toViewSrMoneyNumber(v) {
  return parseFloat(formatViewSrMoney(v)) || 0;
}

function escapeHtmlForSwal(value) {
  return escapeHtml(value);
}

function buildViewSrHref(parentSrCode, childSrCode) {
  const parent = String(parentSrCode || "").trim();
  const child = String(childSrCode || "").trim();
  if (!parent) return "#";
  let href = `/complaints/${encodeURIComponent(parent)}`;
  if (child) {
    href += `?childSrCode=${encodeURIComponent(child)}`;
  }
  return href;
}

function getSrDisplayInfoForMessage(srData, childSrCodeFromQuery, id) {
  const parentSrCode = (srData?.srCode || srData?.SRCode || id || "").toString().trim();
  const childSrNumber = (
    srData?.childSRNumber ||
    srData?.ChildSRNumber ||
    childSrCodeFromQuery ||
    ""
  )
    .toString()
    .trim();
  const srNumberForMsg = childSrNumber || parentSrCode || "";
  const srViewHref = buildViewSrHref(parentSrCode, childSrNumber || null);
  return { srNumberForMsg, srViewHref };
}

function buildSrActionSuccessSwalHtml(srNumberForMsg, actionVerb, srViewHref, { linkify = true } = {}) {
  const srDisplay = String(srNumberForMsg || "").trim() || "-";
  const safeSr = escapeHtmlForSwal(srDisplay);
  const linkHtml =
    linkify && srDisplay !== "-"
      ? `<a href="${escapeHtmlForSwal(srViewHref)}" style="color:#007bff;text-decoration:underline;">${safeSr}</a>`
      : safeSr;
  return `<div style="font-size:calc(1em - 1px);">SR Number ${linkHtml} is ${actionVerb} successfully.</div>`;
}

/** Keep all approve qty/value aliases in sync (footer sums ApprvValue first). */
function syncMaterialRowApproveFields(rowData, qtyInt, valueNum) {
  if (!rowData) return;
  const q = Math.max(0, Math.floor(Number(qtyInt) || 0));
  const v = toViewSrMoneyNumber(valueNum);
  rowData.ApprovedQty = q;
  rowData.approvedQty = q;
  rowData.ApprovedQuantity = q;
  rowData.apprvQuantity = q;
  rowData.ApprvQuantity = q;
  rowData.ApprovedValue = v;
  rowData.approvedValue = v;
  rowData.ApprvValue = v;
  rowData.apprvValue = v;
}

/** Sum of line Approve Value amounts from material grid rows (same rules as footer). */
function computeMaterialRowsApprovedValueTotal(rows) {
  let approvedValueTotal = 0;
  (rows || []).forEach((p) => {
    const srQty =
      parseFloat(p?.ComplaintQty ?? p?.complaintQty ?? p?.SRQuantity ?? p?.srQuantity ?? 0) || 0;
    const price = parseFloat(p?.Price ?? p?.price ?? 0) || 0;
    const qtyRaw =
      parseFloat(
        p?.ApprovedQty ??
          p?.approvedQty ??
          p?.ApprovedQuantity ??
          p?.approvedQuantity ??
          p?.ApprvQuantity ??
          p?.apprvQuantity ??
          0
      ) || 0;
    const valRaw =
      parseFloat(
        p?.ApprvValue ?? p?.apprvValue ?? p?.ApprovedValue ?? p?.approvedValue ?? 0
      ) || 0;
    let qty = qtyRaw > 0 ? qtyRaw : price > 0 && valRaw > 0 ? valRaw / price : 0;
    if (srQty > 0 && qty > srQty) qty = srQty;
    const val = toViewSrMoneyNumber(valRaw > 0 ? valRaw : qty * price);
    approvedValueTotal += val;
  });
  return toViewSrMoneyNumber(approvedValueTotal);
}

/**
 * SR-level Final Approved Value Total (header / INV aggregate from sp_GetServiceRequestDetail).
 * Header is authoritative — do not max with journey rows (those are per-approver log values).
 */
function resolveFinalApprovedValueTotal(products, headerFallback) {
  if (headerFallback != null && headerFallback !== "") {
    const h = parseFloat(headerFallback);
    if (Number.isFinite(h)) return h;
  }
  if (Array.isArray(products)) {
    for (const p of products) {
      const raw = p?.FinalApprovedValue ?? p?.finalApprovedValue;
      if (raw != null && raw !== "") {
        const n = parseFloat(raw);
        if (Number.isFinite(n)) return n;
      }
    }
  }
  return 0;
}

/**
 * Amount in Approve/Reject modal: always show stored Final Approved Value when present.
 * If it equals Cal. Approved (line total), same number; if different, still Final Approved.
 */
function pickModalFinalApprovalAmount(lineApprovedTotal, headerFinalRaw) {
  const line = toViewSrMoneyNumber(lineApprovedTotal);
  const finalN = toViewSrMoneyNumber(headerFinalRaw);
  if (finalN > 0) {
    return formatViewSrMoney(finalN === line ? line : finalN);
  }
  return formatViewSrMoney(line);
}

/** Is RCA Required? (RCAGiven) / Remarks from header or first material line. */
function resolveRcaFromSr(sr, products) {
  const givenH = sr?.rcaGiven ?? sr?.RCAGiven ?? sr?.RcaGiven ?? "";
  const remarksH = sr?.rcaRemarks ?? sr?.RCARemarks ?? sr?.RcaRemarks ?? "";
  if (String(givenH).trim() || String(remarksH).trim()) {
    return { given: String(givenH).trim(), remarks: String(remarksH).trim() };
  }
  if (Array.isArray(products)) {
    for (const p of products) {
      const g = p?.RCAGiven ?? p?.rcaGiven ?? p?.RcaGiven ?? "";
      const r = p?.RCARemarks ?? p?.rcaRemarks ?? p?.RcaRemarks ?? "";
      if (String(g).trim() || String(r).trim()) {
        return { given: String(g).trim(), remarks: String(r).trim() };
      }
    }
  }
  return { given: "", remarks: "" };
}

function parseJourneyTruthyFlag(value) {
  if (value === true || value === 1) return true;
  const t = String(value ?? "").trim().toLowerCase();
  return t === "1" || t === "true" || t === "yes";
}

function parseJourneyDisplayDate(dateStr) {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  if (!s || s.toUpperCase() === "N/A") return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s.slice(0, 10));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const ddmmyyyy = s.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (ddmmyyyy) {
    const d = new Date(
      Number(ddmmyyyy[3]),
      Number(ddmmyyyy[2]) - 1,
      Number(ddmmyyyy[1])
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const months = {
    Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
    Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11,
  };
  const parts = s.split(/\s+/);
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const month = months[parts[1]];
    const year = parseInt(parts[2], 10);
    if (!Number.isNaN(day) && month !== undefined && !Number.isNaN(year)) {
      const d = new Date(year, month, day);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  const fallback = new Date(s);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

/** Strict: red only when SP returns IsSlaBreached = 1 (no client-side date math). */
function isJourneyStepSlaBreached(step) {
  return parseJourneyTruthyFlag(step?.isSlaBreached ?? step?.IsSlaBreached);
}

function formatJourneyDateLabel(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
}

function getJourneyDueDateLabel(step, previousStep = null) {
  const raw = step?.tatDueDate || step?.TATDueDate || "";
  if (raw) {
    const parsed = parseJourneyDisplayDate(raw);
    if (parsed) return formatJourneyDateLabel(parsed);
    return String(raw).trim();
  }
  if (!previousStep) return "";
  const fromDate = parseJourneyDisplayDate(previousStep?.date);
  if (!fromDate) return "";
  const limitRaw =
    step?.configTATDays ??
    step?.ConfigTATDays ??
    step?.tat ??
    step?.TATDays ??
    null;
  const limitDays = Number(limitRaw);
  if (!Number.isFinite(limitDays) || limitDays <= 0) return "";
  const due = new Date(fromDate);
  due.setDate(due.getDate() + limitDays);
  return formatJourneyDateLabel(due);
}

function buildJourneyStepSlaTooltip(step, previousStep = null) {
  const due = getJourneyDueDateLabel(step, previousStep);
  return due ? `SLA Breached (Due date: ${due})` : "SLA Breached";
}

function resolveJourneyIconTatDays(step) {
  const values = [
    step?.HistoryTATDays ?? step?.historyTATDays,
    step?.tat,
    step?.TATDays ?? step?.tatDays,
    step?.ConfigTATDays ?? step?.configTATDays,
  ];
  let fallback = null;
  for (const v of values) {
    if (v == null || v === "") continue;
    const n = Number(v);
    if (!Number.isFinite(n)) continue;
    if (fallback == null) fallback = n;
    if (n > 0) return String(n);
  }
  return fallback != null ? String(fallback) : "0";
}

function resolveJourneyIconActualTat(step) {
  const raw =
    step?.ActualTAT ??
    step?.actualTAT ??
    step?.actualTat ??
    step?.ActualTATDays ??
    step?.actualTATDays ??
    null;
  if (raw == null || raw === "" || String(raw).toUpperCase() === "N/A") return "0";
  return String(raw);
}

function buildJourneyStepIconAriaLabel(step, previousStep = null) {
  const tat = resolveJourneyIconTatDays(step);
  const actualTat = resolveJourneyIconActualTat(step);
  const slaTip = isJourneyStepSlaBreached(step)
    ? buildJourneyStepSlaTooltip(step, previousStep)
    : "";
  return slaTip
    ? `TAT: ${tat}. Actual TAT: ${actualTat}. ${slaTip}`
    : `TAT: ${tat}. Actual TAT: ${actualTat}`;
}

function readJourneyIconTipFromEl(el, tat, actualTat, slaTip) {
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return {
    left: rect.left + rect.width / 2,
    top: rect.top,
    tat,
    actualTat,
    slaTip: slaTip || "",
  };
}

function JourneyIconTatTooltip({ tip }) {
  if (!tip || typeof document === "undefined" || !document.body) return null;
  return createPortal(
    <div
      className={`journey-icon-tat-tooltip${tip.slaTip ? " is-breached" : ""}`}
      style={{ left: tip.left, top: tip.top }}
    >
      <div className="journey-icon-tat-tooltip-shine" />
      <div className="journey-icon-tat-tooltip-grid">
        <div className="journey-icon-tat-tooltip-col">
          <span className="journey-icon-tat-tooltip-label">TAT</span>
          <span className="journey-icon-tat-tooltip-value">{tip.tat}</span>
        </div>
        <div className="journey-icon-tat-tooltip-col">
          <span className="journey-icon-tat-tooltip-label">Actual TAT</span>
          <span className="journey-icon-tat-tooltip-value actual">{tip.actualTat}</span>
        </div>
      </div>
      {tip.slaTip ? <div className="journey-icon-tat-tooltip-note">{tip.slaTip}</div> : null}
    </div>,
    document.body
  );
}

/** Invoice lines from one getServiceRequestByCode response (avoids extra SP runs). */
function pickInvoiceDetailsFromFullDetail(fullDetail) {
  const raw = fullDetail?.invoiceDetails ?? fullDetail?.InvoiceDetails;
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === "object") {
    const nested = raw.items ?? raw.Items;
    if (Array.isArray(nested)) return nested;
  }
  return [];
}

/** Map SR attachments to invoice panel file lists. */
function mapSrAttachmentsForPanels(list, baseUrl) {
  if (!Array.isArray(list) || list.length === 0) {
    return { invoiceFiles: [], secondaryInvoiceFiles: [] };
  }
  const mapOne = (a) => {
    const filePath = (a.filePath || a.FilePath || "").replace(/^\/+/, "");
    const url = filePath && baseUrl ? `${baseUrl}/${filePath}` : "";
    return {
      id: a.slNo ?? a.SLNo ?? a.fileName ?? a.FileName,
      name: a.fileName || a.FileName || "Attachment",
      url,
      fileType: a.fileType || a.FileType,
      copyFor: a.copyFor || a.CopyFor,
    };
  };
  const copyForLower = (v) => (v || "").toLowerCase();
  return {
    invoiceFiles: list
      .filter((a) => copyForLower(a.copyFor || a.CopyFor).includes("invoice copy"))
      .map(mapOne),
    secondaryInvoiceFiles: list
      .filter((a) => copyForLower(a.copyFor || a.CopyFor).includes("secondary invoice"))
      .map(mapOne),
  };
}

/** Sum of line Approve Value amounts for Final approval Value default (modal items). */
function computeApproveItemsValueTotal(items) {
  const total = (items || []).reduce((sum, it) => {
    const v =
      parseFloat(it?.apprvValue ?? it?.ApprvValue ?? it?.approvedValue ?? it?.ApprovedValue ?? 0) ||
      0;
    return sum + toViewSrMoneyNumber(v);
  }, 0);
  return toViewSrMoneyNumber(total);
}

/** Sum of Invoice / Total Price for Mikasa Doors / Floors / Sturdo Final approval Value max. */
function computeApproveItemsTotalPriceMax(items) {
  const total = (items || []).reduce((sum, it) => {
    const direct = parseFloat(it?.totalPrice ?? it?.TotalPrice ?? 0);
    if (Number.isFinite(direct) && direct > 0) {
      return sum + toViewSrMoneyNumber(direct);
    }
    const qty =
      parseFloat(it?.invoiceQty ?? it?.InvoiceQty ?? it?.InvoiceQuantity ?? it?.invoiceQuantity ?? 0) ||
      0;
    const unit =
      parseFloat(
        it?.invoiceUnitPrice ?? it?.InvoiceUnitPrice ?? it?.Price ?? it?.price ?? 0
      ) || 0;
    const invVal = parseFloat(it?.InvoiceValue ?? it?.invoiceValue ?? 0) || 0;
    const line = invVal > 0 ? invVal : qty * unit;
    return sum + toViewSrMoneyNumber(line);
  }, 0);
  return toViewSrMoneyNumber(total);
}

function computeProductsInvoiceTotalPriceMax(products) {
  const total = (products || []).reduce((sum, p) => {
    const qty = parseInvoiceNumberLoose(pickInvoiceQuantityRaw(p));
    const lineVal = parseInvoiceNumberLoose(pickInvoiceValueRaw(p));
    const price = parseInvoiceNumberLoose(
      p?.Price ?? p?.price ?? p?.UnitPrice ?? p?.unitPrice ?? 0
    );
    return sum + toViewSrMoneyNumber(lineVal > 0 ? lineVal : qty * price);
  }, 0);
  return toViewSrMoneyNumber(total);
}

function getSeApproveRoutingMarketCode(sr) {
  if (!sr || typeof sr !== "object") return "";
  return normalizeSeApproveRoutingCode(
    sr.marketCode ?? sr.MarketCode ?? sr.MARKETCODE
  );
}

/**
 * Maps invoice MARKETCODE / name (e.g. Domestic, Export, M001, M002) to M001 / M002 for routing rules.
 * Matches SRINVOICEMASTER storing "Domestic" while USERMASTER uses M001.
 */
function getCanonicalSeApproveRoutingMarketKey(sr) {
  const code = getSeApproveRoutingMarketCode(sr);
  const name = normalizeSeApproveRoutingCode(
    sr?.marketName ?? sr?.MarketName ?? ""
  );
  if (code === "M001" || code === "DOMESTIC" || name === "DOMESTIC" || code.includes("DOMESTIC")) {
    return "M001";
  }
  if (code === "M002" || code === "EXPORT" || name === "EXPORT" || code.includes("EXPORT")) {
    return "M002";
  }
  if (code.includes("DOM") && !code.includes("EXP")) return "M001";
  if (code.includes("EXP")) return "M002";
  return "";
}

/** True when market is Domestic (M001). */
function isDomesticMarketSr(sr) {
  return getCanonicalSeApproveRoutingMarketKey(sr) === "M001";
}

/** True when any line / header SR Category is Product Quality (SRTYPE-001). */
function hasProductQualitySrCategory(sr, items = []) {
  const candidates = collectSrCategoryCandidates(sr, items);
  return candidates.some((v) => {
    const n = normalizeSeApproveRoutingCode(v);
    if (!n) return false;
    if (n === "SRTYPE-001" || n === "SRTYPE001") return true;
    return n.includes("PRODUCT") && n.includes("QUALITY");
  });
}

/** Collect SR category / type codes & names from header, products, and modal items. */
function collectSrCategoryCandidates(sr, items = []) {
  const candidates = [];
  if (sr && typeof sr === "object") {
    candidates.push(
      sr.srCategory,
      sr.SRCategory,
      sr.complaintTypeName,
      sr.ComplaintTypeName,
      sr.srTypeCode,
      sr.SRTypeCode,
      sr.SRTypeCODE,
      sr.srTypeDesc,
      sr.SRTypeDesc
    );
    const products = Array.isArray(sr.products) ? sr.products : [];
    products.forEach((p) => {
      candidates.push(
        p?.Category,
        p?.category,
        p?.SRCategory,
        p?.srCategory,
        p?.SRTypeDesc,
        p?.srTypeDesc,
        p?.SRTypeCode,
        p?.srTypeCode,
        p?.SRTYPECODE
      );
    });
  }
  (Array.isArray(items) ? items : []).forEach((it) => {
    candidates.push(
      it?.srCategory,
      it?.SRCategory,
      it?.category,
      it?.Category,
      it?.srTypeCode,
      it?.SRTypeCode,
      it?.SRTYPECODE
    );
  });
  return candidates;
}

/** True when SR Category is Packaging/Dispatch (SRTYPE-002). */
function hasPackagingDispatchSrCategory(sr, items = []) {
  const candidates = collectSrCategoryCandidates(sr, items);
  return candidates.some((v) => {
    const n = normalizeSeApproveRoutingCode(v);
    if (!n) return false;
    if (n === "SRTYPE-002" || n === "SRTYPE002") return true;
    return (
      (n.includes("PACKAGING") || n.includes("PACKING")) && n.includes("DISPATCH")
    );
  });
}

/** True when any header/line business vertical is Sturdo / Greenlam Cubicle (Sturdo). */
function hasSturdoBusinessVertical(sr, items = []) {
  const rows = [];
  if (sr && typeof sr === "object") {
    const headerBv = sr.businessVertical ?? sr.BusinessVertical ?? sr.productCategory ?? sr.ProductCategory;
    if (headerBv) rows.push({ businessVertical: headerBv });
    if (Array.isArray(sr.products)) rows.push(...sr.products);
    if (Array.isArray(sr.invoiceDetails)) rows.push(...sr.invoiceDetails);
  }
  if (Array.isArray(items)) rows.push(...items);
  return rows.some((row) => {
    if (!row || typeof row !== "object") return false;
    const bv =
      row.businessVertical ??
      row.BusinessVertical ??
      row.BVName ??
      row.bvName ??
      row.productCategory ??
      row.ProductCategory ??
      "";
    return matchesSturdoBusinessVertical(bv);
  });
}

/**
 * Domestic RCA carry-forward (same as Product Quality) also for:
 * - Packaging/Dispatch (SRTYPE-002)
 * - Sturdo business vertical
 */
function hasDomesticRcaEligibleSrCategory(sr, items = []) {
  return (
    hasProductQualitySrCategory(sr, items) ||
    hasPackagingDispatchSrCategory(sr, items) ||
    hasSturdoBusinessVertical(sr, items)
  );
}

/**
 * Carry-forward visit questions in Approve modal (read-only) when
 * Domestic + Product Quality for non–Zonal Product Specialist users
 * and answers already exist (same pattern as RCA summary).
 */
function shouldShowDomesticProductQualityVisitReadonly(sr, items = []) {
  if (isZonalProductSpecialistUserType()) return false;
  if (!isDomesticMarketSr(sr) || !hasProductQualitySrCategory(sr, items)) return false;
  const hasAnswers = [sr, ...(Array.isArray(items) ? items : [])].some((row) => {
    if (!row || typeof row !== "object") return false;
    return Boolean(
      String(row.physicalVisitRequired ?? row.PhysicalVisitRequired ?? "").trim() ||
        String(row.siteVisitTraveler ?? row.SiteVisitTraveler ?? "").trim() ||
        String(row.technicalVisitType ?? row.TechnicalVisitType ?? "").trim()
    );
  });
  return hasAnswers;
}

/**
 * Carry-forward Is RCA Required? + RCA Remarks in Approve modal (read-only) when
 * Domestic + (Product Quality | Packaging/Dispatch | Sturdo) for non–RCA users
 * and answers already exist (same pattern as visit questions).
 */
function shouldShowDomesticProductQualityRcaReadonly(sr, items = []) {
  if (isRcaUserType()) return false;
  if (!isDomesticMarketSr(sr) || !hasDomesticRcaEligibleSrCategory(sr, items)) return false;
  const hasAnswers = [sr, ...(Array.isArray(items) ? items : [])].some((row) => {
    if (!row || typeof row !== "object") return false;
    return Boolean(
      String(row.rcaGiven ?? row.RCAGiven ?? row.RcaGiven ?? "").trim() ||
        String(row.rcaRemarks ?? row.RCARemarks ?? row.RcaRemarks ?? "").trim()
    );
  });
  return hasAnswers;
}

/** Special Case = reopened SR (dashboard Special Case count uses IsReopened). */
function isSpecialCaseSr(sr) {
  if (!sr || typeof sr !== "object") return false;
  const v = sr.isReopened ?? sr.IsReopened;
  return v === true || v === 1 || String(v).trim() === "1" || String(v).toLowerCase() === "true";
}

function resolveDeductFromChKitty(sr, items = []) {
  const fromHeader = String(
    sr?.deductFromChKitty ?? sr?.DeductFromChKitty ?? ""
  ).trim();
  if (fromHeader) return fromHeader;
  for (const row of Array.isArray(items) ? items : []) {
    const v = String(
      row?.deductFromChKitty ?? row?.DeductFromChKitty ?? ""
    ).trim();
    if (v) return v;
  }
  return "";
}

/** CH Kitty Yes/No editable for UT0000009 on Reopen or Special Case Approve. */
function shouldEditChKittyField(sr, canReopen) {
  if (!isChUserType()) return false;
  return Boolean(canReopen) || isSpecialCaseSr(sr);
}

/** Carry-forward CH Kitty on Close/Approve modal (read-only) when already answered. */
function shouldShowChKittyReadonly(sr, items = []) {
  if (isChUserType() && (isSpecialCaseSr(sr) || resolveDeductFromChKitty(sr, items))) {
    // Editable path handles CH users on reopen/special-case approve.
    return false;
  }
  return Boolean(resolveDeductFromChKitty(sr, items));
}

/** SR Flow dropdown options by market (Approve/Reject modal). */
const SE_APPROVE_SR_FLOW_DOMESTIC = [
  { id: "Plant", text: "Plant" },
  { id: "RDC", text: "RDC" },
  { id: "BCM", text: "BCM" },
];

const SE_APPROVE_SR_FLOW_EXPORT = [
  { id: "Plant", text: "Plant" },
  { id: "CPD", text: "CPD" },
];

function getSeApproveSrFlowOptions(sr) {
  const mKey = getCanonicalSeApproveRoutingMarketKey(sr);
  if (mKey === "M002") return SE_APPROVE_SR_FLOW_EXPORT;
  if (mKey === "M001") return SE_APPROVE_SR_FLOW_DOMESTIC;
  return [
    { id: "Plant", text: "Plant" },
    { id: "RDC", text: "RDC" },
    { id: "BCM", text: "BCM" },
    { id: "CPD", text: "CPD" },
  ];
}

/** Prefer header SR type; otherwise first material line. */
function getSeApproveRoutingSrTypeCode(sr) {
  if (!sr || typeof sr !== "object") return "";
  const fromHeader = normalizeSeApproveRoutingCode(
    sr.srTypeCode ?? sr.SRTypeCode ?? sr.SRTypeCODE ?? ""
  );
  if (fromHeader) return fromHeader;
  const p0 = Array.isArray(sr.products) ? sr.products[0] : null;
  if (!p0) return "";
  return normalizeSeApproveRoutingCode(
    p0.SRTypeCode ?? p0.srTypeCode ?? p0.SRTYPECODE
  );
}

/**
 * Next approver routing dropdowns (SR Flow + Role) for:
 * - Domestic Packaging/Dispatch (SRTYPE-002 / M001) + DOS Admin (UT0000024)
 * - Export Packaging/Dispatch (SRTYPE-002 / M002) + UT0000004
 * - Domestic Installation (SRTYPE-003 / M001) + Plant QC (UT0000006) + WIP
 * API SeApproveRoutingEligible also enables when set.
 */
function isPackagingDispatchSrType(sr) {
  const wantType = normalizeSeApproveRoutingCode(SE_APPROVE_ROUTING_SR_TYPE_CODE);
  if (getSeApproveRoutingSrTypeCode(sr) === wantType) return true;
  const nameCandidates = [
    sr?.complaintTypeName,
    sr?.ComplaintTypeName,
    sr?.srCategory,
    sr?.SRCategory,
    sr?.srTypeDesc,
    sr?.SRTypeDesc,
  ];
  const products = Array.isArray(sr?.products) ? sr.products : [];
  products.forEach((p) => {
    nameCandidates.push(
      p?.Category,
      p?.category,
      p?.SRCategory,
      p?.srCategory,
      p?.SRTypeDesc,
      p?.srTypeDesc
    );
  });
  return nameCandidates.some((v) => {
    const n = normalizeSeApproveRoutingCode(v);
    return n.includes("PACKAGING") && n.includes("DISPATCH");
  });
}

function isInstallationSrType(sr) {
  const wantType = normalizeSeApproveRoutingCode(INSTALLATION_SR_TYPE_CODE);
  if (getSeApproveRoutingSrTypeCode(sr) === wantType) return true;
  const nameCandidates = [
    sr?.complaintTypeName,
    sr?.ComplaintTypeName,
    sr?.srCategory,
    sr?.SRCategory,
    sr?.srTypeDesc,
    sr?.SRTypeDesc,
  ];
  const products = Array.isArray(sr?.products) ? sr.products : [];
  products.forEach((p) => {
    nameCandidates.push(
      p?.Category,
      p?.category,
      p?.SRCategory,
      p?.srCategory,
      p?.SRTypeDesc,
      p?.srTypeDesc
    );
  });
  return nameCandidates.some((v) => {
    const n = normalizeSeApproveRoutingCode(v);
    return n === "INSTALLATION" || n.includes("INSTALLATION");
  });
}

function isSrWipStatusValue(sr) {
  if (!sr || typeof sr !== "object") return false;
  const byName =
    String(sr.statusName ?? sr.StatusName ?? "")
      .trim()
      .toLowerCase() === "wip";
  const byCode =
    String(sr.srStatusCODE ?? sr.SRStatusCODE ?? "")
      .trim()
      .toLowerCase() === "wip";
  return byName || byCode;
}

/** Plant QC Assign: Domestic Installation + WIP (UI only; no SP change). */
function isPlantQcDomesticInstallationWipAssign(sr) {
  if (!sr || typeof sr !== "object") return false;
  if (!hasUserType("UT0000006")) return false;
  if (!isDomesticMarketSr(sr)) return false;
  if (!isInstallationSrType(sr)) return false;
  return isSrWipStatusValue(sr);
}

function shouldShowSeApproveRoutingControls(sr) {
  if (!sr || typeof sr !== "object") return false;
  const apiFlag =
    sr.seApproveRoutingEligible === true ||
    sr.SeApproveRoutingEligible === true ||
    sr.seApproveRoutingEligible === 1 ||
    sr.SeApproveRoutingEligible === 1;
  if (apiFlag) return true;

  if (isPackagingDispatchSrType(sr)) {
    const mKey = getCanonicalSeApproveRoutingMarketKey(sr);
    const isDosAdmin = isDosAdminUser() || hasUserType("UT0000024");
    if ((mKey === "M001" || !mKey) && isDosAdmin) return true;
    if (mKey === "M002" && (isZonalHeadUser() || hasUserType("UT0000004"))) return true;
  }

  if (isPlantQcDomesticInstallationWipAssign(sr)) return true;

  return false;
}

/** SR Flow types that show Role + scoped User dropdowns. */
function isSeApproveScopedSpec(spec) {
  const s = canonicalSeUserSpecificationType(spec);
  return s === "Plant" || s === "RDC" || s === "BCM" || s === "CPD";
}

/** Role options allowed per SR Flow (Approve/Reject modal). */
const SE_APPROVE_SR_FLOW_ROLE_BY_SPEC = {
  Plant: {
    // Fallback when market unknown — prefer market-specific byMarket below.
    codes: ["UT0000015", "UT0000032"],
    names: ["Plant Dispatch", "Export Plant Dispatch"],
    // Domestic → Plant Dispatch only; Export → Export Plant Dispatch only.
    byMarket: {
      M001: { codes: ["UT0000015"], names: ["Plant Dispatch"] },
      M002: { codes: ["UT0000032"], names: ["Export Plant Dispatch"] },
    },
  },
  RDC: { codes: ["UT0000014"], names: ["RDC Incharge"] },
  BCM: { codes: ["UT0000002"], names: ["BCM", "Branch Commercial Manager"] },
  CPD: {
    codes: ["UT0000022"],
    names: ["Logistics Head"],
    byMarket: {
      M002: { codes: ["UT0000022"], names: ["Logistics Head"] },
    },
  },
};

function filterSeApproveRoutingRolesBySrFlow(spec, roles, marketKey = "") {
  const s = canonicalSeUserSpecificationType(spec);
  const base = SE_APPROVE_SR_FLOW_ROLE_BY_SPEC[s];
  if (!base) return Array.isArray(roles) ? roles : [];
  const mKey = String(marketKey || "").trim().toUpperCase();
  const cfg =
    base.byMarket && mKey && base.byMarket[mKey] ? base.byMarket[mKey] : base;
  const codeSet = new Set((cfg.codes || []).map((c) => String(c).trim().toUpperCase()).filter(Boolean));
  const nameNeedles = (cfg.names || [])
    .map((n) => String(n).trim().toLowerCase())
    .filter(Boolean);
  return (Array.isArray(roles) ? roles : []).filter((r) => {
    const code = String(r.userTypeCode || "").trim().toUpperCase();
    const name = String(r.userTypeName || "").trim().toLowerCase();
    if (code && codeSet.has(code)) return true;
    if (!name) return false;
    // Exact name only — do not use includes() ("Plant Dispatch" must not match "Export Plant Dispatch").
    return nameNeedles.some((needle) => name === needle);
  });
}

/** RDC / BCM / CPD use RdcIds + allowedScopeCodes (same API shape). */
function isSeApproveRdcScopedSpec(spec) {
  const s = canonicalSeUserSpecificationType(spec);
  return s === "RDC" || s === "BCM" || s === "CPD";
}

/** User dropdown placeholder by SR Flow specification. */
function getSeApproveRoutingUserSelectPlaceholder(spec) {
  const s = canonicalSeUserSpecificationType(spec);
  if (s === "Plant") return "Select Plant user";
  if (s === "RDC") return "Select RDC user";
  if (s === "BCM") return "Select BCM user";
  if (s === "CPD") return "Select CPD user";
  if (s === "General") return "Select user (general flow)";
  return "Select user";
}

function getSeApproveRoutingUserRequiredMessage(spec) {
  const s = canonicalSeUserSpecificationType(spec);
  if (s === "Plant") return "Please select a Plant user for the chosen SR Flow.";
  if (s === "RDC") return "Please select an RDC user for the chosen SR Flow.";
  if (s === "BCM") return "Please select a BCM user for the chosen SR Flow.";
  if (s === "CPD") return "Please select a CPD user for the chosen SR Flow.";
  if (s === "General") return "Please select a user for the chosen SR Flow.";
  return "Please select a user for the chosen SR Flow.";
}

/** Display label for SE approve routing user dropdown (keeps "Name (Plant Name)" from SP; strips legacy " (UserId)" suffix only). */
function seApproveRoutingUserDisplayName(displayText, userIdCode) {
  const text = String(displayText ?? "").trim();
  const id = String(userIdCode ?? "").trim();
  if (!text) return id;
  if (id) {
    const suffix = ` (${id})`;
    if (text.endsWith(suffix)) return text.slice(0, -suffix.length).trim();
  }
  return text;
}

/** Prototype / fallback plant names in approve grid when API has no plant — exclude from SR-based plant filtering. */
const SE_APPROVE_ROUTING_DUMMY_PLANT_NAMES = new Set([
  "DEFAULT PLANT",
  "PLANT A",
  "PLANT B",
  "MANUFACTURING PLANT 1",
  "PRODUCTION FACILITY",
]);

function normalizeSeApprovePlantLabel(v) {
  return String(v == null ? "" : v)
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
}

/**
 * Resolve one SR/invoice plant label to a PlantMaster PlantCode (exact or prefix match).
 * SAP ProductPlantDescription is often "BEHROR LAMINATE" while master PlantName is "BEHROR".
 */
function resolveSeApprovePlantCodeFromLabel(label, plants) {
  const pn = normalizeSeApprovePlantLabel(label);
  if (!pn || SE_APPROVE_ROUTING_DUMMY_PLANT_NAMES.has(pn)) return null;

  const list = Array.isArray(plants) ? plants : [];
  let bestCode = null;
  let bestNameLen = 0;

  for (const p of list) {
    const code = String(p.plantCode ?? p.PlantCode ?? "").trim();
    const masterName = normalizeSeApprovePlantLabel(p.plantName ?? p.PlantName ?? "");
    if (!code || !masterName) continue;

    if (pn === masterName) return code;

    const matchesPrefix =
      pn.startsWith(`${masterName} `) ||
      pn.startsWith(masterName) ||
      pn.split(" ")[0] === masterName;
    if (matchesPrefix && masterName.length > bestNameLen) {
      bestCode = code;
      bestNameLen = masterName.length;
    }
  }

  return bestCode;
}

/**
 * Map approve-modal line items to PlantMaster plant codes (PlantCode on row, SAP plant id, or name match).
 */
function getSeApprovePlantCodesFromApproveModalItems(items, plants) {
  const list = Array.isArray(plants) ? plants : [];
  const codes = new Set();
  const codeSet = new Set(
    list.map((p) => String(p.plantCode ?? p.PlantCode ?? "").trim()).filter(Boolean)
  );
  const byPlantId = new Map();
  for (const p of list) {
    const code = String(p.plantCode ?? p.PlantCode ?? "").trim();
    const pid = String(p.plantId ?? p.PlantId ?? p.PLANTID ?? "").trim();
    if (pid && code) byPlantId.set(pid, code);
  }

  for (const it of items || []) {
    const direct = String(it.plantCode ?? it.PlantCode ?? "").trim();
    if (direct && codeSet.has(direct)) {
      codes.add(direct);
      continue;
    }
    const sapPlantId = String(
      it.productPlantId ?? it.ProductPlantId ?? it.plantId ?? it.PlantId ?? ""
    ).trim();
    if (sapPlantId && byPlantId.has(sapPlantId)) {
      codes.add(byPlantId.get(sapPlantId));
      continue;
    }
    const label =
      it.plantName ??
      it.PlantName ??
      it.productPlantDescription ??
      it.ProductPlantDescription ??
      "";
    const hit = resolveSeApprovePlantCodeFromLabel(label, list);
    if (hit) codes.add(hit);
  }
  return [...codes];
}

/** Distinct PlantMaster plant codes for current approve-modal items (for approve/reject SP). */
function buildApproveRejectPlantCodesCsv(items, plants) {
  const codes = getSeApprovePlantCodesFromApproveModalItems(items || [], plants || []);
  return codes.length > 0 ? codes.join(",") : null;
}

function normalizeSeApproveRdcLabel(v) {
  return String(v == null ? "" : v)
    .trim()
    .replace(/\s+/g, " ")
    .toUpperCase();
}

/** Map approve-modal line items to RDCMaster codes using RdcCode on the row or RDC name match. */
function getSeApproveRdcCodesFromApproveModalItems(items, rdcs) {
  const list = Array.isArray(rdcs) ? rdcs : [];
  const codes = new Set();
  const byName = new Map();
  for (const r of list) {
    const code = String(r.rdcCode ?? r.RdcCode ?? r.RDCCODE ?? "").trim();
    const name = normalizeSeApproveRdcLabel(r.rdcName ?? r.RdcName ?? r.RDCNAME ?? "");
    if (name && code) byName.set(name, code);
  }
  for (const it of items || []) {
    const direct = String(it.rdcCode ?? it.RdcCode ?? "").trim();
    if (direct && list.some((x) => String(x.rdcCode ?? x.RdcCode ?? x.RDCCODE ?? "").trim() === direct)) {
      codes.add(direct);
      continue;
    }
    const rn = normalizeSeApproveRdcLabel(it.rdcName ?? it.RdcName ?? "");
    if (!rn) continue;
    const hit = byName.get(rn);
    if (hit) codes.add(hit);
  }
  return [...codes];
}

/**
 * Must match APPROVALWORKFLOWS.WorkflowSource JSON for SQL:
 * JSON_VALUE(WorkflowSource, '$.userSpecificationType') = @UserSpecificationType (exact).
 */
function canonicalSeUserSpecificationType(raw) {
  const s = String(raw == null ? "" : raw).trim();
  if (!s) return "None";
  const u = s.toUpperCase();
  if (u === "NONE") return "None";
  if (u === "PLANT") return "Plant";
  if (u === "RDC") return "RDC";
  if (u === "BCM") return "BCM";
  if (u === "CPD") return "CPD";
  if (u === "GENERAL") return "General";
  if (u === "LOGHEAD") return "CPD";
  return "None";
}

/** Unwrap ApiResponse payload to a plain array. */
function parseSeApproveApiList(res) {
  const payload = res?.data ?? res?.Data ?? res;
  const inner = payload?.data ?? payload?.Data ?? payload;
  return Array.isArray(inner) ? inner : [];
}

function parseSeApproveRoutingRoleList(res) {
  return parseSeApproveApiList(res)
    .map((x) => {
      if (typeof x === "string") {
        const c = x.trim();
        return c ? { userTypeCode: c, userTypeName: c } : null;
      }
      const userTypeCode = String(x?.userTypeCode ?? x?.UserTypeCode ?? "").trim();
      const userTypeName = String(
        x?.userTypeName ?? x?.UserTypeName ?? userTypeCode
      ).trim();
      return userTypeCode ? { userTypeCode, userTypeName } : null;
    })
    .filter(Boolean);
}

/**
 * Legacy workflow endpoints use /api/workflow/complaint/{complaintId}/… with one path segment.
 * View SR routes use :id = ParentSRCode (e.g. c_7/D/0001); slashes break routing → 404 if passed raw.
 */
function resolveLegacyWorkflowComplaintId(header, routeId) {
  const h = header && typeof header === "object" ? header : {};
  const candidates = [
    h.complaintId,
    h.ComplaintId,
    h.legacyComplaintId,
    h.LegacyComplaintId,
  ];
  for (const c of candidates) {
    if (c == null || c === "") continue;
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  const rid = String(routeId ?? "").trim();
  if (/^\d+$/.test(rid)) {
    const n = Number(rid);
    if (n > 0) return n;
  }
  return null;
}

/** Parse invoice/API numbers that may include thousands separators or unusual keys */
function parseInvoiceNumberLoose(value) {
  if (value == null || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const s = String(value).replace(/,/g, "").replace(/\s+/g, "").trim();
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : 0;
}

function pickInvoiceQuantityRaw(row) {
  return (
    row?.InvoiceQuantity ??
    row?.invoiceQuantity ??
    row?.Quantity ??
    row?.quantity ??
    row?.Qty ??
    row?.qty ??
    row?.BilledQty ??
    row?.billedQty ??
    row?.BillQuantity ??
    row?.billQuantity ??
    0
  );
}

function pickInvoiceValueRaw(row) {
  return (
    row?.InvoiceValue ??
    row?.invoiceValue ??
    row?.LineAmount ??
    row?.lineAmount ??
    row?.LineTotal ??
    row?.lineTotal ??
    row?.Amount ??
    row?.amount ??
    row?.TotalAmount ??
    row?.totalAmount ??
    0
  );
}

/**
 * ViewApproverServiceRequest - View-only page for approvers to view Service Request details
 * This page displays all SR information filled by SE in read-only mode.
 * Sections displayed:
 * - Basic Information (SR Number, Status, Category, Created Date, Raised By, Assigned To)
 * - Invoice Details
 * - Customer Details (including End Consumer, if B2C)
 * - Product / Material Details
 * - Complaint Quantity & Value Details
 * - Sample Information (if applicable)
 * - Checklist Responses (product-wise)
 * - Uploaded Attachments / Images
 * - Previous Complaints (if exists)
 * - Approval History & Workflow Journey
 * - Query History (threaded view, read-only)
 * - Audit Trail (read-only)
 */
const ViewApproverServiceRequest = () => {
  const { id: routeId } = useParams();
  const [searchParams] = useSearchParams();
  const id = routeId ? decodeURIComponent(routeId) : "";
  const childSrCodeFromQuery = searchParams.get("childSrCode")?.trim() || "";
  const navigate = useNavigate();
  const navigateBackToComplaintsList = () => {
    const fromList = searchParams.get("fromList");
    const cameFromBcm =
      fromList === "bcm" || Boolean(readBcmServiceRequestListState()?.pendingRestore);
    const cameFromAdminServiceRequests =
      fromList === ADMIN_SERVICE_REQUESTS_FROM_LIST ||
      Boolean(readAdminServiceRequestListState()?.pendingRestore);

    if (cameFromBcm) {
      markBcmServiceRequestListPendingRestore();
      const bcmSaved = readBcmServiceRequestListState();
      navigate(bcmSaved?.returnPath || "/bcm-service-requests", {
        state: { restoreBcmServiceRequestList: true },
      });
      return;
    }

    if (cameFromAdminServiceRequests) {
      markAdminServiceRequestListPendingRestore();
      const adminSaved = readAdminServiceRequestListState();
      navigate(adminSaved?.returnPath || "/admin-service-requests", {
        state: { restoreAdminServiceRequestList: true },
      });
      return;
    }

    markComplaintsListPendingRestore();
    const saved = readComplaintsListState();
    navigate(saved?.returnPath || "/complaints", {
      state: { restoreComplaintsList: true },
    });
  };
  const { collapseSidebar, restoreSidebar } = useSidebar();
  const currentUserIdCode = (() => {
    try {
      const user = getUser();
      return user?.userIdCode || user?.UserIdCode || user?.userId || user?.UserId || user?.userCode || user?.UserCode || user?.email || user?.id || "";
    } catch {
      return "";
    }
  })();

  // Must match NextApprover / @ChangedBy in sp_GetServiceRequestDetail (HasClose) and approve/reject APIs.
  // Include PascalCase fields — login payloads often use UserIdCode only, which the old parse missed.
  const changedByForAction = (() => {
    try {
      const user = getUser() || {};
      return (
        user.userIdCode ||
        user.UserIdCode ||
        user.userCode ||
        user.UserCode ||
        user.email ||
        user.Email ||
        user.id ||
        user.Id ||
        ""
      );
    } catch {
      return "";
    }
  })();

  // (Note: we intentionally use `changedByForAction` below, because it must match
  // the same identifier sent to the backend approve/reject API.)
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [printDownloading, setPrintDownloading] = useState(false);
  const [printProgress, setPrintProgress] = useState(null); // percent (0-100) or null when unknown
  const [printMenuOpen, setPrintMenuOpen] = useState(false);
  const [srData, setSrData] = useState(null);
  const [journeyIconTip, setJourneyIconTip] = useState(null);
  /** After successful WIP Close, hide Close until navigating to another SR. */
  const [wipCloseLocallyCompleted, setWipCloseLocallyCompleted] = useState(false);
  const [invoiceLoaded, setInvoiceLoaded] = useState(false);
  const [attachmentsLoaded, setAttachmentsLoaded] = useState(false);
  const productTableRef = useRef(null);
  const dataTableInstanceRef = useRef(null);
  const materialApproveFooterLastRef = useRef({ qty: null, value: null });
  const materialApproveFooterRecalcRafRef = useRef(null);
  const finalApprovedModalSyncTimerRef = useRef(null);
  const journeyStatusTableRef = useRef(null);
  const journeyStatusDataTableRef = useRef(null);
  const productDataRef = useRef({});
  const historyTableRef = useRef(null);
  const historyDataTableInstanceRef = useRef(null);
  const [showEndCustomer, setShowEndCustomer] = useState(false);
  const isMaterialApproveFieldFocused = () => {
    const el = document.activeElement;
    return (
      el &&
      el.classList &&
      el.classList.contains("approve-qty-input")
    );
  };

  const syncApproveValueCellDisplay = ($row, valueText) => {
    const $cell = $row.find("td.approve-value-cell");
    if ($cell.length > 0) {
      $cell.text(valueText);
    }
  };

  const recalcApproveFooterTotals = () => {
    try {
      if (!productTableRef.current || !dataTableInstanceRef.current || !$.fn || !$.fn.DataTable) {
        return;
      }
      const api = dataTableInstanceRef.current;
      if (!api || !api.rows) return;
      const rows = api.rows({ filter: "applied" }).data().toArray();
      let approvedQtyTotal = 0;
      rows.forEach((p) => {
        const srQty = parseFloat(p?.ComplaintQty ?? p?.complaintQty ?? p?.SRQuantity ?? p?.srQuantity ?? 0) || 0;
        const price = parseFloat(p?.Price ?? p?.price ?? 0) || 0;
        const qtyRaw =
          parseFloat(
            p?.ApprovedQty ??
              p?.approvedQty ??
              p?.ApprovedQuantity ??
              p?.approvedQuantity ??
              p?.ApprvQuantity ??
              p?.apprvQuantity ??
              0
          ) || 0;
        const valRaw =
          parseFloat(
            p?.ApprvValue ?? p?.apprvValue ?? p?.ApprovedValue ?? p?.approvedValue ?? 0
          ) || 0;
        let qty = qtyRaw > 0 ? qtyRaw : price > 0 && valRaw > 0 ? valRaw / price : 0;
        if (srQty > 0 && qty > srQty) qty = srQty;
        approvedQtyTotal += qty;
      });
      const approvedValueTotal = computeMaterialRowsApprovedValueTotal(rows);
      const $wrapper = $(productTableRef.current).closest(
        ".dataTables_wrapper, .dt-container"
      );
      const qtyText = formatViewSrQty(approvedQtyTotal);
      const valText = formatViewSrMoney(approvedValueTotal);
      const last = materialApproveFooterLastRef.current;
      if (last.qty === qtyText && last.value === valText) {
        return;
      }
      materialApproveFooterLastRef.current = { qty: qtyText, value: valText };
      // Debounce modal field sync — avoid full-page React re-render on every qty keystroke (jank).
      if (approveRejectOpenRef.current && !finalApprovedValueTouchedRef.current) {
        if (finalApprovedModalSyncTimerRef.current) {
          clearTimeout(finalApprovedModalSyncTimerRef.current);
        }
        finalApprovedModalSyncTimerRef.current = setTimeout(() => {
          finalApprovedModalSyncTimerRef.current = null;
          const syncedVal = materialApproveFooterLastRef.current.value;
          if (syncedVal == null) return;
          setApproveData((prev) => {
            if (prev.finalApprovedValue === syncedVal) return prev;
            const headerFinal = resolveFinalApprovedValueTotal(
              srData?.products,
              srData?.finalApprovedValue ?? srData?.FinalApprovedValue
            );
            const headerN = toViewSrMoneyNumber(headerFinal);
            const lineN = toViewSrMoneyNumber(syncedVal);
            if (headerN > 0 && headerN !== lineN) return prev;
            return { ...prev, finalApprovedValue: syncedVal };
          });
        }, 350);
      }
      // scrollX clones tfoot into .dt-scroll-footInner — update only the visible clone (not source <table> tfoot).
      const $scrollFoot = $wrapper.find(".dt-scroll-footInner");
      if ($scrollFoot.length) {
        $scrollFoot.find("td.foot-approved-qty").text(qtyText);
        $scrollFoot.find("td.foot-approved-value").text(valText);
      } else if ($wrapper.length) {
        $wrapper.find("td.foot-approved-qty").text(qtyText);
        $wrapper.find("td.foot-approved-value").text(valText);
      } else {
        $(productTableRef.current)
          .find("td.foot-approved-qty")
          .text(qtyText);
        $(productTableRef.current)
          .find("td.foot-approved-value")
          .text(valText);
      }
    } catch (e) {
      console.error("Error recalculating approve footer totals:", e);
    }
  };

  const scheduleRecalcApproveFooterTotals = () => {
    if (materialApproveFooterRecalcRafRef.current != null) {
      cancelAnimationFrame(materialApproveFooterRecalcRafRef.current);
    }
    materialApproveFooterRecalcRafRef.current = requestAnimationFrame(() => {
      materialApproveFooterRecalcRafRef.current = null;
      recalcApproveFooterTotals();
    });
  };

  const syncMaterialGridAfterApproveEdit = () => {
    recalcApproveFooterTotals();
    requestAnimationFrame(() => {
      if (productTableRef.current) {
        const $wrapper = $(productTableRef.current).closest(
          ".dataTables_wrapper, .dt-container"
        );
        const $sourceTfoot = $(productTableRef.current).children("tfoot");
        if ($wrapper.find(".dt-scroll-footInner").length && $sourceTfoot.length) {
          $sourceTfoot.css("display", "none");
        }
      }
      syncScrollHeadWidth(productTableRef);
    });
  };
  
  // Previous Complaints States
  const [previousComplaintsCount, setPreviousComplaintsCount] = useState(0);
  const [previousComplaintsData, setPreviousComplaintsData] = useState([]);
  const [showPreviousComplaintsTable, setShowPreviousComplaintsTable] = useState(false);
  const [showPreviousComplaintsDetailModal, setShowPreviousComplaintsDetailModal] = useState(false);
  const [selectedComplaintIndex, setSelectedComplaintIndex] = useState(null);
  
  // Product Details Modal States
  const [productDetailsOpen, setProductDetailsOpen] = useState(false);
  const [selectedProductData, setSelectedProductData] = useState(null);
  const productDetailsModalRef = useRef(null);
  const prevProductDetailsOpenRef = useRef(productDetailsOpen);

  // Component Details Modal States
  const [componentDetailsOpen, setComponentDetailsOpen] = useState(false);
  const [selectedComponentProductKey, setSelectedComponentProductKey] = useState(null);
  const componentDetailsModalRef = useRef(null);
  const componentDetailsRef = useRef({});
  const prevComponentDetailsOpenRef = useRef(componentDetailsOpen);
  const [componentDetailsData, setComponentDetailsData] = useState({
    materialGroupDesc: "",
    componentCategory: "",
    componentList: "",
    componentQty: "",
    componentIssue: "",
    doorMaterialCode: "",
    componentIssueRemarks: "",
  });
  /** Multi-row Component Details from Greenlam.SRCOMPONENTDETAILS (view popup). */
  const [componentDetailsRowsList, setComponentDetailsRowsList] = useState([]);
  const componentDetailsRowsListRef = useRef([]);
  /** Survives DataTable row copies — keyed by MaterialRowKey / productKey. */
  const componentDetailsByRowKeyRef = useRef({});
  const [componentDetailsSaving, setComponentDetailsSaving] = useState(false);
  useEffect(() => {
    componentDetailsRowsListRef.current = componentDetailsRowsList;
  }, [componentDetailsRowsList]);

  const parseComponentDetailsArray = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : parsed ? [parsed] : [];
      } catch {
        return [];
      }
    }
    // JsonElement-like / object wrappers
    if (typeof raw === "object") {
      if (Array.isArray(raw.value)) return raw.value;
      if (Array.isArray(raw.Value)) return raw.Value;
      return [raw];
    }
    return [];
  };

  const mapViewComponentDetailRow = (row, index = 0) => ({
    _rowId: row?._rowId || row?.rowId || `view_comp_${index}`,
    materialGroupDesc:
      row?.MaterialGroupDesc ?? row?.materialGroupDesc ?? "",
    componentCategory:
      row?.ComponentCategory ?? row?.componentCategory ?? "",
    componentList: row?.ComponentList ?? row?.componentList ?? "",
    componentQty:
      row?.ComponentQty != null && row?.ComponentQty !== ""
        ? String(row.ComponentQty)
        : row?.componentQty != null && row?.componentQty !== ""
          ? String(row.componentQty)
          : "",
    componentIssue: row?.ComponentIssue ?? row?.componentIssue ?? "",
    doorMaterialCode:
      row?.DoorMaterialCode ?? row?.doorMaterialCode ?? "",
    componentIssueRemarks:
      row?.ComponentIssueRemarks ?? row?.componentIssueRemarks ?? "",
    componentValue: row?.ComponentValue ?? row?.componentValue ?? null,
  });

  /** Invoice unit price (2 dp) for Component Value = qty × unit price. */
  const getInvoiceUnitPriceForViewProduct = (product) => {
    if (!product) return 0;
    const direct = parseFloat(
      product?.Price ??
        product?.price ??
        product?.invoiceUnitPrice ??
        product?.InvoiceUnitPrice ??
        0
    );
    if (Number.isFinite(direct) && direct > 0) return Math.round(direct * 100) / 100;
    const qty = parseFloat(
      product?.Quantity ??
        product?.quantity ??
        product?.InvoiceQuantity ??
        product?.invoiceQuantity ??
        0
    );
    const val = parseFloat(
      product?.InvoiceValue ??
        product?.invoiceValue ??
        product?.TotalPrice ??
        product?.totalPrice ??
        0
    );
    if (Number.isFinite(qty) && qty > 0 && Number.isFinite(val) && val > 0) {
      return Math.round((val / qty) * 100) / 100;
    }
    return 0;
  };

  const computeViewComponentRowValue = (productKey, compQty) => {
    const product =
      resolveProductDataForComponentDetails(productKey) ||
      productDataRef.current?.[productKey] ||
      {};
    const qtyNum = parseFloat(compQty) || 0;
    const unit = getInvoiceUnitPriceForViewProduct(product);
    return Math.round(qtyNum * unit * 100) / 100;
  };

  /** Plant QC (UT0000006) may edit Component Qty and Save. */
  const canPlantQcEditComponentQty = isRcaUserType();

  const buildViewComponentDetailsRows = (productData) => {
    const sources = [
      productData?.ComponentDetails,
      productData?.componentDetails,
      productData?.ComponentDetailsJson,
      productData?.componentDetailsJson,
    ];
    for (const src of sources) {
      const apiRows = parseComponentDetailsArray(src);
      if (apiRows.length) {
        return apiRows.map((r, i) => mapViewComponentDetailRow(r, i));
      }
    }
    const legacy = mapViewComponentDetailRow(productData || {}, 0);
    const hasAny = [
      legacy.materialGroupDesc,
      legacy.componentCategory,
      legacy.componentList,
      legacy.componentQty,
      legacy.componentIssue,
      legacy.doorMaterialCode,
      legacy.componentIssueRemarks,
    ].some((v) => String(v || "").trim() !== "");
    return hasAny ? [legacy] : [];
  };

  const resolveProductDataForComponentDetails = (productKey) => {
    const key = String(productKey || "").trim();
    const fromRef = productDataRef.current?.[key] || {};
    const fromCache = componentDetailsByRowKeyRef.current?.[key];
    const products = Array.isArray(srData?.products) ? srData.products : [];
    const invoices = Array.isArray(srData?.invoiceDetails)
      ? srData.invoiceDetails
      : [];
    const fromProduct = products.find(
      (p) => String(getMaterialRowKey(p, 0)) === key
    );
    const fromInvoice = invoices.find((row, index) => {
      const slNo = row?.SLNo ?? row?.slNo ?? index + 1;
      const materialRowKey = `${row?.ProductCode ?? row?.productCode ?? ""}|${row?.SRTypeCode ?? row?.srTypeCode ?? ""}|${row?.BatchNo ?? row?.batchNo ?? ""}|${row?.InvoiceNumber ?? row?.invoiceNumber ?? ""}|${slNo}`;
      return (
        String(materialRowKey) === key ||
        String(row?.MaterialRowKey ?? row?.materialRowKey ?? "") === key
      );
    });
    const merged = {
      ...fromInvoice,
      ...fromProduct,
      ...fromRef,
    };
    if (
      fromCache != null &&
      parseComponentDetailsArray(
        merged.ComponentDetails ?? merged.componentDetails
      ).length === 0
    ) {
      merged.ComponentDetails = fromCache;
      merged.componentDetails = fromCache;
    }
    return merged;
  };

  const [materialSectionError, setMaterialSectionError] = useState("");
  const approveQtyLimitWarningMessage =
    "Approved Quantity cannot exceed SR Qty. The value has been adjusted to the maximum allowed quantity.";
  const clearApproveQtyLimitWarning = () => {
    setMaterialSectionError((prev) =>
      prev === approveQtyLimitWarningMessage ? "" : prev
    );
  };
  /**
   * HasActed from sp_GetServiceRequestDetail:
   * false = current user is the CURRENT pending next approver (Approve / Raise Query enabled)
   * true  = not the current pending level (prior level read-only, or future level still waiting)
   * Same user at multiple levels can act again when they become pending.
   */
  const isSrActed = (() => {
    const v = srData?.hasActed ?? srData?.HasActed;
    if (v === true || v === 1) return true;
    const s = String(v ?? "").trim().toLowerCase();
    return s === "1" || s === "true" || s === "yes";
  })();
  const notCurrentPendingApproverMessage =
    "Approve and Raise Query are enabled only for the current pending approval level.";
  const srStatusNormalized = String(srData?.statusName ?? srData?.StatusName ?? "")
    .trim()
    .toLowerCase();
  const isSrWipStatus = (() => {
    const byName = srStatusNormalized === "wip";
    const byCode = String(srData?.srStatusCODE ?? srData?.SRStatusCODE ?? "")
      .trim()
      .toLowerCase() === "wip";
    return byName || byCode;
  })();
  const isSrRejectedStatus = srStatusNormalized === "rejected";
  /** Final closed SR — hide Approve/Reject, Raise Query, and Close entirely. */
  const isSrClosedStatus = (() => {
    const byName =
      srStatusNormalized === "closed" ||
      srStatusNormalized === "sr closed" ||
      srStatusNormalized.replace(/\s+/g, "") === "srclosed";
    const byCode = String(srData?.srStatusCODE ?? srData?.SRStatusCODE ?? "")
      .trim()
      .toLowerCase() === "closed";
    return byName || byCode;
  })();
  /**
   * True when the logged-in user is the person who rejected this SR.
   * After reject, HasActed can stay 0 because NextApproverUserIdCode still points at the rejector —
   * so we must disable Approve/Reject and Raise Query for that user explicitly.
   */
  const isCurrentUserTheRejector = (() => {
    if (!isSrRejectedStatus) return false;
    const meKeys = new Set(
      [changedByForAction, currentUserIdCode]
        .map((v) => String(v ?? "").trim().toLowerCase())
        .filter(Boolean)
    );
    if (meKeys.size === 0) return false;

    const isRejectedStatusValue = (v) =>
      String(v ?? "").trim().toLowerCase() === "rejected";

    const history = srData?.statusHistory ?? srData?.StatusHistory ?? [];
    if (Array.isArray(history) && history.length > 0) {
      const rejectedRows = history.filter(
        (h) =>
          isRejectedStatusValue(h?.NewStatuscode ?? h?.newStatuscode) ||
          isRejectedStatusValue(h?.NewStatusName ?? h?.newStatusName) ||
          isRejectedStatusValue(h?.Reason ?? h?.reason)
      );
      if (rejectedRows.length > 0) {
        const sortedRejected = [...rejectedRows].sort((a, b) => {
          const da = new Date(a?.ChangedOn ?? a?.changedOn ?? 0).getTime();
          const db = new Date(b?.ChangedOn ?? b?.changedOn ?? 0).getTime();
          return da - db;
        });
        const last = sortedRejected[sortedRejected.length - 1];
        const by = String(last?.ChangedBy ?? last?.changedBy ?? "")
          .trim()
          .toLowerCase();
        if (by && meKeys.has(by)) return true;
      }
    }

    const steps = Array.isArray(srData?.journeyStatus) ? srData.journeyStatus : [];
    for (let i = steps.length - 1; i >= 0; i--) {
      const step = steps[i];
      const act = String(step?.status ?? step?.Activity ?? "")
        .trim()
        .toUpperCase();
      const rejected = step?.rejected === true || act === "REJECTED";
      if (!rejected) continue;
      const ids = [
        step?.UserIdCode,
        step?.userIdCode,
        step?.ChangedBy,
        step?.changedBy,
      ]
        .map((v) => String(v ?? "").trim().toLowerCase())
        .filter(Boolean);
      if (ids.some((id) => meKeys.has(id))) return true;
      break;
    }
    return false;
  })();
  /** Country Head (UT0000009) may reopen a rejected SR (Special Case), including when they were the rejector. */
  const canReopenRejectedSr =
    hasUserType("UT0000009") && isSrRejectedStatus;
  // DOS Admin / Export Zonal Head: Assign flow only for Packaging/Dispatch (not restricted header actions).
  const isDosAdminApprover = isDosAdminUser() || hasUserType("UT0000024");
  const isExportZonalHeadAssignApprover =
    getCanonicalSeApproveRoutingMarketKey(srData) === "M002" &&
    (isZonalHeadUser() || hasUserType("UT0000004"));
  /**
   * Assign button + locked Final approval Value + hide Reject — only when:
   * - Domestic Packaging/Dispatch + DOS Admin, or
   * - Export Packaging/Dispatch + ZH, or
   * - Domestic Installation + Plant QC (UT0000006) + WIP
   * All other SR categories keep normal Approve / Reject for these roles.
   */
  const isPlantQcInstallationWipAssignFlow =
    isPlantQcDomesticInstallationWipAssign(srData);
  const isAssignFlowApprover =
    ((isDosAdminApprover || isExportZonalHeadAssignApprover) &&
      isPackagingDispatchSrType(srData)) ||
    isPlantQcInstallationWipAssignFlow;
  const isHeaderActionRestrictedUserType =
    !isDosAdminApprover &&
    !isExportZonalHeadAssignApprover &&
    (hasUserType("UT0000016") || hasUserType("UT0000023") || hasUserType("UT0000017"));
  const isValidateSrLabelUserType =
    hasUserType("UT0000013") || hasUserType("UT0000021");
  const closeActionButtonLabel = isValidateSrLabelUserType ? "Validate SR" : "Close SR";
  const wipCloseModalTitle = isValidateSrLabelUserType
    ? "Validate Service Request"
    : "Close Service Request";
  const wipCloseSubmitButtonLabel = isValidateSrLabelUserType
    ? "Validate SR"
    : "Submit Close SR";

  const hasCloseFromSp = (() => {
    const v = srData?.hasClose ?? srData?.HasClose;
    if (v === true || v === 1) return true;
    const t = String(v ?? "").trim().toLowerCase();
    return t === "1" || t === "true" || t === "yes";
  })();
  /** Close when sp HasClose: SP must mean "eligible to Close" (e.g. hist.NextApproverUserIdCode = @ChangedBy), not "already closed". */
  const canCloseWipQcChild =
    !isSeUserType() &&
    hasCloseFromSp &&
    !isSrRejectedStatus &&
    !isSrClosedStatus &&
    !canReopenRejectedSr;
  const getMaterialRowKey = (row, fallbackIndex = 0) => {
    if (!row) return `row_${fallbackIndex}`;
    const explicitKey =
      row?.MaterialRowKey ??
      row?.materialRowKey ??
      row?.RowSelectionKey ??
      row?.rowSelectionKey;
    if (explicitKey != null && String(explicitKey).trim() !== "") {
      return String(explicitKey).trim();
    }
    const productCode = row?.ProductCode ?? row?.productCode ?? row?.Productcode ?? "";
    const srTypeCode = row?.SRTypeCode ?? row?.srTypeCode ?? "";
    const batchNo = row?.BatchNo ?? row?.batchNo ?? "";
    const invoiceNo = row?.InvoiceNumber ?? row?.invoiceNumber ?? row?.InvoiceNo ?? row?.invoiceNo ?? "";
    const slNo = row?.SLNo ?? row?.slNo ?? fallbackIndex;
    const composite = `${productCode}|${srTypeCode}|${batchNo}|${invoiceNo}|${slNo}`;
    return composite.trim() !== "||||" ? composite : `row_${fallbackIndex}`;
  };
  // SR Checklist Modal States
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [checklistData, setChecklistData] = useState({
    category: "",
    productName: "",
    productCode: "",
    orderNo: "",
    quantity: "",
  });
  const newLine = () => ({ id: Date.now(), type: "", qty: "" });
  const [complaintLines, setComplaintLines] = useState([newLine()]);
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [loadingComplaintTypes, setLoadingComplaintTypes] = useState(false);
  const checklistModalRef = useRef(null);
  const modalInstanceRef = useRef(null);

  // Checklist media attachments from Greenlam.SRCHECKLISTATTACHMENTS (damage images + video + checklist-level metadata)
  const [checklistAttachmentItems, setChecklistAttachmentItems] = useState([]);

  // Helper: group checklist responses (result set 4 from sp_GetServiceRequestDetail) by template
  const groupChecklistByTemplate = (responses = []) => {
    const groups = {};
    responses.forEach((row) => {
      const templateCode =
        getValueCaseInsensitive(row, [
          "templateCode",
          "templatecode",
          "TemplateCode",
          "TEMPLATECODE",
          "tEMPLATECODE",
        ]) ?? "";
      const templateName =
        getValueCaseInsensitive(row, [
          "templateName",
          "templatename",
          "TemplateName",
        ]) ??
        (templateCode || "Checklist");
      const key = templateCode || templateName;
      if (!groups[key]) {
        groups[key] = {
          templateCode,
          templateName,
          questions: [],
          answersByQuestionCode: {},
          responseRows: [],
        };
      }
      groups[key].responseRows.push(row);
      const questionCode =
        getValueCaseInsensitive(row, [
          "questionCode",
          "questionCODE",
          "QuestionCode",
          "QuestionCODE",
          "QUESTIONCODE",
          "qUESTIONCODE",
        ]) || "";
      const questionText =
        getValueCaseInsensitive(row, [
          "questionText",
          "QuestionText",
          "question",
        ]) || "";
      const responseText =
        getValueCaseInsensitive(row, [
          "responseText",
          "ResponseText",
          "response",
        ]) || "";
      const responseValue =
        getValueCaseInsensitive(row, [
          "responseValue",
          "ResponseValue",
        ]) ?? null;
      const isBoolean =
        getValueCaseInsensitive(row, [
          "isBooleanResponse",
          "IsBooleanResponse",
        ]) ?? false;

      let finalResponse = responseText;
      if (!finalResponse && responseValue !== null && responseValue !== undefined) {
        finalResponse = String(responseValue);
      }
      if (isBoolean && finalResponse !== "" && !looksLikeNumericChecklistAnswer(finalResponse)) {
        const normalized = String(finalResponse).toLowerCase();
        if (["true", "yes", "y"].includes(normalized)) finalResponse = "Yes";
        else if (["false", "no", "n"].includes(normalized)) finalResponse = "No";
      }

      groups[key].questions.push({
        questionCode,
        questionText: questionText || questionCode,
        response: finalResponse,
        isBoolean,
      });
      if (questionCode) {
        groups[key].answersByQuestionCode[questionCode] = finalResponse;
      }
    });

    return Object.values(groups);
  };

  const groupChecklistAttachmentsByTemplate = (attachments = []) => {
    const map = {};
    const baseUrl = getApiBaseUrl();
    const toFullUrl = (filePath) => {
      const p = filePath == null ? "" : String(filePath).trim();
      if (!p) return "";
      if (/^https?:\/\//i.test(p)) return p;
      const clean = p.replace(/^\/+/, "");
      return clean ? `${baseUrl}/${clean}` : "";
    };
    const isVideo = (a) => {
      const copyFor = String(a?.CopyFor ?? a?.copyFor ?? "")
        .trim()
        .toLowerCase();
      return copyFor === "video" || copyFor === "videolink";
    };

    (Array.isArray(attachments) ? attachments : []).forEach((a) => {
      const templateCode = String(
        a?.TemplateCode ??
          a?.templateCode ??
          a?.templatecode ??
          a?.TEMPLATECODE ??
          a?.template_code ??
          ""
      ).trim();
      const key = templateCode ? templateCode.toUpperCase() : "__DEFAULT__";
      if (!map[key]) {
        map[key] = {
          templateCode: templateCode || null,
          complaintReceivedDate: "",
          visitReportComments: "",
          videoUrl: "",
          damageFiles: [],
        };
      }

      const vDate = a?.ComplaintReceivedDate ?? a?.complaintReceivedDate ?? "";
      if (vDate != null && String(vDate).trim() !== "" && !map[key].complaintReceivedDate) {
        map[key].complaintReceivedDate = String(vDate);
      }
      const vComments = a?.VisitReportComments ?? a?.visitReportComments ?? "";
      if (vComments != null && String(vComments).trim() !== "" && !map[key].visitReportComments) {
        map[key].visitReportComments = String(vComments);
      }

      const filePath = String(a?.FilePath ?? a?.filePath ?? "").trim();
      if (!filePath) return;

      if (isVideo(a)) {
        if (!map[key].videoUrl) {
          map[key].videoUrl = toFullUrl(filePath);
        }
      } else {
        const name =
          String(a?.FileName ?? a?.fileName ?? "").trim() ||
          String(filePath.split("/").pop() ?? "attachment");
        map[key].damageFiles.push({
          id: `checklist-damage-${key}-${a?.SLNo ?? a?.slNo ?? map[key].damageFiles.length}`,
          name,
          url: toFullUrl(filePath),
          fileType: a?.FileType ?? a?.fileType ?? "",
        });
      }
    });

    return map;
  };

  const checklistAttachmentsByTemplate = React.useMemo(
    () => groupChecklistAttachmentsByTemplate(checklistAttachmentItems),
    [checklistAttachmentItems]
  );

  // Raise Query Modal States
  const [raiseQueryOpen, setRaiseQueryOpen] = useState(false);
  const [raiseQueryData, setRaiseQueryData] = useState({
    name: "",
    query: "",
  });
  const [nameOptions, setNameOptions] = useState([]);
  const [raiseQuerySubmitting, setRaiseQuerySubmitting] = useState(false);
  const raiseQuerySubmittingRef = useRef(false);
  raiseQuerySubmittingRef.current = raiseQuerySubmitting;
  const raiseQueryModalRef = useRef(null);
  const raiseQueryModalInstanceRef = useRef(null);

  // Query Response Modal States (for SE)
  const [responseQueryOpen, setResponseQueryOpen] = useState(false);
  const [selectedQuery, setSelectedQuery] = useState(null);
  const [responseQueryData, setResponseQueryData] = useState({
    responseText: "",
    attachments: [],
  });
  const [queries, setQueries] = useState([]);
  const [loadingQueries, setLoadingQueries] = useState(false);
  /** When set, Query History shows only queries raised by this journey user. */
  const [queryHistoryFilter, setQueryHistoryFilter] = useState(null); // { userId, userName } | null
  const openQueryHistoryDrilldownRef = useRef(null);
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [loadingTicketAttachments, setLoadingTicketAttachments] = useState(false);
  // QueryNo -> attachment[] cache (so we can pass QueryNo to API/SP)
  const [ticketAttachmentsByQueryNo, setTicketAttachmentsByQueryNo] = useState({});
  const [loadingTicketAttachmentsByQueryNo, setLoadingTicketAttachmentsByQueryNo] = useState({});
  const responseQueryModalRef = useRef(null);
  const responseQueryModalInstanceRef = useRef(null);
  const [responseQuerySubmitting, setResponseQuerySubmitting] = useState(false);
  const responseQuerySubmittingRef = useRef(false);
  responseQuerySubmittingRef.current = responseQuerySubmitting;

  useEffect(() => {
    setQueryHistoryFilter(null);
  }, [id, childSrCodeFromQuery]);

  useEffect(() => {
    setWipCloseLocallyCompleted(false);
  }, [id, childSrCodeFromQuery]);

  useEffect(() => {
    if (!journeyIconTip) return undefined;
    const hideTip = () => setJourneyIconTip(null);
    window.addEventListener("scroll", hideTip, true);
    window.addEventListener("resize", hideTip);
    return () => {
      window.removeEventListener("scroll", hideTip, true);
      window.removeEventListener("resize", hideTip);
    };
  }, [journeyIconTip]);

  // Disable Approve/Reject when at least one query is still pending
  // for the currently loaded SR (parent/child context).
  // HasActed=true means the viewer is NOT the current pending next approver.
  const hasPendingQueryForCurrentSR = Array.isArray(queries)
    ? queries.some(
        (q) =>
          String(q?.status || "")
            .trim()
            .toLowerCase() === "pending"
      )
    : false;

  /** After refresh, if server no longer exposes Close and there are no pending queries, clear local hide flag (avoid stale state). */
  useEffect(() => {
    if (loading || !srData) return;
    if (!hasCloseFromSp && !hasPendingQueryForCurrentSR) {
      setWipCloseLocallyCompleted(false);
    }
  }, [loading, srData, hasCloseFromSp, hasPendingQueryForCurrentSR]);

  const rejectorActionsDisabledMessage =
    "Approve / Reject and Raise Query are disabled because you rejected this service request.";
  /** Rejector may still open Reopen (Special Case); block only Approve/Reject/Raise Query. */
  const disableApproverHeaderActions =
    hasPendingQueryForCurrentSR ||
    (isSrActed && !canReopenRejectedSr) ||
    (isCurrentUserTheRejector && !canReopenRejectedSr);

  // Based on HasClose logic:
  // - HasClose=true means the current user is eligible to Close (so they have NOT completed Close yet).
  // - In WIP flow, if HasActed=true AND HasClose=false, treat it as "current user already completed Close".
  // Keep all other existing Raise Query rules unchanged.
  const hasCurrentUserDoneCloseActivity =
    isSrWipStatus && isSrActed && !hasCloseFromSp;

  // Additional Raise Query guard (override):
  // If SR is already approved (HasActed=true) but is currently WIP AND Close is pending for this user (HasClose=true)
  // AND there is no pending query, then allow Raise Query (all other logic remains same).
  const allowRaiseQueryWhenWipClosePending =
    isSrWipStatus &&
    hasCloseFromSp &&
    !hasPendingQueryForCurrentSR &&
    !hasCurrentUserDoneCloseActivity;

  const disableRaiseQueryButton =
    hasPendingQueryForCurrentSR ||
    hasCurrentUserDoneCloseActivity ||
    isCurrentUserTheRejector ||
    ((isSrActed && !canReopenRejectedSr) && !allowRaiseQueryWhenWipClosePending);

  const closeButtonEligibleRole =
    !isSeUserType() && !isSrRejectedStatus && !isSrClosedStatus && !canReopenRejectedSr;
  /** Show Close only when API allows it (HasClose). Hide after Close, and never for Plant QC Installation Assign. */
  const showWipCloseButton =
    closeButtonEligibleRole &&
    !wipCloseLocallyCompleted &&
    hasCloseFromSp &&
    !isPlantQcInstallationWipAssignFlow;
  // HasClose already means this user may Close at WIP. Approve/Reject stay gated by HasActed.
  const closeDisabledDueToPendingQuery = hasPendingQueryForCurrentSR;
  const disableWipCloseHeader = !hasCloseFromSp || closeDisabledDueToPendingQuery;

  // Show Response Query button only when a pending query is assigned to current user.
  // Use direct user payload here to avoid depending on getCurrentUserId declaration order.
  const visibilityUser = (() => {
    try {
      return getUser() || {};
    } catch {
      return {};
    }
  })();
  const currentUserIdForVisibility = String(
    visibilityUser?.userId ||
      visibilityUser?.id ||
      visibilityUser?.UserId ||
      visibilityUser?.Id ||
      ""
  ).trim();
  const currentUserIdCodeForVisibility = String(
    visibilityUser?.userIdCode ||
      visibilityUser?.UserIdCode ||
      visibilityUser?.userCode ||
      visibilityUser?.UserCode ||
      currentUserIdCode ||
      ""
  ).trim();
  const hasPendingQueryAssignedToCurrentUser = Array.isArray(queries)
    ? queries.some((q) => {
        const status = String(q?.status || "").trim().toLowerCase();
        const raisedTo = String(q?.raisedToUserId || "").trim();
        return (
          status === "pending" &&
          raisedTo !== "" &&
          (raisedTo === currentUserIdForVisibility ||
            raisedTo === currentUserIdCodeForVisibility)
        );
      })
    : false;

  // Approve/Reject Modal States
  const [approveRejectOpen, setApproveRejectOpen] = useState(false);
  const [approveOpen, setApproveOpen] = useState(false);
  const [approveData, setApproveData] = useState({
    items: [],
    remarks: "",
    factoryIntentNo: "",
    factoryIntentDate: "",
    settlementType: "",
    finalApprovedValue: "",
    rcaGiven: "",
    rcaRemarks: "",
    deductFromChKitty: "",
    physicalVisitRequired: "",
    siteVisitTraveler: "",
    technicalVisitType: "",
    seUserSpecificationType: "",
    seNextApproverUserTypeCode: "",
    sePlantIds: [],
    seRdcIds: [],
    seNextApproverRoutingUserId: "",
  });
  /** Mikasa Door(s) / Mikasa Floor / Sturdo / Greenlam Cubicle (Sturdo) — Component Details + Factory intent. */
  const businessVerticalRequiresComponentDetails = useMemo(() => {
    const candidates = [];
    if (Array.isArray(srData?.products)) candidates.push(...srData.products);
    if (Array.isArray(approveData?.items)) candidates.push(...approveData.items);
    if (Array.isArray(srData?.invoiceDetails)) candidates.push(...srData.invoiceDetails);
    const headerBv = srData?.businessVertical ?? srData?.BusinessVertical;
    if (headerBv) candidates.push({ businessVertical: headerBv });
    return candidates.some((row) => materialRowShowsComponentDetailsIcon(row));
  }, [
    srData?.products,
    srData?.invoiceDetails,
    srData?.businessVertical,
    srData?.BusinessVertical,
    approveData?.items,
  ]);
  const showMaterialComponentDetailsColumn = businessVerticalRequiresComponentDetails;
  const showFactoryIntentSettlementSection = businessVerticalRequiresComponentDetails;
  /** Mikasa Doors / Floors / Sturdo: hide SR Qty, SR Value, Approve Qty, Cal. Approved Value (+ summary tiles). */
  const showMaterialSrQtyValueApproveColumns = useMemo(() => {
    const products = Array.isArray(srData?.products) ? srData.products : [];
    if (products.length > 0) {
      return products.some((row) => !materialRowShowsComponentDetailsIcon(row));
    }
    return !businessVerticalRequiresComponentDetails;
  }, [srData?.products, businessVerticalRequiresComponentDetails]);

  /**
   * Three BV: write ApprovedValue / FinalApprovedValue to SRApprovalLog only at Plant QC
   * (UT0000006) or any level after Plant QC has already approved. Before that → 0.
   */
  const shouldWriteThreeBvApprovalMoneyValues = useMemo(() => {
    if (!businessVerticalRequiresComponentDetails) return true;
    if (hasUserType("UT0000006")) return true;
    const statuses = Array.isArray(srData?.journeyStatus) ? srData.journeyStatus : [];
    return statuses.some((step) => {
      const code = String(
        step?.UserTypeCode ?? step?.userTypeCode ?? ""
      )
        .toUpperCase()
        .trim();
      const role = String(
        step?.designation ??
          step?.role ??
          step?.UserTypeName ??
          step?.userTypeName ??
          ""
      ).toLowerCase();
      const isPlantQc =
        code === "UT0000006" ||
        role.includes("plant qc") ||
        role.includes("plantqc");
      if (!isPlantQc) return false;
      const act = String(step?.status ?? step?.Activity ?? "")
        .toUpperCase()
        .trim();
      if (act === "REJECTED") return false;
      if (step?.approved === true || step?.rejected === true) return !!step.approved;
      const done = step?.isDone ?? step?.IsDone;
      if (done === true || done === 1) return true;
      const s = String(done ?? "").trim().toLowerCase();
      return s === "true" || s === "yes" || act === "APPROVED";
    });
  }, [businessVerticalRequiresComponentDetails, srData?.journeyStatus]);
  const [approveSeDropdowns, setApproveSeDropdowns] = useState({
    userTypes: [],
    plants: [],
    rdcs: [],
    loading: false,
  });
  /** Plant/RDC/BCM roles from routing SPs (userTypeCode + userTypeName). */
  const [seApproveRoutingRoleOptions, setSeApproveRoutingRoleOptions] = useState([]);
  /** Scoped USERMASTER rows for Role + SR filter (User dropdown for Plant / RDC / BCM). */
  const [seApproveRoutingUserOptions, setSeApproveRoutingUserOptions] = useState([]);
  const [seApproveRoutingRoleCodesLoading, setSeApproveRoutingRoleCodesLoading] = useState(false);
  const [seApproveRoutingUsersLoading, setSeApproveRoutingUsersLoading] = useState(false);
  const approveModalRef = useRef(null);
  const approveModalInstanceRef = useRef(null);
  const approveRejectModalRef = useRef(null);
  const approveRejectModalInstanceRef = useRef(null);
  const approveRejectLastFocusRef = useRef(null);
  const [approveRejectSubmitting, setApproveRejectSubmitting] = useState(false);
  const approveRejectSubmittingRef = useRef(false);
  approveRejectSubmittingRef.current = approveRejectSubmitting;
  const [wipCloseFlow, setWipCloseFlow] = useState(false);
  const pendingWipCloseOpenRef = useRef(false);
  const approveRejectOpenRef = useRef(false);
  const finalApprovedValueTouchedRef = useRef(false);

  useEffect(() => {
    approveRejectOpenRef.current = approveRejectOpen;
  }, [approveRejectOpen]);

  useEffect(() => {
    if (!approveRejectOpen || !shouldShowSeApproveRoutingControls(srData)) return undefined;
    let cancelled = false;
    setApproveSeDropdowns((p) => ({ ...p, loading: true }));
    (async () => {
      try {
        const results = await Promise.allSettled([
          masterDataAPI.getUserTypes(),
          plantMasterAPI.getPaged({ status: "active", pageNumber: 1, pageSize: 8000 }),
          rdcMasterAPI.getPaged({ status: "active", pageNumber: 1, pageSize: 8000 }),
        ]);
        if (cancelled) return;
        const [userTypesRes, plantsRes, rdcsRes] = results.map((r) =>
          r.status === "fulfilled" ? r.value : null
        );
        const plantsPayload = plantsRes?.data;
        const plantsList =
          plantsPayload?.Plants ||
          plantsPayload?.plants ||
          plantsPayload?.data?.Plants ||
          [];
        const rdcsPayload = rdcsRes?.data;
        const rdcsList =
          rdcsPayload?.Rdcs || rdcsPayload?.rdcs || rdcsPayload?.data?.Rdcs || [];
        setApproveSeDropdowns({
          userTypes: userTypesRes?.data || [],
          plants: Array.isArray(plantsList) ? plantsList : [],
          rdcs: Array.isArray(rdcsList) ? rdcsList : [],
          loading: false,
        });
      } catch (e) {
        if (!cancelled) {
          console.error("Approve modal SE routing dropdowns failed", e);
          setApproveSeDropdowns((p) => ({ ...p, loading: false }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [approveRejectOpen, srData]);

  // Plant master list for resolving Plant Name -> PlantCode on Approve/Reject (all approvers).
  useEffect(() => {
    if (!approveRejectOpen) return undefined;
    if ((approveSeDropdowns.plants || []).length > 0) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const plantsRes = await plantMasterAPI.getPaged({
          status: "active",
          pageNumber: 1,
          pageSize: 8000,
        });
        if (cancelled) return;
        const plantsPayload = plantsRes?.data;
        const plantsList =
          plantsPayload?.Plants ||
          plantsPayload?.plants ||
          plantsPayload?.data?.Plants ||
          [];
        if (Array.isArray(plantsList) && plantsList.length > 0) {
          setApproveSeDropdowns((p) => ({
            ...p,
            plants: plantsList,
          }));
        }
      } catch (e) {
        console.warn("Approve modal: plant master load failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [approveRejectOpen, approveSeDropdowns.plants]);

  useEffect(() => {
    if (!approveRejectOpen || !shouldShowSeApproveRoutingControls(srData)) {
      setSeApproveRoutingRoleOptions([]);
      setSeApproveRoutingRoleCodesLoading(false);
      return undefined;
    }
    const spec = canonicalSeUserSpecificationType(approveData.seUserSpecificationType);
    if (!isSeApproveScopedSpec(spec)) {
      setSeApproveRoutingRoleOptions([]);
      setSeApproveRoutingRoleCodesLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSeApproveRoutingRoleCodesLoading(true);
    (async () => {
      try {
        const res = await userAPI.getSeApproveRoutingSpecUserTypeCodes(spec);
        const list = filterSeApproveRoutingRolesBySrFlow(
          spec,
          parseSeApproveRoutingRoleList(res),
          getCanonicalSeApproveRoutingMarketKey(srData)
        );

        if (!cancelled) {
          setSeApproveRoutingRoleOptions(list);
          const selectedRole = String(
            approveData.seNextApproverUserTypeCode || ""
          )
            .trim()
            .toUpperCase();
          if (
            selectedRole &&
            !list.some(
              (r) =>
                String(r.userTypeCode || "")
                  .trim()
                  .toUpperCase() === selectedRole
            )
          ) {
            setApproveData((prev) => ({
              ...prev,
              seNextApproverUserTypeCode: "",
              seNextApproverRoutingUserId: "",
            }));
          }
        }
      } catch (e) {
        if (!cancelled) {
          console.warn("SE approve: spec role codes lookup failed", e);
          setSeApproveRoutingRoleOptions([]);
        }
      } finally {
        if (!cancelled) {
          setSeApproveRoutingRoleCodesLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      setSeApproveRoutingRoleCodesLoading(false);
    };
  }, [
    approveRejectOpen,
    srData,
    approveData.seUserSpecificationType,
  ]);

  useEffect(() => {
    if (!approveRejectOpen || !shouldShowSeApproveRoutingControls(srData)) return undefined;
    const allowed = new Set(getSeApproveSrFlowOptions(srData).map((o) => o.id));
    const current = canonicalSeUserSpecificationType(approveData.seUserSpecificationType || "");
    if (current && !allowed.has(current)) {
      setApproveData((prev) => ({
        ...prev,
        seUserSpecificationType: "",
        seNextApproverUserTypeCode: "",
        seNextApproverRoutingUserId: "",
        sePlantIds: [],
        seRdcIds: [],
      }));
    }
    return undefined;
  }, [approveRejectOpen, srData, approveData.seUserSpecificationType]);

  useEffect(() => {
    if (!approveRejectOpen || !shouldShowSeApproveRoutingControls(srData)) {
      setSeApproveRoutingUserOptions([]);
      setSeApproveRoutingUsersLoading(false);
      return undefined;
    }
    const spec = canonicalSeUserSpecificationType(approveData.seUserSpecificationType);
    if (!isSeApproveScopedSpec(spec)) {
      setSeApproveRoutingUserOptions([]);
      setSeApproveRoutingUsersLoading(false);
      return undefined;
    }
    const role = String(approveData.seNextApproverUserTypeCode || "").trim();
    if (!role) {
      setSeApproveRoutingUserOptions([]);
      setSeApproveRoutingUsersLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSeApproveRoutingUsersLoading(true);
    (async () => {
      try {
        const plantsList = approveSeDropdowns.plants || [];
        const rdcsList = approveSeDropdowns.rdcs || [];
        let allowedCsv = null;
        if (spec === "Plant") {
          const srCodes = getSeApprovePlantCodesFromApproveModalItems(approveData.items || [], plantsList);
          allowedCsv = srCodes.length > 0 ? srCodes.join(",") : null;
        } else if (isSeApproveRdcScopedSpec(spec)) {
          const rdcCodes = getSeApproveRdcCodesFromApproveModalItems(approveData.items || [], rdcsList);
          allowedCsv = rdcCodes.length > 0 ? rdcCodes.join(",") : null;
        }
        const parentSRCode = String(srData?.srCode || srData?.SRCode || id || "").trim();
        const childSRCode = String(
          srData?.childSRNumber ||
            srData?.ChildSRNumber ||
            childSrCodeFromQuery ||
            ""
        ).trim();
        const marketCode =
          getCanonicalSeApproveRoutingMarketKey(srData) ||
          getSeApproveRoutingMarketCode(srData) ||
          "";
        const res = await userAPI.getSeApproveRoutingSpecUsers({
          userSpecificationType: spec,
          userTypeCode: role,
          allowedScopeCodes: allowedCsv,
          marketCode: marketCode || undefined,
          parentSRCode: parentSRCode || undefined,
          childSRCode: childSRCode || undefined,
        });
        const list = parseSeApproveApiList(res);
        if (!cancelled) {
          setSeApproveRoutingUserOptions(
            list.map((u) => {
              const userIdCode = String(u.userIdCode ?? u.UserIdCode ?? "").trim();
              const rawDisplay = String(
                u.displayText ?? u.DisplayText ?? u.userIdCode ?? u.UserIdCode ?? ""
              ).trim();
              return {
                userIdCode,
                displayText: seApproveRoutingUserDisplayName(rawDisplay, userIdCode) || userIdCode,
              };
            }).filter((u) => u.userIdCode)
          );
        }
      } catch (e) {
        if (!cancelled) {
          console.warn("SE approve: spec users lookup failed", e);
          setSeApproveRoutingUserOptions([]);
        }
      } finally {
        if (!cancelled) {
          setSeApproveRoutingUsersLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
      setSeApproveRoutingUsersLoading(false);
    };
  }, [
    approveRejectOpen,
    srData,
    approveData.seUserSpecificationType,
    approveData.seNextApproverUserTypeCode,
    approveData.items,
    approveSeDropdowns.plants,
    approveSeDropdowns.rdcs,
    srData,
    id,
    childSrCodeFromQuery,
  ]);

  useEffect(() => {
    if (!approveRejectOpen) return;
    const spec = canonicalSeUserSpecificationType(approveData.seUserSpecificationType);
    if (!isSeApproveScopedSpec(spec)) return;
    const role = String(approveData.seNextApproverUserTypeCode || "").trim();
    if (!role || seApproveRoutingRoleOptions.length === 0) return;
    const ok = new Set(
      seApproveRoutingRoleOptions.map((r) => String(r.userTypeCode || "").trim())
    );
    if (!ok.has(role)) {
      setApproveData((prev) => ({
        ...prev,
        seNextApproverUserTypeCode: "",
        seNextApproverRoutingUserId: "",
      }));
    }
  }, [
    approveRejectOpen,
    approveData.seUserSpecificationType,
    approveData.seNextApproverUserTypeCode,
    seApproveRoutingRoleOptions,
  ]);

  useEffect(() => {
    if (!approveRejectOpen) return;
    const uid = String(approveData.seNextApproverRoutingUserId || "").trim();
    if (!uid) return;
    const stillThere = (seApproveRoutingUserOptions || []).some((u) => u.userIdCode === uid);
    if (!stillThere) {
      setApproveData((prev) => ({ ...prev, seNextApproverRoutingUserId: "" }));
    }
  }, [approveRejectOpen, approveData.seNextApproverRoutingUserId, seApproveRoutingUserOptions]);

  // Format date to dd-mm-yyyy
  const formatDate = (dateString) => {
    const normalized =
      dateString === null || dateString === undefined ? "" : String(dateString);

    if (!normalized || normalized === "" || normalized === "-") {
      return normalized || "-";
    }

    try {
      // Try to parse the date
      const date = new Date(normalized);

      // Check if date is valid
      if (isNaN(date.getTime())) {
        // If it's not a valid date object, try to parse manually
        // Handle formats like "23 Jul 2024" or "26-06-2024"
        const parts = normalized.trim().split(/[\s-/]+/);

        if (parts.length === 3) {
          // Check if it's already in dd-mm-yyyy or similar format
          if (
            parts[0].length <= 2 &&
            parts[1].length <= 2 &&
            parts[2].length === 4
          ) {
            return normalized; // Already in correct format
          }

          // Handle "23 Jul 2024" format
          const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
          ];
          const monthIndex = monthNames.findIndex((m) =>
            normalized.includes(m)
          );

          if (monthIndex !== -1) {
            const day = parts[0].padStart(2, "0");
            const month = String(monthIndex + 1).padStart(2, "0");
            const year = parts[2];
            return `${day}-${month}-${year}`;
          }
        }

        return normalized; // Return as-is if we can't parse it
      }

      // Format as dd-mm-yyyy
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      return normalized; // Return normalized string if any error occurs
    }
  };

  // Prevent runtime crashes if backend returns unexpected object values.
  const renderSafeText = (value, fallback = "") => {
    if (value === null || value === undefined) return fallback;
    const t = typeof value;
    if (t === "string" || t === "number" || t === "boolean") return String(value);
    return fallback;
  };

  // Ensure we never pass an object/array as a React child (fixes "Objects are not valid as a React child").
  const safeChild = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") return value;
    if (Array.isArray(value)) return value.map(safeChild).filter(Boolean).join(", ");
    if (typeof value === "object") return "";
    return String(value);
  };

  // Country/Zone/Region/Territory: truncate long lists and show full text on hover.
  const LONG_GEO_TOOLTIP_CHARS = 40;
  // Location code 0000 means every region or territory under the parent, not a named master row.
  const formatGeoAllLabel = (raw) => {
    const text = String(safeChild(raw) ?? "").trim();
    if (!text) return text;
    return text
      .split(",")
      .map((part) => {
        const token = part.trim();
        if (!token) return "";
        if (token === "0000" || token.toLowerCase() === "all") return "All";
        return token;
      })
      .filter(Boolean)
      .join(", ");
  };
  const renderGeoDetailValue = (raw, { allCodeAsAll = false } = {}) => {
    const text = allCodeAsAll ? formatGeoAllLabel(raw) : safeChild(raw);
    const display = text || "—";
    const showTooltip = Boolean(text) && String(text).length > LONG_GEO_TOOLTIP_CHARS;
    return (
      <div
        className={`detail-value${showTooltip ? " detail-value-truncate" : ""}`}
        title={showTooltip ? String(text) : undefined}
      >
        {display}
      </div>
    );
  };

  const getRoleCode = (roleNameOrCode) => {
    const role = String(roleNameOrCode || "").trim();
    if (!role) return "";
    // Special-case: show full BCM label (so UI becomes "(Branch Commercial (BCM))")
    // instead of incorrectly deriving initials like "BC(".
    if (role.toUpperCase().includes("BRANCH COMMERCIAL")) return role;
    // If role is already a descriptive label like "Initiator (SE)",
    // return it as-is so the UI becomes: Name (Initiator (SE))
    if (role.toUpperCase().includes("INITIATOR")) return role;
    // If a short role code exists inside parentheses like "(SE)" or "(BCM)", use it.
    const parenCodeMatch = role.match(/\(([A-Za-z]{2,4})\)/);
    if (parenCodeMatch?.[1]) return parenCodeMatch[1].toUpperCase();
    // If already looks like a short code (e.g., BCM, SE), keep it.
    if (role.length <= 4 && /^[A-Za-z]+$/.test(role)) return role.toUpperCase();
    // Otherwise derive initials from words (e.g., Sales Executive -> SE).
    const parts = role
      .split(/\s+/)
      .filter(Boolean)
      // Avoid punctuation-only parts like "(BCM)" from contributing a '(' character.
      .filter((p) => /^[A-Za-z]/.test(p));
    return parts.map((p) => p[0]).join("").toUpperCase();
  };

  const isInitiatorSEStep = (step) => {
    const designation = String(step?.designation || "").toUpperCase();
    const status = String(step?.status || "").toUpperCase();
    const roleCode = getRoleCode(step?.designation || step?.status || "");
    return (
      designation === "SE" ||
      status === "SE" ||
      roleCode === "SE" ||
      designation.includes("INITIATOR") ||
      status.includes("INITIATOR") ||
      designation.includes("SALES EXECUTIVE") ||
      status.includes("SALES EXECUTIVE")
    );
  };

  const getJourneyStepActivityUpper = (step) =>
    String(step?.status ?? step?.Activity ?? "").toUpperCase().trim();

  const isJourneyStepCloseActivity = (step) => {
    const act = getJourneyStepActivityUpper(step);
    const reason = String(step?.Reason ?? step?.reason ?? "")
      .toUpperCase()
      .trim();
    return (
      act === "CLOSE" ||
      act === "CLOSED" ||
      act === "FINAL CLOSE" ||
      act.includes("FINAL CLOSE") ||
      reason === "CLOSE" ||
      reason === "CLOSED"
    );
  };

  /** Level-1 Close → "Closed"; Level-2 Final Close → "SR Closed". */
  const getJourneyCloseStatusLabel = (step) => {
    const act = getJourneyStepActivityUpper(step);
    const reason = String(step?.Reason ?? step?.reason ?? "")
      .toUpperCase()
      .trim();
    if (
      act === "FINAL CLOSE" ||
      act.includes("FINAL CLOSE") ||
      act === "CLOSED" ||
      reason === "CLOSED"
    ) {
      return "SR Closed";
    }
    if (act === "CLOSE" || reason === "CLOSE") {
      return "Closed";
    }
    return null;
  };

  const isJourneyStepRejected = (step) => {
    if (isInitiatorSEStep(step)) return false;
    return step?.rejected === true || getJourneyStepActivityUpper(step) === "REJECTED";
  };

  /** Approved / Close / Final Close (excludes Initiator Created and Rejected). */
  const isJourneyStepApprovedAction = (step) => {
    if (!step || isInitiatorSEStep(step) || isJourneyStepRejected(step)) return false;
    const act = getJourneyStepActivityUpper(step);
    if (act === "CREATED" || act.includes("CREATED")) return false;
    const isDone =
      step.isDone === true ||
      step.IsDone === true ||
      step.isDone === 1 ||
      step.IsDone === 1;
    return (
      step.approved === true ||
      isDone ||
      act === "APPROVED" ||
      isJourneyStepCloseActivity(step)
    );
  };

  const JOURNEY_APPROVAL_PALETTE = {
    first: {
      solid: "#28a745",
      gradient: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
      shadow:
        "0 4px 12px rgba(40, 167, 69, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
      shadowHover:
        "0 6px 20px rgba(40, 167, 69, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
    },
    reopen: {
      solid: "#9c27b0",
      gradient: "linear-gradient(135deg, #ab47bc 0%, #7b1fa2 100%)",
      shadow:
        "0 4px 12px rgba(156, 39, 176, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
      shadowHover:
        "0 6px 20px rgba(156, 39, 176, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
    },
    repeat: {
      // Approvals that happen after a WIP/Close step in the journey.
      solid: "#0d6efd",
      gradient: "linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)",
      shadow:
        "0 4px 12px rgba(13, 110, 253, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
      shadowHover:
        "0 6px 20px rgba(13, 110, 253, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)",
    },
  };

  const getJourneyApprovalColors = (variant = "first") =>
    JOURNEY_APPROVAL_PALETTE[variant] ?? JOURNEY_APPROVAL_PALETTE.first;

  /** WIP / Close step already present earlier in the journey. */
  const hasJourneyWipOrCloseBefore = (steps, index) => {
    if (!Array.isArray(steps) || index <= 0) return false;
    for (let i = 0; i < index; i++) {
      const act = getJourneyStepActivityUpper(steps[i]);
      if (act === "WIP" || act.includes("WIP") || isJourneyStepCloseActivity(steps[i])) {
        return true;
      }
    }
    return false;
  };

  /** Only the first approval after reject gets reopen (purple) styling — not every later step. */
  const getFirstPostRejectApprovalIndex = (steps, lastRejectedIndex) => {
    if (!Array.isArray(steps) || lastRejectedIndex < 0) return -1;
    for (let i = lastRejectedIndex + 1; i < steps.length; i++) {
      if (
        isJourneyStepApprovedAction(steps[i]) &&
        !isJourneyStepRejected(steps[i])
      ) {
        return i;
      }
    }
    return -1;
  };

  const isPostRejectJourneyApproval = (steps, index, lastRejectedIndex) =>
    index === getFirstPostRejectApprovalIndex(steps, lastRejectedIndex);

  /**
   * Green: normal approvals (including multi-level same user before WIP).
   * Purple: first approval after reject/reopen.
   * Blue: any approval that occurs after a WIP/Close step.
   * Pending colors stay orange (unchanged).
   */
  const resolveJourneyApprovalVariant = (steps, index, lastRejectedIndex) => {
    if (isPostRejectJourneyApproval(steps, index, lastRejectedIndex)) return "reopen";
    const step = steps?.[index];
    if (
      step &&
      isJourneyStepApprovedAction(step) &&
      !isJourneyStepCloseActivity(step) &&
      hasJourneyWipOrCloseBefore(steps, index)
    ) {
      return "repeat";
    }
    return "first";
  };

  const isJourneyStepPending = (step) => {
    const act = getJourneyStepActivityUpper(step);
    if (isInitiatorSEStep(step)) return false;
    if (isJourneyStepRejected(step)) return false;
    if (isJourneyStepApprovedAction(step)) return false;
    if (act === "CREATED" || act.includes("CREATED")) return false;
    if (act === "REOPENED" || act.includes("REOPEN")) return false;
    return act === "PENDING" || act === "";
  };

  /** Details grid: all acted steps + at most one next Pending row. */
  const buildJourneyDetailDisplayRows = (sortedStatuses, showRowsPastRejection) => {
    if (!Array.isArray(sortedStatuses) || sortedStatuses.length === 0) return [];

    if (!showRowsPastRejection) {
      const out = [];
      for (const step of sortedStatuses) {
        out.push(step);
        const activity = getJourneyStepActivityUpper(step);
        if (activity === "PENDING") break;
        if (activity === "REJECTED") break;
      }
      return out;
    }

    const out = [];
    let pendingIncluded = false;
    for (const step of sortedStatuses) {
      if (isJourneyStepPending(step)) {
        if (pendingIncluded) continue;
        pendingIncluded = true;
      }
      out.push(step);
    }
    return out;
  };

  /** SR reopened + journey still has a rejected step → show rows/connectors past reject (details grid + horizontal journey). */
  const computeShowJourneyPastRejection = (data, steps) => {
    if (!data || !Array.isArray(steps) || steps.length === 0) return false;
    const norm = (v) => String(v ?? "").trim();
    const headerStatusLower = norm(data.statusName ?? data.StatusName).toLowerCase();
    const headerCodeUpper = norm(data.srStatusCODE ?? data.SRStatusCODE).toUpperCase();
    const srHeaderSaysReopened =
      data.isReopened === true ||
      data.IsReopened === true ||
      data.isReopened === 1 ||
      data.IsReopened === 1 ||
      headerStatusLower.includes("reopen") ||
      headerCodeUpper.includes("REOPEN");
    const journeyHasReopenedNewStatus = steps.some((s) => {
      const ns = norm(s.newStatuscode ?? s.NewStatuscode).toUpperCase();
      const act = norm(s.status ?? s.Activity).toUpperCase();
      return (
        ns === "REOPENED" ||
        ns.includes("REOPEN") ||
        act === "REOPENED" ||
        act.includes("REOPEN")
      );
    });
    const journeyHasRejectedStep = steps.some((s) => {
      const act = norm(s.status ?? s.Activity).toUpperCase();
      return act === "REJECTED" || s.rejected === true;
    });
    const statusHistory = data.statusHistory ?? data.StatusHistory ?? [];
    const historySaysReopened =
      Array.isArray(statusHistory) &&
      statusHistory.some((h) => {
        const reason = norm(h.reason ?? h.Reason).toUpperCase();
        const newCode = norm(h.newStatuscode ?? h.NewStatuscode).toUpperCase();
        return reason === "REOPENED" || newCode.includes("REOPEN");
      });

    let lastRejectedLevel = -1;
    steps.forEach((s) => {
      const act = norm(s.status ?? s.Activity).toUpperCase();
      if (act === "REJECTED" || s.rejected === true) {
        lastRejectedLevel = Math.max(lastRejectedLevel, s.levelSequence ?? 0);
      }
    });
    const stepIsCompleted = (s) => {
      const act = norm(s.status ?? s.Activity).toUpperCase();
      const isDone =
        s.isDone === true ||
        s.IsDone === true ||
        s.isDone === 1 ||
        s.IsDone === 1;
      return (
        isDone ||
        s.approved === true ||
        act === "APPROVED" ||
        act === "CLOSE" ||
        act === "FINAL CLOSE" ||
        act.includes("FINAL CLOSE")
      );
    };
    const hasCompletedStepAfterReject =
      lastRejectedLevel >= 0 &&
      steps.some((s) => (s.levelSequence ?? 0) > lastRejectedLevel && stepIsCompleted(s));

    if (!journeyHasRejectedStep) return false;
    return (
      srHeaderSaysReopened ||
      journeyHasReopenedNewStatus ||
      historySaysReopened ||
      hasCompletedStepAfterReject
    );
  };

  // Build a display-only query object with ONLY primitives (no spread, no API objects in JSX).
  const toDisplayQuery = (q) => {
    if (!q || typeof q !== "object") return null;
    const safe = (v, fallback = "") =>
      typeof v === "string" || typeof v === "number" || typeof v === "boolean"
        ? String(v)
        : fallback;
    const safeNum = (v) => (typeof v === "number" ? v : null);
    let raisedOnDate = null;
    let respondedOnDate = null;
    if (q.raisedOn instanceof Date && !isNaN(q.raisedOn.getTime())) raisedOnDate = q.raisedOn;
    else if (typeof q.raisedOn === "string" || typeof q.raisedOn === "number") {
      const d = new Date(q.raisedOn);
      if (!isNaN(d.getTime())) raisedOnDate = d;
    }
    if (q.respondedOn instanceof Date && !isNaN(q.respondedOn.getTime())) respondedOnDate = q.respondedOn;
    else if (typeof q.respondedOn === "string" || typeof q.respondedOn === "number") {
      const d = new Date(q.respondedOn);
      if (!isNaN(d.getTime())) respondedOnDate = d;
    }
    const pad2 = (n) => String(n).padStart(2, "0");
    const formatDateDashed = (d) =>
      d
        ? `${pad2(d.getDate())}-${pad2(d.getMonth() + 1)}-${d.getFullYear()}`
        : "N/A";

    const formattedQueryDate = raisedOnDate
      ? formatDateDashed(raisedOnDate)
      : "N/A";
    const formattedQueryTime = raisedOnDate
      ? raisedOnDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
      : "N/A";
    const formattedResponseDate = respondedOnDate
      ? formatDateDashed(respondedOnDate)
      : null;
    const formattedResponseTime = respondedOnDate
      ? respondedOnDate.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })
      : null;
    const formattedRaisedOnForModal = raisedOnDate
      ? (() => {
          const pad2 = (n) => String(n).padStart(2, "0");
          const day = pad2(raisedOnDate.getDate());
          const month = pad2(raisedOnDate.getMonth() + 1);
          const year = raisedOnDate.getFullYear();

          const h24 = raisedOnDate.getHours();
          const ampm = h24 >= 12 ? "pm" : "am";
          const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
          const hour12 = pad2(h12);
          const minute = pad2(raisedOnDate.getMinutes());

          // Format exactly: DD-MM-YYYY, hh:mm am/pm
          return `${day}-${month}-${year}, ${hour12}:${minute} ${ampm}`;
        })()
      : "N/A";
    const status = safe(q.status, "Pending");
    return {
      queryId: safeNum(q.queryId),
      queryText: safe(q.queryText, ""),
      raisedByUserId: safe(q.raisedBy ?? q.RaisedBy ?? q.raisedByUserId ?? q.RaisedByUserId, ""),
      raisedByName: safe(q.raisedByName, ""),
      raisedByRoleName: safe(q.raisedByRoleName, ""),
      raisedToUserId: safe(q.raisedToUserId, ""),
      raisedToUserName: safe(q.raisedToUserName, ""),
      raisedToRoleName: safe(q.raisedToRoleName, ""),
      // Backend may return QueryNo (PascalCase) depending on serializer / environment.
      queryNo: safe(q.queryNo ?? q.QueryNo, ""),
      responseText: safe(q.responseText, ""),
      respondedByName: safe(q.respondedByName, ""),
      status,
      isResolved: typeof q.isResolved === "boolean" ? q.isResolved : Boolean(q.isResolved),
      formattedQueryDate,
      formattedQueryTime,
      formattedResponseDate,
      formattedResponseTime,
      formattedRaisedOnForModal,
      respondedOnTs: respondedOnDate ? respondedOnDate.getTime() : null,
    };
  };

  const expandQueryHistorySection = () => {
    const el = document.getElementById("collapseQueryHistory");
    if (!el) return;
    try {
      const Collapse = window.bootstrap?.Collapse;
      if (Collapse) {
        const instance = Collapse.getOrCreateInstance(el, { toggle: false });
        instance.show();
      } else {
        el.classList.add("show");
      }
    } catch {
      el.classList.add("show");
    }
    const toggle = document.querySelector(
      '[data-bs-target="#collapseQueryHistory"]'
    );
    if (toggle) toggle.setAttribute("aria-expanded", "true");
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  openQueryHistoryDrilldownRef.current = (userId, userName) => {
    setQueryHistoryFilter({
      userId: String(userId || "").trim(),
      userName: String(userName || "").trim(),
    });
    expandQueryHistorySection();
  };

  const clearQueryHistoryFilter = () => setQueryHistoryFilter(null);

  const visibleQueryHistory = (() => {
    if (!queryHistoryFilter) return queries;
    const filterId = String(queryHistoryFilter.userId || "")
      .trim()
      .toUpperCase();
    const filterName = String(queryHistoryFilter.userName || "")
      .trim()
      .toLowerCase();
    return queries.filter((q) => {
      const byId = String(q.raisedByUserId || "").trim().toUpperCase();
      const byName = String(q.raisedByName || "").trim().toLowerCase();
      if (filterId && byId && byId === filterId) return true;
      if (filterName && byName && byName === filterName) return true;
      return false;
    });
  })();

  const setQueriesSafe = (maybeArray) => {
    const arr = Array.isArray(maybeArray) ? maybeArray : [];
    const displayList = [];
    for (let i = 0; i < arr.length; i++) {
      const d = toDisplayQuery(arr[i]);
      // Avoid rendering a bare empty object `{}` by accident.
      if (d && typeof d === "object" && Object.keys(d).length > 0) displayList.push(d);
    }
    setQueries(displayList);
  };








  // SR Checklist Modal Functions
  const fetchComplaintTypes = async () => {
    setLoadingComplaintTypes(true);
    try {
      const marketCode = srData?.MarketCode || srData?.marketCode || "";
      const marketName = srData?.MarketName || srData?.marketName || "";
      let market = null;

      if (marketCode || marketName) {
        const codeUpper = marketCode.toUpperCase();
        const nameUpper = marketName.toUpperCase();

        if (
          codeUpper === "M001" ||
          codeUpper.includes("DOM") ||
          nameUpper.includes("DOMESTIC") ||
          codeUpper === "DOMESTIC" ||
          nameUpper === "DOMESTIC"
        ) {
          market = "Domestic";
        } else if (
          codeUpper === "M002" ||
          codeUpper.includes("EXP") ||
          nameUpper.includes("EXPORT") ||
          codeUpper === "EXPORT" ||
          nameUpper === "EXPORT"
        ) {
          market = "Export";
        }
      }

      const response = await masterDataAPI.getComplaintTypes(market);

      if (response && response.success && response.data?.complaintTypes) {
        const types =
          response.data.complaintTypes || response.data.ComplaintTypes || [];
        const activeTypes = types.filter((type) => {
          const isActive =
            type.isActive !== undefined ? type.isActive : type.IsActive;
          return isActive === true;
        });

        const normalizedTypes = activeTypes.map((type) => ({
          ComplaintTypeId:
            type.complaintTypeId || type.ComplaintTypeId || type.id,
          ComplaintTypeName:
            type.complaintTypeName ||
            type.ComplaintTypeName ||
            type.name ||
            "Unknown",
          ComplaintTypeCode:
            type.complaintTypeCode || type.ComplaintTypeCode || type.code,
          Description: type.description || type.Description,
          IsForDomestic:
            type.isForDomestic !== undefined
              ? type.isForDomestic
              : type.IsForDomestic !== undefined
              ? type.IsForDomestic
              : false,
          IsForExport:
            type.isForExport !== undefined
              ? type.isForExport
              : type.IsForExport !== undefined
              ? type.IsForExport
              : false,
          IsActive:
            type.isActive !== undefined
              ? type.isActive
              : type.IsActive !== undefined
              ? type.IsActive
              : true,
        }));

        setComplaintTypes(normalizedTypes);
      } else {
        setComplaintTypes([]);
      }
    } catch (err) {
      console.error("Error fetching complaint types:", err);
      setComplaintTypes([]);
    } finally {
      setLoadingComplaintTypes(false);
    }
  };

  const openChecklist = async (productKey) => {
    const products = srData?.products || [];
    const matched = products.find(
      (p) => p?.ProductCode === productKey || p?.productCode === productKey
    );

    setChecklistData({
      category: matched?.ProductCategory || matched?.productCategory || "",
      productName: matched?.ProductName || matched?.productName || "",
      productCode:
        matched?.ProductCode || matched?.productCode || productKey || "",
      orderNo: matched?.OrderNo || matched?.orderNo || srData?.orderNo || "",
      quantity: matched?.Quantity || matched?.quantity || "",
    });

    setComplaintLines([newLine()]);

    // Fetch complaint types when popup opens
    await fetchComplaintTypes();

    setChecklistOpen(true);
  };

  const handleProductDetailsOpen = (productData) => {
    setSelectedProductData(productData);
    setProductDetailsOpen(true);
  };

  const handleProductDetailsClose = () => {
    requestAnimationFrame(() => {
      setProductDetailsOpen(false);
      setSelectedProductData(null);
    });
  };

  // Handle ESC key to close product details modal and sidebar collapse/restore
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && productDetailsOpen) {
        handleProductDetailsClose();
      }
    };

    // Only update sidebar if state actually changed
    const prevState = prevProductDetailsOpenRef.current;
    const currentState = productDetailsOpen;

    if (currentState && !prevState) {
      // Modal just opened
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      // Modal just closed
      document.body.style.overflow = "";
      restoreSidebar();
    }

    // Update ref for next render
    prevProductDetailsOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      // Only restore body overflow, don't restore sidebar here
      // Sidebar restoration is handled by the state change above
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productDetailsOpen]); // Only depend on productDetailsOpen, functions are stable

  // Cleanup on unmount - restore sidebar if modal was open
  useEffect(() => {
    return () => {
      if (productDetailsOpen) {
        restoreSidebar();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on unmount

  // Component Details Modal — multi-row from SRCOMPONENTDETAILS (via ComponentDetails API field)
  const handleComponentDetailsOpen = (productKey) => {
    setSelectedComponentProductKey(productKey);
    const productData = resolveProductDataForComponentDetails(productKey);
    const rows = buildViewComponentDetailsRows(productData);
    componentDetailsRowsListRef.current = rows;
    setComponentDetailsRowsList(rows);
    const first = rows[0] || {};
    setComponentDetailsData({
      materialGroupDesc: first.materialGroupDesc || "",
      componentCategory: first.componentCategory || "",
      componentList: first.componentList || "",
      componentQty: first.componentQty || "",
      componentIssue: first.componentIssue || "",
      doorMaterialCode: first.doorMaterialCode || "",
      componentIssueRemarks: first.componentIssueRemarks || "",
    });
    setComponentDetailsOpen(true);
  };

  const handleComponentDetailsClose = () => {
    requestAnimationFrame(() => {
      setComponentDetailsOpen(false);
      setSelectedComponentProductKey(null);
      setComponentDetailsRowsList([]);
      componentDetailsRowsListRef.current = [];
    });
  };

  // Update editable Component Details fields (per row) and keep backing refs/state in sync
  const updateComponentDetailsField = (field, value, rowIndex = 0) => {
    const idx = Number.isFinite(rowIndex) ? rowIndex : 0;
    const productKey = selectedComponentProductKey;
    setComponentDetailsRowsList((prev) => {
      const next = (prev || []).map((r, i) => {
        if (i !== idx) return r;
        const updated = { ...r, [field]: value };
        if (field === "componentQty" && canPlantQcEditComponentQty && productKey) {
          updated.componentValue = computeViewComponentRowValue(productKey, value);
        }
        return updated;
      });
      componentDetailsRowsListRef.current = next;
      return next;
    });
    if (idx === 0) {
      setComponentDetailsData((prev) => ({ ...prev, [field]: value }));
    }

    if (!productKey) return;

    const currentRefRow = productDataRef.current?.[productKey] || {};
    const existingApiRows = parseComponentDetailsArray(
      currentRefRow.ComponentDetails ?? currentRefRow.componentDetails
    );
    const baseRows =
      (componentDetailsRowsListRef.current || []).length > 0
        ? componentDetailsRowsListRef.current
        : existingApiRows.length
          ? existingApiRows.map((r, i) => mapViewComponentDetailRow(r, i))
          : buildViewComponentDetailsRows(currentRefRow);
    const nextRows = baseRows.map((r, i) => {
      if (i !== idx) return r;
      const updated = { ...r, [field]: value };
      if (field === "componentQty" && canPlantQcEditComponentQty) {
        updated.componentValue = computeViewComponentRowValue(productKey, value);
      }
      return updated;
    });
    const nextApi = nextRows.map((r) => ({
      MaterialGroupDesc: r.materialGroupDesc || null,
      ComponentCategory: r.componentCategory || null,
      ComponentList: r.componentList || null,
      ComponentQty: r.componentQty || null,
      ComponentIssue: r.componentIssue || null,
      DoorMaterialCode: r.doorMaterialCode || null,
      ComponentIssueRemarks: r.componentIssueRemarks || null,
      ComponentValue: r.componentValue ?? null,
    }));
    const first = nextRows[0] || {};
    const nextRefRow = {
      ...currentRefRow,
      ComponentDetails: nextApi,
      componentDetails: nextApi,
      [field]: idx === 0 ? value : currentRefRow[field],
    };
    if (field === "doorMaterialCode" && idx === 0) {
      nextRefRow.DoorMaterialCode = value;
    }
    if (field === "componentIssueRemarks" && idx === 0) {
      nextRefRow.ComponentIssueRemarks = value;
    }
    if (idx === 0) {
      nextRefRow.MaterialGroupDesc = first.materialGroupDesc || nextRefRow.MaterialGroupDesc;
      nextRefRow.ComponentCategory = first.componentCategory || nextRefRow.ComponentCategory;
      nextRefRow.ComponentList = first.componentList || nextRefRow.ComponentList;
      nextRefRow.ComponentQty = first.componentQty || nextRefRow.ComponentQty;
      nextRefRow.ComponentIssue = first.componentIssue || nextRefRow.ComponentIssue;
    }
    productDataRef.current[productKey] = nextRefRow;
    componentDetailsByRowKeyRef.current[productKey] = nextApi;

    setSrData((prev) => {
      if (!prev || !Array.isArray(prev.products)) return prev;
      const nextProducts = prev.products.map((p) => {
        const code = p?.ProductCode ?? p?.productCode ?? "";
        const key =
          p?.MaterialRowKey ??
          p?.materialRowKey ??
          p?._rowId ??
          code;
        if (String(key) !== String(productKey) && String(code) !== String(productKey)) {
          return p;
        }
        return { ...p, ...nextRefRow };
      });
      return { ...prev, products: nextProducts };
    });

    // Keep Approve/Reject modal line in sync (Plant QC qty → approve qty source).
    if (approveRejectOpen) {
      const qtyTotal = nextRows.reduce(
        (sum, r) => sum + (parseFloat(r.componentQty) || 0),
        0
      );
      const qtyInt = Math.floor(qtyTotal) || 0;
      setApproveData((prev) => {
        if (!prev || !Array.isArray(prev.items)) return prev;
        const items = prev.items.map((it) => {
          const itKey = String(it.materialRowKey ?? it.MaterialRowKey ?? "");
          const itCode = String(it.productCode ?? it.ProductCode ?? "");
          if (
            itKey !== String(productKey) &&
            itCode !== String(productKey) &&
            itCode !== String(currentRefRow.ProductCode ?? currentRefRow.productCode ?? "")
          ) {
            return it;
          }
          const unit = getInvoiceUnitPriceForViewProduct(it);
          const apprvValue =
            shouldWriteThreeBvApprovalMoneyValues && qtyInt > 0
              ? Math.round(qtyInt * unit * 100) / 100
              : it.apprvValue;
          return {
            ...it,
            ComponentDetails: nextApi,
            componentDetails: nextApi,
            doorMaterialCode: first.doorMaterialCode ?? it.doorMaterialCode,
            componentIssueRemarks:
              first.componentIssueRemarks ?? it.componentIssueRemarks,
            apprvQuantity: qtyInt > 0 ? qtyInt : it.apprvQuantity,
            approvedQty: qtyInt > 0 ? String(qtyInt) : it.approvedQty,
            apprvValue,
          };
        });
        return { ...prev, items };
      });
    }
  };

  const handleComponentDetailsSave = async () => {
    if (!canPlantQcEditComponentQty) return;
    const productKey = selectedComponentProductKey;
    if (!productKey) return;
    const productData = resolveProductDataForComponentDetails(productKey);
    const rows =
      (componentDetailsRowsListRef.current || []).length > 0
        ? componentDetailsRowsListRef.current
        : componentDetailsRowsList;
    if (!rows.length) {
      Swal.fire({
        icon: "warning",
        title: "No rows",
        text: "No Component Details rows to save.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }
    const invalidQty = rows.some((r) => {
      const q = String(r.componentQty ?? "").trim();
      if (!q) return true;
      const n = Number(q);
      return !Number.isFinite(n) || n < 0;
    });
    if (invalidQty) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Component Qty",
        text: "Please enter a valid Component Qty (0 or greater) for every row.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    const srCode = String(srData?.srCode || srData?.SRCode || id || "").trim();
    const childSrCode = String(
      srData?.childSRNumber ||
        srData?.ChildSRNumber ||
        childSrCodeFromQuery ||
        ""
    ).trim();
    const invoiceNumber = String(
      productData?.InvoiceNumber ??
        productData?.invoiceNumber ??
        srData?.invoiceNumber ??
        srData?.invoiceNo ??
        ""
    ).trim();
    const productCode = String(
      productData?.ProductCode ?? productData?.productCode ?? ""
    ).trim();
    if (!srCode || !invoiceNumber || !productCode) {
      Swal.fire({
        icon: "error",
        title: "Missing keys",
        text: "Cannot save: SR / Invoice / Product code is missing.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    const payload = {
      srCode,
      childSrCode: childSrCode || null,
      invoiceNumber,
      srTypeCode: productData?.SRTypeCode ?? productData?.srTypeCode ?? null,
      productCode,
      batchNo: productData?.BatchNo ?? productData?.batchNo ?? null,
      componentDetails: rows.map((r) => ({
        materialGroupDesc: r.materialGroupDesc || null,
        componentCategory: r.componentCategory || null,
        componentList: r.componentList || null,
        componentQty: r.componentQty != null ? String(r.componentQty) : null,
        componentIssue: r.componentIssue || null,
        doorMaterialCode: r.doorMaterialCode || null,
        componentIssueRemarks: r.componentIssueRemarks || null,
        componentValue:
          r.componentValue != null
            ? Number(r.componentValue)
            : computeViewComponentRowValue(productKey, r.componentQty),
      })),
    };

    try {
      setComponentDetailsSaving(true);
      await complaintAPI.saveSrComponentDetails(payload);
      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Component Details saved successfully.",
        confirmButtonColor: "#0b6bff",
        timer: 1600,
        showConfirmButton: true,
      });
      handleComponentDetailsClose();
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        "Failed to save Component Details.";
      Swal.fire({
        icon: "error",
        title: "Save failed",
        text: msg,
        confirmButtonColor: "#0b6bff",
      });
    } finally {
      setComponentDetailsSaving(false);
    }
  };

  // Handle ESC key to close component details modal and sidebar collapse/restore
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && componentDetailsOpen) {
        handleComponentDetailsClose();
      }
    };

    // Only update sidebar if state actually changed
    const prevState = prevComponentDetailsOpenRef.current;
    const currentState = componentDetailsOpen;

    if (currentState && !prevState) {
      // Modal just opened
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      // Modal just closed
      document.body.style.overflow = "";
      restoreSidebar();
    }

    // Update ref for next render
    prevComponentDetailsOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [componentDetailsOpen]);

  // Cleanup on unmount - restore sidebar if component details modal was open
  useEffect(() => {
    return () => {
      if (componentDetailsOpen) {
        restoreSidebar();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on unmount

  const handleChecklistClose = () => {
    if (modalInstanceRef.current) {
      try {
        modalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing modal:", error);
      }
    }

    requestAnimationFrame(() => {
      setChecklistOpen(false);
    });
  };

  /** Max Final approval Value.
   * Default: sum of material grid Approve Value.
   * Mikasa Doors / Floors / Sturdo: Approve Qty/Value hidden — cap at Invoice Total Price.
   */
  const getFinalApprovalMaxTotal = (itemsFallback = []) => {
    if (businessVerticalRequiresComponentDetails) {
      const fromItems = computeApproveItemsTotalPriceMax(itemsFallback);
      if (fromItems > 0) return fromItems;
      try {
        if (dataTableInstanceRef.current?.rows) {
          const dtRows = dataTableInstanceRef.current
            .rows({ filter: "applied" })
            .data()
            .toArray();
          if (Array.isArray(dtRows) && dtRows.length > 0) {
            return computeProductsInvoiceTotalPriceMax(dtRows);
          }
        }
      } catch (e) {
        /* ignore */
      }
      return computeProductsInvoiceTotalPriceMax(srData?.products || []);
    }
    try {
      if (dataTableInstanceRef.current?.rows) {
        const dtRows = dataTableInstanceRef.current.rows({ filter: "applied" }).data().toArray();
        if (Array.isArray(dtRows) && dtRows.length > 0) {
          return computeMaterialRowsApprovedValueTotal(dtRows);
        }
      }
    } catch (e) {
      /* ignore */
    }
    return computeApproveItemsValueTotal(itemsFallback);
  };

  // Material grid: same row source + approve-qty rules as Approve/Reject footer (DataTable all pages when available).
  const resolveMaterialRowsForApproveValidation = () => {
    const allProducts = srData?.products || [];
    let rowsForValidation = allProducts;
    try {
      if (dataTableInstanceRef.current && typeof dataTableInstanceRef.current.rows === "function") {
        const dtRows = dataTableInstanceRef.current.rows({ page: "all" }).data().toArray();
        if (Array.isArray(dtRows) && dtRows.length > 0) {
          rowsForValidation = dtRows;
        }
      }
    } catch (e) {
      /* ignore */
    }
    return { allProducts, rowsForValidation };
  };

  const expandMaterialSectionAndFocusApproveQty = (rowKey) => {
    let focused = false;
    let scheduled = false;

    const focusApproveQtyInput = () => {
      if (focused) return;
      const tableEl = productTableRef.current;
      const panel = document.querySelector(".material-section-panel");
      if (!tableEl) {
        if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      const $wrapper = $(tableEl).closest(".dataTables_wrapper, .dt-container");
      const $scope = $wrapper.length ? $wrapper : $(tableEl);
      const wantedKey = rowKey != null ? String(rowKey) : "";

      let $inp = $();
      if (wantedKey) {
        $inp = $scope.find(".approve-qty-input").filter(function () {
          return String($(this).attr("data-key") || "") === wantedKey;
        });
      }
      if (!$inp.length) {
        $inp = $scope
          .find(".approve-qty-input")
          .filter(function () {
            return (parseFloat($(this).val()) || 0) <= 0;
          })
          .first();
      }
      if (!$inp.length) {
        $inp = $scope.find(".approve-qty-input").first();
      }
      if (!$inp.length) {
        if (panel) panel.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }

      focused = true;
      const el = $inp[0];
      const $scrollBody = $wrapper.find(".dt-scroll-body");
      if ($scrollBody.length) {
        const cell = el.closest("td") || el;
        const bodyEl = $scrollBody[0];
        const cellRect = cell.getBoundingClientRect();
        const bodyRect = bodyEl.getBoundingClientRect();
        const overflowLeft = cellRect.left - bodyRect.left;
        const overflowRight = cellRect.right - bodyRect.right;
        if (overflowLeft < 0) {
          bodyEl.scrollLeft += overflowLeft - 24;
        } else if (overflowRight > 0) {
          bodyEl.scrollLeft += overflowRight + 24;
        }
      }

      $scope.find(".approve-qty-input.is-invalid").removeClass("is-invalid");
      $inp.addClass("is-invalid");
      el.scrollIntoView({ behavior: "smooth", block: "center", inline: "nearest" });
      try {
        el.focus({ preventScroll: true });
        if (typeof el.select === "function") el.select();
      } catch (e) {
        try {
          el.focus();
        } catch (_) {
          /* ignore */
        }
      }
    };

    const goToRowPageThenFocus = () => {
      try {
        const api = dataTableInstanceRef.current;
        if (api && rowKey != null && String(rowKey) !== "") {
          const orderedIndexes = api
            .rows({ search: "applied", order: "current" })
            .indexes()
            .toArray();
          let pos = -1;
          for (let i = 0; i < orderedIndexes.length; i++) {
            const idx = orderedIndexes[i];
            const data = api.row(idx).data();
            if (getMaterialRowKey(data, idx) === String(rowKey)) {
              pos = i;
              break;
            }
          }
          if (pos >= 0) {
            const pageLen = api.page.len() || 5;
            const page = Math.floor(pos / pageLen);
            if (api.page() !== page) {
              api.page(page).draw(false);
              setTimeout(focusApproveQtyInput, 80);
              return;
            }
          }
        }
      } catch (e) {
        /* ignore */
      }
      focusApproveQtyInput();
    };

    const afterExpand = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        try {
          if (dataTableInstanceRef.current && $.fn?.DataTable) {
            dataTableInstanceRef.current.columns.adjust();
          }
        } catch (e) {
          /* ignore */
        }
        setTimeout(goToRowPageThenFocus, 60);
      });
    };

    const collapseEl = document.getElementById("collapseProductDetails");
    if (collapseEl && !collapseEl.classList.contains("show")) {
      const onShown = () => {
        collapseEl.removeEventListener("shown.bs.collapse", onShown);
        afterExpand();
      };
      collapseEl.addEventListener("shown.bs.collapse", onShown);
      try {
        const Collapse = window.bootstrap?.Collapse;
        if (Collapse) {
          Collapse.getOrCreateInstance(collapseEl, { toggle: false }).show();
        } else {
          collapseEl.classList.add("show");
          afterExpand();
        }
      } catch (e) {
        collapseEl.classList.add("show");
        afterExpand();
      }
      const toggle = document.querySelector('[data-bs-target="#collapseProductDetails"]');
      if (toggle) toggle.setAttribute("aria-expanded", "true");
      setTimeout(afterExpand, 450);
      return;
    }

    afterExpand();
  };

  const validateMaterialApproveQtyForAction = (rowsForValidation) => {
    const getEffectiveApprovedQtyInt = (product) => {
      const srQty =
        parseFloat(
          product?.ComplaintQty ?? product?.complaintQty ?? product?.SRQuantity ?? product?.srQuantity ?? 0
        ) || 0;
      const price = parseFloat(product?.Price ?? product?.price ?? 0) || 0;
      const qtyRaw =
        parseFloat(
          product?.ApprovedQty ??
            product?.approvedQty ??
            product?.ApprovedQuantity ??
            product?.approvedQuantity ??
            product?.ApprvQuantity ??
            product?.apprvQuantity ??
            0
        ) || 0;
      const valRaw =
        parseFloat(
          product?.ApprvValue ?? product?.apprvValue ?? product?.ApprovedValue ?? product?.approvedValue ?? 0
        ) || 0;
      let qty = qtyRaw > 0 ? qtyRaw : price > 0 && valRaw > 0 ? valRaw / price : 0;
      if (srQty > 0 && qty > srQty) qty = srQty;
      const qtyInt = toViewSrQtyInt(qty);
      return qtyInt > 0 ? qtyInt : 0;
    };

    // Mikasa Doors / Floors / Sturdo: Approve Qty column is hidden — do not block Approve/Reject on qty.
    const rowsNeedingApproveQty = (rowsForValidation || []).filter(
      (p) => !materialRowShowsComponentDetailsIcon(p)
    );
    const invalidIndex = rowsNeedingApproveQty.findIndex(
      (p) => getEffectiveApprovedQtyInt(p) <= 0
    );
    const invalidApproveRow = invalidIndex >= 0 ? rowsNeedingApproveQty[invalidIndex] : null;
    if (invalidApproveRow) {
      const invalidRowKey = getMaterialRowKey(invalidApproveRow, invalidIndex);
      setMaterialSectionError(
        "Please enter an Approved Quantity greater than 0 for all material rows before proceeding with Approve or Reject."
      );
      Swal.fire({
        icon: "warning",
        title: "Approve Qty required",
        text: "Please enter an Approved Quantity greater than 0 for all material rows before proceeding with Approve or Reject.",
        confirmButtonColor: "#0b6bff",
        returnFocus: false,
      }).then(() => {
        expandMaterialSectionAndFocusApproveQty(invalidRowKey);
      });
      return false;
    }
    setMaterialSectionError("");
    return true;
  };

  /** Sum Component Qty from SRCOMPONENTDETAILS / ComponentDetails for three-BV rows. */
  const getComponentDetailsQtyTotalForProduct = (product) => {
    if (!product) return 0;
    const key = getMaterialRowKey(product, 0);
    const cached = componentDetailsByRowKeyRef.current?.[key];
    const sources = [
      product.ComponentDetails,
      product.componentDetails,
      cached,
      product.ComponentDetailsJson,
      product.componentDetailsJson,
    ];
    for (const src of sources) {
      const rows = parseComponentDetailsArray(src);
      if (!rows.length) continue;
      return rows.reduce(
        (sum, r) =>
          sum + (parseFloat(r?.ComponentQty ?? r?.componentQty ?? 0) || 0),
        0
      );
    }
    return 0;
  };

  // Raise Query Modal Functions
  const handleRaiseQueryOpen = async () => {
    if (isSrClosedStatus) return;
    if (isCurrentUserTheRejector) {
      Swal.fire({
        icon: "info",
        title: "Action not allowed",
        text: rejectorActionsDisabledMessage,
        confirmButtonColor: "#0b6bff",
      });
      return;
    }
    // Only current pending approver may raise query (WIP+HasClose override still allowed).
    if (isSrActed && !allowRaiseQueryWhenWipClosePending) {
      Swal.fire({
        icon: "info",
        title: "Not current pending level",
        text: notCurrentPendingApproverMessage,
        confirmButtonColor: "#0b6bff",
      });
      return;
    }
    if (hasPendingQueryForCurrentSR) {
      Swal.fire({
        icon: "warning",
        title: "Pending Query Exists",
        text: "Raise Query is disabled until all pending queries are responded for this SR.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    const allProducts = srData?.products || [];
    if (allProducts.length === 0) {
      setMaterialSectionError("No material rows available to raise a query.");
      Swal.fire({
        icon: "warning",
        title: "No items",
        text: "There are no material line items for this service request.",
      });
      return;
    }

    setRaiseQueryData({
      name: "",
      query: "",
    });

    Swal.fire({
      title: "Please wait",
      text: "Loading users…",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    // Populate dropdown from down-level users (USERMASTER hierarchy) before opening modal
    try {
      const user = getUser();
      const currentUserIdCode =
        user?.userIdCode ||
        user?.UserIdCode ||
        user?.userCode ||
        user?.UserCode ||
        user?.id ||
        "";

      if (currentUserIdCode) {
        const res =
          await workflowAPI.getDownLevelUsersForRaiseQuery(
            currentUserIdCode,
            childSrCodeFromQuery || null
          );

        const list = res?.success ? res?.data : [];
        if (Array.isArray(list)) {
          // SP returns one row per workflow level; same user can appear twice
          // (e.g. BCM at two levels) — keep a single option per UserIdCode.
          const seen = new Set();
          const unique = [];
          for (const u of list) {
            const id = String(
              u.UserIdCode || u.userIdCode || u.id || u.UserId || ""
            ).trim();
            const name = String(
              u.Name ||
                u.name ||
                u.DisplayName ||
                u.FullName ||
                u.userName ||
                u.UserName ||
                ""
            ).trim();
            const dedupeKey = (id || name).toLowerCase();
            if (!dedupeKey || seen.has(dedupeKey)) continue;
            // Cannot raise a query to yourself
            if (
              id &&
              currentUserIdCode &&
              id.toLowerCase() === String(currentUserIdCode).trim().toLowerCase()
            ) {
              continue;
            }
            seen.add(dedupeKey);
            unique.push({
              id,
              name,
              userRole:
                u.UserTypeName ||
                u.userTypeName ||
                u.UserRole ||
                u.userRole ||
                u.RoleName ||
                u.roleName ||
                u.UserTypeCode ||
                u.userTypeCode ||
                "",
            });
          }
          setNameOptions(unique);
        } else {
          setNameOptions([]);
        }
      } else {
        setNameOptions([]);
      }
    } catch (e) {
      console.error("Error fetching down-level users:", e);
      setNameOptions([]);
    } finally {
      Swal.close();
    }

    setRaiseQueryOpen(true);

    // Show modal using Bootstrap if available
    if (raiseQueryModalInstanceRef.current) {
      try {
        raiseQueryModalInstanceRef.current.show();
      } catch (error) {
        console.debug("Error showing Raise Query modal:", error);
      }
    }
  };

  const handleRaiseQueryClose = () => {
    if (raiseQuerySubmittingRef.current) {
      return;
    }
    if (raiseQueryModalInstanceRef.current) {
      try {
        raiseQueryModalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing Raise Query modal:", error);
      }
    }

    requestAnimationFrame(() => {
      setRaiseQueryOpen(false);
      setRaiseQuerySubmitting(false);
    });
  };

  const handleRaiseQuerySubmit = async () => {
    const queryText = (raiseQueryData?.query || "").trim();
    // Use state first; fallback to current select value (Select2 can leave state out of sync)
    const selectedFromState = (raiseQueryData?.name || "").trim();
    const selectEl = document.getElementById("raiseQuerySelect");
    const selectedFromDom = selectEl ? (selectEl.value || "").trim() : "";
    const raisedToUserIdCode = selectedFromState || selectedFromDom;

    if (!raisedToUserIdCode) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please select a user to raise the query.",
      });
      return;
    }

    if (!queryText) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Query text is mandatory.",
      });
      return;
    }

    // Validation: prevent raising another query to the same user
    // when an existing query is already pending for that user on this SR.
    const selectedUserNormalized = String(raisedToUserIdCode || "").trim().toUpperCase();
    const hasPendingForSelectedUser = Array.isArray(queries)
      ? queries.some((q) => {
          const status = String(q?.status || "").trim().toLowerCase();
          const raisedTo = String(q?.raisedToUserId || "").trim().toUpperCase();
          return status === "pending" && raisedTo !== "" && raisedTo === selectedUserNormalized;
        })
      : false;

    if (hasPendingForSelectedUser) {
      Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "A pending query already exists for this user. Please wait for response before raising another query.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    const user = getUser();
    const raisedByUserIdCode =
      user?.userIdCode || user?.UserIdCode || user?.userCode || user?.UserCode || user?.id || "";

    if (
      raisedByUserIdCode &&
      String(raisedToUserIdCode).trim().toLowerCase() ===
        String(raisedByUserIdCode).trim().toLowerCase()
    ) {
      Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "You cannot raise a query to yourself.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    // Parent SRCode comes from loaded SR header; fallback to route id.
    const parentSRCode = srData?.srCode || srData?.SRCode || id;
    const childSRCode =
      srData?.childSRNumber ||
      srData?.ChildSRNumber ||
      childSrCodeFromQuery ||
      null;

    setRaiseQuerySubmitting(true);
    try {
      const payload = {
        SRCode: parentSRCode,
        ChildSRCode: childSRCode,
        QueryText: queryText,
        RaisedToUserIdCode: raisedToUserIdCode,
      };

      const res = await workflowAPI.raiseTicketQueryV2(
        payload,
        raisedByUserIdCode
      );

      setRaiseQuerySubmitting(false);
      if (res?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: "Query raised successfully.",
        });
        handleRaiseQueryClose();
        window.location.reload();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: res?.message || "Failed to raise query.",
        });
      }
    } catch (e) {
      setRaiseQuerySubmitting(false);
      console.error("Error raising ticket query:", e);
      const message =
        e?.response?.data?.message ||
        "An error occurred while raising the query.";
      const isValidation =
        message.toLowerCase().includes("query limit reached") ||
        message.toLowerCase().includes("maximum allowed per sr");
      Swal.fire({
        icon: isValidation ? "warning" : "error",
        title: isValidation ? "Validation" : "Error",
        text: message,
      });
    }
  };

  // Query Response Functions (for SE)
  const getCurrentUserId = () => {
    try {
      const user = getUser();
      return user?.userId || user?.id || user?.UserId || user?.Id || 1;
    } catch {
      return 1;
    }
  };

  const fetchQueries = async () => {
    if (!id) return;
    
    setLoadingQueries(true);
    try {
      const isNumeric = id && !isNaN(Number(id));
      if (isNumeric) {
        const complaintId = parseInt(id);
        const response = await workflowAPI.getQueries({
          complaintId,
          status: "Pending" // Only fetch pending queries for SE to respond
        });

        if (response && response.success && response.data) {
          // Filter queries where SE is the recipient (raisedToUserId matches current user)
          const currentUserId = getCurrentUserId();
          const seQueries = response.data.filter(
            (query) =>
              query.status === "Pending" &&
              (String(query.raisedToUserId ?? "") === String(currentUserId ?? "") ||
                String(query.raisedToUserId ?? "") === String(currentUserIdCode ?? ""))
          );
          setQueriesSafe(seQueries);
        } else {
          setQueries([]);
        }
      } else {
        // SR pages: fetch query history by SRCode, then filter pending for current recipient.
        const parentSrCode = srData?.srCode || srData?.SRCode || id;
        const childSrCode =
          srData?.childSRNumber || srData?.ChildSRNumber || childSrCodeFromQuery || null;

        const response = await workflowAPI.getTicketQueriesBySR({
          srCode: parentSrCode,
          childSrCode
        });

        if (response && response.success && response.data) {
          const currentUserId = getCurrentUserId();
          const seQueries = response.data.filter(
            (query) =>
              query.status === "Pending" &&
              (String(query.raisedToUserId ?? "") === String(currentUserId ?? "") ||
                String(query.raisedToUserId ?? "") === String(currentUserIdCode ?? ""))
          );
          setQueriesSafe(seQueries);
        } else {
          setQueries([]);
        }
      }
    } catch (error) {
      console.error("Error fetching queries:", error);
      setQueries([]);
    } finally {
      setLoadingQueries(false);
    }
  };

  const handleResponseQueryOpen = async (query) => {
    setSelectedQuery(query);
    setResponseQueryData({
      responseText: "",
      attachments: [],
    });
    setResponseQueryOpen(true);

    // Show modal using Bootstrap if available
    if (responseQueryModalInstanceRef.current) {
      try {
        responseQueryModalInstanceRef.current.show();
      } catch (error) {
        console.debug("Error showing Response Query modal:", error);
      }
    }
  };

  const handleResponseQueryClose = () => {
    if (responseQuerySubmittingRef.current) {
      return;
    }
    if (responseQueryModalInstanceRef.current) {
      try {
        responseQueryModalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing Response Query modal:", error);
      }
    }

    requestAnimationFrame(() => {
      setResponseQueryOpen(false);
      setSelectedQuery(null);
      setResponseQueryData({
        responseText: "",
        attachments: [],
      });
      setResponseQuerySubmitting(false);
    });
  };

  const handleResponseQuerySubmit = async () => {
    if (!selectedQuery) return;

    // Security guard on client side: only assigned recipient can respond.
    // (Backend must also enforce this rule.)
    const currentUserId = getCurrentUserId();
    const assignedTo = String(selectedQuery?.raisedToUserId ?? "").trim();
    const isAssignedToCurrentUser =
      assignedTo !== "" &&
      (assignedTo === String(currentUserId ?? "").trim() ||
        assignedTo === String(currentUserIdCode ?? "").trim());

    if (!isAssignedToCurrentUser) {
      Swal.fire({
        icon: "error",
        title: "Not allowed",
        text: "You can respond only to queries assigned to you.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    // Validate response text is mandatory
    if (!responseQueryData.responseText || responseQueryData.responseText.trim() === "") {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Response field is mandatory. Please enter your response.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    setResponseQuerySubmitting(true);
    try {
      const uploadedAttachmentMeta = [];
      const responseAttachments = Array.isArray(responseQueryData.attachments)
        ? responseQueryData.attachments
        : [];

      for (const item of responseAttachments) {
        if (item?.file) {
          const uploadRes = await complaintAPI.uploadInvoiceAttachment(
            item.file,
            item?.copyFor || "Query Response"
          );
          const uploaded = uploadRes?.data || {};
          uploadedAttachmentMeta.push({
            fileName: uploaded.FileName || uploaded.fileName || item.name || "",
            filePath: uploaded.FilePath || uploaded.filePath || "",
            fileSize: uploaded.FileSize ?? uploaded.fileSize ?? item.size ?? null,
            fileType: uploaded.FileType || uploaded.fileType || item.type || "",
            copyFor: uploaded.CopyFor || uploaded.copyFor || item?.copyFor || "Query Response",
          });
        } else if (item?.path || item?.filePath) {
          uploadedAttachmentMeta.push({
            fileName: item?.name || item?.fileName || "",
            filePath: item?.path || item?.filePath || "",
            fileSize: item?.size || item?.fileSize || null,
            fileType: item?.type || item?.fileType || "",
            copyFor: item?.copyFor || "Query Response",
          });
        }
      }

      const respondPayload = {
        queryId: selectedQuery.queryId,
        responseText: responseQueryData.responseText.trim(),
        srCode: id || srData?.SRCode || srData?.srCode || null,
        childSrCode:
          childSrCodeFromQuery || srData?.ChildSRCode || srData?.childSrCode || null,
        businessVertical: srData?.BusinessVertical || srData?.businessVertical || null,
        categoryCode: srData?.CategoryCode || srData?.categoryCode || null,
        marketCode: srData?.MarketCode || srData?.marketCode || null,
        submittedBy: currentUserIdCode || String(currentUserId || "") || null,
        attachments: uploadedAttachmentMeta,
      };

      const response = await workflowAPI.respondToQuery(
        selectedQuery.queryId,
        respondPayload,
        currentUserIdCode || String(currentUserId || "").trim() || null
      );

      setResponseQuerySubmitting(false);
      if (response && response.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text: "Query response submitted successfully. The approver will be notified.",
          confirmButtonColor: "#0b6bff",
        });

        // Refresh queries list
        await fetchQueries();
        
        // Close modal
        handleResponseQueryClose();
        window.location.reload();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response?.message || "Failed to submit query response. Please try again.",
          confirmButtonColor: "#0b6bff",
        });
      }
    } catch (error) {
      setResponseQuerySubmitting(false);
      console.error("Error submitting query response:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "An error occurred while submitting the response. Please try again.",
        confirmButtonColor: "#0b6bff",
      });
    }
  };

  // Fetch all queries (including history) for display
  const fetchAllQueries = async () => {
    if (!id) {
      setQueries([]);
      return;
    }
    setLoadingQueries(true);
    try {
      const isNumeric = id && !isNaN(Number(id));
      if (isNumeric) {
        const complaintId = parseInt(id);
        const response = await workflowAPI.getQueries({ complaintId });





        
        if (response && response.success && response.data && response.data.length > 0) {
          setQueriesSafe(response.data);
        } else {
          setQueries([]);
        }
      } else {
        // SR pages: fetch by SRCode + optional childSrCode for card display.
        const parentSrCode = srData?.srCode || srData?.SRCode || id;
        const childSrCode =
          srData?.childSRNumber || srData?.ChildSRNumber || childSrCodeFromQuery || null;

        const response = await workflowAPI.getTicketQueriesBySR({
          srCode: parentSrCode,
          childSrCode
        });

        if (response && response.success && Array.isArray(response.data)) {
          setQueriesSafe(response.data);
        } else {
          setQueries([]);
        }
      }
    } catch (error) {
      console.error("Error fetching all queries:", error);
      setQueries([]);
    } finally {
      setLoadingQueries(false);
    }
  };

  // Approve Modal Functions
  const handleApproveOpen = () => {
    // Extract complaint items from srData.products
    const items = (srData?.products || []).map((product, index) => {
      const compQty = parseFloat(
        product?.ComplaintQty || product?.complaintQty || 0
      );
      const unitPrice = parseFloat(product?.Price || product?.price || 0);
      // Calculate Comp Value if not available: Comp Qty * Unit Price
      const compValue =
        product?.ComplaintValue ||
        product?.complaintValue ||
        (compQty * unitPrice).toFixed(2);

      return {
        id: product?.ProductCode || product?.productCode || `item-${index}`,
        itemCategory:
          product?.ProductCategory || product?.productCategory || "N/A",
        compQty: compQty.toString(),
        compValue: compValue.toString(),
        changeValue: "",
      };
    });

    setApproveData({
      items: items,
      remarks: "",
    });
    setApproveOpen(true);
    // Show modal using Bootstrap if available
    if (approveModalInstanceRef.current) {
      try {
        approveModalInstanceRef.current.show();
      } catch (error) {
        console.debug("Error showing Approve modal:", error);
      }
    }
  };

  const handleApproveClose = () => {
    if (approveModalInstanceRef.current) {
      try {
        approveModalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing Approve modal:", error);
      }
    }
    setTimeout(() => {
      setApproveOpen(false);
    }, 300);
  };

  const handleChangeValueUpdate = (itemId, value) => {
    setApproveData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === itemId ? { ...item, changeValue: value } : item
      ),
    }));
  };

  // Approve/Reject Modal Functions — always includes every material row (no row checkboxes)
  const handleApproveRejectOpen = () => {
    const requestedWipClose = pendingWipCloseOpenRef.current;
    pendingWipCloseOpenRef.current = false;
    // Closed SR: no Approve/Reject (or Close via this modal).
    if (isSrClosedStatus) return;
    // Rejector cannot Approve/Reject — but Country Head Reopen (Special Case) is allowed.
    if (isCurrentUserTheRejector && !canReopenRejectedSr) {
      Swal.fire({
        icon: "info",
        title: "Action not allowed",
        text: rejectorActionsDisabledMessage,
        confirmButtonColor: "#0b6bff",
      });
      return;
    }
    // Extra rule: when SR is in WIP, do not allow Approve/Reject flow.
    // Exceptions: WIP "Close" (same modal), and Plant QC Domestic Installation Assign.
    if (
      isSrWipStatus &&
      !requestedWipClose &&
      !isPlantQcInstallationWipAssignFlow
    )
      return;

    // Save focus so we can restore it after modal closes (prevents aria-hidden/focus warnings)
    try {
      approveRejectLastFocusRef.current = document.activeElement;
    } catch (e) {
      approveRejectLastFocusRef.current = null;
    }

    if (isSrActed && !canReopenRejectedSr && !(requestedWipClose && canCloseWipQcChild)) {
      Swal.fire({
        icon: "info",
        title: "Not current pending level",
        text: notCurrentPendingApproverMessage,
        confirmButtonColor: "#0b6bff",
      });
      return;
    }
    if (hasPendingQueryForCurrentSR) {
      Swal.fire({
        icon: "warning",
        title: "Pending Query Exists",
        text: "Approve / Reject is disabled until all pending queries are responded for this SR.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }

    const { allProducts, rowsForValidation } = resolveMaterialRowsForApproveValidation();
    if (allProducts.length === 0) {
      setMaterialSectionError("No material rows available to approve/reject.");
      Swal.fire({
        icon: "warning",
        title: "No items",
        text: "There are no material line items for this service request.",
      });
      return;
    }

    if (!canReopenRejectedSr && !requestedWipClose && !validateMaterialApproveQtyForAction(rowsForValidation)) {
      return;
    }

    // Close STO/CN "Approval Amount": prefer SR Final Approved Value over Cal. Approved.
    const headerFinalApprovedForStoCn = resolveFinalApprovedValueTotal(
      rowsForValidation,
      srData?.finalApprovedValue ?? srData?.FinalApprovedValue
    );

    const items = rowsForValidation.map((product, index) => {
      const compQtyRaw = parseFloat(product?.ComplaintQty || product?.complaintQty || 0);
      const unitPrice = parseFloat(product?.Price || product?.price || 0);
      const invoiceQtyRaw = parseFloat(product?.Quantity || product?.quantity || 0);
      const approvedQtyRaw = parseFloat(
        product?.ApprovedQty ||
          product?.approvedQty ||
          product?.ApprovedQuantity ||
          product?.approvedQuantity ||
          0
      );

      const compQtyInt = toViewSrQtyInt(compQtyRaw);
      // Keep SR Qty non-zero like previous logic; default to 1 when 0/invalid.
      const srQtyInt = compQtyInt === 0 || isNaN(compQtyInt) ? 1 : compQtyInt;

      const invoiceQtyInt = toViewSrQtyInt(invoiceQtyRaw);
      const approvedQtyInt = approvedQtyRaw > 0 ? toViewSrQtyInt(approvedQtyRaw) : 0;
      const isThreeBvMaterialRow = materialRowShowsComponentDetailsIcon(product);
      const componentQtyTotal = isThreeBvMaterialRow
        ? toViewSrQtyInt(getComponentDetailsQtyTotalForProduct(product))
        : 0;
      // Three BV: Approve Qty is hidden — always use category component qty (do not cap
      // against Complaint/SR Qty, which can be invoice qty e.g. 42 vs component 60).
      let effectiveApprovedQtyInt = 0;
      if (isThreeBvMaterialRow) {
        if (componentQtyTotal > 0) {
          effectiveApprovedQtyInt = componentQtyTotal;
        } else if (approvedQtyInt > 0) {
          effectiveApprovedQtyInt = approvedQtyInt;
        } else if (srQtyInt > 0) {
          effectiveApprovedQtyInt = srQtyInt;
        } else if (invoiceQtyInt > 0) {
          effectiveApprovedQtyInt = invoiceQtyInt;
        }
      } else {
        const cappedApprovedQtyInt =
          srQtyInt > 0 ? Math.min(approvedQtyInt, srQtyInt) : approvedQtyInt;
        // Solid rule: if approve qty is 0, keep it 0 (do not fallback to SR qty).
        // Reopen (rejected SR): same payload as approve — default approve qty to SR qty when grid has none.
        effectiveApprovedQtyInt = cappedApprovedQtyInt > 0 ? cappedApprovedQtyInt : 0;
        if (canReopenRejectedSr && effectiveApprovedQtyInt <= 0 && srQtyInt > 0) {
          effectiveApprovedQtyInt = srQtyInt;
        }
      }

      const compValueRounded = formatViewSrMoney(compQtyInt * unitPrice);
      const totalPriceRounded = formatViewSrMoney(invoiceQtyInt * unitPrice);
      const approvedValueRounded = formatViewSrMoney(effectiveApprovedQtyInt * unitPrice);
      const srValueRounded = formatViewSrMoney(compQtyInt * unitPrice);

      // Use default values for prototype if comp value is 0 or not available
      const finalCompValue =
        parseFloat(compValueRounded) === 0 || isNaN(parseFloat(compValueRounded))
          ? "100.00"
          : compValueRounded.toString();

      // Get product category with mapping
      let category =
        product?.ProductCategory ||
        product?.productCategory ||
        product?.Productcategory ||
        "";
      const normalizedCategory = (category || "").trim().toLowerCase();
      if (normalizedCategory === "makasaply" || normalizedCategory === "mikasaply") {
        category = "Plain Particle Board";
      } else if (normalizedCategory === "floors") {
        category = "Prelam";
      }

      // Get plant name / code (SAP invoice uses ProductPlantDescription; master uses short PlantName)
      let plantName =
        product?.ProductPlantDescription ||
        product?.productPlantDescription ||
        product?.PlantName ||
        product?.plantName ||
        product?.Plantname ||
        product?.plant_name ||
        "";
      const plantCodeRaw = String(
        product?.PlantCode ||
          product?.plantCode ||
          product?.PLANTCODE ||
          product?.PlantID ||
          product?.plantId ||
          ""
      ).trim();
      const productPlantIdRaw = String(
        product?.ProductPlantId || product?.productPlantId || ""
      ).trim();
      const rdcCodeRaw = String(
        product?.RdcCode || product?.rdcCode || product?.RDCCODE || product?.RDCCode || ""
      ).trim();
      const rdcNameRaw = String(
        product?.RdcName || product?.rdcName || product?.RDCNAME || product?.RDCName || ""
      ).trim();
      if (!plantName) {
        const dummyPlantNames = [
          "Default Plant",
          "Plant A",
          "Plant B",
          "Manufacturing Plant 1",
          "Production Facility",
        ];
        plantName = dummyPlantNames[index % dummyPlantNames.length];
      }

      return {
        id: product?.ProductCode || product?.productCode || `item-${index}`,
        sno: index + 1,
        productCategory: category,
        plantName: plantName,
        plantCode: plantCodeRaw,
        productPlantId: productPlantIdRaw,
        productPlantDescription: plantName,
        rdcCode: rdcCodeRaw,
        rdcName: rdcNameRaw,
        itemDescription: product?.ProductName || product?.productName || product?.Productname || "",
        invoiceQty: invoiceQtyInt.toString(),
        uom: "PCS",
        invoiceUnitPrice: formatViewSrMoney(unitPrice),
        totalPrice: totalPriceRounded,
        srCategory: product?.Category || product?.category || product?.SRCategory || product?.srCategory || "",
        srQty: srQtyInt.toString(),
        srValue: srValueRounded,
        approvedQty: effectiveApprovedQtyInt > 0 ? effectiveApprovedQtyInt.toString() : "",
        approvedValue: approvedValueRounded,
        itemCategory: category,
        compQty: srQtyInt.toString(),
        compValue: finalCompValue,
        changeValue: "",
        // Backend keys for approve/reject API (InvoiceNumber, SRTYPECODE, ProductCode, BatchNo)
        invoiceNumber: product?.InvoiceNumber ?? product?.invoiceNumber ?? srData?.invoiceNumber ?? srData?.invoiceNo ?? "",
        srTypeCode: product?.SRTypeCode ?? product?.srTypeCode ?? "",
        productCode: product?.ProductCode ?? product?.productCode ?? "",
        batchNo: product?.BatchNo ?? product?.batchNo ?? null,
        doorMaterialCode: product?.DoorMaterialCode ?? product?.doorMaterialCode ?? "",
        componentIssueRemarks: product?.ComponentIssueRemarks ?? product?.componentIssueRemarks ?? "",
        /* Three BV: open Component Details from Approve/Reject modal */
        materialRowKey: getMaterialRowKey(product, index),
        businessVertical:
          product?.BusinessVertical ?? product?.businessVertical ?? "",
        ComponentDetails:
          product?.ComponentDetails ??
          product?.componentDetails ??
          componentDetailsByRowKeyRef.current?.[getMaterialRowKey(product, index)] ??
          null,
        componentDetails:
          product?.ComponentDetails ??
          product?.componentDetails ??
          componentDetailsByRowKeyRef.current?.[getMaterialRowKey(product, index)] ??
          null,
        apprvQuantity: effectiveApprovedQtyInt > 0 ? effectiveApprovedQtyInt : 0,
        apprvValue: (() => {
          const v = parseFloat(approvedValueRounded);
          return Number.isFinite(v) ? v : (srQtyInt * unitPrice) || 0;
        })(),

        // STO/CN Details (Close only) — shared Level 1/2 fields
        stoCnValue: (() => {
          const raw = String(product?.StoCnValue ?? product?.stoCnValue ?? "").trim();
          if (raw === "STO") return "SRN";
          if (raw === "CN") {
            const cn = String(product?.CnType ?? product?.cnType ?? "").trim();
            if (cn === "Credit Note") return "Credit Note";
            if (cn === "Replacement" || cn === "Replacement & Returnable") return "Replacement";
            return "";
          }
          return raw;
        })(),
        isReturnable: (() => {
          const r = Boolean(product?.IsReturnable ?? product?.isReturnable ?? false);
          const nr = Boolean(product?.IsNonReturnable ?? product?.isNonReturnable ?? false);
          return r && !nr;
        })(),
        isNonReturnable: (() => {
          const r = Boolean(product?.IsReturnable ?? product?.isReturnable ?? false);
          const nr = Boolean(product?.IsNonReturnable ?? product?.isNonReturnable ?? false);
          return nr && !r;
        })(),
        referenceNo: (() => {
          const direct = product?.ReferenceNo ?? product?.referenceNo ?? "";
          if (String(direct).trim()) return String(direct);
          const raw = String(product?.StoCnValue ?? product?.stoCnValue ?? "").trim();
          if (raw === "STO") return String(product?.SapIndentOrderNo ?? product?.sapIndentOrderNo ?? "");
          if (raw === "CN") return String(product?.CreditNoteNumber ?? product?.creditNoteNumber ?? "");
          return "";
        })(),
        // Close / Validate: autofill Qty from Approved Qty; Approval Amount from Final Approved Value.
        amount: (() => {
          const saved = String(
            product?.Amount ?? product?.amount ?? product?.StoCnAmount ?? product?.stoCnAmount ?? ""
          ).trim();
          if (saved !== "" && saved !== "0" && saved !== "0.00") return saved;
          const lineFinal = toViewSrMoneyNumber(
            product?.FinalApprovedValue ?? product?.finalApprovedValue
          );
          if (lineFinal > 0) return formatViewSrMoney(lineFinal);
          if (headerFinalApprovedForStoCn > 0) {
            return formatViewSrMoney(headerFinalApprovedForStoCn);
          }
          return approvedValueRounded || "";
        })(),
        qty: (() => {
          const raw = product?.Qty ?? product?.qty ?? product?.StoCnQty ?? product?.stoCnQty ?? "";
          if (raw !== "" && raw != null) {
            const n = Math.floor(Number(raw));
            const s = Number.isFinite(n) ? String(n) : String(raw).replace(/[^\d]/g, "");
            if (s !== "" && s !== "0") return s;
          }
          return effectiveApprovedQtyInt > 0 ? String(effectiveApprovedQtyInt) : "";
        })(),
        srnOrderNo: product?.SrnOrderNo ?? product?.srnOrderNo ?? "",
        reBillOrderNo: (() => {
          const direct = product?.ReBillOrderNo ?? product?.reBillOrderNo ?? "";
          if (String(direct).trim()) return String(direct);
          return String(product?.NewBillingNo ?? product?.newBillingNo ?? "");
        })(),
        finalClosureReferenceNo:
          product?.FinalClosureReferenceNo ?? product?.finalClosureReferenceNo ?? "",
        finalClosureAmount: (() => {
          const saved = String(
            product?.FinalClosureAmount ?? product?.finalClosureAmount ?? ""
          ).trim();
          if (saved !== "" && saved !== "0" && saved !== "0.00") return saved;
          return approvedValueRounded || "";
        })(),
        finalClosureQty: (() => {
          const raw = product?.FinalClosureQty ?? product?.finalClosureQty ?? "";
          if (raw !== "" && raw != null) {
            const n = Math.floor(Number(raw));
            const s = Number.isFinite(n) ? String(n) : String(raw).replace(/[^\d]/g, "");
            if (s !== "" && s !== "0") return s;
          }
          return effectiveApprovedQtyInt > 0 ? String(effectiveApprovedQtyInt) : "";
        })(),
      };
    });

    const seedRow = rowsForValidation[0] || {};
    const toDateInput = (raw) => {
      if (raw == null || raw === "") return "";
      try {
        const d = raw instanceof Date ? raw : new Date(raw);
        if (!Number.isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, "0");
          const day = String(d.getDate()).padStart(2, "0");
          return `${y}-${m}-${day}`;
        }
      } catch (e) {
        /* ignore */
      }
      const s = String(raw);
      return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : "";
    };
    const getTodayDateInput = () => {
      const d = new Date();
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };

    // Normalize STO/CN dates to yyyy-mm-dd for <input type="date" /> (edit mode).
    const itemsWithStoCnPrefill = (items || []).map((it) => ({
      ...it,
      billingDate: toDateInput(it?.billingDate),
      rdcInvoiceDate: toDateInput(it?.rdcInvoiceDate),
    }));
    const seededFactoryIntentDate = toDateInput(
      seedRow.FactoryIntentDate ?? seedRow.factoryIntentDate
    );

    finalApprovedValueTouchedRef.current = false;
    const autoFinalApprovedTotal = getFinalApprovalMaxTotal(itemsWithStoCnPrefill);
    const headerFinalApproved = resolveFinalApprovedValueTotal(
      srData?.products,
      srData?.finalApprovedValue ?? srData?.FinalApprovedValue
    );
    const seededRca = resolveRcaFromSr(srData, itemsWithStoCnPrefill);
    const defaultFinalApprovedValue =
      businessVerticalRequiresComponentDetails &&
      !shouldWriteThreeBvApprovalMoneyValues
        ? "0"
        : pickModalFinalApprovalAmount(
            autoFinalApprovedTotal,
            headerFinalApproved
          );
    setApproveData({
      items: itemsWithStoCnPrefill,
      remarks: "",
      factoryIntentNo: (seedRow.FactoryIntentNo ?? seedRow.factoryIntentNo ?? "").toString(),
      factoryIntentDate: seededFactoryIntentDate || getTodayDateInput(),
      settlementType: (seedRow.SettlementType ?? seedRow.settlementType ?? "").toString(),
      finalApprovedValue: defaultFinalApprovedValue,
      rcaGiven: seededRca.given,
      rcaRemarks: seededRca.remarks,
      deductFromChKitty: resolveDeductFromChKitty(srData, itemsWithStoCnPrefill),
      physicalVisitRequired: (
        seedRow.PhysicalVisitRequired ??
        seedRow.physicalVisitRequired ??
        ""
      ).toString(),
      siteVisitTraveler: (
        seedRow.SiteVisitTraveler ??
        seedRow.siteVisitTraveler ??
        ""
      ).toString(),
      technicalVisitType: (
        seedRow.TechnicalVisitType ??
        seedRow.technicalVisitType ??
        ""
      ).toString(),
      seUserSpecificationType: "",
      seNextApproverUserTypeCode: "",
      sePlantIds: [],
      seRdcIds: [],
      seNextApproverRoutingUserId: "",
    });
    setWipCloseFlow(requestedWipClose);
    setApproveRejectOpen(true);
    if (approveRejectModalInstanceRef.current) {
      try {
        approveRejectModalInstanceRef.current.show();
        // Initialize Bootstrap tooltips after modal is shown
        setTimeout(() => {
          const tooltipTriggerList = [].slice.call(
            document.querySelectorAll('[data-bs-toggle="tooltip"]')
          );
          tooltipTriggerList.map(function (tooltipTriggerEl) {
            return new window.bootstrap.Tooltip(tooltipTriggerEl);
          });
        }, 300);
      } catch (error) {
        console.debug("Error showing Approve/Reject modal:", error);
      }
    }
  };

  const isSeApproveRoutingUser = () => shouldShowSeApproveRoutingControls(srData);

  const buildSeApproveRejectExtras = () => {
    const plantCodesCsv = buildApproveRejectPlantCodesCsv(
      approveData.items || [],
      approveSeDropdowns.plants || []
    );
    if (!isSeApproveRoutingUser()) {
      return plantCodesCsv ? { plantCodesCsv, sePlantIdsCsv: plantCodesCsv } : {};
    }
    const spec = canonicalSeUserSpecificationType(approveData.seUserSpecificationType);
    const role = (approveData.seNextApproverUserTypeCode || "").trim();
    const scopeUser = String(approveData.seNextApproverRoutingUserId || "").trim();
    const needsScopeUser = isSeApproveScopedSpec(spec);
    return {
      plantCodesCsv,
      seUserSpecificationType: spec,
      seNextApproverUserTypeCode: role || null,
      sePlantIdsCsv: plantCodesCsv,
      seRdcIdsCsv: null,
      seNextApproverUserIdCodesCsv: needsScopeUser && scopeUser ? scopeUser : null,
    };
  };

  const validateSeRoutingForApproveOrReopen = () => {
    if (!isSeApproveRoutingUser()) return true;
    const allowedFlowIds = new Set(getSeApproveSrFlowOptions(srData).map((o) => o.id));
    const spec = canonicalSeUserSpecificationType(approveData.seUserSpecificationType || "");
    if (!spec || !allowedFlowIds.has(spec) || !isSeApproveScopedSpec(spec)) {
      Swal.fire({
        icon: "warning",
        title: "SR Flow required",
        text: "Please select SR Flow before continuing.",
      });
      return false;
    }
    const role = (approveData.seNextApproverUserTypeCode || "").trim();
    if (!role) {
      Swal.fire({
        icon: "warning",
        title: "Role required",
        text: "Please select Role before continuing.",
      });
      return false;
    }
    if (isSeApproveScopedSpec(spec)) {
      const uid = String(approveData.seNextApproverRoutingUserId || "").trim();
      if (!uid) {
        Swal.fire({
          icon: "warning",
          title: "User required",
          text: getSeApproveRoutingUserRequiredMessage(spec),
        });
        return false;
      }
    }
    return true;
  };

  const handleApproveRejectClose = () => {
    if (approveRejectSubmittingRef.current) {
      return;
    }
    // Ensure focus is not trapped on an element that may become aria-hidden
    try {
      if (document.activeElement && typeof document.activeElement.blur === "function") {
        document.activeElement.blur();
      }
    } catch (e) {
      // ignore
    }
    if (approveRejectModalInstanceRef.current) {
      try {
        approveRejectModalInstanceRef.current.hide();
      } catch (error) {
        console.error("Error closing Approve/Reject modal:", error);
      }
    }
    setTimeout(() => {
      setApproveRejectOpen(false);
      setWipCloseFlow(false);
      finalApprovedValueTouchedRef.current = false;
      setApproveData({
        items: [],
        remarks: "",
        factoryIntentNo: "",
        factoryIntentDate: "",
        settlementType: "",
        finalApprovedValue: "",
        rcaGiven: "",
        rcaRemarks: "",
        deductFromChKitty: "",
        physicalVisitRequired: "",
        siteVisitTraveler: "",
        technicalVisitType: "",
        seUserSpecificationType: "",
        seNextApproverUserTypeCode: "",
        sePlantIds: [],
        seRdcIds: [],
        seNextApproverRoutingUserId: "",
      });
      setApproveRejectSubmitting(false);
      // Restore focus back to the trigger button/link when possible
      try {
        const el = approveRejectLastFocusRef.current;
        if (el && typeof el.focus === "function") el.focus();
      } catch (e) {
        // ignore
      } finally {
        approveRejectLastFocusRef.current = null;
      }
    }, 300);
  };

  const resetApproveRejectSubmitting = () => {
    approveRejectSubmittingRef.current = false;
    setApproveRejectSubmitting(false);
  };

  const showApproveRejectActionError = async (message) => {
    // Close the 9999 overlay before Swal. Leaving both open freezes the page:
    // same z-index, body overflow lock, leftover aria-hidden / invisible Swal backdrop.
    resetApproveRejectSubmitting();
    handleApproveRejectClose();
    await Swal.fire({
      icon: "error",
      title: "Error",
      text: message || "The action could not be completed.",
      confirmButtonColor: "#0b6bff",
      allowOutsideClick: true,
      allowEscapeKey: true,
      heightAuto: false,
      scrollbarPadding: false,
    });
  };

  const handleApproveSubmit = async () => {
    if (!srData?.srCode && !srData?.SRCode) {
      Swal.fire({ icon: "error", title: "Error", text: "SR data not loaded." });
      return;
    }
    const items = (approveData.items || []).filter(
      (i) => (i.invoiceNumber || i.InvoiceNumber) && (i.productCode || i.ProductCode)
    );
    if (items.length === 0) {
      Swal.fire({ icon: "warning", title: "No items", text: "No valid items to approve." });
      return;
    }
    const remarksTrimmedApprove = (approveData.remarks || "").trim();
    const chKittyEdit = shouldEditChKittyField(srData, canReopenRejectedSr);
    const chKittyVal = (approveData.deductFromChKitty || "").trim();
    if (chKittyEdit) {
      if (chKittyVal !== "Yes" && chKittyVal !== "No") {
        Swal.fire({
          icon: "warning",
          title: "Required",
          text: 'Please select "Money to be deducted from CH Kitty?" (Yes or No).',
        });
        return;
      }
      if (chKittyVal === "No" && !remarksTrimmedApprove) {
        Swal.fire({
          icon: "warning",
          title: "Remarks required",
          text: "Please enter remarks when Money to be deducted from CH Kitty is No.",
        });
        return;
      }
    } else if (!remarksTrimmedApprove) {
      Swal.fire({
        icon: "warning",
        title: "Remarks required",
        text: "Please enter remarks before approving.",
      });
      return;
    }
    if (isBcmUser() && showFactoryIntentSettlementSection) {
      const finNo = (approveData.factoryIntentNo || "").trim();
      const finDt = (approveData.factoryIntentDate || "").trim();
      // Settlement Type is optional (per requirement)
      if (!finNo || !finDt) {
        Swal.fire({
          icon: "warning",
          title: "Required for approval",
          text: "BCM must enter Factory Intent No. and Factory Intent Date before approving. These are not required for Reject.",
        });
        return;
      }
    }
    if (!validateSeRoutingForApproveOrReopen()) return;
    if (isRcaUserType()) {
      const rcaGivenVal = (approveData.rcaGiven || "").trim();
      if (rcaGivenVal !== "Yes" && rcaGivenVal !== "No") {
        Swal.fire({
          icon: "warning",
          title: "RCA required",
          text: "Please select Is RCA Required? (Yes or No) before approving.",
        });
        return;
      }
      if (rcaGivenVal === "Yes" && !(approveData.rcaRemarks || "").trim()) {
        Swal.fire({
          icon: "warning",
          title: "RCA required",
          text: "Please enter RCA Remarks before approving.",
        });
        return;
      }
    }
    if (isZonalProductSpecialistUserType()) {
      const visitRequired = (approveData.physicalVisitRequired || "").trim();
      if (visitRequired !== "Yes" && visitRequired !== "No") {
        Swal.fire({
          icon: "warning",
          title: "Required",
          text: "Please select whether a physical visit is required by the technical person.",
        });
        return;
      }
      if (visitRequired === "Yes") {
        if (!(approveData.siteVisitTraveler || "").trim()) {
          Swal.fire({
            icon: "warning",
            title: "Required",
            text: "Please select who would be traveling to the site.",
          });
          return;
        }
        if (!(approveData.technicalVisitType || "").trim()) {
          Swal.fire({
            icon: "warning",
            title: "Required",
            text: "Please select whether it is a local visit or outstation visit.",
          });
          return;
        }
      }
    }
    const rowTotalMax = toViewSrMoneyNumber(getFinalApprovalMaxTotal(items));
    const finalApprovedValueNumRaw = toViewSrMoneyNumber(approveData.finalApprovedValue);
    // Three BV: before Plant QC persist 0 for ApprovedValue / FinalApprovedValue in SRApprovalLog.
    const finalApprovedValueNum = shouldWriteThreeBvApprovalMoneyValues
      ? finalApprovedValueNumRaw
      : 0;
    // Three BV: Final approval Value may be 0; still cannot exceed Total Price.
    if (businessVerticalRequiresComponentDetails) {
      if (finalApprovedValueNumRaw < 0) {
        Swal.fire({
          icon: "warning",
          title: "Invalid amount",
          text: "Final approval Value cannot be negative.",
          confirmButtonColor: "#0b6bff",
        });
        return;
      }
    } else if (finalApprovedValueNumRaw <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid amount",
        text: "Final approval Value must be greater than zero.",
        confirmButtonColor: "#0b6bff",
      });
      return;
    }
    if (rowTotalMax > 0 && finalApprovedValueNumRaw > rowTotalMax) {
      Swal.fire({
        icon: "warning",
        title: "Invalid amount",
        text: businessVerticalRequiresComponentDetails
          ? `Final approval Value cannot exceed Total Price (${formatViewSrMoney(rowTotalMax)}).`
          : `Final approval Value cannot exceed the row total (${formatViewSrMoney(rowTotalMax)}).`,
        confirmButtonColor: "#0b6bff",
      });
      return;
    }
    const inv = Array.isArray(srData.invoiceDetails) ? srData.invoiceDetails[0] : null;
    const prod0 = Array.isArray(srData.products) ? srData.products[0] : null;
    const businessVerticalForApprove =
      srData.businessVertical ||
      srData.BusinessVertical ||
      (inv && (inv.BusinessVertical || inv.businessVertical)) ||
      (prod0 && (prod0.BusinessVertical || prod0.businessVertical)) ||
      null;
    const bcmExtras =
      isBcmUser() && showFactoryIntentSettlementSection
        ? {
            factoryIntentNo: approveData.factoryIntentNo?.trim() || null,
            factoryIntentDate: approveData.factoryIntentDate?.trim() || null,
            settlementType: approveData.settlementType?.trim() || null,
          }
        : {};
    const payload = {
      srCode: srData.srCode || srData.SRCode,
      childSrCode: srData.childSRNumber || srData.ChildSRNumber || childSrCodeFromQuery || null,
      action: "Approve",
      remarks: remarksTrimmedApprove,
      marketCode: srData.marketCode || srData.MarketCode || null,
      businessVertical: businessVerticalForApprove,
      ...bcmExtras,
      finalApprovedValue: finalApprovedValueNum,
      ...(isRcaUserType()
        ? {
            rcaGiven: (approveData.rcaGiven || "").trim(),
            rcaRemarks: (approveData.rcaRemarks || "").trim(),
          }
        : {}),
      ...(shouldEditChKittyField(srData, canReopenRejectedSr)
        ? {
            deductFromChKitty: (approveData.deductFromChKitty || "").trim(),
          }
        : {}),
      ...(isZonalProductSpecialistUserType()
        ? {
            physicalVisitRequired: (approveData.physicalVisitRequired || "").trim() || null,
            siteVisitTraveler:
              (approveData.physicalVisitRequired || "").trim() === "Yes"
                ? (approveData.siteVisitTraveler || "").trim() || null
                : null,
            technicalVisitType:
              (approveData.physicalVisitRequired || "").trim() === "Yes"
                ? (approveData.technicalVisitType || "").trim() || null
                : null,
          }
        : {}),
      ...buildSeApproveRejectExtras(),
      items: items.map((i) => ({
        invoiceNumber: i.invoiceNumber || i.InvoiceNumber,
        srTypeCode: i.srTypeCode || i.SRTypeCode || null,
        productCode: i.productCode || i.ProductCode,
        batchNo: i.batchNo ?? i.BatchNo ?? null,
        apprvQuantity: parseFloat(i.apprvQuantity ?? i.srQty ?? 0) || null,
        apprvValue: shouldWriteThreeBvApprovalMoneyValues
          ? parseFloat(i.apprvValue ?? i.srValue ?? 0) || null
          : 0,
        doorMaterialCode: i.doorMaterialCode ?? null,
        componentIssueRemarks: i.componentIssueRemarks ?? null,
        ...(canPlantQcEditComponentQty
          ? {
              componentDetails: (() => {
                const key = i.materialRowKey || i.MaterialRowKey || i.productCode;
                const fromItem =
                  i.ComponentDetails ?? i.componentDetails ?? null;
                const fromCache =
                  key != null
                    ? componentDetailsByRowKeyRef.current?.[key]
                    : null;
                const fromRef =
                  key != null
                    ? productDataRef.current?.[key]?.ComponentDetails ??
                      productDataRef.current?.[key]?.componentDetails
                    : null;
                const raw = fromItem || fromCache || fromRef;
                const rows = parseComponentDetailsArray(raw);
                if (!rows.length) return null;
                return rows.map((r) => ({
                  materialGroupDesc:
                    r.MaterialGroupDesc ?? r.materialGroupDesc ?? null,
                  componentCategory:
                    r.ComponentCategory ?? r.componentCategory ?? null,
                  componentList: r.ComponentList ?? r.componentList ?? null,
                  componentQty:
                    r.ComponentQty != null
                      ? String(r.ComponentQty)
                      : r.componentQty != null
                        ? String(r.componentQty)
                        : null,
                  componentIssue: r.ComponentIssue ?? r.componentIssue ?? null,
                  doorMaterialCode:
                    r.DoorMaterialCode ?? r.doorMaterialCode ?? null,
                  componentIssueRemarks:
                    r.ComponentIssueRemarks ?? r.componentIssueRemarks ?? null,
                  componentValue:
                    r.ComponentValue ?? r.componentValue ?? null,
                }));
              })(),
            }
          : {}),
      })),
    };
    try {
      setApproveRejectSubmitting(true);
      await complaintAPI.approveRejectServiceRequestItems(
        payload,
        changedByForAction || undefined
      );
      setApproveRejectSubmitting(false);
      approveRejectSubmittingRef.current = false;
      const { srNumberForMsg, srViewHref } = getSrDisplayInfoForMessage(
        srData,
        childSrCodeFromQuery,
        id
      );
      handleApproveRejectClose();
      await Swal.fire({
        icon: "success",
        title: "Done",
        html: buildSrActionSuccessSwalHtml(srNumberForMsg, "approved", srViewHref, { linkify: false }),
      });
      window.location.reload();
    } catch (err) {
      await showApproveRejectActionError(getApiErrorMessage(err, "Failed to approve."));
    } finally {
      resetApproveRejectSubmitting();
    }
  };

  const handleReopenSubmit = async () => {
    if (!canReopenRejectedSr) return;
    if (!srData?.srCode && !srData?.SRCode) {
      Swal.fire({ icon: "error", title: "Error", text: "SR data not loaded." });
      return;
    }
    const items = (approveData.items || []).filter(
      (i) => (i.invoiceNumber || i.InvoiceNumber) && (i.productCode || i.ProductCode)
    );
    if (items.length === 0) {
      Swal.fire({ icon: "warning", title: "No items", text: "No valid items to reopen." });
      return;
    }
    const remarksTrimmedReopen = (approveData.remarks || "").trim();
    const chKittyValReopen = (approveData.deductFromChKitty || "").trim();
    if (isChUserType()) {
      if (chKittyValReopen !== "Yes" && chKittyValReopen !== "No") {
        Swal.fire({
          icon: "warning",
          title: "Required",
          text: 'Please select "Money to be deducted from CH Kitty?" (Yes or No).',
        });
        return;
      }
      if (chKittyValReopen === "No" && !remarksTrimmedReopen) {
        Swal.fire({
          icon: "warning",
          title: "Remarks required",
          text: "Please enter remarks when Money to be deducted from CH Kitty is No.",
        });
        return;
      }
    } else if (!remarksTrimmedReopen) {
      Swal.fire({
        icon: "warning",
        title: "Remarks required",
        text: "Please enter remarks before reopening.",
      });
      return;
    }
    if (isBcmUser() && showFactoryIntentSettlementSection) {
      const finNo = (approveData.factoryIntentNo || "").trim();
      const finDt = (approveData.factoryIntentDate || "").trim();
      if (!finNo || !finDt) {
        Swal.fire({
          icon: "warning",
          title: "Required for reopen",
          text: "BCM must enter Factory Intent No. and Factory Intent Date before reopening. These are not required for Reject.",
        });
        return;
      }
    }
    if (!validateSeRoutingForApproveOrReopen()) return;
    const inv = Array.isArray(srData.invoiceDetails) ? srData.invoiceDetails[0] : null;
    const prod0 = Array.isArray(srData.products) ? srData.products[0] : null;
    const businessVerticalForReopen =
      srData.businessVertical ||
      srData.BusinessVertical ||
      (inv && (inv.BusinessVertical || inv.businessVertical)) ||
      (prod0 && (prod0.BusinessVertical || prod0.businessVertical)) ||
      null;
    const bcmExtras =
      isBcmUser() && showFactoryIntentSettlementSection
        ? {
            factoryIntentNo: approveData.factoryIntentNo?.trim() || null,
            factoryIntentDate: approveData.factoryIntentDate?.trim() || null,
            settlementType: approveData.settlementType?.trim() || null,
          }
        : {};
    const payload = {
      srCode: srData.srCode || srData.SRCode,
      childSrCode: srData.childSRNumber || srData.ChildSRNumber || childSrCodeFromQuery || null,
      action: "Reopened",
      remarks: remarksTrimmedReopen,
      marketCode: srData.marketCode || srData.MarketCode || null,
      businessVertical: businessVerticalForReopen,
      ...bcmExtras,
      ...(isChUserType()
        ? { deductFromChKitty: (approveData.deductFromChKitty || "").trim() }
        : {}),
      ...buildSeApproveRejectExtras(),
      items: items.map((i) => ({
        invoiceNumber: i.invoiceNumber || i.InvoiceNumber,
        srTypeCode: i.srTypeCode || i.SRTypeCode || null,
        productCode: i.productCode || i.ProductCode,
        batchNo: i.batchNo ?? i.BatchNo ?? null,
        apprvQuantity: parseFloat(i.apprvQuantity ?? i.srQty ?? 0) || null,
        apprvValue: parseFloat(i.apprvValue ?? i.srValue ?? 0) || null,
        doorMaterialCode: i.doorMaterialCode ?? null,
        componentIssueRemarks: i.componentIssueRemarks ?? null,
      })),
    };
    try {
      setApproveRejectSubmitting(true);
      await complaintAPI.approveRejectServiceRequestItems(
        payload,
        changedByForAction || undefined
      );
      setApproveRejectSubmitting(false);
      approveRejectSubmittingRef.current = false;
      const { srNumberForMsg, srViewHref } = getSrDisplayInfoForMessage(
        srData,
        childSrCodeFromQuery,
        id
      );
      handleApproveRejectClose();
      await Swal.fire({
        icon: "success",
        title: "Done",
        html: buildSrActionSuccessSwalHtml(srNumberForMsg, "reopened", srViewHref, { linkify: false }),
      });
      window.location.reload();
    } catch (err) {
      await showApproveRejectActionError(getApiErrorMessage(err, "Failed to reopen."));
    } finally {
      resetApproveRejectSubmitting();
    }
  };

  const handleRejectSubmit = async () => {
    if (!srData?.srCode && !srData?.SRCode) {
      Swal.fire({ icon: "error", title: "Error", text: "SR data not loaded." });
      return;
    }
    const items = (approveData.items || []).filter(
      (i) => (i.invoiceNumber || i.InvoiceNumber) && (i.productCode || i.ProductCode)
    );
    if (items.length === 0) {
      Swal.fire({ icon: "warning", title: "No items", text: "No valid items to reject." });
      return;
    }
    const remarksTrimmedReject = (approveData.remarks || "").trim();
    if (!remarksTrimmedReject) {
      Swal.fire({
        icon: "warning",
        title: "Remarks required",
        text: "Please enter remarks before rejecting.",
      });
      return;
    }
    const invR = Array.isArray(srData.invoiceDetails) ? srData.invoiceDetails[0] : null;
    const prod0R = Array.isArray(srData.products) ? srData.products[0] : null;
    const businessVerticalForReject =
      srData.businessVertical ||
      srData.BusinessVertical ||
      (invR && (invR.BusinessVertical || invR.businessVertical)) ||
      (prod0R && (prod0R.BusinessVertical || prod0R.businessVertical)) ||
      null;
    const plantCodesCsv = buildApproveRejectPlantCodesCsv(items, approveSeDropdowns.plants || []);
    const payload = {
      srCode: srData.srCode || srData.SRCode,
      childSrCode: srData.childSRNumber || srData.ChildSRNumber || childSrCodeFromQuery || null,
      action: "Reject",
      remarks: remarksTrimmedReject,
      marketCode: srData.marketCode || srData.MarketCode || null,
      businessVertical: businessVerticalForReject,
      ...(plantCodesCsv ? { plantCodesCsv, sePlantIdsCsv: plantCodesCsv } : {}),
      items: items.map((i) => ({
        invoiceNumber: i.invoiceNumber || i.InvoiceNumber,
        srTypeCode: i.srTypeCode || i.SRTypeCode || null,
        productCode: i.productCode || i.ProductCode,
        batchNo: i.batchNo ?? i.BatchNo ?? null,
        doorMaterialCode: i.doorMaterialCode ?? null,
        componentIssueRemarks: i.componentIssueRemarks ?? null,
      })),
    };
    try {
      setApproveRejectSubmitting(true);
      await complaintAPI.approveRejectServiceRequestItems(
        payload,
        changedByForAction || undefined
      );
      setApproveRejectSubmitting(false);
      approveRejectSubmittingRef.current = false;
      const { srNumberForMsg, srViewHref } = getSrDisplayInfoForMessage(
        srData,
        childSrCodeFromQuery,
        id
      );
      handleApproveRejectClose();
      await Swal.fire({
        icon: "success",
        title: "Done",
        html: buildSrActionSuccessSwalHtml(srNumberForMsg, "rejected", srViewHref, { linkify: false }),
      });
      window.location.reload();
    } catch (err) {
      await showApproveRejectActionError(getApiErrorMessage(err, "Failed to reject."));
    } finally {
      resetApproveRejectSubmitting();
    }
  };

  const handleCloseWipHeaderClick = () => {
    if (isSeUserType()) return;
    pendingWipCloseOpenRef.current = true;
    handleApproveRejectOpen();
  };

  const handleCloseWipSubmit = async () => {
    if (isSeUserType()) return;
    if (!wipCloseFlow) return;
    if (!srData?.srCode && !srData?.SRCode) {
      Swal.fire({ icon: "error", title: "Error", text: "SR data not loaded." });
      return;
    }
    const normalizeDateForApi = (v) => {
      if (v == null) return null;
      if (v instanceof Date && !Number.isNaN(v.getTime())) {
        return v.toISOString().slice(0, 10); // yyyy-mm-dd
      }
      const s = String(v).trim();
      if (!s) return null; // IMPORTANT: empty string breaks DateTime? binding
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
      const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/); // dd/mm/yyyy
      if (m) {
        const dd = m[1].padStart(2, "0");
        const mm = m[2].padStart(2, "0");
        const yyyy = m[3];
        return `${yyyy}-${mm}-${dd}`;
      }
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
      return null;
    };
    const getApiErrorMessage = (err, fallback) => {
      const data = err?.response?.data;
      if (typeof data === "string" && data.trim()) return data.trim();
      // ASP.NET validation errors: { title: "...", status: 400, errors: { Field: ["msg1","msg2"] } }
      const errorsObj = data?.errors || data?.Errors;
      if (errorsObj && typeof errorsObj === "object") {
        try {
          const lines = [];
          Object.keys(errorsObj).forEach((k) => {
            const v = errorsObj[k];
            const msgs = Array.isArray(v) ? v : v != null ? [v] : [];
            msgs
              .map((m) => (m == null ? "" : String(m).trim()))
              .filter(Boolean)
              .forEach((m) => lines.push(k && k !== "''" ? `${k}: ${m}` : m));
          });
          if (lines.length) return lines.join("\n");
        } catch (_) {}
      }
      const msg =
        data?.message ||
        data?.Message ||
        data?.title ||
        data?.Title ||
        err?.message ||
        fallback;
      if (msg && String(msg).trim()) return String(msg).trim();
      try {
        return data ? JSON.stringify(data) : fallback;
      } catch {
        return fallback;
      }
    };
    const items = (approveData.items || []).filter(
      (i) => (i.invoiceNumber || i.InvoiceNumber) && (i.productCode || i.ProductCode)
    );
    if (items.length === 0) {
      Swal.fire({ icon: "warning", title: "No items", text: "No valid items to close." });
      return;
    }
    const remarksTrimmed = (approveData.remarks || "").trim();
    if (!remarksTrimmed) {
      Swal.fire({
        icon: "warning",
        title: "Remarks required",
        text: "Please enter remarks before closing.",
      });
      return;
    }

    // Close: STO/CN Details — SRN / Credit Note / Replacement (+ Level 2 final closure)
    const isCloseLevel2 = isValidateSrLabelUserType;
    for (let idx = 0; idx < items.length; idx++) {
      const i = items[idx] || {};
      const productCode = (i.productCode || i.ProductCode || "").toString().trim();
      const stoCnValue = (i.stoCnValue ?? "").toString().trim();
      if (!stoCnValue) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Please select STO/CN Details for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
        });
        return;
      }
      if (!["SRN", "Credit Note", "Replacement"].includes(stoCnValue)) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Invalid STO/CN Details selection for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
        });
        return;
      }
      const amountVal = String(i.amount ?? "").trim();
      const qtyVal = String(i.qty ?? "").trim();
      if (!qtyVal) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Quantity (Qty) cannot be blank for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
        });
        return;
      }
      if (!/^\d+$/.test(qtyVal) || Number(qtyVal) <= 0) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Quantity (Qty) must be a whole number greater than 0 for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
        });
        return;
      }
      const qtyNum = Number(qtyVal);
      const approvedQtyMax = Math.max(
        0,
        Math.floor(
          Number(
            i.apprvQuantity ??
              i.approvedQty ??
              i.ApprovedQty ??
              i.ApprvQuantity ??
              0
          ) || 0
        )
      );
      if (approvedQtyMax > 0 && qtyNum > approvedQtyMax) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Quantity (Qty) cannot be greater than Approved Qty (${approvedQtyMax})`,
        });
        return;
      }
      if (!amountVal) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Approval Amount cannot be blank for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
        });
        return;
      }
      const amountNum = Number(amountVal);
      if (!Number.isFinite(amountNum) || amountNum <= 0) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Approval Amount must be greater than 0 for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
        });
        return;
      }
      const finalApprovedMax = toViewSrMoneyNumber(
        approveData.finalApprovedValue ??
          srData?.finalApprovedValue ??
          srData?.FinalApprovedValue ??
          getFinalApprovalMaxTotal(items)
      );
      if (finalApprovedMax > 0 && amountNum > finalApprovedMax) {
        Swal.fire({
          icon: "warning",
          title: "Validation Error",
          text: `Approval Amount cannot be greater than Final Approved Value (${formatViewSrMoney(finalApprovedMax)})`,
        });
        return;
      }
      if (stoCnValue === "SRN" || stoCnValue === "Credit Note") {
        if (!(i.referenceNo ?? "").toString().trim()) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Please enter Reference No. for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
          });
          return;
        }
      }
      if (stoCnValue === "Replacement") {
        const isReturnable = Boolean(i.isReturnable);
        const isNonReturnable = Boolean(i.isNonReturnable);
        if (!isReturnable && !isNonReturnable) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Please select Returnable or Non-Returnable for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
          });
          return;
        }
        if (isReturnable && !(i.srnOrderNo ?? "").toString().trim()) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: "Please enter SRN Order No.",
          });
          return;
        }
        if (!(i.reBillOrderNo ?? "").toString().trim()) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: "Please enter Re-Bill Order No.",
          });
          return;
        }
      }
      if (isCloseLevel2) {
        if (!(i.finalClosureReferenceNo ?? "").toString().trim()) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: "Please enter Final Closure Reference No.",
          });
          return;
        }
        const finalQtyVal = String(i.finalClosureQty ?? "").trim();
        if (!finalQtyVal) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Final Closure Quantity (Qty) cannot be blank for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
          });
          return;
        }
        if (!/^\d+$/.test(finalQtyVal) || Number(finalQtyVal) <= 0) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Final Closure Quantity (Qty) must be a whole number greater than 0 for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
          });
          return;
        }
        const finalQtyNum = Number(finalQtyVal);
        if (approvedQtyMax > 0 && finalQtyNum > approvedQtyMax) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Final Closure Quantity (Qty) cannot be greater than Approved Qty (${approvedQtyMax})`,
          });
          return;
        }
        const finalAmtVal = String(i.finalClosureAmount ?? "").trim();
        if (!finalAmtVal) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Final Closure Amount cannot be blank for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
          });
          return;
        }
        const finalAmtNum = Number(finalAmtVal);
        if (!Number.isFinite(finalAmtNum) || finalAmtNum <= 0) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Final Closure Amount must be greater than 0 for item ${idx + 1}${productCode ? ` (${productCode})` : ""}.`,
          });
          return;
        }
        if (finalApprovedMax > 0 && finalAmtNum > finalApprovedMax) {
          Swal.fire({
            icon: "warning",
            title: "Validation Error",
            text: `Final Closure Amount cannot be greater than Final Approved Value (${formatViewSrMoney(finalApprovedMax)})`,
          });
          return;
        }
      }
    }

    const invC = Array.isArray(srData.invoiceDetails) ? srData.invoiceDetails[0] : null;
    const prod0C = Array.isArray(srData.products) ? srData.products[0] : null;
    const businessVerticalForClose =
      srData.businessVertical ||
      srData.BusinessVertical ||
      (invC && (invC.BusinessVertical || invC.businessVertical)) ||
      (prod0C && (prod0C.BusinessVertical || prod0C.businessVertical)) ||
      null;
    const plantCodesCsvClose = buildApproveRejectPlantCodesCsv(items, approveSeDropdowns.plants || []);
    const payload = {
      srCode: srData.srCode || srData.SRCode,
      childSrCode: srData.childSRNumber || srData.ChildSRNumber || childSrCodeFromQuery || null,
      action: isValidateSrLabelUserType ? "Validate" : "Close",
      remarks: remarksTrimmed,
      marketCode: srData.marketCode || srData.MarketCode || null,
      businessVertical: businessVerticalForClose,
      ...(plantCodesCsvClose ? { plantCodesCsv: plantCodesCsvClose, sePlantIdsCsv: plantCodesCsvClose } : {}),
      items: items.map((i) => ({
        invoiceNumber: i.invoiceNumber || i.InvoiceNumber,
        srTypeCode: i.srTypeCode || i.SRTypeCode || null,
        productCode: i.productCode || i.ProductCode,
        batchNo: i.batchNo ?? i.BatchNo ?? null,
        doorMaterialCode: i.doorMaterialCode ?? null,
        componentIssueRemarks: i.componentIssueRemarks ?? null,
        stoCnValue: i.stoCnValue ?? null,
        isReturnable: i.isReturnable ?? null,
        isNonReturnable: i.isNonReturnable ?? null,
        referenceNo: i.referenceNo ?? null,
        amount:
          i.amount === "" || i.amount == null ? null : Number(i.amount),
        qty: i.qty === "" || i.qty == null ? null : Number(i.qty),
        srnOrderNo: i.srnOrderNo ?? null,
        reBillOrderNo: i.reBillOrderNo ?? null,
        finalClosureReferenceNo: i.finalClosureReferenceNo ?? null,
        finalClosureAmount:
          i.finalClosureAmount === "" || i.finalClosureAmount == null
            ? null
            : Number(i.finalClosureAmount),
        finalClosureQty:
          i.finalClosureQty === "" || i.finalClosureQty == null
            ? null
            : Number(i.finalClosureQty),
      })),
    };
    try {
      setApproveRejectSubmitting(true);
      await complaintAPI.approveRejectServiceRequestItems(
        payload,
        changedByForAction || undefined
      );
      setApproveRejectSubmitting(false);
      approveRejectSubmittingRef.current = false;
      const { srNumberForMsg, srViewHref } = getSrDisplayInfoForMessage(
        srData,
        childSrCodeFromQuery,
        id
      );
      handleApproveRejectClose();
      await Swal.fire({
        icon: "success",
        title: "Done",
        html: buildSrActionSuccessSwalHtml(srNumberForMsg, "closed", srViewHref, { linkify: false }),
      });
      window.location.reload();
    } catch (err) {
      await showApproveRejectActionError(getApiErrorMessage(err, "Failed to close."));
    } finally {
      resetApproveRejectSubmitting();
    }
  };

  const updateStoCnFieldById = (itemId, field, value) => {
    if (!itemId) return;
    setApproveData((prev) => ({
      ...prev,
      items: (prev.items || []).map((it) =>
        it.id === itemId ? { ...it, [field]: value } : it
      ),
    }));
  };

  const updateStoCnFieldForAll = (field, value) => {
    if (!field) return;
    setApproveData((prev) => ({
      ...prev,
      items: (prev.items || []).map((it) => ({ ...it, [field]: value })),
    }));
  };

  const getTodayIsoDate = () => {
    try {
      return new Date().toISOString().slice(0, 10); // yyyy-mm-dd for <input type="date" />
    } catch {
      return "";
    }
  };

  const addComplaintLine = () => {
    setComplaintLines((prev) => [...prev, newLine()]);
  };

  const removeComplaintLine = (id) => {
    setComplaintLines((prev) =>
      prev.length > 1 ? prev.filter((l) => l.id !== id) : prev
    );
  };

  const updateComplaintLine = (id, field, value) => {
    setComplaintLines((prev) =>
      prev.map((line) => (line.id === id ? { ...line, [field]: value } : line))
    );
  };

  // Initialize Bootstrap modal instance for SR Checklist
  useEffect(() => {
    if (!checklistModalRef.current) return;

    const modalElement = checklistModalRef.current;

    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !modalInstanceRef.current) {
        try {
          modalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false,
              keyboard: true,
            });

          const handleHidden = () => {
            setChecklistOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    setTimeout(initBootstrapModal, 100);

    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []);

  // Handle modal state and sidebar toggling
  const prevChecklistOpenRef = useRef(checklistOpen);
  const modalOpenRef = useRef(checklistOpen);

  useEffect(() => {
    modalOpenRef.current = checklistOpen;
  }, [checklistOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && checklistOpen) {
        handleChecklistClose();
      }
    };

    const prevState = prevChecklistOpenRef.current;
    const currentState = checklistOpen;

    if (currentState && !prevState) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      document.body.style.overflow = "";
      restoreSidebar();
    }

    prevChecklistOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [checklistOpen, collapseSidebar, restoreSidebar]);

  useEffect(() => {
    return () => {
      if (modalOpenRef.current) {
        restoreSidebar();
      }
    };
  }, [restoreSidebar]);

  // Initialize Select2 on modal selects when modal opens
  useEffect(() => {
    if (checklistOpen) {
      const timer = setTimeout(() => {
        $(".checklist-modal select.form-select").each(function () {
          const $select = $(this);

          if ($select.data("select2")) {
            $select.select2("destroy");
          }

          $select.select2({
            width: "100%",
            placeholder: "Select an option",
            allowClear: false,
            dropdownParent: $(".checklist-modal"),
            minimumResultsForSearch: 5,
          });

          $select.on("change", function () {
            const event = new Event("change", { bubbles: true });
            this.dispatchEvent(event);
          });
        });
      }, 100);

      return () => {
        clearTimeout(timer);
        $(".checklist-modal select.form-select").each(function () {
          const $select = $(this);
          if ($select.data("select2")) {
            $select.off("change");
            $select.select2("destroy");
          }
        });
      };
    }
  }, [checklistOpen, complaintLines.length, complaintTypes.length]);

  // Initialize Bootstrap modal instance for Raise Query
  useEffect(() => {
    if (!raiseQueryModalRef.current) return;

    const modalElement = raiseQueryModalRef.current;

    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !raiseQueryModalInstanceRef.current) {
        try {
          raiseQueryModalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false,
              keyboard: true,
            });

          const handleHidden = () => {
            setRaiseQueryOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    setTimeout(initBootstrapModal, 100);

    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []);

  // Initialize Bootstrap modal instance for Response Query
  useEffect(() => {
    if (!responseQueryModalRef.current) return;

    const modalElement = responseQueryModalRef.current;

    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !responseQueryModalInstanceRef.current) {
        try {
          responseQueryModalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false,
              keyboard: true,
            });

          const handleHidden = () => {
            setResponseQueryOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    setTimeout(initBootstrapModal, 100);

    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []);

  // Initialize Bootstrap modal instance for Approve
  useEffect(() => {
    if (!approveModalRef.current) return;

    const modalElement = approveModalRef.current;

    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !approveModalInstanceRef.current) {
        try {
          approveModalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false,
              keyboard: true,
            });

          const handleHidden = () => {
            setApproveOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    setTimeout(initBootstrapModal, 100);

    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []);

  // Initialize Bootstrap modal instance for Approve/Reject
  useEffect(() => {
    if (!approveRejectModalRef.current) return;

    const modalElement = approveRejectModalRef.current;

    const initBootstrapModal = () => {
      if (typeof window === "undefined") return;

      let BootstrapModal = null;
      if (window.bootstrap && window.bootstrap.Modal) {
        BootstrapModal = window.bootstrap.Modal;
      }

      if (BootstrapModal && !approveRejectModalInstanceRef.current) {
        try {
          approveRejectModalInstanceRef.current =
            BootstrapModal.getInstance(modalElement) ||
            new BootstrapModal(modalElement, {
              backdrop: false,
              keyboard: true,
            });

          const handleHidden = () => {
            setApproveRejectOpen(false);
          };

          modalElement.addEventListener("hidden.bs.modal", handleHidden);
          modalElement._handleHidden = handleHidden;
        } catch (error) {
          console.debug("Bootstrap Modal not available, using CSS fallback");
        }
      }
    };

    setTimeout(initBootstrapModal, 100);

    return () => {
      if (modalElement && modalElement._handleHidden) {
        modalElement.removeEventListener(
          "hidden.bs.modal",
          modalElement._handleHidden
        );
        delete modalElement._handleHidden;
      }
    };
  }, []);

  // Initialize tooltips and event handlers for Approve/Reject modal action buttons
  useEffect(() => {
    if (!approveRejectOpen || !approveRejectModalRef.current) return;

    const modalElement = approveRejectModalRef.current;
    let tooltipList = [];

    // Initialize Bootstrap tooltips
    const initTooltips = () => {
      if (typeof window !== "undefined" && window.bootstrap && window.bootstrap.Tooltip) {
        const tooltipTriggerList = [].slice.call(
          modalElement.querySelectorAll('[data-bs-toggle="tooltip"]')
        );
        tooltipList = tooltipTriggerList.map(function (tooltipTriggerEl) {
          // Dispose existing tooltip if any
          const existingTooltip = window.bootstrap.Tooltip.getInstance(tooltipTriggerEl);
          if (existingTooltip) {
            existingTooltip.dispose();
          }
          return new window.bootstrap.Tooltip(tooltipTriggerEl);
        });
      }
    };

    // Add event handler for approve/reject button
    const handleApproveRejectClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const itemId = e.currentTarget.getAttribute("data-item-id");
      console.log("Approve/Reject item:", itemId);
      // Add approve/reject logic here - could open a sub-modal or show options
    };

    // Wait for modal to be fully rendered
    const timeoutId = setTimeout(() => {
      initTooltips();
      
      // Attach event handlers
      const approveRejectButtons = Array.from(modalElement.querySelectorAll(".approve-reject-item-btn"));
      
      approveRejectButtons.forEach((btn) => {
        btn.addEventListener("click", handleApproveRejectClick);
      });
    }, 300);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      
      // Dispose tooltips
      tooltipList.forEach((tooltip) => {
        if (tooltip) tooltip.dispose();
      });
      
      // Remove event listeners
      const approveRejectButtons = Array.from(modalElement.querySelectorAll(".approve-reject-item-btn"));
      approveRejectButtons.forEach((btn) => {
        btn.removeEventListener("click", handleApproveRejectClick);
      });
    };
  }, [approveRejectOpen]);

  // Handle Raise Query modal state and sidebar toggling
  const prevRaiseQueryOpenRef = useRef(raiseQueryOpen);
  const raiseQueryModalOpenRef = useRef(raiseQueryOpen);

  useEffect(() => {
    raiseQueryModalOpenRef.current = raiseQueryOpen;
  }, [raiseQueryOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && raiseQueryOpen) {
        handleRaiseQueryClose();
      }
    };

    const prevState = prevRaiseQueryOpenRef.current;
    const currentState = raiseQueryOpen;

    if (currentState && !prevState) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      document.body.style.overflow = "";
      restoreSidebar();
    }

    prevRaiseQueryOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [raiseQueryOpen, collapseSidebar, restoreSidebar]);

  useEffect(() => {
    return () => {
      if (raiseQueryModalOpenRef.current) {
        restoreSidebar();
      }
    };
  }, [restoreSidebar]);

  // Handle Approve/Reject modal state and sidebar toggling
  const prevApproveRejectOpenRef = useRef(approveRejectOpen);
  const approveRejectModalOpenRef = useRef(approveRejectOpen);

  useEffect(() => {
    approveRejectModalOpenRef.current = approveRejectOpen;
  }, [approveRejectOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && approveRejectOpen) {
        handleApproveRejectClose();
      }
    };

    const prevState = prevApproveRejectOpenRef.current;
    const currentState = approveRejectOpen;

    if (currentState && !prevState) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      document.body.style.overflow = "";
      restoreSidebar();
    }

    prevApproveRejectOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [approveRejectOpen, collapseSidebar, restoreSidebar]);

  useEffect(() => {
    return () => {
      if (approveRejectModalOpenRef.current) {
        restoreSidebar();
      }
    };
  }, [restoreSidebar]);

  // Handle Approve modal state and sidebar toggling
  const prevApproveOpenRef = useRef(approveOpen);
  const approveModalOpenRef = useRef(approveOpen);

  useEffect(() => {
    approveModalOpenRef.current = approveOpen;
  }, [approveOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && approveOpen) {
        handleApproveClose();
      }
    };

    const prevState = prevApproveOpenRef.current;
    const currentState = approveOpen;

    if (currentState && !prevState) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
      collapseSidebar();
    } else if (!currentState && prevState) {
      document.body.style.overflow = "";
      restoreSidebar();
    }

    prevApproveOpenRef.current = currentState;

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [approveOpen, collapseSidebar, restoreSidebar]);

  useEffect(() => {
    return () => {
      if (approveModalOpenRef.current) {
        restoreSidebar();
      }
    };
  }, [restoreSidebar]);

  // Initialize Select2 on Raise Query modal select when modal opens or options load
  useEffect(() => {
    if (!raiseQueryOpen) return;

    const timer = setTimeout(() => {
      const selectElement = document.getElementById("raiseQuerySelect");
      if (!selectElement || !raiseQueryModalRef.current) return;

      const $select = $(selectElement);
      const $modal = $(raiseQueryModalRef.current);

      // Destroy existing Select2 instance if any
      if ($select.data("select2")) {
        $select.off("select2:select select2:clear");
        $select.select2("destroy");
      }

      // Initialize Select2 (after options are in DOM so selection stays in sync)
      $select.select2({
        width: "100%",
        placeholder: "Search for name...",
        allowClear: true,
        dropdownParent: $modal,
        minimumResultsForSearch: 0,
        containerCssClass: "raise-query-select2-container-left",
        dropdownCssClass: "raise-query-select2-dropdown-left",
      });

      // Handle change event so state stays in sync
      $select.on("select2:select select2:clear", function () {
        const selectedValue = $(this).val() || "";
        setRaiseQueryData((prev) => ({
          ...prev,
          name: selectedValue,
        }));
      });
    }, nameOptions.length > 0 ? 100 : 400);

    return () => {
      clearTimeout(timer);
      const selectElement = document.getElementById("raiseQuerySelect");
      if (selectElement) {
        const $select = $(selectElement);
        if ($select.data("select2")) {
          $select.off("select2:select select2:clear");
          $select.select2("destroy");
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raiseQueryOpen, nameOptions.length]);

  useEffect(() => {
    if (!raiseQueryOpen) return;
    try {
      const el = document.getElementById("raiseQuerySelect");
      if (!el) return;
      const $select = $(el);
      if ($select.data("select2")) {
        $select.prop("disabled", raiseQuerySubmitting);
      }
    } catch {
      /* ignore */
    }
  }, [raiseQuerySubmitting, raiseQueryOpen]);

  // Handle collapse icon rotation
  useEffect(() => {
    const collapseElements = document.querySelectorAll(".collapse");

    const handleShown = (event) => {
      const collapseId = event.target.id;
      const toggle = document.querySelector(
        `[data-bs-target="#${collapseId}"]`
      );
      if (toggle) {
        toggle.setAttribute("aria-expanded", "true");
      }
    };

    const handleHidden = (event) => {
      const collapseId = event.target.id;
      const toggle = document.querySelector(
        `[data-bs-target="#${collapseId}"]`
      );
      if (toggle) {
        toggle.setAttribute("aria-expanded", "false");
      }
    };

    collapseElements.forEach((element) => {
      element.addEventListener("shown.bs.collapse", handleShown);
      element.addEventListener("hidden.bs.collapse", handleHidden);
    });

    return () => {
      collapseElements.forEach((element) => {
        element.removeEventListener("shown.bs.collapse", handleShown);
        element.removeEventListener("hidden.bs.collapse", handleHidden);
      });
    };
  }, [srData]);

  // Helper function to transform ApprovalHistory to journeyStatus format
  const transformApprovalHistoryToJourneyStatus = (approvalHistory, approvalLevels) => {
    if ((!approvalHistory || !Array.isArray(approvalHistory) || approvalHistory.length === 0) &&
        (!approvalLevels || !Array.isArray(approvalLevels) || approvalLevels.length === 0)) {
      return [];
    }

    // If we have approval levels, use them as the base structure
    if (approvalLevels && Array.isArray(approvalLevels) && approvalLevels.length > 0) {
      // Sort approval levels by sequence
      const sortedLevels = [...approvalLevels].sort((a, b) => a.levelSequence - b.levelSequence);
      
      // Create a map of approval history by level sequence
      const historyMap = new Map();
      if (approvalHistory && Array.isArray(approvalHistory)) {
        approvalHistory.forEach(history => {
          historyMap.set(history.levelSequence, history);
        });
      }

      // Build journeyStatus array from approval levels (order by LevelSequence)
      const journeyStatus = sortedLevels.map(level => {
        const history = historyMap.get(level.levelSequence);
        
        return {
          levelSequence: level.levelSequence ?? 0,
          status: history?.actionType || "Pending",
          date: history?.actionOn ? formatDate(history.actionOn) : "",
          designation: level.roleName || level.roleCode || "",
          name: history?.actionByName || "",
          approved: history?.actionType === "Approved",
          rejected: history?.actionType === "Rejected",
          newStatuscode:
            history?.NewStatuscode ??
            history?.newStatuscode ??
            history?.NewStatusCode ??
            history?.newStatusCode ??
            "",

          // SR Journey Status Details table values:
          // - SLA/TAT from HistorySlaDays/HistoryTATDays
          // - SR Qty/Value from ActualSRQty/ActualSRValue
          sla: history?.HistorySlaDays ?? history?.historySlaDays ?? null,
          tat: history?.HistoryTATDays ?? history?.historyTATDays ?? null,


          
          srQty: history?.ActualSRQty ?? history?.actualSrQty ?? undefined,



          srValue: history?.ActualSRValue ?? history?.actualSrValue ?? undefined,
          approvedQty: history?.actionType === "Approved" && history?.approvedValue ? String(history.approvedValue) : undefined,
          approvedValue: history?.actionType === "Approved" && history?.approvedValue ? String(history.approvedValue) : undefined,
          comment: history?.comments || "",
        };
      });

      return journeyStatus;
    }
    
    // If we only have approval history (no levels), build from history directly
    if (approvalHistory && Array.isArray(approvalHistory) && approvalHistory.length > 0) {
      // Sort by level sequence
      const sortedHistory = [...approvalHistory].sort((a, b) => a.levelSequence - b.levelSequence);
      
      return sortedHistory.map(history => ({
        levelSequence: history.levelSequence ?? 0,
        status: history.actionType || "Pending",
        date: history.actionOn ? formatDate(history.actionOn) : "",
        designation: history.roleName || "",
        name: history.actionByName || "",
        approved: history.actionType === "Approved",
        rejected: history.actionType === "Rejected",
        newStatuscode:
          history?.NewStatuscode ??
          history?.newStatuscode ??
          history?.NewStatusCode ??
          history?.newStatusCode ??
          "",
        sla: history?.HistorySlaDays ?? history?.historySlaDays ?? null,
        tat: history?.HistoryTATDays ?? history?.historyTATDays ?? null,
        srQty: history?.ActualSRQty ?? history?.actualSrQty ?? undefined,
        srValue: history?.ActualSRValue ?? history?.actualSrValue ?? undefined,
        approvedQty: history.actionType === "Approved" && history.approvedValue ? String(history.approvedValue) : undefined,
        approvedValue: history.actionType === "Approved" && history.approvedValue ? String(history.approvedValue) : undefined,
        comment: history.comments || "",
      }));
    }

    return [];
  };

  const filterSkippedJourneySteps = (steps) => {
    if (!Array.isArray(steps)) return [];
    return steps.filter((step) => {
      const activity = (step?.Activity || step?.activity || step?.status || "")
        .toString()
        .trim()
        .toUpperCase();
      return activity !== "SKIPPED";
    });
  };

  // Expected TAT for journey details: prefer stamped history when > 0, else ConfigTATDays
  // (API often coerces NULL HistoryTATDays → 0, which must not hide configured TAT).
  const resolveExpectedTatDays = (...values) => {
    let fallback = null;
    for (const v of values) {
      if (v == null || v === "") continue;
      const n = Number(v);
      if (!Number.isFinite(n)) continue;
      if (fallback == null) fallback = n;
      if (n > 0) return String(n);
    }
    return fallback != null ? String(fallback) : "0";
  };

  // Initialize DataTable for SR Journey Status Details (row-wise grid)
  useEffect(() => {
    const statuses = srData?.journeyStatus;
    if (journeyStatusTableRef.current && Array.isArray(statuses)) {
      if (!$.fn.DataTable) {
        DataTable($);
      }

      if (journeyStatusDataTableRef.current) {
        journeyStatusDataTableRef.current.destroy();
        journeyStatusDataTableRef.current = null;
      }

      // Sort by LevelSequence (approval level order); fallback to status priority when levelSequence missing
      const sortedStatuses = [...statuses].sort((a, b) => {
        const levelA = a.levelSequence ?? 999;
        const levelB = b.levelSequence ?? 999;
        if (levelA !== levelB) return levelA - levelB;
        const getStatusPriority = (step) => (step.approved ? 1 : step.rejected ? 3 : 2);
        return getStatusPriority(a) - getStatusPriority(b);
      });
      // Keep all journey rows from DB (including Initiator / LevelSequence 1).
      const showRowsPastRejection = computeShowJourneyPastRejection(srData, sortedStatuses);

      const displayStatuses = buildJourneyDetailDisplayRows(
        sortedStatuses,
        showRowsPastRejection
      );

      const escapeHtml = (value) =>
        String(value)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;")
          .replace(/'/g, "&#39;");

      let lastRejectedIdxInDisplay = -1;
      displayStatuses.forEach((s, i) => {
        const r =
          s.rejected === true ||
          String(s.status ?? s.Activity ?? "")
            .toUpperCase()
            .trim() === "REJECTED";
        if (r) lastRejectedIdxInDisplay = i;
      });

      // Three BV (Doors/Floors/Sturdo): SR Qty = category-wise component qty;
      // hide SR Value & Cal. Approved Value; Final Approved Val = 0 before Plant QC.
      const isThreeBvJourney = Boolean(businessVerticalRequiresComponentDetails);
      const threeBvJourneySrQty = (() => {
        if (!isThreeBvJourney) return null;
        const products = Array.isArray(srData?.products) ? srData.products : [];
        let total = 0;
        products.forEach((p) => {
          if (!materialRowShowsComponentDetailsIcon(p)) return;
          total += getComponentDetailsQtyTotalForProduct(p);
        });
        if (!(total > 0)) {
          total = products.reduce((sum, p) => {
            const q = parseFloat(
              p?.SRQuantity ??
                p?.srQuantity ??
                p?.ComplaintQty ??
                p?.complaintQty ??
                0
            );
            return sum + (Number.isFinite(q) ? q : 0);
          }, 0);
        }
        return String(Math.floor(total) || 0);
      })();
      const isPlantQcJourneyStep = (step) => {
        const code = String(
          step?.UserTypeCode ?? step?.userTypeCode ?? ""
        )
          .toUpperCase()
          .trim();
        if (code === "UT0000006") return true;
        const role = String(
          step?.designation ??
            step?.role ??
            step?.UserTypeName ??
            step?.userTypeName ??
            ""
        ).toLowerCase();
        return role.includes("plant qc") || role.includes("plantqc");
      };
      const plantQcJourneyIdx = isThreeBvJourney
        ? displayStatuses.findIndex((s) => isPlantQcJourneyStep(s))
        : -1;
      // Carry Plant QC Final Approved Value onto later pending/approved steps (print + view).
      let carriedFinalAfterPlantQc = 0;

      const tableData = displayStatuses.map((step, idx) => {
        const toTwoDecimalOrZero = (value) => {
          const raw = String(value ?? "").replace(/,/g, "").trim();
          if (!raw || raw.toUpperCase() === "N/A") return "0.00";
          const num = Number(raw);
          return Number.isFinite(num) ? num.toFixed(2) : "0.00";
        };
        const stepActivityUpper = String(step.status ?? step.Activity ?? "")
          .toUpperCase()
          .trim();
        const isCloseActivity =
          stepActivityUpper === "CLOSE" ||
          stepActivityUpper === "CLOSED" ||
          stepActivityUpper === "FINAL CLOSE" ||
          stepActivityUpper.includes("FINAL CLOSE");
        const stepIsDone = (() => {
          const v =
            step?.isDone ??
            step?.IsDone ??
            step?.isdone ??
            step?.ISDONE ??
            step?.done;
          if (v === true || v === 1) return true;
          const s = String(v ?? "").trim().toLowerCase();
          if (s === "true" || s === "yes") return true;
          const n = Number(s);
          return Number.isFinite(n) && n > 0;
        })();
        const stepRejected =
          step.rejected === true || stepActivityUpper === "REJECTED";
        const stepApproved =
          !stepRejected &&
          (stepIsDone || step.approved || isCloseActivity);
        const isReopenedActivity =
          stepActivityUpper === "REOPENED" ||
          stepActivityUpper.includes("REOPEN") ||
          String(step.newStatuscode ?? step.NewStatuscode ?? "")
            .toUpperCase()
            .includes("REOPEN");
        const isCreatedActivity =
          stepActivityUpper === "CREATED" || stepActivityUpper.includes("CREATED");
        const isInitiatorRole = isInitiatorSEStep(step);
        const pendingInReopenTrail =
          showRowsPastRejection &&
          lastRejectedIdxInDisplay >= 0 &&
          idx > lastRejectedIdxInDisplay &&
          !stepApproved &&
          !step.rejected;
        const closeStatusLabel = getJourneyCloseStatusLabel(step);
        const statusLabel = stepRejected
          ? "Rejected"
          : isCreatedActivity
          ? "Created"
          : closeStatusLabel
          ? closeStatusLabel
          : stepApproved
          ? "Approved"
          : isReopenedActivity
          ? "Reopened"
          : "Pending";
        const slaStatus = (step.slaStatus || step.SlaStatus || "").toString().trim();
        const isBreachedSla = isJourneyStepSlaBreached(step);
        const pendingOrange = pendingInReopenTrail || isReopenedActivity ? "#ff9800" : "#ff8c00";
        const approvalVariant =
          stepApproved && !isInitiatorRole
            ? resolveJourneyApprovalVariant(
                displayStatuses,
                idx,
                lastRejectedIdxInDisplay
              )
            : "first";
        const approvalColors = getJourneyApprovalColors(approvalVariant);
        const statusColor = isInitiatorRole
          ? "#28a745"
          : isBreachedSla
          ? "#dc3545"
          : stepRejected
          ? "#dc3545"
          : stepApproved
          ? approvalColors.solid
          : pendingOrange;
        const slaBreachTooltip = isBreachedSla
          ? buildJourneyStepSlaTooltip(step, idx > 0 ? displayStatuses[idx - 1] : null)
          : "";
        const levelSeq = step.levelSequence ?? 999;
        return {
          levelSequence: levelSeq,
          role: step.designation || "N/A",
          actionBy: step.name || "N/A",
          date: step.date ? formatDate(step.date) : "N/A",
          status: statusLabel,
          statusColor,
          isBreachedSla,
          slaBreachTooltip,
          slaStatus: slaStatus || (step.tatDueDate ? "—" : "N/A"),
          slaStatusColor: isBreachedSla ? "#dc3545" : slaStatus ? "#28a745" : "#6c757d",
          // UI labels expected by the screenshot:
          // - TAT: config TAT days (fallback when history TAT is empty/0)
          // - SR QTY: SLA days (or history SLA days)
          // - SR VALUE: history/config "TAT days" numeric (as already returned by the backend fields)
          sla:
            step?.HistorySlaDays ??
            step?.historySlaDays ??
            step?.sla ??
            step?.configSlaDays ??
            "N/A",
          tat: resolveExpectedTatDays(
            step?.HistoryTATDays ?? step?.historyTATDays,
            step?.tat,
            step?.ConfigTATDays ?? step?.configTATDays
          ),
          actualTat:
            step?.ActualTAT ??
            step?.actualTAT ??
            step?.actualTat ??
            (step?.ActualTATDays != null || step?.actualTATDays != null
              ? String(step?.ActualTATDays ?? step?.actualTATDays)
              : "N/A"),
          actualTatDays: (() => {
            const raw = step?.ActualTATDays ?? step?.actualTATDays;
            if (raw == null || raw === "") return null;
            const n = Number(raw);
            return Number.isFinite(n) ? n : null;
          })(),
          actualTatColor: (() => {
            const actualRaw = step?.ActualTATDays ?? step?.actualTATDays;
            const tatRaw = resolveExpectedTatDays(
              step?.HistoryTATDays ?? step?.historyTATDays,
              step?.tat,
              step?.ConfigTATDays ?? step?.configTATDays
            );
            if (actualRaw == null || actualRaw === "") return null;
            const actualDays = Number(actualRaw);
            const tatDays = Number(tatRaw);
            if (!Number.isFinite(actualDays) || !Number.isFinite(tatDays)) return null;
            return actualDays <= tatDays ? "#28a745" : "#dc3545";
          })(),
          userIdCode: String(
            step?.UserIdCode ?? step?.userIdCode ?? step?.userId ?? ""
          ).trim(),
          queries: (() => {
            const raw = step?.Queries ?? step?.queries;
            if (raw == null || raw === "") return "0";
            const n = Number(raw);
            return Number.isFinite(n) ? String(n) : "0";
          })(),
          srQty:
            threeBvJourneySrQty != null
              ? threeBvJourneySrQty
              : step?.ActualSRQty ?? step?.actualSrQty ?? step?.srQty ?? "N/A",
          srValue: toTwoDecimalOrZero(
            step?.ActualSRValue ?? step?.actualSrValue ?? step?.srValue ?? "N/A"
          ),
          approvedQty:
            isThreeBvJourney && stepApproved && threeBvJourneySrQty != null
              ? threeBvJourneySrQty
              : stepApproved && (step.approvedQtyFromLog || step.approvedQty) != null
                ? (step.approvedQtyFromLog || step.approvedQty)
                : "0",
          approvedValue: toTwoDecimalOrZero(
            stepApproved ? (step.approvedValueFromLog || step.approvedValue) : "N/A"
          ),
          finalApprovedValue: (() => {
            if (
              isThreeBvJourney &&
              plantQcJourneyIdx >= 0 &&
              idx < plantQcJourneyIdx
            ) {
              return "0.00";
            }
            const raw =
              step.finalApprovedValueFromLog ??
              step.finalApprovedValue ??
              null;
            const num = Number(String(raw ?? "").replace(/,/g, "").trim());
            if (
              isThreeBvJourney &&
              plantQcJourneyIdx >= 0 &&
              idx >= plantQcJourneyIdx
            ) {
              if (stepApproved && Number.isFinite(num) && num > 0) {
                carriedFinalAfterPlantQc = num;
              }
              const display = stepApproved
                ? Number.isFinite(num) && num > 0
                  ? num
                  : carriedFinalAfterPlantQc
                : carriedFinalAfterPlantQc;
              return toTwoDecimalOrZero(display);
            }
            return toTwoDecimalOrZero(
              stepApproved ? (raw ?? "N/A") : "N/A"
            );
          })(),
          comment: String(step.comment ?? "").trim() ? step.comment : " ",
        };
      });

      const syncCommentsColumnWidth = (api) => {
        const commentsColumn = api.column("comments:name");
        if (!commentsColumn || commentsColumn.length === 0) return;
        const widthPx = "350px";

        $(commentsColumn.header()).css({
          width: widthPx,
          minWidth: widthPx,
          maxWidth: widthPx,
        });

        $(commentsColumn.nodes()).css({
          width: widthPx,
          minWidth: widthPx,
          maxWidth: widthPx,
        });
      };

      const syncQueriesColumnWidth = (api) => {
        const queriesColumn = api.column("queries:name");
        if (!queriesColumn || queriesColumn.length === 0) return;
        const widthPx = "70px";

        $(queriesColumn.header()).css({
          width: widthPx,
          minWidth: widthPx,
          maxWidth: widthPx,
        });

        $(queriesColumn.nodes()).css({
          width: widthPx,
          minWidth: widthPx,
          maxWidth: widthPx,
        });
      };

      const syncRoleColumnMinWidth = (api) => {
        const roleColumn = api.column(1);
        if (!roleColumn || roleColumn.length === 0) return;
        const minWidthPx = "170px";

        $(roleColumn.header()).css({
          minWidth: minWidthPx,
        });

        $(roleColumn.nodes()).css({
          minWidth: minWidthPx,
        });
      };






































      journeyStatusDataTableRef.current = $(journeyStatusTableRef.current).DataTable({
        data: tableData,
        paging: false,
        searching: false,
        info: false,
        lengthChange: false,
        lengthMenu: [5, 10, 25, 50, 100],
        pageLength: 5,
        dom: "rt",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        fixedColumns: false,
        order: [[0, "asc"]], // Sort by levelSequence (hidden) ascending
        columnDefs: [
          { targets: 0, className: "text-start" }, // hidden sort column
          { targets: 1, className: "text-start" }, // Role
          { targets: 2, className: "text-start" }, // Action Taken By
          { targets: 3, className: "text-start" }, // Date
          { targets: 4, className: "text-start" }, // Status
          { targets: 5, className: "text-center", visible: false }, // SLA (hidden)
          { targets: 6, className: "text-center" }, // TAT
          { targets: 7, className: "text-center" }, // Actual TAT
          { targets: 8, className: "text-center" }, // Queries (count)
          { targets: [9, 10, 11, 12, 13], className: "text-end" }, // numeric
          { targets: 14, className: "text-start" }, // Comments
        ],
        initComplete: function () {
          const api = this.api();
          syncCommentsColumnWidth(api);
          syncQueriesColumnWidth(api);
          syncRoleColumnMinWidth(api)
          api.columns.adjust();
        },
        drawCallback: function () {
          const api = this.api();
          syncCommentsColumnWidth(api);
          syncQueriesColumnWidth(api);
          syncRoleColumnMinWidth(api);
        },
        columns: [
          { 
            title: "", 
            data: "levelSequence", 
            visible: false, 
            orderable: true 
          }, // Hidden column: LevelSequence for order
          // { title: "Role", data: "role", width: "220px" },


          {
            title: "Role",
            data: "role",
            className: "journey-role-cell",
            width: "170px",
            createdCell: (td) => {
              td.style.minWidth = "170px";
            },
            render: (data) => {
              const fullText = (data ?? "").toString();
              const trimmedText = fullText.trim();
              const displayText =
                trimmedText.length > 30 ? `${trimmedText.slice(0, 30)}...` : trimmedText;
              const safeFullText = escapeHtml(trimmedText);
              const safeDisplayText = escapeHtml(displayText);
              return `<span class="journey-role-text" title="${safeFullText}">${safeDisplayText}</span>`;
            },
          },
          { title: "Action Taken By", data: "actionBy", className: "text-start", width: "200px" },
          { title: "Date Of Action", data: "date", className: "text-start", width: "120px" },
          {
            title: "Status",
            data: null,
            className: "text-start",
            width: "110px",
            render: (_d, _t, row) => {
              const safeStatus = escapeHtml(row.status ?? "");
              const tip = row.slaBreachTooltip
                ? ` title="${escapeHtml(row.slaBreachTooltip)}"`
                : "";
              const cursor = row.isBreachedSla ? "help" : "default";
              return `<span style="color:${row.statusColor}; font-weight:600; cursor:${cursor};"${tip}>${safeStatus}</span>`;
            },
          },
          { title: "SLA", data: "sla", className: "text-center", width: "70px", visible: false },
          { title: "TAT", data: "tat", className: "text-center", width: "70px" },
          {
            title: "Actual TAT",
            data: "actualTat",
            className: "text-center",
            width: "100px",
            render: (_d, _t, row) => {
              const text = String(row.actualTat ?? "").trim() || "N/A";
              const color = row.actualTatColor || "#6c757d";
              const weight = row.actualTatColor ? "600" : "400";
              return `<span style="color:${color}; font-weight:${weight};">${escapeHtml(text)}</span>`;
            },
          },
          {
            title: "Queries",
            name: "queries",
            data: "queries",
            className: "text-center",
            width: "70px",
            createdCell: (td) => {
              td.style.width = "70px";
              td.style.minWidth = "70px";
              td.style.maxWidth = "70px";
            },
            render: (_d, _t, row) => {
              const count = Number(row.queries);
              const safeCount = Number.isFinite(count) ? count : 0;
              if (safeCount <= 0) {
                return `<span>0</span>`;
              }
              const uid = escapeHtml(row.userIdCode || "");
              const uname = escapeHtml(row.actionBy || "");
              return `<a href="#collapseQueryHistory" class="js-journey-queries-link" data-user-id="${uid}" data-user-name="${uname}" style="color:#0d6efd; text-decoration:underline; font-weight:600; cursor:pointer;">${safeCount}</a>`;
            },
          },
          { title: "SR QTY", data: "srQty", className: "text-end", width: "90px" },
          {
            title: "SR VALUE",
            data: "srValue",
            className: "text-end",
            width: "100px",
            visible: !isThreeBvJourney,
          },
          { title: "Approved Qty", data: "approvedQty", className: "text-end", width: "110px" },
          {
            title: "Cal. Approved Value",
            data: "approvedValue",
            className: "text-end",
            width: "130px",
            visible: !isThreeBvJourney,
          },
          {
            title: "Final approved Val",
            data: "finalApprovedValue",
            className: "text-end",
            width: "130px",
          },
          {
            title: "Comments",
            name: "comments",
            data: "comment",
            className: "journey-comments-cell",
            width: "350px",
            createdCell: (td) => {
              td.style.width = "350px";
              td.style.minWidth = "350px";
              td.style.maxWidth = "350px";
            },
            render: (data) => {
              const fullText = (data ?? "").toString();
              const trimmedText = fullText.trim();
              const displayText =
                trimmedText.length > 50 ? `${trimmedText.slice(0, 50)}...` : trimmedText;
              const safeFullText = escapeHtml(trimmedText);
              const safeDisplayText = escapeHtml(displayText);
              return `<span class="journey-comments-text" title="${safeFullText}">${safeDisplayText}</span>`;
            },
          },
        ],
      });

      $(journeyStatusTableRef.current)
        .off("click.journeyQueries")
        .on("click.journeyQueries", ".js-journey-queries-link", function (e) {
          e.preventDefault();
          e.stopPropagation();
          const userId = $(this).attr("data-user-id") || "";
          const userName = $(this).attr("data-user-name") || "";
          openQueryHistoryDrilldownRef.current?.(userId, userName);
        });
    }

    return () => {
      if (journeyStatusTableRef.current) {
        $(journeyStatusTableRef.current).off("click.journeyQueries");
      }
      if (journeyStatusDataTableRef.current) {
        journeyStatusDataTableRef.current.destroy();
        journeyStatusDataTableRef.current = null;
      }
    };
  }, [srData, businessVerticalRequiresComponentDetails]);

  useEffect(() => {
    const hasParentSr = String(id || "").trim();
    const hasChildOnly = String(childSrCodeFromQuery || "").trim();
    // Route is /complaints/:id — without parent or child code, API helpers return
    // synchronously (no HTTP) and the UI would otherwise stay confusing; stop loading immediately.
    if (!hasParentSr && !hasChildOnly) {
      setLoading(false);
      setSrData(null);
      return;
    }

    const fetchServiceRequest = async () => {
      setLoading(true);
      setLoadError(null);
      const pageLog = startViewServiceRequestPageLog({
        parentSrCode: id,
        childSrCode: childSrCodeFromQuery || undefined,
        changedBy: changedByForAction || undefined,
        getApiBaseUrl,
      });
      try {

        // One SP call (was 3× header + invoice + full detail — each ran sp_GetServiceRequestDetail).
        try {
          const tDetail0 = Date.now();
          let fullDetailForChecklistMedia = null;
          let header = {};
          let invoiceDetails = [];
          let checklistResponses = [];
          let checklistAttachmentItemsLocal = [];
          let statusHistoryFromApi = [];

          try {
            const fullDetailRes = await complaintAPI.getServiceRequestByCode(
              id,
              childSrCodeFromQuery || null,
              changedByForAction || undefined
            );
            const fullDetail = fullDetailRes?.data ?? fullDetailRes;
            fullDetailForChecklistMedia = fullDetail;

            header = fullDetail?.header ?? fullDetail?.Header ?? {};
            invoiceDetails = pickInvoiceDetailsFromFullDetail(fullDetail);

            const fullDetailChecklistResponses =
              fullDetail?.checklistResponses ?? fullDetail?.ChecklistResponses;
            if (Array.isArray(fullDetailChecklistResponses)) {
              checklistResponses = fullDetailChecklistResponses;
            }

            const clAttachments =
              fullDetail?.checklistAttachments ?? fullDetail?.ChecklistAttachments ?? [];
            if (Array.isArray(clAttachments) && clAttachments.length > 0) {
              checklistAttachmentItemsLocal = clAttachments;
            }

            if (Array.isArray(fullDetail?.statusHistory)) {
              statusHistoryFromApi = fullDetail.statusHistory;
            } else if (Array.isArray(fullDetail?.StatusHistory)) {
              statusHistoryFromApi = fullDetail.StatusHistory;
            }

            const journeyFromDetail =
              fullDetail?.journeyStatus ?? fullDetail?.JourneyStatus ?? [];
            pageLog.step("api-full-detail", {
              ms: Date.now() - tDetail0,
              success: fullDetailRes?.success !== false,
              headerKeys: header && typeof header === "object" ? Object.keys(header).length : 0,
              invoiceRows: invoiceDetails.length,
              journeyStatusRows: Array.isArray(journeyFromDetail) ? journeyFromDetail.length : 0,
              checklistResponses: fullDetailChecklistResponses,
              checklistAttachments: clAttachments,
              statusHistory: statusHistoryFromApi,
              srCode: header?.srCode || header?.SRCode,
              childSRNumber: header?.childSRNumber || header?.ChildSRNumber,
            });
          } catch (detailErr) {
            pageLog.stepFail("api-full-detail", detailErr);
            throw detailErr;
          }

          setChecklistAttachmentItems(checklistAttachmentItemsLocal);

          const srAttachmentsRaw =
            fullDetailForChecklistMedia?.attachments ??
            fullDetailForChecklistMedia?.Attachments ??
            [];
          const { invoiceFiles: srInvoiceFiles, secondaryInvoiceFiles: srSecondaryInvoiceFiles } =
            mapSrAttachmentsForPanels(srAttachmentsRaw, getApiBaseUrl());

          const payload = {
            header,
            journeyStatus:
              fullDetailForChecklistMedia?.journeyStatus ??
              fullDetailForChecklistMedia?.JourneyStatus ??
              [],
          };

          // Fetch approval workflow, levels, and history (journey fallback only)
          let approvalLevels = [];
          let approvalHistory = [];

          const complaintIdForWorkflow = resolveLegacyWorkflowComplaintId(header, id);
          if (complaintIdForWorkflow != null) {
            try {
              const workflowResponse =
                await workflowAPI.getApprovalWorkflow(complaintIdForWorkflow);
              const workflow = workflowResponse.data || workflowResponse;

              if (workflow && workflow.workflowId) {
                const levelsResponse = await workflowAPI.getApprovalLevels(
                  workflow.workflowId
                );
                approvalLevels = levelsResponse.data || levelsResponse || [];

                const historyResponse =
                  await workflowAPI.getApprovalHistory(complaintIdForWorkflow);
                approvalHistory = historyResponse.data || historyResponse || [];
              }
            } catch (error) {
              console.warn("Could not fetch workflow data:", error);
            }
          }

          // Prefer SR Journey Status from sp_GetServiceRequestDetail (result set 8); order by LevelSequence
          const journeyStatusFromApi = filterSkippedJourneySteps(payload.journeyStatus);
          const journeyStatus = journeyStatusFromApi.length > 0
            ? journeyStatusFromApi
                .map((row) => {
                  const activity = (row.Activity || row.activity || "Pending").toString();
                  const actUpper = activity.toUpperCase();
                  const configSla = row.ConfigSlaDays ?? row.configSlaDays;
                  const configTat = row.ConfigTATDays ?? row.configTATDays;
                  const historySla = row.HistorySlaDays ?? row.historySlaDays;
                  const historyTat = row.HistoryTATDays ?? row.historyTATDays;
                  const slaDaysDirect = row.SlaDays ?? row.slaDays;
                  const tatDaysDirect = row.TATDays ?? row.tatDays;
                  const actualSrQty = row.ActualSRQty ?? row.actualSrQty;
                  const actualSrValue = row.ActualSRValue ?? row.actualSrValue;
                  const approvedQty = row.ApprovedQty ?? row.approvedQty;
                  const approvedValue = row.ApprovedValue ?? row.approvedValue;
                  const finalApprovedValue =
                    row.FinalApprovedValue ?? row.finalApprovedValue;
                  const levelSeq = row.LevelSequence ?? row.levelSequence ?? 0;
                  const isActionDone = actUpper === "APPROVED" || actUpper === "REJECTED";
                  return {
                    ...row,
                    levelSequence: typeof levelSeq === "number" ? levelSeq : parseFloat(levelSeq) || 0,
                    status: activity,
                    date: (row.ChangedOn || row.changedOn) ? formatDate(row.ChangedOn || row.changedOn) : "",
                    designation:
                      row.UserTypeName ||
                      row.userTypeName ||
                      row.UserTypeCode ||
                      row.userTypeCode ||
                      "",
                    name: row.FullName || row.fullName || "",
                    userIdCode: row.UserIdCode || row.userIdCode || "",
                    rejected: actUpper === "REJECTED",
                    approved:
                      actUpper !== "REJECTED" &&
                      (actUpper === "APPROVED" ||
                        row.IsDone === true ||
                        row.isDone === true ||
                        row.IsDone === 1 ||
                        row.isDone === 1),
                    // Show ConfigSlaDays/ConfigTATDays for Pending; show History* when action is done.
                    // History TAT of 0/null must fall back to ConfigTATDays (TAT already given in SLA config).
                    sla:
                      slaDaysDirect != null
                        ? String(slaDaysDirect)
                        : historySla != null
                        ? String(historySla)
                        : configSla != null
                        ? String(configSla)
                        : "0",
                    tat: resolveExpectedTatDays(historyTat, tatDaysDirect, configTat),
                    srQty:
                      actualSrQty != null
                        ? String(actualSrQty)
                        : isActionDone
                        ? historySla != null
                          ? String(historySla)
                          : configSla != null
                          ? String(configSla)
                          : "0"
                        : configSla != null
                        ? String(configSla)
                        : "0",
                    srValue:
                      actualSrValue != null
                        ? String(actualSrValue)
                        : isActionDone
                        ? historyTat != null
                          ? String(historyTat)
                          : configTat != null
                          ? String(configTat)
                          : "0"
                        : configTat != null
                        ? String(configTat)
                        : "0",
                    approvedQty: approvedQty != null ? String(approvedQty) : undefined,
                    approvedValue: approvedValue != null ? String(approvedValue) : undefined,
                    comment: row.Comments || row.comments || "",
                    actualTat:
                      row.ActualTAT ||
                      row.actualTAT ||
                      row.actualTat ||
                      (row.ActualTATDays != null || row.actualTATDays != null
                        ? String(row.ActualTATDays ?? row.actualTATDays)
                        : null),
                    ActualTATDays: row.ActualTATDays ?? row.actualTATDays ?? null,
                    actualTATDays: row.ActualTATDays ?? row.actualTATDays ?? null,
                    queries: (() => {
                      const raw = row.Queries ?? row.queries;
                      if (raw == null || raw === "") return "0";
                      const n = Number(raw);
                      return Number.isFinite(n) ? String(n) : "0";
                    })(),
                    slaStatus: row.SlaStatus || row.slaStatus,
                    isSlaBreached: parseJourneyTruthyFlag(
                      row.IsSlaBreached ?? row.isSlaBreached
                    ),
                    configSlaDays: configSla != null ? String(configSla) : null,
                    configTATDays: configTat != null ? String(configTat) : null,
                    tatDueDate: row.TATDueDate || row.tatDueDate ? formatDate(row.TATDueDate || row.tatDueDate) : "",
                    approvedQtyFromLog: approvedQty != null ? String(approvedQty) : null,
                    approvedValueFromLog: approvedValue != null ? String(approvedValue) : null,
                    finalApprovedValue:
                      finalApprovedValue != null ? String(finalApprovedValue) : undefined,
                    finalApprovedValueFromLog:
                      finalApprovedValue != null ? String(finalApprovedValue) : null,
                    newStatuscode:
                      row.NewStatuscode ??
                      row.newStatuscode ??
                      row.NewStatusCode ??
                      row.newStatusCode ??
                      "",
                  };
                })
                .sort((a, b) => (a.levelSequence ?? 0) - (b.levelSequence ?? 0))
            : transformApprovalHistoryToJourneyStatus(approvalHistory, approvalLevels);

          // Flatten SR detail into shape used by this page
          console.log("SR header from API:", header);

          const data = {
            ...header,

            // Normalize key identifiers and status
            srCode: header.srCode || header.SRCode || id,
            // Basic Information "SR Number" shows ChildSRNumber when viewing a child (from URL or API)
            srNumber:
              header.childSRNumber ||
              header.ChildSRNumber ||
              (childSrCodeFromQuery || null) ||
              header.srNumber ||
              header.SRNumber ||
              header.srCode ||
              header.SRCode ||
              id,
            childSRNumber:
              header.childSRNumber ||
              header.ChildSRNumber ||
              childSrCodeFromQuery ||
              null,

            statusName:
              header.statusName || header.StatusName || header.srStatusCODE || header.SRStatusCODE || "",

            // Normalize created date from header
            createdDate:
              header.srRaisedDate ||
              header.SRRaisedDate ||
              header.createdOn ||
              header.CreatedOn ||
              null,

            // Normalize invoice fields
            invoiceDate: header.invoiceDate || header.InvoiceDate || null,

            invoiceNumber:
              header.invoiceNumber ||
              header.InvoiceNumber ||
              header.billingInvoiceNo ||
              header.BillingInvoiceNo ||
              "",
            invoiceNo:
              header.invoiceNumber ||
              header.InvoiceNumber ||
              header.billingInvoiceNo ||
              header.BillingInvoiceNo ||
              "",

            // Customer Billing Details (im.BillingInvoiceNo, BillingInvoiceDate, PONo, PODate)
            billingInvoiceNo:
              header.billingInvoiceNo ||
              header.BillingInvoiceNo ||
              header.invoiceNumber ||
              header.InvoiceNumber ||
              "",
            billingInvoiceDate:
              header.billingInvoiceDate ?? header.BillingInvoiceDate ?? null,
            billingPONo:
              header.billingPONo ||
              header.BillingPONo ||
              header.poNo ||
              header.PONo ||
              "",
            billingPODate:
              header.billingPODate ?? header.BillingPODate ?? header.poDate ?? header.PODate ?? null,
            orderNo:
              header.orderNo || header.OrderNo || header.poNo || header.PONo || "",
            orderDate:
              header.orderDate ?? header.OrderDate ?? header.poDate ?? header.PODate ?? null,
            factoryIntentNo:
              header.factoryIntentNo ||
              header.FactoryIntentNo ||
              invoiceDetails?.[0]?.FactoryIntentNo ||
              invoiceDetails?.[0]?.factoryIntentNo ||
              "",
            factoryIntentDate:
              header.factoryIntentDate ??
              header.FactoryIntentDate ??
              invoiceDetails?.[0]?.FactoryIntentDate ??
              invoiceDetails?.[0]?.factoryIntentDate ??
              null,
            settlementType:
              header.settlementType ||
              header.SettlementType ||
              invoiceDetails?.[0]?.SettlementType ||
              invoiceDetails?.[0]?.settlementType ||
              "",
            physicalVisitRequired:
              header.physicalVisitRequired ||
              header.PhysicalVisitRequired ||
              invoiceDetails?.[0]?.PhysicalVisitRequired ||
              invoiceDetails?.[0]?.physicalVisitRequired ||
              "",
            siteVisitTraveler:
              header.siteVisitTraveler ||
              header.SiteVisitTraveler ||
              invoiceDetails?.[0]?.SiteVisitTraveler ||
              invoiceDetails?.[0]?.siteVisitTraveler ||
              "",
            technicalVisitType:
              header.technicalVisitType ||
              header.TechnicalVisitType ||
              invoiceDetails?.[0]?.TechnicalVisitType ||
              invoiceDetails?.[0]?.technicalVisitType ||
              "",

            // Normalize customer fields
            customerName: header.customerName || header.CustomerName || "",
            customerType: header.customerType || header.CustomerType || "",
            addCustomerType:
              header.addCustomerType || header.AddCustomerType || "",
            dealerDistributor:
              header.dealerDistributor || header.DealerDistributor || "",
            representativeName:
              header.representativeName || header.RepresentativeName || "",
            customerAddress:
              header.customerAddress || header.CustomerAddress || "",
            customerCity: header.customerCity || header.CustomerCity || "",
            customerState: header.customerState || header.CustomerState || "",
            customerCountry:
              header.customerCountry || header.CustomerCountry || "",
            customerPinCode:
              header.customerPinCode || header.CustomerPinCode || "",
            customerPhone: header.customerPhone || header.CustomerPhone || "",
            customerEmail: header.customerEmail || header.CustomerEmail || "",

            // End consumer / B2C: names from CountryMaster, ZoneMaster, TerritoryMaster
            consumerCountryName:
              header.consumerCountryName || header.ConsumerCountryName || "",
            consumerZoneName:
              header.consumerZoneName || header.ConsumerZoneName || "",
            consumerTerritoryName:
              header.consumerTerritoryName || header.ConsumerTerritoryName || "",
            consumerName: header.consumerName || header.ConsumerName || "",
            consumerAddress: header.consumerAddress || header.ConsumerAddress || "",
            consumerCountry: header.consumerCountry || header.ConsumerCountry || "",
            consumerZone: header.consumerZone || header.ConsumerZone || "",
            consumerState: header.consumerState || header.ConsumerState || "",
            consumerPostalCode: header.consumerPostalCode || header.ConsumerPostalCode || "",
            consumerContactNumber: header.consumerContactNumber || header.ConsumerContactNumber || "",
            projectSiteDetails:
              header.projectSiteDetails || header.ProjectSiteDetails || "",

            // Product / category
            productCategory:
              header.productCategory || header.ProductCategory || "",
            // SR Category (from SRTYPES.NAME via API: SRCategory / ComplaintTypeName)
            srCategory:
              header.srCategory || header.SRCategory || "",
            complaintTypeName:
              header.complaintTypeName || header.ComplaintTypeName || header.srCategory || header.SRCategory || "",

            // Sample Information (im.SampleType, CourierAgency, CourierDate, DocketNo, SentTo, SampleDetails)
            sampleType: header.sampleType || header.SampleType || "",
            courierAgency: header.courierAgency || header.CourierAgency || "",
            courierDate: header.courierDate ?? header.CourierDate ?? null,
            docketNo: header.docketNo || header.DocketNo || "",
            sentTo: header.sentTo || header.SentTo || "",
            sampleDetails: header.sampleDetails || header.SampleDetails || "",
            sampleInfo: (() => {
              const st = header.sampleType || header.SampleType || "";
              const stNorm = (st || "").toString().trim().toLowerCase();
              const ca = header.courierAgency || header.CourierAgency || "";
              const cd = header.courierDate ?? header.CourierDate ?? null;
              const dn = header.docketNo || header.DocketNo || "";
              const sent = header.sentTo || header.SentTo || "";
              const sd = header.sampleDetails || header.SampleDetails || "";
              const hasAny = !!(((stNorm && stNorm !== "no") ? st : "") || ca || cd || dn || sent || sd);
              return {
                sampleSent: stNorm === "no" ? "No" : (hasAny ? "Yes" : "No"),
                sampleType: st,
                courierAgency: ca,
                courierDate: cd,
                docketNo: dn,
                sentTo: sent,
                sampleDetails: sd,
              };
            })(),

            // Created / assigned names
            createdByName:
              header.createdByName || header.CreatedByName || "",
            assignedToName:
              header.assignedToName || header.AssignedToName || "",

            // Initiator (Raised By) geography from Greenlam.UserLocationMapping
            initiatorCountryName:
              header.initiatorCountryName || header.InitiatorCountryName || "",
            initiatorZoneName:
              header.initiatorZoneName || header.InitiatorZoneName || "",
            initiatorRegionName:
              header.initiatorRegionName || header.InitiatorRegionName || "",
            initiatorTerritoryName:
              header.initiatorTerritoryName || header.InitiatorTerritoryName || "",

            // Action guard flag from sp_GetServiceRequestDetail header (BIT serializes as true/false, not "1")
            hasActed: (() => {
              const parseFlag = (h) => {
                if (!h || typeof h !== "object") return false;
                const x = h.hasActed ?? h.HasActed;
                if (x === true || x === 1) return true;
                const t = String(x ?? "").trim().toLowerCase();
                return t === "1" || t === "true" || t === "yes";
              };
              const fullH =
                fullDetailForChecklistMedia?.Header ??
                fullDetailForChecklistMedia?.header;
              return parseFlag(header) || parseFlag(fullH);
            })(),

            hasClose: (() => {
              const parseFlag = (h) => {
                if (!h || typeof h !== "object") return false;
                const x = h.hasClose ?? h.HasClose;
                if (x === true || x === 1) return true;
                const t = String(x ?? "").trim().toLowerCase();
                return t === "1" || t === "true" || t === "yes";
              };
              const fullH =
                fullDetailForChecklistMedia?.Header ??
                fullDetailForChecklistMedia?.header;
              return parseFlag(header) || parseFlag(fullH);
            })(),

            // Market code (from DB column MARKETCODE)
            marketCode:
              header.marketCode || header.MarketCode || header.MARKETCODE || "",
            MarketCode:
              header.marketCode || header.MarketCode || header.MARKETCODE || "",

            srTypeCode: header.srTypeCode || header.SRTypeCode || "",
            SRTypeCode: header.srTypeCode || header.SRTypeCode || "",
            seApproveRoutingEligible: (() => {
              const x = header.seApproveRoutingEligible ?? header.SeApproveRoutingEligible;
              if (x === true || x === 1) return true;
              const t = String(x ?? "").trim().toLowerCase();
              return t === "1" || t === "true" || t === "yes";
            })(),

            divisionName:
              invoiceDetails?.[0]?.DivisionName ?? invoiceDetails?.[0]?.divisionName ?? header.divisionName ?? header.DivisionName ?? "",
            businessVertical:
              invoiceDetails?.[0]?.BusinessVertical ??
              invoiceDetails?.[0]?.businessVertical ??
              header.businessVertical ??
              header.BusinessVertical ??
              "",
            BusinessVertical:
              invoiceDetails?.[0]?.BusinessVertical ??
              invoiceDetails?.[0]?.businessVertical ??
              header.businessVertical ??
              header.BusinessVertical ??
              "",

            // Value fields
            srValue:
              header.srValue ?? header.SRValue ?? 0,
            SRValue:
              header.srValue ?? header.SRValue ?? 0,
            ApprovedValue:
              header.approvedValue ?? header.ApprovedValue ?? 0,
            FinalApprovedValue: (() => {
              const fullHdr =
                fullDetailForChecklistMedia?.header ??
                fullDetailForChecklistMedia?.Header ??
                {};
              return resolveFinalApprovedValueTotal(
                invoiceDetails,
                header.finalApprovedValue ??
                  header.FinalApprovedValue ??
                  fullHdr.finalApprovedValue ??
                  fullHdr.FinalApprovedValue
              );
            })(),
            finalApprovedValue: (() => {
              const fullHdr =
                fullDetailForChecklistMedia?.header ??
                fullDetailForChecklistMedia?.Header ??
                {};
              return resolveFinalApprovedValueTotal(
                invoiceDetails,
                header.finalApprovedValue ??
                  header.FinalApprovedValue ??
                  fullHdr.finalApprovedValue ??
                  fullHdr.FinalApprovedValue
              );
            })(),
            ...(() => {
              const fullHdr =
                fullDetailForChecklistMedia?.header ??
                fullDetailForChecklistMedia?.Header ??
                {};
              const rca = resolveRcaFromSr(
                {
                  rcaGiven:
                    header.rcaGiven ??
                    header.RCAGiven ??
                    fullHdr.rcaGiven ??
                    fullHdr.RCAGiven,
                  rcaRemarks:
                    header.rcaRemarks ??
                    header.RCARemarks ??
                    fullHdr.rcaRemarks ??
                    fullHdr.RCARemarks,
                },
                invoiceDetails
              );
              return {
                rcaGiven: rca.given,
                RCAGiven: rca.given,
                rcaRemarks: rca.remarks,
                RCARemarks: rca.remarks,
              };
            })(),
            ...(() => {
              const kitty =
                resolveDeductFromChKitty(header, invoiceDetails) ||
                String(
                  fullDetailForChecklistMedia?.deductFromChKitty ??
                    fullDetailForChecklistMedia?.DeductFromChKitty ??
                    ""
                ).trim();
              return {
                deductFromChKitty: kitty,
                DeductFromChKitty: kitty,
              };
            })(),

            // Currency / FX
            currency: header.currency || header.Currency || "",
            exchangeRate: header.exchangeRate || header.ExchangeRate || "",
            conversionRate: header.conversionRate || header.ConversionRate || "",
            invoiceCurrency:
              header.invoiceCurrency ||
              header.InvoiceCurrency ||
              header.currency ||
              header.Currency ||
              "",
            documentCurrency:
              header.documentCurrency ||
              header.DocumentCurrency ||
              "",
            localCurrency:
              header.documentCurrency ||
              header.DocumentCurrency ||
              "INR",
            reportingCurrency:
              header.reportingCurrency ||
              header.ReportingCurrency ||
              "USD",
            reportingAmount:
              header.reportingAmount ?? header.ReportingAmount ?? null,
            complaintValueUsd:
              header.complaintValueUsd ?? header.ComplaintValueUsd ?? null,

            // Material Section: map invoice details from API to product shape for DataTable
            products: (() => {
              if (!Array.isArray(invoiceDetails) || invoiceDetails.length === 0) {
                componentDetailsByRowKeyRef.current = {};
                return [];
              }
              const nextCache = {};
              const mapped = invoiceDetails.map((row, index) => {
                const invQty = parseInvoiceNumberLoose(pickInvoiceQuantityRaw(row));
                const slNo = row.SLNo ?? row.slNo ?? (index + 1);
                const invVal = parseInvoiceNumberLoose(pickInvoiceValueRaw(row));
                const unitPrice =
                  invQty > 0 ? invVal / invQty : parseInvoiceNumberLoose(row?.Price ?? row?.price ?? row?.UnitPrice ?? row?.unitPrice ?? 0);
                const srQty = parseFloat(row.SRQuantity ?? row.srQuantity ?? 0) || 0;
                const srVal = parseFloat(row.SRValue ?? row.srValue ?? 0) || 0;
                const apprvQty = parseFloat(row.ApprvQuantity ?? row.apprvQuantity ?? row.ApprovedQty ?? row.approvedQty ?? 0) || 0;
                const apprvVal = parseFloat(row.ApprvValue ?? row.apprvValue ?? row.ApprovedValue ?? row.approvedValue ?? 0) || 0;
                const finalApprvValRaw = row.FinalApprovedValue ?? row.finalApprovedValue;
                const finalApprvVal =
                  finalApprvValRaw != null && finalApprvValRaw !== ""
                    ? parseFloat(finalApprvValRaw)
                    : null;
                const productCategory = row.ProductCategoryDescription ?? row.productCategoryDescription ?? row.ProductCategory ?? row.productCategory ?? "";
                const plantName = row.ProductPlantDescription ?? row.productPlantDescription ?? row.PlantName ?? row.plantName ?? "";
                const productName = row.MaterialDescription ?? row.materialDescription ?? row.ProductName ?? row.productName ?? "";
                const category = row.SRTypeDesc ?? row.srTypeDesc ?? row.ComponentCategory ?? row.componentCategory ?? row.Category ?? row.category ?? "";
                const srTypeCode = row.SRTypeCode ?? row.srTypeCode ?? "";
                const srTypeDesc = (category || (row.SRTypeDesc ?? row.srTypeDesc ?? "")).toString().trim();
                const businessVertical = row.BusinessVertical ?? row.businessVertical ?? "";
                const materialRowKey = `${row.ProductCode ?? row.productCode ?? ""}|${srTypeCode}|${row.BatchNo ?? row.batchNo ?? ""}|${row.InvoiceNumber ?? row.invoiceNumber ?? ""}|${slNo}`;
                const componentDetails =
                  row.ComponentDetails ??
                  row.componentDetails ??
                  row.ComponentDetailsJson ??
                  row.componentDetailsJson ??
                  null;
                if (componentDetails != null) {
                  nextCache[materialRowKey] = componentDetails;
                }
                return {
                  ...row,
                  SLNo: slNo,
                  slNo: slNo,
                  // Dual naming for column renderers (camel + Pascal)
                  ProductCode: row.ProductCode ?? row.productCode,
                  productCode: row.ProductCode ?? row.productCode,
                  ProductCategory: productCategory,
                  productCategory: productCategory,
                  BusinessVertical: businessVertical,
                  businessVertical: businessVertical,
                  PlantName: plantName,
                  plantName: plantName,
                  ProductName: productName,
                  productName: productName,
                  MaterialDescription: productName,
                  Quantity: invQty,
                  quantity: invQty,
                  InvoiceQuantity: invQty,
                  Price: unitPrice,
                  price: unitPrice,
                  InvoiceValue: invVal,
                  UOM: row.UOM ?? row.uom ?? "PCS",
                  Category: category,
                  category: category,
                  SRCategory: category,
                  srCategory: category,
                  SRTypeDesc: srTypeDesc,
                  srTypeDesc: srTypeDesc,
                  SRTypeCode: srTypeCode,
                  srTypeCode: srTypeCode,
                  ComplaintQty: srQty,
                  complaintQty: srQty,
                  SRQuantity: srQty,
                  SRValue: srVal,
                  ApprovedQty: apprvQty,
                  approvedQty: apprvQty,
                  ApprovedQuantity: apprvQty,
                  ApprovedValue: apprvVal,
                  approvedValue: apprvVal,
                  FinalApprovedValue:
                    finalApprvVal != null && Number.isFinite(finalApprvVal) ? finalApprvVal : null,
                  finalApprovedValue:
                    finalApprvVal != null && Number.isFinite(finalApprvVal) ? finalApprvVal : null,
                  ApprvQuantity: apprvQty,
                  ApprvValue: apprvVal,
                  FactoryIntentNo: row.FactoryIntentNo ?? row.factoryIntentNo ?? "",
                  factoryIntentNo: row.FactoryIntentNo ?? row.factoryIntentNo ?? "",
                  FactoryIntentDate: row.FactoryIntentDate ?? row.factoryIntentDate ?? null,
                  factoryIntentDate: row.FactoryIntentDate ?? row.factoryIntentDate ?? null,
                  SettlementType: row.SettlementType ?? row.settlementType ?? "",
                  settlementType: row.SettlementType ?? row.settlementType ?? "",
                  PhysicalVisitRequired:
                    row.PhysicalVisitRequired ?? row.physicalVisitRequired ?? "",
                  physicalVisitRequired:
                    row.PhysicalVisitRequired ?? row.physicalVisitRequired ?? "",
                  SiteVisitTraveler:
                    row.SiteVisitTraveler ?? row.siteVisitTraveler ?? "",
                  siteVisitTraveler:
                    row.SiteVisitTraveler ?? row.siteVisitTraveler ?? "",
                  TechnicalVisitType:
                    row.TechnicalVisitType ?? row.technicalVisitType ?? "",
                  technicalVisitType:
                    row.TechnicalVisitType ?? row.technicalVisitType ?? "",
                  DeductFromChKitty:
                    row.DeductFromChKitty ?? row.deductFromChKitty ?? "",
                  deductFromChKitty:
                    row.DeductFromChKitty ?? row.deductFromChKitty ?? "",
                  /* Multi-row Component Details (Mikasa Doors/Floors/Sturdo) from SRCOMPONENTDETAILS */
                  ComponentDetails: componentDetails,
                  componentDetails: componentDetails,
                  ComponentDetailsJson:
                    row.ComponentDetailsJson ?? row.componentDetailsJson ?? null,
                  MaterialRowKey: materialRowKey,
                  materialRowKey: materialRowKey,
                  ChildSRNumber: row.ChildSRNumber ?? row.childSRNumber ?? "",
                  childSRNumber: row.ChildSRNumber ?? row.childSRNumber ?? "",
                };
              });
              componentDetailsByRowKeyRef.current = nextCache;
              return mapped;
            })(),
            invoiceDetails: Array.isArray(invoiceDetails) ? invoiceDetails : [],
            attachments: Array.isArray(srAttachmentsRaw) ? srAttachmentsRaw : [],
            invoiceFiles: srInvoiceFiles,
            secondaryInvoiceFiles: srSecondaryInvoiceFiles,
            checklistResponses,
            statusHistory: statusHistoryFromApi,
            commentsHistory: [],
            rootCauseAnalysis: [],

            previousComplaints: [],

            // Timeline card + details grid use mapped steps (status, designation, name, date, approved).
            journeyStatus:
              journeyStatus.length > 0
                ? journeyStatus
                : filterSkippedJourneySteps(
                    payload.journeyStatus ||
                      header.journeyStatus ||
                      header.JourneyStatus ||
                      []
                  ),
          };

          console.log("Final srData in ViewServiceRequest:", data);

          setSrData(data);
          pageLog.finish({
            products: data.products?.length ?? 0,
            journeyStatus: data.journeyStatus?.length ?? 0,
            checklistResponses: data.checklistResponses?.length ?? 0,
            statusHistory: data.statusHistory?.length ?? 0,
            hasActed: data.hasActed,
            hasClose: data.hasClose,
          });

          if (
            data.addCustomerType === "B2C" ||
            header.AddCustomerType === "B2C" ||
            header.addCustomerType === "B2C"
          ) {
            setShowEndCustomer(true);
          }

          setLoading(false);
        } catch (apiError) {
          pageLog.fail(apiError, "api-detail-or-build");
          console.error("Error fetching SR detail from SR* tables:", apiError);
          setSrData(null);
          setLoadError(
            getApiErrorMessage(
              apiError,
              "Unable to load this service request. The API may be unavailable or returned an error."
            )
          );
          setLoading(false);
        }
      } catch (error) {
        pageLog.fail(error, "load-outer");
        console.error("Error fetching service request:", error);
        setSrData(null);
        setLoadError(getApiErrorMessage(error));
        setLoading(false);
      }
    };

    fetchServiceRequest();
  }, [id, childSrCodeFromQuery]);

  // Fetch SR attachments (invoice copies etc.) and set invoiceFiles for Invoice Details panel
  // Use parent id so attachments show when viewing parent or child SR
  useEffect(() => {
    if (!id || !srData) return;
    // Avoid re-fetch if we already loaded attachments (invoiceFiles or secondaryInvoiceFiles set)
    const hasLoaded =
      (Array.isArray(srData.invoiceFiles) && srData.invoiceFiles.length > 0) ||
      (Array.isArray(srData.secondaryInvoiceFiles) && srData.secondaryInvoiceFiles.length > 0);
    if (hasLoaded) return;

    let mounted = true;
    (async () => {
      try {
        const res = await complaintAPI.getServiceRequestAttachments(id);
        if (!mounted) return;
        const list = res?.data ?? res;
        if (!Array.isArray(list) || list.length === 0) return;

        const baseUrl = getApiBaseUrl();
        const mapOne = (a) => {
          const filePath = (a.filePath || a.FilePath || "").replace(/^\/+/, "");
          const url = filePath ? `${baseUrl}/${filePath}` : "";
          return {
            id: a.slNo ?? a.SLNo ?? a.fileName ?? a.FileName,
            name: a.fileName || a.FileName || "Attachment",
            url,
            fileType: a.fileType || a.FileType,
            copyFor: a.copyFor || a.CopyFor,
          };
        };
        const copyForLower = (v) => (v || "").toLowerCase();
        // Invoice Copy → Invoice Details card panel
        const invoiceFiles = list
          .filter((a) => copyForLower(a.copyFor || a.CopyFor).includes("invoice copy"))
          .map(mapOne);
        // Secondary Invoice → Customer Details card panel
        const secondaryInvoiceFiles = list
          .filter((a) => copyForLower(a.copyFor || a.CopyFor).includes("secondary invoice"))
          .map(mapOne);
        setSrData((prev) =>
          prev ? { ...prev, invoiceFiles, secondaryInvoiceFiles } : prev
        );
      } catch (err) {
        if (mounted) console.warn("Failed to load SR attachments:", err);
      }
    })();
    return () => { mounted = false; };
  }, [id, srData]);

  // Load previous SRs for this invoice (view page only: exclude the current SR itself)
  useEffect(() => {
    let mounted = true;
    const invoiceNo = String(
      srData?.invoiceNumber || srData?.invoiceNo || ""
    )
      .trim()
      .toUpperCase();

    if (!invoiceNo) {
      setPreviousComplaintsCount(0);
      setPreviousComplaintsData([]);
      setShowPreviousComplaintsTable(false);
      return () => {
        mounted = false;
      };
    }

    const norm = (v) => String(v ?? "").trim().toUpperCase();
    const currentChild = norm(
      childSrCodeFromQuery ||
        srData?.childSRNumber ||
        srData?.ChildSRNumber ||
        ""
    );
    const currentParent = norm(
      id || srData?.srCode || srData?.SRCode || srData?.parentSRNumber || ""
    );

    // forView: all SRs on this invoice (initiator / approver / closer), not CreatedBy-filtered
    complaintAPI
      .getPreviousComplaintsByInvoice(invoiceNo, null, { forView: true })
      .then((prevRes) => {
        if (!mounted) return;
        const list = prevRes?.data ?? prevRes?.Data;
        if (prevRes?.success && Array.isArray(list) && list.length > 0) {
          // View page: exclude only the current child SR (siblings / other SRs still show)
          const filtered = list.filter((item) => {
            const sn = norm(item.srNumber ?? item.SRNumber);
            const sc = norm(item.srCode ?? item.SRCode);
            if (currentChild) {
              return sn !== currentChild && sc !== currentChild;
            }
            // No child in URL: exclude parent-level match only
            if (currentParent) {
              return sn !== currentParent;
            }
            return true;
          });
          setPreviousComplaintsData(filtered);
          setPreviousComplaintsCount(filtered.length);
          if (filtered.length === 1) setShowPreviousComplaintsTable(true);
          else setShowPreviousComplaintsTable(false);
        } else {
          setPreviousComplaintsData([]);
          setPreviousComplaintsCount(0);
          setShowPreviousComplaintsTable(false);
        }
      })
      .catch(() => {
        if (!mounted) return;
        setPreviousComplaintsData([]);
        setPreviousComplaintsCount(0);
        setShowPreviousComplaintsTable(false);
      });

    return () => {
      mounted = false;
    };
  }, [
    srData?.invoiceNumber,
    srData?.invoiceNo,
    srData?.childSRNumber,
    srData?.ChildSRNumber,
    srData?.srCode,
    srData?.SRCode,
    id,
    childSrCodeFromQuery,
  ]);

  // Fetch queries when component loads or id changes.
  // For SR pages this calls `/api/workflow/ticket-queries/by-sr` from frontend.
  useEffect(() => {
    if (!id) return;
    fetchAllQueries();
  }, [id, srData, childSrCodeFromQuery]);

  // Fetch SR query response attachments (SRTICKETACHMENTS) once per SR context.
  // NOTE: We now fetch attachments per-query by QueryNo (server-side filtered) via `ensureQueryAttachmentsLoaded`.
  // Keeping the old "fetch all attachments for SR" call will hit the API with QueryNo = null and confuse debugging.
  useEffect(() => {
    // Clear any legacy state; per-query cache is used instead.
    setTicketAttachments([]);
    setLoadingTicketAttachments(false);
  }, [id, srData, childSrCodeFromQuery]);

  // Fetch attachments for a single queryNo (server-side filtered), cached.
  const ensureQueryAttachmentsLoaded = async (queryNo) => {
    const qno = String(queryNo || "").trim();
    if (!qno) return;

    // If we already have them, don't refetch.
    if (ticketAttachmentsByQueryNo && Array.isArray(ticketAttachmentsByQueryNo[qno])) return;
    if (loadingTicketAttachmentsByQueryNo && loadingTicketAttachmentsByQueryNo[qno]) return;

    const parentSrCode = (srData?.srCode || srData?.SRCode || id || "").toString().trim();
    const childSrCode =
      (srData?.childSRNumber || srData?.ChildSRNumber || childSrCodeFromQuery || "").toString().trim();
    if (!parentSrCode) return;

    setLoadingTicketAttachmentsByQueryNo((prev) => ({ ...(prev || {}), [qno]: true }));
    try {
      const res = await workflowAPI.getTicketAttachmentsBySR({
        srCode: parentSrCode,
        childSrCode: childSrCode || null,
        queryNo: qno,
      });

      // Normalize response shapes
      const ok =
        (res && typeof res === "object" && ("success" in res || "Success" in res))
          ? (res?.success ?? res?.Success)
          : null;
      const raw =
        ok === true
          ? (res?.data ?? res?.Data)
          : (res?.data ?? res?.Data ?? res);
      const list = Array.isArray(raw) ? raw : [];

      const baseUrl = getApiBaseUrl();
      const toFullUrl = (filePath) => {
        const p = filePath == null ? "" : String(filePath).trim();
        if (!p) return "";
        if (/^https?:\/\//i.test(p)) return p;
        const normalized = p.replace(/\\/g, "/").replace(/^~\//, "").trim();
        const clean = normalized.replace(/^\/+/, "");
        return clean ? `${baseUrl}/${clean}` : "";
      };
      const parseTs = (v) => {
        if (!v) return null;
        const d = new Date(v);
        const t = d.getTime();
        return Number.isFinite(t) ? t : null;
      };

      const mapped = list.map((a, idx) => {
        const filePath = (a?.filePath ?? a?.FilePath ?? "").toString();
        const url = toFullUrl(filePath);
        const name =
          a?.fileName ??
          a?.FileName ??
          (filePath ? String(filePath).split("/").pop() : "") ??
          `Attachment ${idx + 1}`;
        const copyFor = a?.copyFor ?? a?.CopyFor ?? "";
        const fileType = a?.fileType ?? a?.FileType ?? "";
        const createdOn = a?.createdOn ?? a?.CreatedOn ?? null;
        const queryId = a?.queryId ?? a?.QueryId ?? null;
        const queryNo2 = a?.queryNo ?? a?.QueryNo ?? qno;
        return {
          id: a?.slNo ?? a?.SLNo ?? `${idx}`,
          queryId: queryId != null && String(queryId).trim() !== "" ? Number(queryId) : null,
          queryNo: String(queryNo2 || "").trim(),
          name,
          url,
          filePath,
          fileSize: a?.fileSize ?? a?.FileSize ?? null,
          fileType,
          copyFor,
          createdOn,
          createdOnTs: parseTs(createdOn),
          createdBy: a?.createdBy ?? a?.CreatedBy ?? "",
          srCode: a?.srCode ?? a?.SRCode ?? "",
          childSrCode: a?.childSrCode ?? a?.ChildSRCode ?? "",
        };
      });

      setTicketAttachmentsByQueryNo((prev) => ({ ...(prev || {}), [qno]: mapped }));
    } catch (e) {
      console.warn("Failed to load query attachments by QueryNo:", qno, e);
      setTicketAttachmentsByQueryNo((prev) => ({ ...(prev || {}), [qno]: [] }));
    } finally {
      setLoadingTicketAttachmentsByQueryNo((prev) => ({ ...(prev || {}), [qno]: false }));
    }
  };

  // Initialize Previous Complaints DataTable
  useEffect(() => {
    // Destroy previous instance
    if (historyDataTableInstanceRef.current) {
      historyDataTableInstanceRef.current.destroy();
      historyDataTableInstanceRef.current = null;
    }

    if (historyTableRef.current && previousComplaintsData.length > 0) {
      // Ensure DataTable plugin is registered on jQuery
      if (!$.fn.DataTable) {
        DataTable($);
      }

      // Transform the data from backend to match DataTable format (same as Raise Complaint)
      const historyData = previousComplaintsData.map((complaint, index) => ({
        originalIndex: index,
        srNo: complaint.srNumber || complaint.SRNumber || "",
        srDate: complaint.srDate || complaint.SRDate || "",
        productsInvolved: complaint.productsInvolved || complaint.ProductsInvolved || "",
        srQty: complaint.srQuantity ?? complaint.SRQuantity ?? 0,
        srValue: complaint.srValue ?? complaint.SRValue ?? 0,
        status: complaint.statusName || complaint.StatusName || complaint.status || "",
        actionTaken: complaint.actionTaken || complaint.ActionTaken || "",
        latestComment: complaint.latestComment || complaint.LatestComment || "",
      }));

      historyDataTableInstanceRef.current = $(
        historyTableRef.current
      ).DataTable({
        data: historyData,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        pageLength: 5,
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        fixedColumns: false,
        order: [[0, "asc"]],
        columns: [
          {
            title: "SR No",
            data: "srNo",
            render: (data, type, row) => {
              const srNo = (data || "").trim();
              if (!srNo) return "";
              const originalIndex = row.originalIndex !== undefined ? row.originalIndex : 0;
              return `<a href="#" class="sr-link-view" data-original-index="${escapeHtmlAttr(String(originalIndex))}" style="color: #007bff; text-decoration: underline; cursor: pointer; font-weight: 500;">${escapeHtml(srNo)}</a>`;
            },
            createdCell: (td) => {
              td.classList.add("text-center");
            },
          },
          {
            title: "SR Date",
            data: "srDate",
            render: (data) => {
              if (!data) return "";
              try {
                const date = new Date(data);
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
              } catch {
                return data;
              }
            },
            createdCell: (td) => {
              td.classList.add("text-center");
            },
          },
          {
            title: "Product(s) involved",
            data: "productsInvolved",
            createdCell: (td) => {
              td.classList.add("text-start");
            },
            render: (data, type) => {
              const fullText = (data ?? "").toString();
              if (type !== "display") return fullText;
              const escaped = escapeHtml(fullText);
              return `<span class="sr-product-involved-truncate" title="${escapeHtmlAttr(fullText)}">${escaped}</span>`;
            },
          },
          {
            title: "SR QTY",
            data: "srQty",
            className: "text-end dt-numeric-col",
          },
          {
            title: "SR VALUE",
            data: "srValue",
            className: "text-end dt-numeric-col",
            render: (data) => {
              if (!data && data !== 0) return "";
              return parseFloat(data).toLocaleString("en-IN", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              });
            },
          },
          {
            title: "Resolution status",
            data: "status",
            createdCell: (td) => {
              td.classList.add("text-center");
            },
            render: (data, type) => {
              const status = String(data ?? "").trim();
              if (!status) return "";
              if (type !== "display") return status;

              let statusClass = "";
              const lower = status.toLowerCase();
              if (lower === "open") statusClass = "status-open";
              else if (lower === "closed") statusClass = "status-closed";
              else if (lower === "approved") statusClass = "status-approved";
              else if (lower === "rejected") statusClass = "status-rejected";
              else if (lower === "wip" || lower.includes("progress")) statusClass = "status-wip";

              return statusClass
                ? `<span class="${statusClass}">${escapeHtml(status)}</span>`
                : escapeHtml(status);
            },
          },
          {
            title: "Action",
            data: null,
            orderable: false,
            createdCell: (td) => {
              td.classList.add("text-center");
            },
            render: (data, type, row) => {
              if (type === "display") {
                const originalIndex = row.originalIndex !== undefined ? row.originalIndex : 0;
                return `<button class="btn-view-complaint" data-original-index="${originalIndex}" style="
                  background: linear-gradient(135deg, #17a2b8 0%, #138496 100%);
                  border: none;
                  color: white;
                  padding: 4px 12px;
                  border-radius: 4px;
                  cursor: pointer;
                  font-size: 12px;
                  font-weight: 500;
                  transition: all 0.3s ease;
                  box-shadow: 0 2px 4px rgba(23, 162, 184, 0.3);
                ">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 4px; display: inline-block;">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  View
                </button>`;
              }
              return "";
            },
          },
        ],
        initComplete: function () {
          try {
            const wrapper = $(historyTableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              const lengthEl = wrapper.find(".dt-length, .dataTables_length");
              const searchEl = wrapper.find(".dt-search, .dataTables_filter");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              const infoEl = wrapper.find(".dt-info, .dataTables_info");
              const pagingEl = wrapper.find(".dt-paging, .dataTables_paginate");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $("<div class='dtFltrBotm d-flex flex-wrap'></div>");
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }

            $(historyTableRef.current).off("click", ".btn-view-complaint");
            $(historyTableRef.current).on("click", ".btn-view-complaint", function (e) {
              e.preventDefault();
              const originalIndex = parseInt($(this).data("original-index"));
              setSelectedComplaintIndex(originalIndex);
              setShowPreviousComplaintsDetailModal(true);
            });

            $(historyTableRef.current)
              .off("click", ".sr-link-view")
              .on("click", ".sr-link-view", function (e) {
                e.preventDefault();
                const originalIndex = parseInt($(this).data("original-index"));
                setSelectedComplaintIndex(originalIndex);
                setShowPreviousComplaintsDetailModal(true);
              });

            syncScrollHeadWidth(historyTableRef);
          } catch (e) {
            console.error("Error in Previous Complaints DataTable initComplete:", e);
          }
        },
        drawCallback: function () {
          $(historyTableRef.current).off("click", ".btn-view-complaint");
          $(historyTableRef.current).on("click", ".btn-view-complaint", function (e) {
            e.preventDefault();
            const originalIndex = parseInt($(this).data("original-index"));
            setSelectedComplaintIndex(originalIndex);
            setShowPreviousComplaintsDetailModal(true);
          });

          $(historyTableRef.current).off("click", ".sr-link-view");
          $(historyTableRef.current).on("click", ".sr-link-view", function (e) {
            e.preventDefault();
            const originalIndex = parseInt($(this).data("original-index"));
            setSelectedComplaintIndex(originalIndex);
            setShowPreviousComplaintsDetailModal(true);
          });

          syncScrollHeadWidth(historyTableRef);
        },
      });

      // Add ResizeObserver for this specific table with debouncing
      let resizeObserver = null;
      let resizeTimeout = null;
      if (window.ResizeObserver) {
        const tableContainer = $(historyTableRef.current).closest(
          ".card-panel, .table-responsive"
        );
        if (tableContainer.length > 0) {
          resizeObserver = new ResizeObserver(() => {
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              if (historyDataTableInstanceRef.current) {
                try {
                  historyDataTableInstanceRef.current.columns.adjust();
                  syncScrollHeadWidth(historyTableRef);
                } catch (e) {
                  console.debug("Error adjusting history table columns:", e);
                }
              }
            }, 100);
          });
          resizeObserver.observe(tableContainer[0]);
        }
      }

      // Store observer and timeout in ref for cleanup
      if (historyTableRef.current) {
        historyTableRef.current._resizeObserver = resizeObserver;
        historyTableRef.current._resizeTimeout = resizeTimeout;
      }

      // Cleanup function
      return () => {
        if (historyTableRef.current) {
          if (historyTableRef.current._resizeObserver) {
            historyTableRef.current._resizeObserver.disconnect();
          }
          if (historyTableRef.current._resizeTimeout) {
            clearTimeout(historyTableRef.current._resizeTimeout);
          }
        }
        if (historyDataTableInstanceRef.current) {
          historyDataTableInstanceRef.current.destroy();
          historyDataTableInstanceRef.current = null;
        }
      };
    }
  }, [previousComplaintsData, showPreviousComplaintsTable]);

  // Sync dt-scroll-headInner / dt-scroll-footInner width with dt-scroll-body (scrollX splits tables).
  // Without foot inner + cell widths, tfoot totals sit under the wrong columns (shifted left vs tbody).
  const syncScrollHeadWidth = (tableRef) => {
    if (!tableRef?.current) return;

    try {
      const $table = $(tableRef.current);
      const $wrapper = $table.closest(".dataTables_wrapper, .dt-container");
      if (!$wrapper.length) return;

      const $headInner = $wrapper.find(".dt-scroll-headInner");
      const $footInner = $wrapper.find(".dt-scroll-footInner");
      const $body = $wrapper.find(".dt-scroll-body");

      if (!$body.length) return;

      const bodyElement = $body[0];
      const bodyWidth = bodyElement?.scrollWidth || $body.width() || 0;

      if (bodyWidth <= 0) return;

      $headInner.each(function () {
        const $inner = $(this);
        const current = $inner.width() || 0;
        if (Math.abs(current - bodyWidth) > 1) {
          $inner.css("width", bodyWidth + "px");
        }
      });

      $footInner.each(function () {
        const $inner = $(this);
        const current = $inner.width() || 0;
        if (Math.abs(current - bodyWidth) > 1) {
          $inner.css("width", bodyWidth + "px");
        }
      });

      const $bodyTable = $body.find("table").first();
      const $footTable = $wrapper.find(".dt-scroll-footInner table").first();
      if ($bodyTable.length && $footTable.length) {
        const $bodyCells = $bodyTable.find("tbody tr:first-child").children("td, th");
        const $footCells = $footTable.find("tfoot tr:first-child").children("td, th");
        const $headCells = $wrapper
          .find(".dt-scroll-headInner table thead tr:first-child")
          .children("th, td");
        if (
          $bodyCells.length > 0 &&
          $footCells.length > 0 &&
          $bodyCells.length === $footCells.length
        ) {
          $bodyCells.each(function (i) {
            let w = $(this).outerWidth();
            if ((!w || w < 1) && $headCells.length > i) {
              w = $headCells.eq(i).outerWidth();
            }
            const $fc = $footCells.eq(i);
            if (w > 0) {
              $fc.css({ width: `${w}px`, minWidth: `${w}px`, boxSizing: "border-box" });
            } else {
              $fc.css({ width: "", minWidth: "", boxSizing: "" });
            }
          });
        }
      }
    } catch (e) {
      // Silently handle errors - table might not be fully initialized
      console.debug("Error syncing scroll table widths:", e);
    }
  };

  // Material Section lives in #collapseProductDetails (starts collapsed). DataTables + scrollX init
  // with zero width so headers on the right (e.g. "Total Price") can mis-measure — re-run adjust when expanded.
  useEffect(() => {
    const el = document.getElementById("collapseProductDetails");
    if (!el) return undefined;

    const adjustMaterialGrid = () => {
      requestAnimationFrame(() => {
        try {
          if (dataTableInstanceRef.current && $.fn?.DataTable) {
            dataTableInstanceRef.current.columns.adjust();
            setTimeout(() => syncScrollHeadWidth(productTableRef), 60);
          }
        } catch (e) {
          console.debug("Material Section column adjust:", e);
        }
      });
    };

    el.addEventListener("shown.bs.collapse", adjustMaterialGrid);
    if (el.classList.contains("show")) {
      adjustMaterialGrid();
    }
    return () => el.removeEventListener("shown.bs.collapse", adjustMaterialGrid);
  }, [srData?.products?.length]);

  // Initialize DataTable for Product Details
  useEffect(() => {
    const products = srData?.products;
    if (
      productTableRef.current &&
      Array.isArray(products) &&
      products.length > 0
    ) {
      // Ensure DataTable plugin is registered on jQuery
      if (!$.fn.DataTable) {
        DataTable($);
      }

      // Destroy existing DataTable instance if it exists
      if (dataTableInstanceRef.current) {
        dataTableInstanceRef.current.destroy();
        dataTableInstanceRef.current = null;
      }

      const isSEUser = isSeUserType();
      // Assign flow (Packaging/Dispatch + Domestic DOS Admin / Export ZH): view qty/value only.
      const isDosAdminMaterialReadonly = isAssignFlowApprover;

      const showComponentDetailsColumn = products.some((row) =>
        materialRowShowsComponentDetailsIcon(row)
      );
      const showSrQtyValueApproveColumns = products.some(
        (row) => !materialRowShowsComponentDetailsIcon(row)
      );

      // Determine if it's Export based on MarketCode/MarketName
      const marketCode = srData?.MarketCode || srData?.marketCode || "";
      const marketName = srData?.MarketName || srData?.marketName || "";
      let isExport = false;
      
      if (marketCode || marketName) {
        const codeUpper = marketCode.toUpperCase();
        const nameUpper = marketName.toUpperCase();
        
        if (
          codeUpper === "M002" ||
          codeUpper.includes("EXP") ||
          nameUpper.includes("EXPORT") ||
          codeUpper === "EXPORT" ||
          nameUpper === "EXPORT"
        ) {
          isExport = true;
        }
      }

      // Inject CSS for numeric column headers
      const styleId = "dt-numeric-header-style";
      if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
          .dt-numeric-col .dt-column-header {
            flex-direction: row !important;
          }
        `;
        document.head.appendChild(style);
      }

      // Build columns array conditionally
      // Hidden column 0: SLNo for default sort (1, 2, 3)
      const baseColumns = [
        {
          title: "",
          data: (row) => {
            const slNo = row?.SLNo ?? row?.slNo;
            return slNo != null ? Number(slNo) : 0;
          },
          orderable: true,
          visible: false,
          className: "text-center",
        },
        {
          title: "SNo.",
          data: null,
          orderable: false,
          visible: false,
          render: (data, type, row, meta) => {
            return (meta.row + 1).toString();
          },
          className: "text-center",
        },
        {
          title: "Business Vertical",
          data: null,
          render: (data, type, row) => {
            return (
              row?.BusinessVertical ||
              row?.businessVertical ||
              row?.BVName ||
              row?.bvName ||
              ""
            );
          },
        },
      ];

      baseColumns.push({
        title: "PlantName",
        data: null,
        render: (data, type, row, meta) => {
          // Handle both camelCase and PascalCase property names
          let plantName =
            row?.PlantName ||
            row?.plantName ||
            row?.Plantname ||
            row?.plant_name ||
            "";
          
          if (!plantName) plantName = "-";
          
          return plantName;
        },
      },
      {
        title: "Item Description",
        data: null,
        render: (data, type, row, meta) => {
          let itemDescription =
            row?.ProductName || row?.productName || row?.Productname || "";
          
          // For SNo 1 (first row, index 0), change "Plain Particle Board 8mm" to "Prelam Board 8mm"
          if (meta.row === 0 && itemDescription === "Plain Particle Board 8mm") {
            itemDescription = "Prelam Board 8mm";
          }
          
          return itemDescription;
        },
      },
      {
        title: "Product Details",
        data: null,
        orderable: false,
        render: (data, type, row, meta) => {
          if (type === "display") {
            const productKey = getMaterialRowKey(row, meta.row);
            const cached =
              componentDetailsByRowKeyRef.current?.[productKey];
            productDataRef.current[productKey] = {
              ...row,
              ComponentDetails:
                row?.ComponentDetails ??
                row?.componentDetails ??
                cached ??
                null,
              componentDetails:
                row?.ComponentDetails ??
                row?.componentDetails ??
                cached ??
                null,
            };
            return `<div class="product-details-icon" data-product-key="${productKey}" style="cursor: pointer; display: inline-block; text-align: center;">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1976d2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle;">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                <circle cx="12" cy="12" r="3"></circle>
              </svg>
            </div>`;
          }
          return "";
        },
        className: "text-center",
      },
      {
        title: "Invoice Quantity",
        data: null,
        render: (data, type, row) => {
          const qty = parseInvoiceNumberLoose(pickInvoiceQuantityRaw(row));
          return formatViewSrQty(qty);
        },
        className: "text-end dt-numeric-col",
      },
      {
        title: "UOM",
        data: null,
        render: (data, type, row) => row?.UOM ?? row?.uom ?? "PCS",
        className: "text-center",
      },
      {
        title: "Invoice Unit Price",
        data: null,
        render: (data, type, row) => {
          const price = parseInvoiceNumberLoose(row?.Price ?? row?.price ?? row?.UnitPrice ?? row?.unitPrice ?? 0);
          const qty = parseInvoiceNumberLoose(pickInvoiceQuantityRaw(row));
          const lineVal = parseInvoiceNumberLoose(pickInvoiceValueRaw(row));
          const unitPrice = qty > 0 && lineVal > 0 ? lineVal / qty : price;
          return formatViewSrMoney(unitPrice);
        },
        className: "text-end dt-numeric-col",
      },
      {
        title: "Total Price",
        data: null,
        render: (data, type, row) => {
          const qty = parseInvoiceNumberLoose(pickInvoiceQuantityRaw(row));
          const price = parseInvoiceNumberLoose(row?.Price ?? row?.price ?? row?.UnitPrice ?? row?.unitPrice ?? 0);
          const lineVal = parseInvoiceNumberLoose(pickInvoiceValueRaw(row));
          const total = lineVal > 0 ? lineVal : qty * price;
          return formatViewSrMoney(total);
        },
        className: "text-end dt-numeric-col",
      },
      {
        title: "Component Details",
        data: null,
        orderable: false,
        visible: showComponentDetailsColumn,
        render: (data, type, row, meta) => {
          if (type === "display") {
            if (!materialRowShowsComponentDetailsIcon(row)) {
              return "";
            }
            const key = getMaterialRowKey(row, meta.row);
            const cached = componentDetailsByRowKeyRef.current?.[key];
            productDataRef.current[key] = {
              ...(productDataRef.current[key] || {}),
              ...row,
              ComponentDetails:
                row?.ComponentDetails ??
                row?.componentDetails ??
                cached ??
                productDataRef.current[key]?.ComponentDetails ??
                null,
              componentDetails:
                row?.ComponentDetails ??
                row?.componentDetails ??
                cached ??
                productDataRef.current[key]?.componentDetails ??
                null,
            };
            return `
              <div class="component-details-icon" data-product-key="${key}" style="cursor: pointer; display: inline-block; text-align: center;">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1976d2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle;">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                </svg>
              </div>
            `;
          }
          return "";
        },
        className: "text-center",
      },
      {
        title: "SR Type Code",
        data: null,
        orderable: false,
        visible: false,
        render: (data, type, row) => {
          const val = row?.SRTypeCode ?? row?.srTypeCode ?? "";
          return (val && String(val).trim()) ? String(val).trim() : "—";
        },
        className: "text-center",
      },
      {
        title: "SR Category",
        data: null,
        orderable: false,
        render: (data, type, row) => {
          const val = row?.SRTypeDesc ?? row?.srTypeDesc ?? row?.Category ?? row?.category ?? row?.SRCategory ?? row?.srCategory ?? "";
          return (val && String(val).trim()) ? String(val).trim() : "—";
        },
        className: "text-left",
      },
      {
        title: "SR Category (dropdown)",
        data: null,
        orderable: false,
        visible: false,
        render: (data, type, row, meta) => {
          // 'row' contains the full product object
          const key = getMaterialRowKey(row, meta.row);
          const val = (row?.Category ?? row?.category ?? row?.SRCategory ?? row?.srCategory ?? row?.SRTypeDesc ?? row?.srTypeDesc ?? "").toString().trim();
          const options = [
            { value: "Packaging / Dispatch", text: "Packaging / Dispatch" },
            { value: "Quality", text: "Quality" },
            { value: "Installation", text: "Installation" },
            { value: "Commercial", text: "Commercial" },
          ];
          // Include current value as option if not empty and not already in list (so API value shows in grid)
          const hasVal = val && val.length > 0;
          const inList = options.some((o) => String(o.value).trim().toLowerCase() === val.toLowerCase());
          if (hasVal && !inList) {
            options.push({ value: val, text: val });
          }
          const optionsHtml = options
            .map(
              (opt) =>
                `<option value="${opt.value}" ${
                  val === opt.value ? "selected" : ""
                }>${opt.text}</option>`
            )
            .join("");
          return `
            <select
              class="form-select form-select-sm category-select"
              data-key="${key}"
              style="min-width: 150px;"
              disabled
              readonly
            >
              <option value="">Select SR Category</option>
              ${optionsHtml}
            </select>
          `;
        },
        className: "text-center category-cell",
      },
      {
        title: "SR Qty",
        data: null,
        orderable: false,
        visible: showSrQtyValueApproveColumns,
        render: (data, type, row, meta) => {
          const key = getMaterialRowKey(row, meta.row);
          const complaintQty = parseFloat(row?.ComplaintQty ?? row?.complaintQty ?? row?.SRQuantity ?? row?.srQuantity ?? 0) || 0;
          return `
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              class="form-control form-control-sm complaint-qty-input"
              data-key="${key}"
              value="${formatViewSrQty(complaintQty)}"
              style="max-width: 120px; text-align: right; background-color: #e9ecef;"
              disabled
              readonly
            />
          `;
        },
        className: "text-end complaint-qty-cell",
      },
      {
        title: "SR Value",
        data: null,
        visible: showSrQtyValueApproveColumns,
        render: (data, type, row) => {
          const srVal = parseFloat(row?.SRValue ?? row?.srValue ?? 0) || 0;
          const complaintQty = parseFloat(row?.ComplaintQty ?? row?.complaintQty ?? row?.SRQuantity ?? row?.srQuantity ?? 0) || 0;
          const price = parseFloat(row?.Price ?? row?.price ?? 0) || 0;
          const val = srVal > 0 ? srVal : complaintQty * price;
          return formatViewSrMoney(val);
        },
        className: "text-end dt-numeric-col",
      },
      {
        title: "Approve Qty",
        data: null,
        orderable: false,
        visible: showSrQtyValueApproveColumns,
        render: (data, type, row, meta) => {
          const key = getMaterialRowKey(row, meta.row);
          const srQty = parseFloat(row?.ComplaintQty ?? row?.complaintQty ?? row?.SRQuantity ?? row?.srQuantity ?? 0) || 0;
          const price = parseFloat(row?.Price ?? row?.price ?? 0) || 0;
          const approvedQtyRaw = parseFloat(row?.ApprovedQty ?? row?.approvedQty ?? row?.ApprovedQuantity ?? row?.approvedQuantity ?? row?.ApprvQuantity ?? row?.apprvQuantity ?? 0) || 0;
          const approvedValRaw = parseFloat(row?.ApprovedValue ?? row?.approvedValue ?? row?.ApprvValue ?? row?.apprvValue ?? 0) || 0;
          let approvedQty = approvedQtyRaw > 0 ? approvedQtyRaw : (price > 0 && approvedValRaw > 0 ? approvedValRaw / price : 0);
          if (srQty > 0 && approvedQty > srQty) approvedQty = srQty;
          if (isSEUser || isDosAdminMaterialReadonly) {
            return formatViewSrQty(approvedQty);
          }
          return `
            <input
              type="text"
              inputmode="numeric"
              pattern="[0-9]*"
              class="form-control form-control-sm approve-qty-input"
              data-key="${key}"
              data-srqty="${srQty}"
              value="${formatViewSrQty(approvedQty)}"
              style="max-width: 120px; text-align: right; background-color: #fff;"
            />
          `;
        },
        className: "text-end approve-qty-cell",
      },
      {
        title: "Cal. Approved Value",
        data: null,
        orderable: false,
        visible: showSrQtyValueApproveColumns,
        render: (data, type, row, meta) => {
          const srQty = parseFloat(row?.ComplaintQty ?? row?.complaintQty ?? row?.SRQuantity ?? row?.srQuantity ?? 0) || 0;
          const price = parseFloat(row?.Price ?? row?.price ?? 0) || 0;
          const approvedQtyRaw = parseFloat(row?.ApprovedQty ?? row?.approvedQty ?? row?.ApprovedQuantity ?? row?.approvedQuantity ?? row?.ApprvQuantity ?? row?.apprvQuantity ?? 0) || 0;
          const approvedValRaw = parseFloat(row?.ApprovedValue ?? row?.approvedValue ?? row?.ApprvValue ?? row?.apprvValue ?? 0) || 0;
          let approvedQty = approvedQtyRaw > 0 ? approvedQtyRaw : (price > 0 && approvedValRaw > 0 ? approvedValRaw / price : 0);
          if (srQty > 0 && approvedQty > srQty) approvedQty = srQty;
          const approvedVal = approvedValRaw > 0 ? approvedValRaw : approvedQty * price;
          return formatViewSrMoney(approvedVal);
        },
        className: "text-end approve-value-cell",
      },
      {
        title: "Action",
        data: null,
        orderable: false,
        render: (data, type, row, meta) => {
          if (type === "display") {
            // Closed SR: no Approve/Reject action at all
            if (isSrClosedStatus) {
              return "";
            }
            // SE: always disabled (no action)
            if (isSEUser || isHeaderActionRestrictedUserType) {
              return `
                <button
                  class="btn btn-sm approve-reject-btn"
                  data-product-key=""
                  data-action-disabled="true"
                  disabled
                  title="Disabled"
                  style="background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
                         border: none;
                         color: white;
                         padding: 8px 10px;
                         border-radius: 6px;
                         cursor: not-allowed;">
                  <i class="bi bi-check-circle-fill"></i>
                </button>
              `;
            }

            const key = getMaterialRowKey(row, meta.row);

            // Not current pending approver: show Action button disabled (do not hide column cell).
            if ((isSrActed && !canReopenRejectedSr) || isCurrentUserTheRejector) {
              return `
                <button
                  type="button"
                  class="btn btn-sm approve-reject-btn"
                  data-product-key="${key}"
                  data-action-disabled="true"
                  disabled
                  data-bs-toggle="tooltip"
                  data-bs-placement="top"
                  title="${
                    isCurrentUserTheRejector
                      ? "Disabled because you rejected this service request"
                      : "Enabled only for the current pending approval level"
                  }"
                  style="background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
                         border: none;
                         color: white;
                         padding: 8px 10px;
                         border-radius: 6px;
                         cursor: not-allowed;
                         opacity: 0.9;">
                  <i class="bi bi-check-circle-fill"></i>
                </button>
              `;
            }

            const approvedQtyRaw =
              parseFloat(
                row?.ApprovedQty ??
                  row?.approvedQty ??
                  row?.ApprovedQuantity ??
                  row?.approvedQuantity ??
                  row?.ApprvQuantity ??
                  row?.apprvQuantity ??
                  0
              ) || 0;
            // Keep existing flow and additionally enable row action once Approved Qty is entered.
            const isDisabled = approvedQtyRaw <= 0;
            const titleText = isDisabled ? "Enter Approved Qty to enable action" : "Approval pending";
            const disabledFlag = isDisabled ? "true" : "false";
            const btnBg = isDisabled
              ? "linear-gradient(135deg, #6c757d 0%, #495057 100%)"
              : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
            return `
              <button
                class="btn btn-sm approve-reject-btn"
                data-product-key="${key}"
                data-action-disabled="${disabledFlag}"
                ${isDisabled ? "disabled" : ""}
                data-bs-toggle="tooltip"
                data-bs-placement="top"
                title="${titleText}"
                style="background: ${btnBg}; 
                       border: none; 
                       color: white; 
                       padding: 8px 10px; 
                       border-radius: 6px;
                       cursor: ${isDisabled ? "not-allowed" : "pointer"};">
                <i class="bi bi-check-circle-fill"></i>
              </button>
            `;
          }
          return "";
        },
        className: "text-center",
        visible: !isSEUser && !isSrClosedStatus,
      });

      const getMaterialGridRoot = () =>
        $(productTableRef.current).closest(".dataTables_wrapper, .dt-container");

      const hideSourceMaterialTableFooterWhenScrollFoot = () => {
        if (!productTableRef.current) return;
        const $wrapper = getMaterialGridRoot();
        const $sourceTfoot = $(productTableRef.current).children("tfoot");
        if ($wrapper.find(".dt-scroll-footInner").length && $sourceTfoot.length) {
          $sourceTfoot.css("display", "none");
        } else if ($sourceTfoot.length) {
          $sourceTfoot.css("display", "");
        }
      };

      dataTableInstanceRef.current = $(productTableRef.current).DataTable({
        data: products,
        paging: true,
        searching: true,
        info: true,
        lengthChange: true,
        lengthMenu: [5, 10, 25, 50, 100],
        pageLength: 5,
        dom: "lfrtip",
        ordering: true,
        destroy: true,
        scrollX: true,
        autoWidth: false,
        fixedColumns: false,
        order: [[0, "asc"]],
        columns: baseColumns,
        headerCallback: function () {},
        initComplete: function (settings, json) {
          try {
            // Wrap dt-length and dt-search into a single top filter wrapper
            const wrapper = $(productTableRef.current).closest(
              ".dataTables_wrapper, .dt-container"
            );
            if (wrapper.length > 0) {
              const lengthEl = wrapper.find(".dt-length");
              const searchEl = wrapper.find(".dt-search");
              if (lengthEl.length > 0 || searchEl.length > 0) {
                let topWrap = wrapper.find(".dtFltrTop");
                if (topWrap.length === 0) {
                  topWrap = $("<div class='dtFltrTop d-flex flex-wrap'></div>");
                  wrapper.prepend(topWrap);
                }
                lengthEl.each(function () {
                  $(this).appendTo(topWrap);
                });
                searchEl.each(function () {
                  $(this).appendTo(topWrap);
                });
              }

              // Wrap dt-info and dt-paging into a single bottom filter wrapper
              const infoEl = wrapper.find(".dt-info");
              const pagingEl = wrapper.find(".dt-paging");
              if (infoEl.length > 0 || pagingEl.length > 0) {
                let bottomWrap = wrapper.find(".dtFltrBotm");
                if (bottomWrap.length === 0) {
                  bottomWrap = $(
                    "<div class='dtFltrBotm d-flex flex-wrap'></div>"
                  );
                  wrapper.append(bottomWrap);
                }
                infoEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
                pagingEl.each(function () {
                  $(this).appendTo(bottomWrap);
                });
              }
            }

            // Wrap dt-scroll with an inner wrapper and ensure outer wrapper has dtScrlWrpr
            const dtScrollDiv = $(productTableRef.current).closest(
              ".dt-scroll"
            );
            if (dtScrollDiv.length > 0) {
              if (!dtScrollDiv.parent().hasClass("dtScrlWrprInner")) {
                dtScrollDiv.wrap('<div class="dtScrlWrprInner"></div>');
              }
              const outer = dtScrollDiv.parent().parent();
              if (outer.length > 0) outer.addClass("dtScrlWrpr");
            } else {
              const container = $(productTableRef.current).closest(
                ".table-responsive"
              );
              if (container.length > 0) {
                const dtScroll = container.find(".dt-scroll");
                if (dtScroll.length > 0) {
                  dtScroll.each(function () {
                    const $this = $(this);
                    if (!$this.parent().hasClass("dtScrlWrprInner")) {
                      $this.wrap('<div class="dtScrlWrprInner"></div>');
                    }
                    const outer = $this.parent().parent();
                    if (outer.length > 0) outer.addClass("dtScrlWrpr");
                  });
                }
              }
            }

            // Initial column adjustment
            setTimeout(() => {
              if (dataTableInstanceRef.current) {
                dataTableInstanceRef.current.columns.adjust();
                // Sync scroll head width with body width after a slight delay to ensure DOM is updated
                setTimeout(() => {
                  syncScrollHeadWidth(productTableRef);
                }, 50);
              }
            }, 100);

            const applyMaterialRowState = () => {
              if (!productTableRef.current) return;
              const $table = $(productTableRef.current);
              $table.find("tbody tr").each(function () {
                const $row = $(this);
                const $controls = $row.find("input, select, button").not(".approve-reject-btn");
                if ((isSrActed && !canReopenRejectedSr) || isCurrentUserTheRejector) {
                  $row.removeClass("material-row-active").addClass("material-row-disabled");
                  $controls.prop("disabled", true);
                } else {
                  $row.removeClass("material-row-disabled").addClass("material-row-active");
                  $controls.prop("disabled", false);
                }
              });
            };
            applyMaterialRowState();

            // Attach click handler for product details icon
            $(productTableRef.current).off("click", ".product-details-icon");
            $(productTableRef.current).on(
              "click",
              ".product-details-icon",
              function (e) {
                e.preventDefault();
                e.stopPropagation();
                const productKey = $(this).data("product-key");
                const productData = productDataRef.current[productKey];
                if (productData) {
                  handleProductDetailsOpen(productData);
                }
              }
            );

            $(productTableRef.current).off("click", ".component-details-icon");
            $(productTableRef.current).on(
              "click",
              ".component-details-icon",
              function (e) {
                e.preventDefault();
                e.stopPropagation();
                const productKey = $(this).data("product-key");
                if (!productKey) return;
                setMaterialSectionError("");
                handleComponentDetailsOpen(productKey);
              }
            );

            $(productTableRef.current).off("click", ".approve-reject-btn");
            $(productTableRef.current).on(
              "click",
              ".approve-reject-btn",
              function (e) {
                e.preventDefault();
                e.stopPropagation();
                const isDisabled = $(e.currentTarget).attr("data-action-disabled") === "true";
                if (isDisabled) return;
                handleApproveRejectOpen();
              }
            );

            // Initialize Bootstrap tooltips
            setTimeout(() => {
              const tooltipTriggerList = [].slice.call(
                $(productTableRef.current).find('[data-bs-toggle="tooltip"]')
              );
              tooltipTriggerList.forEach(function (tooltipTriggerEl) {
                // Destroy existing tooltip if any
                const existingTooltip = window.bootstrap?.Tooltip?.getInstance(tooltipTriggerEl);
                if (existingTooltip) {
                  existingTooltip.dispose();
                }
                // Create new tooltip
                if (window.bootstrap?.Tooltip) {
                  new window.bootstrap.Tooltip(tooltipTriggerEl);
                }
              });
            }, 100);

            // Attach change handler for Approve Qty input (wrapper: scrollX body is outside source <table>)
            const $matGridRoot = getMaterialGridRoot();
            $matGridRoot.off("input.materialApproveQty", ".approve-qty-input");
            $matGridRoot.on(
              "input.materialApproveQty",
              ".approve-qty-input",
              function () {
                // While typing:
                // - allow only whole numbers (no decimals)
                // - hard cap: Approved Qty must never exceed SR Qty
                const $el = $(this);
                $el.removeClass("is-invalid");
                const sanitized = ($el.val() ?? "").toString().replace(/[^\d]/g, "");
                if ($el.val() !== sanitized) $el.val(sanitized);
                const row = dataTableInstanceRef.current.row($el.closest("tr"));
                const rowData = row.data();
                const srQty = parseFloat(rowData?.ComplaintQty ?? rowData?.complaintQty ?? rowData?.SRQuantity ?? rowData?.srQuantity ?? $el.data("srqty") ?? 0) || 0;
                let approvedQty = parseFloat($el.val()) || 0;
                if (srQty > 0 && approvedQty > srQty) {
                  approvedQty = srQty;
                  $el.val(Math.floor(approvedQty));
                  setMaterialSectionError(approveQtyLimitWarningMessage);
                } else {
                  clearApproveQtyLimitWarning();
                }

                // Keep DataTable row object in sync so footer totals update.
                const approvedQtyInt = Math.max(0, Math.floor(Number($el.val() || 0)));
                const price = parseFloat(rowData?.Price ?? rowData?.price ?? 0) || 0;
                const rawVal = approvedQtyInt * price;
                const roundedVal = formatViewSrMoney(rawVal);

                if (rowData) {
                  syncMaterialRowApproveFields(rowData, approvedQtyInt, rawVal);
                }
                const $actionBtn = $el.closest("tr").find(".approve-reject-btn");
                if ($actionBtn.length > 0) {
                  const isDisabled = approvedQtyInt <= 0;
                  $actionBtn.attr("data-action-disabled", isDisabled ? "true" : "false");
                  $actionBtn.prop("disabled", isDisabled);
                  $actionBtn.attr("title", isDisabled ? "Enter Approved Qty to enable action" : "Approval pending");
                  $actionBtn.css({
                    background: isDisabled
                      ? "linear-gradient(135deg, #6c757d 0%, #495057 100%)"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  });
                }
                syncApproveValueCellDisplay($el.closest("tr"), roundedVal);
                scheduleRecalcApproveFooterTotals();
              }
            );
            $matGridRoot.off("blur.materialApproveQty", ".approve-qty-input");
            $matGridRoot.on(
              "blur.materialApproveQty",
              ".approve-qty-input",
              function (e) {
                const key = $(this).data("key");
                let approvedQtyRaw = parseFloat($(this).val()) || 0;
                const row = dataTableInstanceRef.current.row($(this).closest("tr"));
                const rowData = row.data();
                const srQty = parseFloat(rowData?.ComplaintQty ?? rowData?.complaintQty ?? rowData?.SRQuantity ?? rowData?.srQuantity ?? 0) || 0;
                const price = parseFloat(rowData?.Price ?? rowData?.price ?? 0) || 0;
                let approvedQty = toViewSrQtyInt(approvedQtyRaw);
                const exceededSrQty = srQty > 0 && approvedQty > srQty;
                if (srQty > 0 && approvedQty > srQty) {
                  approvedQty = toViewSrQtyInt(srQty);
                  setMaterialSectionError(approveQtyLimitWarningMessage);
                } else {
                  clearApproveQtyLimitWarning();
                }
                $(this).val(formatViewSrQty(approvedQty));
                if (exceededSrQty) {
                  Swal.fire({
                    icon: "warning",
                    title: "Approved Qty adjusted",
                    text: approveQtyLimitWarningMessage,
                    confirmButtonColor: "#0b6bff",
                  });
                }

                const rawVal = approvedQty * price;
                const roundedVal = formatViewSrMoney(rawVal);
                // Ensure qty and derived value never exceed SR qty
                if (srQty > 0 && approvedQty > srQty) {
                  approvedQty = srQty;
                }
                syncApproveValueCellDisplay($(this).closest("tr"), roundedVal);
                if (rowData) {
                  syncMaterialRowApproveFields(rowData, approvedQty, rawVal);
                }
                const $actionBtn = $(this).closest("tr").find(".approve-reject-btn");
                if ($actionBtn.length > 0) {
                  const isDisabled = approvedQty <= 0;
                  $actionBtn.attr("data-action-disabled", isDisabled ? "true" : "false");
                  $actionBtn.prop("disabled", isDisabled);
                  $actionBtn.attr("title", isDisabled ? "Enter Approved Qty to enable action" : "Approval pending");
                  $actionBtn.css({
                    background: isDisabled
                      ? "linear-gradient(135deg, #6c757d 0%, #495057 100%)"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    cursor: isDisabled ? "not-allowed" : "pointer",
                  });
                }
                recalcApproveFooterTotals();
              }
            );

            materialApproveFooterLastRef.current = { qty: null, value: null };
            hideSourceMaterialTableFooterWhenScrollFoot();
            recalcApproveFooterTotals();
          } catch (e) {
            console.error("Error in DataTable initComplete wrapper:", e);
          }
        },
        drawCallback: function (settings) {
          const activeEl = document.activeElement;
          const restoreApproveQty =
            activeEl &&
            activeEl.classList &&
            activeEl.classList.contains("approve-qty-input")
              ? {
                  key: $(activeEl).data("key"),
                  val: $(activeEl).val(),
                  sel:
                    typeof activeEl.selectionStart === "number"
                      ? activeEl.selectionStart
                      : null,
                }
              : null;

          hideSourceMaterialTableFooterWhenScrollFoot();

          if (!isMaterialApproveFieldFocused()) {
            setTimeout(() => {
              if (!isMaterialApproveFieldFocused()) {
                syncScrollHeadWidth(productTableRef);
              }
            }, 10);
          }

          const $matRoot = getMaterialGridRoot();
          let $rows = $matRoot.find(".dt-scroll-body tbody tr");
          if (!$rows.length) {
            $rows = $(productTableRef.current).find("tbody tr");
          }
          $rows.each(function () {
            const $row = $(this);
            const $controls = $row.find("input, select, button").not(".approve-reject-btn");
            if ((isSrActed && !canReopenRejectedSr) || isCurrentUserTheRejector) {
              $row.removeClass("material-row-active").addClass("material-row-disabled");
              $controls.prop("disabled", true);
            } else {
              $row.removeClass("material-row-disabled").addClass("material-row-active");
              $controls.prop("disabled", false);
            }
          });

          $(productTableRef.current).off("click", ".product-details-icon");
          $(productTableRef.current).on(
            "click",
            ".product-details-icon",
            function (e) {
              e.preventDefault();
              e.stopPropagation();
              const productKey = $(this).data("product-key");
              const productData = productDataRef.current[productKey];
              if (productData) {
                handleProductDetailsOpen(productData);
              }
            }
          );

          $(productTableRef.current).off("click", ".component-details-icon");
          $(productTableRef.current).on(
            "click",
            ".component-details-icon",
            function (e) {
              e.preventDefault();
              e.stopPropagation();
              const productKey = $(this).data("product-key");
              if (!productKey) return;
              setMaterialSectionError("");
              handleComponentDetailsOpen(productKey);
            }
          );

          $(productTableRef.current).off("click", ".approve-reject-btn");
          $(productTableRef.current).on(
            "click",
            ".approve-reject-btn",
            function (e) {
              e.preventDefault();
              e.stopPropagation();
              const isDisabled = $(e.currentTarget).attr("data-action-disabled") === "true";
              if (isDisabled) return;
              handleApproveRejectOpen();
            }
          );

          // Re-initialize Bootstrap tooltips after redraw
          setTimeout(() => {
            const tooltipTriggerList = [].slice.call(
              $(productTableRef.current).find('[data-bs-toggle="tooltip"]')
            );
            tooltipTriggerList.forEach(function (tooltipTriggerEl) {
              // Destroy existing tooltip if any
              const existingTooltip = window.bootstrap?.Tooltip?.getInstance(tooltipTriggerEl);
              if (existingTooltip) {
                existingTooltip.dispose();
              }
              // Create new tooltip
              if (window.bootstrap?.Tooltip) {
                new window.bootstrap.Tooltip(tooltipTriggerEl);
              }
            });
          }, 100);

          if (restoreApproveQty && restoreApproveQty.key != null) {
            requestAnimationFrame(() => {
              const $scope = $matRoot.length ? $matRoot : $(productTableRef.current);
              const $inp = $scope.find(
                `.approve-qty-input[data-key="${restoreApproveQty.key}"]`
              );
              if ($inp.length) {
                $inp.val(restoreApproveQty.val);
                const el = $inp[0];
                el.focus();
                try {
                  const pos =
                    restoreApproveQty.sel ??
                    String(restoreApproveQty.val ?? "").length;
                  el.setSelectionRange(pos, pos);
                } catch (_) {
                  /* ignore */
                }
              }
            });
          }
        },
      });

      // Add ResizeObserver for this specific table with debouncing
      let resizeObserver = null;
      let resizeTimeout = null;
      if (window.ResizeObserver) {
        const tableContainer = $(productTableRef.current).closest(
          ".card-panel, .table-responsive"
        )[0];
        if (tableContainer) {
          resizeObserver = new ResizeObserver((entries) => {
            // Debounce the resize adjustments to prevent rapid firing
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              if (isMaterialApproveFieldFocused()) {
                return;
              }
              if (dataTableInstanceRef.current) {
                try {
                  // Use requestAnimationFrame to ensure smooth rendering
                  requestAnimationFrame(() => {
                    if (dataTableInstanceRef.current && !isMaterialApproveFieldFocused()) {
                      // columns.adjust() only — draw() recreates row inputs and steals focus while typing Approve Qty
                      dataTableInstanceRef.current.columns.adjust();
                      setTimeout(() => {
                        if (!isMaterialApproveFieldFocused()) {
                          syncScrollHeadWidth(productTableRef);
                        }
                      }, 50);
                    }
                  });
                } catch (e) {
                  // Silently handle errors - ResizeObserver errors are usually benign
                  if (!e.message.includes("ResizeObserver")) {
                    console.error("Error in ResizeObserver adjust:", e);
                  }
                }
              }
            }, 150); // 150ms debounce
          });
          resizeObserver.observe(tableContainer);
        }
      }

      // Store resizeObserver and timeout for cleanup
      if (productTableRef.current) {
        productTableRef.current._resizeObserver = resizeObserver;
        productTableRef.current._resizeTimeout = resizeTimeout;
      }
    }

    // Cleanup on unmount
    return () => {
      // Cleanup ResizeObserver and timeout
      if (productTableRef.current) {
        if (productTableRef.current._resizeTimeout) {
          clearTimeout(productTableRef.current._resizeTimeout);
          delete productTableRef.current._resizeTimeout;
        }
        if (productTableRef.current._resizeObserver) {
          productTableRef.current._resizeObserver.disconnect();
          delete productTableRef.current._resizeObserver;
        }
      }
      if (materialApproveFooterRecalcRafRef.current != null) {
        cancelAnimationFrame(materialApproveFooterRecalcRafRef.current);
        materialApproveFooterRecalcRafRef.current = null;
      }
      if (productTableRef.current) {
        $(productTableRef.current)
          .closest(".dataTables_wrapper, .dt-container")
          .off(".materialApproveQty .materialApproveValue");
      }
      if (dataTableInstanceRef.current) {
        try {
          dataTableInstanceRef.current.destroy();
          dataTableInstanceRef.current = null;
        } catch (err) {
          console.error("Error destroying DataTable:", err);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    srData?.products,
    srData?.journeyStatus,
    srData?.statusHistory,
    srData?.statusName,
    srData?.StatusName,
    childSrCodeFromQuery,
    hasPendingQueryForCurrentSR,
    isSrActed,
    isSrClosedStatus,
    canReopenRejectedSr,
    isCurrentUserTheRejector,
    isAssignFlowApprover,
  ]);

  const getStatusColor = (status) => {
    if (status === "Open") return "#1976d2"; // blue
    if (status === "Pending") return "#ff9800"; // orange
    if (status === "Closed") return "#4caf50"; // green
    return "#000000"; // default black
  };

  const getPriorityColor = (priority) => {
    if (priority === "High") return "#f44336"; // red
    if (priority === "Medium") return "#ff9800"; // orange
    if (priority === "Low") return "#4caf50"; // green
    return "#000000"; // default black
  };

  if (loading) {
    return (
      <div className="p-3">
        <p>Loading...</p>
      </div>
    );
  }

  if (!srData) {
    return (
      <div className="p-3">
        {loadError ? (
          <>
            <p className="text-danger mb-2">{loadError}</p>
            <button
              type="button"
              className="btn btn-outline-primary me-2"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </>
        ) : (
          <p>Service Request not found</p>
        )}
        <button
          className="btn btn-primary mt-2"
          onClick={navigateBackToComplaintsList}
          style={{
            background: "linear-gradient(135deg, #dc3545 0%, #ff0022 100%)",
            border: "none",
            color: "white",
            fontWeight: 500,
            letterSpacing: "0.5px",
            fontSize: "11px",
            boxShadow: "0 4px 15px rgba(245, 87, 108, 0.4)",
            borderRadius: "4px",
            textTransform: "uppercase",
            padding: "8px 24px",
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = "0 6px 20px rgba(245, 87, 108, 0.6)";
            e.currentTarget.style.transform = "translateY(-2px)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = "0 4px 15px rgba(245, 87, 108, 0.4)";
            e.currentTarget.style.transform = "translateY(0)";
          }}
        >
          <ArrowBackIcon style={{ fontSize: "16px" }} />
          Back to List
        </button>
      </div>
    );
  }

  return (
    <div className="viewServReqPg">
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap">
        <div className="d-flex align-items-center">
          <i
            className="bi bi-arrow-left"
            style={{
              fontSize: "24px",
              cursor: "pointer",
              marginRight: "15px",
              color: "#333333",
            }}
            onClick={navigateBackToComplaintsList}
            title="Back to View SR"
          ></i>
          <h1
            className="service-request-heading"
            style={{ color: "#333333", fontWeight: 600, margin: 0 }}
          >
            Service Request Details
          </h1>
        </div>
        <div className="d-flex gap-2 flex-wrap">
              {/* Response Query button:
                  visible only when a pending query is assigned to current user. */}
              {hasPendingQueryAssignedToCurrentUser && (
                <button
                  className="btn"
                  style={{
                    background: "linear-gradient(135deg, #ff8c00 0%, #ff6b00 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 500,
                    letterSpacing: "0.5px",
                    fontSize: "11px",
                    boxShadow: "0 4px 15px rgba(255, 140, 0, 0.4)",
                    borderRadius: "4px",
                    textTransform: "uppercase",
                    padding: "8px 24px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(255, 140, 0, 0.6)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "0 4px 15px rgba(255, 140, 0, 0.4)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                  onClick={() => {
                    const currentUserId = getCurrentUserId();
                    const pendingQueries = (queries || []).filter(
                      (q) =>
                        q.status === "Pending" ||
                        (q.status && q.status.toLowerCase() === "pending")
                    );

                    const pendingForCurrentUser = pendingQueries.filter(
                      (q) =>
                        (String(q.raisedToUserId ?? "").trim() === String(currentUserId ?? "").trim() ||
                          String(q.raisedToUserId ?? "").trim() === String(currentUserIdCode ?? "").trim())
                    );

                    if (pendingForCurrentUser.length > 0) {
                      handleResponseQueryOpen(pendingForCurrentUser[0]);
                    }
                  }}
                >
                  <ReplyIcon style={{ fontSize: "16px" }} />
                  Response Query
                </button>
              )}

              {/* Non-SE: Approve/Reject, Raise Query, Close */}
              {!isSeUserType() && (
                <>
                  {/* Combined Approve/Reject/Assign — hidden when Closed; WIP only for Plant QC Installation Assign */}
                  {!isHeaderActionRestrictedUserType &&
                    !isSrClosedStatus &&
                    (!isSrWipStatus || isPlantQcInstallationWipAssignFlow) && (
                    <button
                      type="button"
                      className="btn"
                      title={
                        canReopenRejectedSr
                          ? "Reopen this rejected service request (same details as approve; status set to Reopened)."
                          : isCurrentUserTheRejector
                          ? rejectorActionsDisabledMessage
                          : isSrActed
                          ? notCurrentPendingApproverMessage
                          : hasPendingQueryForCurrentSR
                            ? "Approve / Reject is disabled until all pending queries are responded for this SR."
                              : undefined
                      }
                      style={{
                        background: disableApproverHeaderActions
                          ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
                          : canReopenRejectedSr
                            ? "linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)"
                            : "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                        border: "none",
                        color: "white",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                        fontSize: "11px",
                        boxShadow: disableApproverHeaderActions
                          ? "0 4px 15px rgba(107, 114, 128, 0.35)"
                          : canReopenRejectedSr
                            ? "0 4px 15px rgba(13, 110, 253, 0.4)"
                            : "0 4px 15px rgba(40, 167, 69, 0.4)",
                        borderRadius: "4px",
                        textTransform: "uppercase",
                        padding: "8px 24px",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      disabled={disableApproverHeaderActions}
                      onMouseEnter={(e) => {
                        if (disableApproverHeaderActions) return;
                        e.currentTarget.style.boxShadow = canReopenRejectedSr
                          ? "0 6px 20px rgba(13, 110, 253, 0.55)"
                          : "0 6px 20px rgba(40, 167, 69, 0.6)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        if (disableApproverHeaderActions) return;
                        e.currentTarget.style.boxShadow = canReopenRejectedSr
                          ? "0 4px 15px rgba(13, 110, 253, 0.4)"
                          : "0 4px 15px rgba(40, 167, 69, 0.4)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                      onClick={handleApproveRejectOpen}
                    >
                      {canReopenRejectedSr ? (
                        <RestartAltIcon style={{ fontSize: "16px" }} />
                      ) : (
                        <CheckCircleIcon style={{ fontSize: "16px" }} />
                      )}
                      {canReopenRejectedSr
                        ? "Reopen"
                        : isAssignFlowApprover
                          ? "Assign"
                          : "Approve / Reject"}
                    </button>
                  )}
                  {showWipCloseButton && (
                    <button
                      type="button"
                      className="btn"
                      title={
                        closeDisabledDueToPendingQuery
                          ? "Close is disabled until all pending queries are responded for this SR."
                          : disableWipCloseHeader
                            ? !hasCloseFromSp
                              ? "Close will be available when this service request is ready for this action."
                              : "Close is not available for this view."
                            : "Close this service request when you are the next approver (remarks required)."
                      }
                      style={{
                        background: disableWipCloseHeader
                          ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
                          : "linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)",
                        border: "none",
                        color: "white",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                        fontSize: "11px",
                        boxShadow: disableWipCloseHeader
                          ? "0 4px 15px rgba(107, 114, 128, 0.35)"
                          : "0 4px 15px rgba(13, 110, 253, 0.4)",
                        borderRadius: "4px",
                        textTransform: "uppercase",
                        padding: "8px 24px",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      disabled={disableWipCloseHeader}
                      onMouseEnter={(e) => {
                        if (disableWipCloseHeader) return;
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(13, 110, 253, 0.55)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        if (disableWipCloseHeader) return;
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(13, 110, 253, 0.4)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                      onClick={handleCloseWipHeaderClick}
                    >
                      <HighlightOffIcon style={{ fontSize: "16px" }} />
                      {closeActionButtonLabel}
                    </button>
                  )}
                  {/* Raise Query Button — hidden when Closed */}
                  {!isHeaderActionRestrictedUserType && !isSrClosedStatus && (
                    <button
                      type="button"
                      className="btn"
                      title={
                        isCurrentUserTheRejector
                          ? rejectorActionsDisabledMessage
                          : hasCurrentUserDoneCloseActivity
                          ? "Raise Query is disabled because you have already closed this service request."
                          : hasPendingQueryForCurrentSR
                            ? "Raise Query is disabled until all pending queries are responded for this SR."
                            : (isSrActed &&
                                !canReopenRejectedSr &&
                                !allowRaiseQueryWhenWipClosePending)
                              ? notCurrentPendingApproverMessage
                              : undefined
                      }
                      style={{
                        background: disableRaiseQueryButton
                          ? "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)"
                          : "linear-gradient(135deg, #ff8c00 0%, #ff6b00 100%)",
                        border: "none",
                        color: "white",
                        fontWeight: 500,
                        letterSpacing: "0.5px",
                        fontSize: "11px",
                        boxShadow: disableRaiseQueryButton
                          ? "0 4px 15px rgba(107, 114, 128, 0.35)"
                          : "0 4px 15px rgba(255, 140, 0, 0.4)",
                        borderRadius: "4px",
                        textTransform: "uppercase",
                        padding: "8px 24px",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                      disabled={disableRaiseQueryButton}
                      onMouseEnter={(e) => {
                        if (disableRaiseQueryButton) return;
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(255, 140, 0, 0.6)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        if (disableRaiseQueryButton) return;
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(255, 140, 0, 0.4)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                      onClick={handleRaiseQueryOpen}
                    >
                      <QuestionAnswerIcon style={{ fontSize: "16px" }} />
                      Raise Query
                    </button>
                  )}
                </>
              )}
              {/* Print — All Print or Checklist Print */}
              <div style={{ position: "relative" }}>
              <button
                className="btn"
                style={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(102, 126, 234, 0.6)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(102, 126, 234, 0.4)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
                onClick={() => {
                  if (!printDownloading) setPrintMenuOpen((open) => !open);
                }}
                disabled={printDownloading}
              >
                <PrintIcon style={{ fontSize: "16px" }} />
                {printDownloading
                  ? `Downloading${typeof printProgress === "number" ? `… ${printProgress}%` : "…"}`
                  : "Print"}
                {!printDownloading && (
                  <KeyboardArrowDownIcon
                    style={{
                      fontSize: "18px",
                      marginLeft: "2px",
                      transform: printMenuOpen ? "rotate(180deg)" : "none",
                      transition: "transform 0.2s ease",
                    }}
                  />
                )}
              </button>
              {printMenuOpen && !printDownloading && (
                <>
                <div
                  onClick={() => setPrintMenuOpen(false)}
                  style={{ position: "fixed", inset: 0, zIndex: 29 }}
                />
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 8px)",
                    right: 0,
                    zIndex: 30,
                    width: "280px",
                    background: "#fff",
                    border: "1px solid #e6eaf2",
                    borderRadius: "10px",
                    boxShadow: "0 12px 32px rgba(26, 32, 44, 0.16)",
                    overflow: "hidden",
                    textAlign: "left",
                  }}
                >
                  <div
                    style={{
                      padding: "10px 14px 8px",
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.08em",
                      color: "#64748b",
                      textTransform: "uppercase",
                      borderBottom: "1px solid #eef2f7",
                    }}
                  >
                    Print options
                  </div>
                  {[
                    {
                      scope: "all",
                      label: "All Print",
                      hint: "Complete service request",
                      iconBg: "#eef2ff",
                      iconColor: "#4f46e5",
                    },
                    {
                      scope: "checklist",
                      label: "Checklist Print",
                      hint: "Basic information, invoice and checklist",
                      iconBg: "#ecfdf5",
                      iconColor: "#047857",
                    },
                  ].map((option) => (
                    <button
                      key={option.scope}
                      type="button"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "12px",
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        borderBottom: option.scope === "all" ? "1px solid #eef2f7" : "none",
                        background: "#fff",
                        color: "#0f172a",
                        padding: "12px 14px",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#f8fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "#fff";
                      }}
                      onClick={async () => {
                        setPrintMenuOpen(false);
                        try {
                          setPrintDownloading(true);
                          setPrintProgress(0);
                          const parentSrCode = (id || "").toString().trim();
                          const childSrCode =
                            (childSrCodeFromQuery || srData?.ChildSRNumber || srData?.childSRNumber || srData?.childSrCode || "")
                              .toString()
                              .trim();
                          const resp = await complaintAPI.getComplaintPrintPdf({
                            srCode: parentSrCode,
                            childSrCode,
                            scope: option.scope,
                            onProgress: ({ percent }) => {
                              if (typeof percent === "number") setPrintProgress(percent);
                            },
                          });
                          const html = await htmlFromPrintResponse(resp);
                          openTrustedPrintHtml(html);
                        } catch (e) {
                          console.error("Failed to open print view:", e);
                          const msg =
                            (e && (e.message || e.toString && e.toString())) ||
                            "Failed to open print view. Please try again.";
                          alert(msg);
                        } finally {
                          setPrintDownloading(false);
                          setPrintProgress(null);
                        }
                      }}
                    >
                      <span
                        style={{
                          width: "34px",
                          height: "34px",
                          borderRadius: "8px",
                          background: option.iconBg,
                          color: option.iconColor,
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {option.scope === "all" ? (
                          <DescriptionOutlinedIcon style={{ fontSize: "18px" }} />
                        ) : (
                          <FactCheckOutlinedIcon style={{ fontSize: "18px" }} />
                        )}
                      </span>
                      <span style={{ display: "flex", flexDirection: "column", gap: "2px" }}>
                        <span style={{ fontSize: "13px", fontWeight: 650, letterSpacing: "0.01em" }}>
                          {option.label}
                        </span>
                        <span style={{ fontSize: "11px", fontWeight: 500, color: "#64748b" }}>
                          {option.hint}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>
                </>
              )}
              </div>
              {printDownloading && (
                <div style={{ width: "220px", marginLeft: "12px" }}>
                  <div
                    style={{
                      height: "6px",
                      background: "rgba(255,255,255,0.35)",
                      borderRadius: "6px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "6px",
                        width: `${typeof printProgress === "number" ? printProgress : 10}%`,
                        background: "rgba(255,255,255,0.9)",
                        transition: "width 120ms linear",
                      }}
                    />
                  </div>
                </div>
              )}
        </div>
      </div>

      <div>
        {/* Basic Information - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseBasicInfo"
                aria-expanded="true"
                aria-controls="collapseBasicInfo"
              >
                <span>Basic Information</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div className="collapse show viewDetCardPnlOutr" id="collapseBasicInfo">
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0 }}>
                <div className="p-3">
                  <div className="row">
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">SR Number</div>
                        <div
                          className="detail-value"
                          style={{ fontWeight: 600 }}
                        >
                          {safeChild(srData?.childSRNumber ?? srData?.ChildSRNumber ?? srData?.srNumber)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Status</div>
                        <div
                          className="detail-value"
                          style={{
                            color: getStatusColor(srData.statusName),
                            fontWeight: 600,
                          }}
                        >
                          {safeChild(srData?.statusName)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">SR Category</div>
                        <div className="detail-value">
                          {srData.complaintTypeName || srData.srCategory || srData.ComplaintTypeName || srData.SRCategory || "—"}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Business Vertical</div>
                        <div className="detail-value">
                          {safeChild(srData?.productCategory ?? srData?.ProductCategory)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Created Date</div>
                        <div className="detail-value">
                          {formatDate(srData.createdDate || srData.createdOn)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Raised By</div>
                        <div className="detail-value">
                          {safeChild(srData?.createdByName ?? srData?.CreatedByName)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Assigned To</div>
                        <div className="detail-value">
                          {safeChild(srData?.assignedToName ?? srData?.AssignedToName)}
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Country</div>
                        {renderGeoDetailValue(
                          srData?.initiatorCountryName ??
                            srData?.InitiatorCountryName
                        )}
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Zone</div>
                        {renderGeoDetailValue(
                          srData?.initiatorZoneName ?? srData?.InitiatorZoneName
                        )}
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Region</div>
                        {renderGeoDetailValue(
                          srData?.initiatorRegionName ??
                            srData?.InitiatorRegionName,
                          { allCodeAsAll: true }
                        )}
                      </div>
                    </div>
                    <div className="col-12 col-md-3">
                      <div className="mb-2">
                        <div className="detail-label">Territory</div>
                        {renderGeoDetailValue(
                          srData?.initiatorTerritoryName ??
                            srData?.InitiatorTerritoryName,
                          { allCodeAsAll: true }
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* SR Journey Status - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseJourneyStatus"
                aria-expanded="true"
                aria-controls="collapseJourneyStatus"
              >
                <span>SR Journey Status</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse show viewDetCardPnlOutr"
            id="collapseJourneyStatus"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0, paddingBottom: "8px" }}>
                <div className="px-2 pt-0 pb-0">
                  {srData.journeyStatus &&
                    (() => {
                      // Find the index of the last approved step (SE is by default approved)
                      let lastApprovedIndex = -1;
                      for (
                        let i = srData.journeyStatus.length - 1;
                        i >= 0;
                        i--
                      ) {
                        const s = srData.journeyStatus[i];
                        const sAct = String(s?.status ?? s?.Activity ?? "")
                          .toUpperCase()
                          .trim();
                        const sRejected =
                          s?.rejected === true || sAct === "REJECTED";
                        const isApproved =
                          isInitiatorSEStep(s) ||
                          (!sRejected && (s.approved || isJourneyStepApprovedAction(s)));
                        if (isApproved) {
                          lastApprovedIndex = i;
                          break;
                        }
                      }

                      const journeyStepsForReopen = srData.journeyStatus;
                      const showPastRejection = computeShowJourneyPastRejection(
                        srData,
                        journeyStepsForReopen
                      );
                      let lastRejectedIndex = -1;
                      for (let ri = 0; ri < journeyStepsForReopen.length; ri++) {
                        const st = journeyStepsForReopen[ri];
                        const se = isInitiatorSEStep(st);
                        const stepRejected =
                          !se &&
                          (st.rejected === true ||
                            String(st.status ?? st.Activity ?? "")
                              .toUpperCase()
                              .trim() === "REJECTED");
                        if (stepRejected) lastRejectedIndex = ri;
                      }

                      // Dynamic expected SLA/TAT:
                      // Prefer current API values (`sla`/`tat`), fall back to configured values for the level.
                      const getExpectedSlaDays = (toStep) => {
                        const raw =
                          toStep?.sla ??
                          toStep?.SlaDays ??
                          toStep?.configSlaDays ??
                          toStep?.ConfigSlaDays ??
                          null;
                        const n = raw === null || raw === undefined || raw === "" ? NaN : Number(raw);
                        return Number.isFinite(n) && n > 0 ? n : null;
                      };

                      // Always use ConfigTATDays for expected TAT
                      const getExpectedTatDays = (toStep) => {
                        const raw =
                          toStep?.ConfigTATDays ??
                          toStep?.configTATDays ??
                          null;
                        const n = raw === null || raw === undefined || raw === "" ? NaN : Number(raw);
                        return Number.isFinite(n) ? n : null;
                      };

                      const getConfigTatLabel = (toStep) => {
                        const n = getExpectedTatDays(toStep);
                        return n == null ? "N/A" : String(n);
                      };

                      // Helper function to calculate actual days between two steps
                      const calculateActualDays = (fromStep, toStep) => {
                        if (!fromStep?.date || !toStep?.date) return null;
                        
                        try {
                          // Parse date strings - handles both "23 Jul 2024" and "2025-12-18" formats
                          const parseDate = (dateStr) => {
                            if (!dateStr) return null;
                            
                            // Try ISO format first (YYYY-MM-DD)
                            if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
                              return new Date(dateStr);
                            }
                            
                            // Try format like "23 Jul 2024"
                            const months = {
                              'Jan': 0, 'Feb': 1, 'Mar': 2, 'Apr': 3, 'May': 4, 'Jun': 5,
                              'Jul': 6, 'Aug': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Dec': 11
                            };
                            const parts = dateStr.trim().split(' ');
                            if (parts.length === 3) {
                              const day = parseInt(parts[0]);
                              const month = months[parts[1]];
                              const year = parseInt(parts[2]);
                              if (!isNaN(day) && month !== undefined && !isNaN(year)) {
                                return new Date(year, month, day);
                              }
                            }
                            return null;
                          };
                          
                          const fromDate = parseDate(fromStep.date);
                          const toDate = parseDate(toStep.date);
                          
                          if (!fromDate || !toDate || isNaN(fromDate.getTime()) || isNaN(toDate.getTime())) return null;
                          
                          const diffTime = Math.abs(toDate - fromDate);
                          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                          return diffDays;
                        } catch (error) {
                          return null;
                        }
                      };

                      // Helper: color for connector count based on expected TAT vs actual days.
                      const getDayCountColor = (_fromDesignation, _toDesignation, _fromStep, toStep) =>
                        isJourneyStepSlaBreached(toStep) ? "#dc3545" : "#007bff";

                      const buildJourneyTatBadgeTooltip = (fromStep, toStep, isSlaBreached) => {
                        if (!isSlaBreached) return undefined;
                        const tatLabel = getConfigTatLabel(toStep);
                        const dueLabel = getJourneyDueDateLabel(toStep, fromStep);
                        return dueLabel
                          ? `SLA Breached (Due date: ${dueLabel}). TAT limit: ${tatLabel} day(s)`
                          : `SLA Breached. TAT limit: ${tatLabel} day(s)`;
                      };

                      return (
                        <>
                        <JourneyIconTatTooltip tip={journeyIconTip} />
                        <div
                          className="journey-status-scroll-container"
                          style={{
                            overflowX: "auto",
                            overflowY: "visible",
                            width: "100%",
                            paddingBottom: 0,
                            WebkitOverflowScrolling: "touch",
                            scrollbarWidth: "thin",
                            scrollbarColor: "#667eea #f1f1f1",
                          }}
                          onScroll={() => setJourneyIconTip(null)}
                        >
                          <div
                          className="journey-status-wrapper"
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "flex-start",
                            position: "relative",
                            minWidth: "max-content",
                            padding: "10px 4px 4px",
                          }}
                        >
                          {srData.journeyStatus && srData.journeyStatus.map((step, index) => {
                            // SE is by default approved for display
                            const isSE = isInitiatorSEStep(step);
                            const norm = (v) => String(v ?? "").trim().toLowerCase();
                            const stepRoleNorm = norm(step?.designation);
                            const stepNameNorm = norm(step?.name);
                            const activityUpper = String(step.status ?? step.Activity ?? "")
                              .toUpperCase()
                              .trim();
                            const isCloseActivity =
                              activityUpper === "CLOSE" ||
                              activityUpper === "CLOSED" ||
                              activityUpper === "FINAL CLOSE" ||
                              activityUpper.includes("FINAL CLOSE");
                            // Close/Final Close should behave like Approved, but with a distinct color theme.
                            const isRejected =
                              isSE
                                ? false
                                : step.rejected === true ||
                                  activityUpper === "REJECTED";
                            // Rejected must not also count as approved (IsDone=1 on reject rows).
                            const isApproved =
                              isSE ||
                              (!isRejected &&
                                (step.approved ||
                                  isCloseActivity ||
                                  isJourneyStepApprovedAction(step)));
                            const approvalVariant =
                              isApproved && !isSE && !isRejected
                                ? resolveJourneyApprovalVariant(
                                    srData.journeyStatus,
                                    index,
                                    lastRejectedIndex
                                  )
                                : "first";
                            const approvalColors = getJourneyApprovalColors(approvalVariant);
                            // Check if this is the immediate next step after the last approved step
                            const isNextPendingAfterApproved =
                              lastApprovedIndex >= 0 &&
                              index === lastApprovedIndex + 1 &&
                              !isApproved &&
                              !isRejected;
                            // Same reopen+rejected rule as journey details grid: next step after reject looks like "active" pending (orange ring + readable labels)
                            const isNextPendingAfterRejectedReopen =
                              showPastRejection &&
                              lastRejectedIndex >= 0 &&
                              index === lastRejectedIndex + 1 &&
                              !isApproved &&
                              !isRejected;
                            const isNextHighlight =
                              isNextPendingAfterApproved || isNextPendingAfterRejectedReopen;
                            const hasQueryRaisedIndicator = Array.isArray(queries)
                              ? queries.some((q) => {
                                  const qStatus = norm(q?.status);
                                  if (qStatus !== "pending") return false;
                                  const byRole = norm(q?.raisedByRoleName);
                                  const byName = norm(q?.raisedByName);
                                  return (
                                    (stepRoleNorm && byRole && stepRoleNorm === byRole) ||
                                    (stepNameNorm && byName && stepNameNorm === byName)
                                  );
                                })
                              : false;
                            const connectorAfterRejectReopen =
                              showPastRejection && isRejected;
                            const connectorLineClass = isApproved
                              ? "journey-connector-completed"
                              : connectorAfterRejectReopen
                              ? "journey-connector-reopen"
                              : "journey-connector-pending";
                            const stepSlaBreached =
                              !isApproved &&
                              !isRejected &&
                              isJourneyStepSlaBreached(step);
                            const previousJourneyStep =
                              index > 0 ? srData.journeyStatus[index - 1] : null;
                            const iconTat = resolveJourneyIconTatDays(step);
                            const iconActualTat = resolveJourneyIconActualTat(step);
                            const iconSlaTip = stepSlaBreached
                              ? buildJourneyStepSlaTooltip(step, previousJourneyStep)
                              : "";

                            return (
                              <React.Fragment key={index}>
                                {/* Step Item */}
                                <div
                                  className="journey-step-item"
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    flex: "0 0 96px",
                                    position: "relative",
                                    zIndex: 1,
                                    minWidth: "72px",
                                    maxWidth: "96px",
                                    width: "96px",
                                  }}
                                >
                                  {/* Circle */}
                                  <div
                                    className="journey-step-icon-wrap"
                                    aria-label={buildJourneyStepIconAriaLabel(
                                      step,
                                      previousJourneyStep
                                    )}
                                    tabIndex={0}
                                    onMouseEnter={(e) =>
                                      setJourneyIconTip(
                                        readJourneyIconTipFromEl(
                                          e.currentTarget,
                                          iconTat,
                                          iconActualTat,
                                          iconSlaTip
                                        )
                                      )
                                    }
                                    onMouseLeave={() => setJourneyIconTip(null)}
                                    onFocus={(e) =>
                                      setJourneyIconTip(
                                        readJourneyIconTipFromEl(
                                          e.currentTarget,
                                          iconTat,
                                          iconActualTat,
                                          iconSlaTip
                                        )
                                      )
                                    }
                                    onBlur={() => setJourneyIconTip(null)}
                                  >
                                  <div
                                    className={`journey-step-circle ${
                                      isApproved && !isRejected
                                        ? "journey-step-approved"
                                        : ""
                                    } ${
                                      isRejected
                                        ? "journey-step-rejected"
                                        : ""
                                    } ${
                                      !isApproved && !isRejected
                                        ? "journey-step-pending"
                                        : ""
                                    } ${
                                      stepSlaBreached ? "journey-step-sla-breached" : ""
                                    }`}
                                    style={{
                                      width: isApproved || isRejected ? "26px" : "24px",
                                      height: isApproved || isRejected ? "26px" : "24px",
                                      borderRadius: "50%",
                                      background: isRejected
                                        ? "linear-gradient(135deg, #dc3545 0%, #c82333 100%)"
                                        : isApproved
                                        ? approvalColors.gradient
                                        : isNextHighlight
                                        ? "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)"
                                        : "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                                      border:
                                        isApproved || isRejected
                                          ? "none"
                                          : stepSlaBreached
                                          ? "2px solid #dc3545"
                                          : isNextPendingAfterRejectedReopen
                                          ? "2px solid #ff9800"
                                          : isNextPendingAfterApproved
                                          ? "2px solid #ff8c00"
                                          : "2px solid #dee2e6",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      marginBottom: "6px",
                                      position: "relative",
                                      zIndex: 2,
                                      boxShadow: isRejected
                                        ? "0 4px 12px rgba(220, 53, 69, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                                        : isApproved
                                        ? approvalColors.shadow
                                        : stepSlaBreached
                                        ? "0 4px 12px rgba(220, 53, 69, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                                        : isNextPendingAfterRejectedReopen
                                        ? "0 4px 12px rgba(255, 152, 0, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.35)"
                                        : isNextPendingAfterApproved
                                        ? "0 4px 12px rgba(255, 140, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                                        : "0 2px 8px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                                      transition: "all 0.3s ease",
                                      cursor: "help",
                                    }}
                                    onMouseEnter={(e) => {
                                      if (isApproved || isRejected) {
                                        e.currentTarget.style.transform = "scale(1.15)";
                                        if (isRejected) {
                                          e.currentTarget.style.boxShadow = "0 6px 20px rgba(220, 53, 69, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                                        } else if (isApproved) {
                                          e.currentTarget.style.boxShadow = approvalColors.shadowHover;
                                        }
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (isApproved || isRejected) {
                                        e.currentTarget.style.transform = "scale(1)";
                                        if (isRejected) {
                                          e.currentTarget.style.boxShadow = "0 4px 12px rgba(220, 53, 69, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                                        } else if (isApproved) {
                                          e.currentTarget.style.boxShadow = approvalColors.shadow;
                                        }
                                      }
                                    }}
                                  >
                                    {isRejected ? (
                                      <i
                                        className="bi bi-x-lg"
                                        style={{
                                          color: "white",
                                          fontSize: "18px",
                                          fontWeight: "bold",
                                          lineHeight: 1,
                                        }}
                                      ></i>
                                    ) : isApproved ? (
                                      <i
                                        className="bi bi-check-lg"
                                        style={{
                                          color: "white",
                                          fontSize: "18px",
                                          fontWeight: "bold",
                                          lineHeight: 1,
                                        }}
                                      ></i>
                                    ) : null}
                                    {!isApproved && !isRejected && hasQueryRaisedIndicator && (
                                      <span
                                        style={{
                                          color: "#ff8c00",
                                          fontSize: "16px",
                                          fontWeight: 700,
                                          lineHeight: 1,
                                        }}
                                      >
                                        ?
                                      </span>
                                    )}
                                  </div>
                                  </div>

                                  {/* Designation */}
                                  <div
                                    className="journey-step-designation"
                                    style={{
                                      fontSize: "10px",
                                      fontWeight: 600,
                                      color:
                                        isApproved || isRejected || isNextHighlight
                                          ? "#495057"
                                          : "#adb5bd",
                                      textAlign: "center",
                                      marginBottom: "2px",
                                      lineHeight: "1.2",
                                    }}
                                  >
                                    {safeChild(step?.designation)}
                                  </div>

                                  {/* Name */}
                                  {safeChild(step?.name) ? (
                                    <div
                                      className="journey-step-name"
                                      style={{
                                        fontSize: "10px",
                                        color:
                                          isApproved || isRejected || isNextHighlight
                                            ? "#6c757d"
                                            : "#ced4da",
                                        textAlign: "center",
                                        marginBottom: "2px",
                                        lineHeight: "1.2",
                                      }}
                                    >
                                      {safeChild(step?.name)}
                                    </div>
                                  ) : null}
                                </div>

                                {/* Connector Line */}
                                {index < srData.journeyStatus.length - 1 && (
                                  <div
                                    style={{
                                      flex: "0 0 56px",
                                      position: "relative",
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      minWidth: "56px",
                                      maxWidth: "56px",
                                    }}
                                  >
                                    {/* TAT (Days): always show next step's ConfigTATDays */}
                                    {srData.journeyStatus[index + 1] && (() => {
                                        const nextStep = srData.journeyStatus[index + 1];
                                        const isSlaBreached =
                                          isJourneyStepSlaBreached(nextStep);
                                        const dayCountColor = getDayCountColor(
                                          step.designation,
                                          nextStep.designation,
                                          step,
                                          nextStep
                                        );
                                        const badgeTooltip = buildJourneyTatBadgeTooltip(
                                          step,
                                          nextStep,
                                          isSlaBreached
                                        );
                                        return (
                                          <div
                                            className={`journey-day-count${
                                              isSlaBreached
                                                ? " journey-day-count-breached"
                                                : ""
                                            }`}
                                            title={badgeTooltip}
                                            style={{
                                              fontSize: "9px",
                                              color: "#ffffff",
                                              fontWeight: 700,
                                              marginBottom: "4px",
                                              textAlign: "center",
                                              zIndex: 1,
                                              cursor: isSlaBreached ? "help" : "default",
                                              background: isSlaBreached
                                                ? "linear-gradient(135deg, #dc3545 0%, #c82333 100%)"
                                                : dayCountColor === "#007bff"
                                                ? "linear-gradient(135deg, #007bff 0%, #0056b3 100%)"
                                                : isApproved
                                                ? "linear-gradient(135deg, #28a745 0%, #20c997 100%)"
                                                : connectorAfterRejectReopen
                                                ? "linear-gradient(135deg, #ab47bc 0%, #7b1fa2 100%)"
                                                : "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                                              padding: "3px 6px",
                                              borderRadius: "6px",
                                              boxShadow: isSlaBreached
                                                ? "0 2px 8px rgba(220, 53, 69, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                                                : "0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)",
                                              minWidth: "25px",
                                              display: "inline-block",
                                              transition: "all 0.3s ease",
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.transform = "scale(1.1)";
                                              e.currentTarget.style.boxShadow = isSlaBreached
                                                ? "0 4px 12px rgba(220, 53, 69, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.3)"
                                                : "0 4px 12px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.3)";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.transform = "scale(1)";
                                              e.currentTarget.style.boxShadow = isSlaBreached
                                                ? "0 2px 8px rgba(220, 53, 69, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.2)"
                                                : "0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)";
                                            }}
                                          >
                                            {getConfigTatLabel(nextStep)}
                                          </div>
                                        );
                                      })()}
                                    {/* Connector Line */}
                                    <div
                                      className={`journey-connector-line ${connectorLineClass}`}
                                    ></div>
                                  </div>
                                )}
                              </React.Fragment>
                            );
                          })}
                          </div>
                        </div>
                        </>
                      );
                    })()}
                </div>

                {/* SR Journey Status Details - Collapsible (Grid like Product Details) */}
                {srData.journeyStatus && srData.journeyStatus.length > 0 && (
                  <div className="viewDetwrpr mb-3 contPdTop">
                    <div className="viewDetCardPnlWrpr">
                      <div className="card-panel journey-status-details-panel" style={{ margin: "30px 0 0 0", backgroundColor: "#eee" }}>
                        <div
                          className="section-heading d-flex justify-content-between align-items-center"
                          style={{ cursor: "pointer", backgroundColor: "#eee", color: "#000" }}
                          data-bs-toggle="collapse"
                          data-bs-target="#collapseJourneyStatusDetails"
                          aria-expanded="false"
                          aria-controls="collapseJourneyStatusDetails"
                        >
                          <span>SR Journey Status Details</span>
                          <i className="bi bi-chevron-down" style={{ color: "#000" }}></i>
                        </div>
                      </div>
                    </div>
                    <div
                      className="collapse viewDetCardPnlOutr"
                      id="collapseJourneyStatusDetails"
                    >
                      {/* Ensure DataTables scroll header matches body width on View Approver Service Request page */}
                      <style>
                        {`
                          #collapseJourneyStatusDetails .dt-scroll-head .dt-scroll-headInner {
                            width: 100% !important;
                            min-width: 100% !important;
                          }
                          #collapseJourneyStatusDetails .dt-scroll-head .dt-scroll-headInner table {
                            width: 100% !important;
                            min-width: 100% !important;
                          }
                          #collapseJourneyStatusDetails .journey-comments-cell {
                            max-width: 350px;
                          }
                           #collapseJourneyStatusDetails .journey-role-cell {
                            min-width: 170px;
                            }
                          #collapseJourneyStatusDetails .journey-role-text {
                              display: inline-block;
                              max-width: 100%;
                              white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            vertical-align: bottom;
                            }
                          #collapseJourneyStatusDetails .journey-comments-text {
                            display: inline-block;
                            max-width: 100%;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                            vertical-align: bottom;
                          }
                        `}
                      </style>
                      <div className="viewDetCardPnlWrpr">
                        <div className="card-panel" style={{ paddingTop: 0 }}>
                          <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                            <div className="table-responsive">
                              <table
                                ref={journeyStatusTableRef}
                                className="table table-striped table-bordered complaint-table"
                                style={{ width: "100%" }}
                              >
                                <thead>
                                  <tr>
                                    <th style={{ display: "none" }}></th>
                                    <th className="text-start">Role</th>
                                    <th className="text-start">Action Taken By</th>
                                    <th className="text-start">Date Of Action</th>
                                    <th className="text-start">Status</th>
                                    <th style={{ display: "none" }}>SLA</th>
                                    <th className="text-center">TAT</th>
                                    <th className="text-center">Actual TAT</th>
                                    <th className="text-center">Queries</th>
                                    <th className="text-end">SR QTY</th>
                                    <th
                                      className="text-end"
                                      style={{
                                        display: businessVerticalRequiresComponentDetails
                                          ? "none"
                                          : undefined,
                                      }}
                                    >
                                      SR VALUE
                                    </th>
                                    <th className="text-end">Approved Qty</th>
                                    <th
                                      className="text-end"
                                      style={{
                                        display: businessVerticalRequiresComponentDetails
                                          ? "none"
                                          : undefined,
                                      }}
                                    >
                                      Cal. Approved Value
                                    </th>
                                    <th className="text-end">Final approved Val</th>
                                    <th className="text-start">Comments</th>
                                  </tr>
                                </thead>
                                <tbody></tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>

        {/* Invoice Details - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseInvoiceDetails"
                aria-expanded="false"
                aria-controls="collapseInvoiceDetails"
              >
                <span>Invoice Details</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse viewDetCardPnlOutr"
            id="collapseInvoiceDetails"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0 }}>
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Invoice Number</div>
                        <div className="detail-value">
                          {srData.invoiceNumber || srData.invoiceNo || "N/A"}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Internal Reference No.</div>
                        <div className="detail-value">
                          {srData.internalReferenceNo || "N/A"}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">SAP Order</div>
                        <div className="detail-value">
                          {srData.orderNo || "N/A"}
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">ODN Number</div>
                        <div className="detail-value">
                          {srData.odnNumber || "N/A"}
                        </div>
                      </div>
                    </div>

                    {srData.factoryIntentNo && String(srData.factoryIntentNo).trim() !== "" && (
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Factory Intent No.</div>
                          <div className="detail-value">
                            {srData.factoryIntentNo}
                          </div>
                        </div>
                      </div>
                    )}

                    {srData.factoryIntentDate && String(srData.factoryIntentDate).trim() !== "" && (
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Factory Intent Date</div>
                          <div className="detail-value">
                            {formatDate(srData.factoryIntentDate)}
                          </div>
                        </div>
                      </div>
                    )}

                    {srData.settlementType && String(srData.settlementType).trim() !== "" && (
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Settlement Type</div>
                          <div className="detail-value">
                            {srData.settlementType}
                          </div>
                        </div>
                      </div>
                    )}

                    {srData.physicalVisitRequired &&
                      String(srData.physicalVisitRequired).trim() !== "" && (
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">
                            Physical visit required by technical person?
                          </div>
                          <div className="detail-value">
                            {srData.physicalVisitRequired}
                          </div>
                        </div>
                      </div>
                    )}

                    {srData.siteVisitTraveler &&
                      String(srData.siteVisitTraveler).trim() !== "" && (
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Who would be traveling to the site?</div>
                          <div className="detail-value">
                            {srData.siteVisitTraveler}
                          </div>
                        </div>
                      </div>
                    )}

                    {srData.technicalVisitType &&
                      String(srData.technicalVisitType).trim() !== "" && (
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">
                            Local visit or outstation visit?
                          </div>
                          <div className="detail-value">
                            {srData.technicalVisitType}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* STO/CN Details - View Only (SRN / Credit Note / Replacement) */}
                    {(() => {
                      const rows = Array.isArray(srData?.products) ? srData.products : [];
                      const hasAnyStoCn = rows.some((r) => String(r?.StoCnValue ?? r?.stoCnValue ?? "").trim() !== "");
                      if (!hasAnyStoCn) return null;

                      const firstWithStoCn =
                        rows.find((r) => String(r?.StoCnValue ?? r?.stoCnValue ?? "").trim() !== "") ||
                        rows[0] ||
                        null;
                      if (!firstWithStoCn) return null;
                      const item = firstWithStoCn;
                      let stoCnValue = String(item?.StoCnValue ?? item?.stoCnValue ?? "").trim();
                      if (stoCnValue === "STO") stoCnValue = "SRN";
                      if (stoCnValue === "CN") {
                        const cn = String(item?.CnType ?? item?.cnType ?? "").trim();
                        if (cn === "Credit Note") stoCnValue = "Credit Note";
                        else if (cn === "Replacement" || cn === "Replacement & Returnable") {
                          stoCnValue = "Replacement";
                        }
                      }
                      const isReturnable = Boolean(item?.IsReturnable ?? item?.isReturnable);
                      const isNonReturnable = Boolean(item?.IsNonReturnable ?? item?.isNonReturnable);
                      const referenceNo = String(
                        item?.ReferenceNo ??
                          item?.referenceNo ??
                          (stoCnValue === "SRN"
                            ? item?.SapIndentOrderNo ?? item?.sapIndentOrderNo
                            : item?.CreditNoteNumber ?? item?.creditNoteNumber) ??
                          ""
                      );
                      const amount = String(item?.Amount ?? item?.amount ?? "");
                      const qty = String(item?.Qty ?? item?.qty ?? "");
                      const srnOrderNo = String(item?.SrnOrderNo ?? item?.srnOrderNo ?? "");
                      const reBillOrderNo = String(
                        item?.ReBillOrderNo ?? item?.reBillOrderNo ?? item?.NewBillingNo ?? item?.newBillingNo ?? ""
                      );
                      const finalClosureReferenceNo = String(
                        item?.FinalClosureReferenceNo ?? item?.finalClosureReferenceNo ?? ""
                      );
                      const finalClosureAmount = String(
                        item?.FinalClosureAmount ?? item?.finalClosureAmount ?? ""
                      );
                      const finalClosureQty = String(
                        item?.FinalClosureQty ?? item?.finalClosureQty ?? ""
                      );

                      return (
                        <div className="col-12 mt-2">
                          <div
                            style={{
                              marginBottom: 0,
                              padding: "16px",
                              backgroundColor: "#f8fafc",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "14px",
                                fontWeight: 700,
                                color: "#334155",
                                marginBottom: "12px",
                              }}
                            >
                              STO/CN Details
                            </div>
                            <div
                              style={{
                                background: "#ffffff",
                                border: "1px solid #e5e7eb",
                                borderRadius: "10px",
                                padding: "14px",
                              }}
                            >
                              <div className="row g-3">
                                <div className="col-md-3">
                                  <label className="form-label" style={{ fontWeight: 600 }}>
                                    STO/CN Details
                                  </label>
                                  <select className="form-select" value={stoCnValue} disabled>
                                    <option value="">Select</option>
                                    <option value="SRN">SRN</option>
                                    <option value="Credit Note">Credit Note</option>
                                    <option value="Replacement">Replacement</option>
                                  </select>
                                </div>
                                {(stoCnValue === "SRN" || stoCnValue === "Credit Note") && (
                                  <>
                                    <div className="col-md-3">
                                      <label className="form-label" style={{ fontWeight: 600 }}>
                                        Reference No.
                                      </label>
                                      <input type="text" className="form-control" value={referenceNo} disabled />
                                    </div>
                                    <div className="col-md-3">
                                      <label className="form-label" style={{ fontWeight: 600 }}>
                                        Quantity (Qty)
                                      </label>
                                      <input type="text" className="form-control text-end" value={qty} disabled />
                                    </div>
                                    <div className="col-md-3">
                                      <label className="form-label" style={{ fontWeight: 600 }}>
                                        Approval Amount
                                      </label>
                                      <input type="text" className="form-control text-end" value={amount} disabled />
                                    </div>
                                  </>
                                )}
                                {stoCnValue === "Replacement" && (
                                  <>
                                    <div className="col-md-3">
                                      <label className="form-label" style={{ fontWeight: 600 }}>
                                        Replacement Type
                                      </label>
                                      <div className="d-flex gap-4 flex-wrap" style={{ marginTop: 8 }}>
                                        <div className="form-check form-check-inline">
                                          <input
                                            type="radio"
                                            className="form-check-input"
                                            checked={isReturnable}
                                            disabled
                                            readOnly
                                          />
                                          <label className="form-check-label">Returnable</label>
                                        </div>
                                        <div className="form-check form-check-inline">
                                          <input
                                            type="radio"
                                            className="form-check-input"
                                            checked={isNonReturnable}
                                            disabled
                                            readOnly
                                          />
                                          <label className="form-check-label">Non-Returnable</label>
                                        </div>
                                      </div>
                                    </div>
                                    {isReturnable && (
                                      <div className="col-md-3">
                                        <label className="form-label" style={{ fontWeight: 600 }}>
                                          SRN Order No.
                                        </label>
                                        <input type="text" className="form-control" value={srnOrderNo} disabled />
                                      </div>
                                    )}
                                    <div className="col-md-3">
                                      <label className="form-label" style={{ fontWeight: 600 }}>
                                        Re-Bill Order No.
                                      </label>
                                      <input type="text" className="form-control" value={reBillOrderNo} disabled />
                                    </div>
                                    <div className="col-md-3">
                                      <label className="form-label" style={{ fontWeight: 600 }}>
                                        Quantity (Qty)
                                      </label>
                                      <input type="text" className="form-control text-end" value={qty} disabled />
                                    </div>
                                    <div className="col-md-3">
                                      <label className="form-label" style={{ fontWeight: 600 }}>
                                        Approval Amount
                                      </label>
                                      <input type="text" className="form-control text-end" value={amount} disabled />
                                    </div>
                                  </>
                                )}
                                {(finalClosureReferenceNo || finalClosureAmount || finalClosureQty) && (
                                  <>
                                    <div className="col-md-3">
                                      <label className="form-label" style={{ fontWeight: 600 }}>
                                        Final Closure Reference No.
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={finalClosureReferenceNo}
                                        disabled
                                      />
                                    </div>
                                    <div className="col-md-3">
                                      <label className="form-label" style={{ fontWeight: 600 }}>
                                        Final Closure Quantity (Qty)
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control text-end"
                                        value={finalClosureQty}
                                        disabled
                                      />
                                    </div>
                                    <div className="col-md-3">
                                      <label className="form-label" style={{ fontWeight: 600 }}>
                                        Final Closure Amount
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control text-end"
                                        value={finalClosureAmount}
                                        disabled
                                      />
                                    </div>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Invoice Copy Upload - View Only */}
                    <div className="col-12 mt-3">
                      <div className="upload-card">
                        {srData.invoiceFiles && srData.invoiceFiles.length > 0 ? (
<UploadedFileList
                            files={srData.invoiceFiles}
                            noTopMargin
                            onOpen={(fileItem) => {
                              if (fileItem.url) window.open(fileItem.url, "_blank");
                            }}
                          />
                        ) : (
                          <div className="upload-helper" style={{ color: "#666", fontStyle: "italic" }}>
                            No invoice files uploaded
                        </div>
                        )}
                      </div>
                        </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Previous SRs - always remain visible (like Query History); exclude current SR from list */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapsePreviousComplaints"
                aria-expanded="false"
                aria-controls="collapsePreviousComplaints"
              >
                <span>Previous SRs with same Invoice</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse viewDetCardPnlOutr"
            id="collapsePreviousComplaints"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 15 }}>
                {previousComplaintsCount <= 0 ? (
                  <div className="text-muted" style={{ padding: "8px 0" }}>
                    No previous SRs found.
                  </div>
                ) : (
                  <>
                    <div
                      className="section-heading previous-complaints-heading"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "10px",
                      }}
                    >
                      <span
                        style={{
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                          WebkitBackgroundClip: "text",
                          WebkitTextFillColor: "transparent",
                          backgroundClip: "text",
                          fontWeight: 600,
                          fontSize: "15px",
                        }}
                      >
                        {previousComplaintsCount == 1
                          ? "Previous SR(s) exist for this invoice. Please review before proceeding"
                          : `Multiple previous SRs exist for this invoice (Total: ${previousComplaintsCount}). Please review before proceeding`}
                      </span>
                      {previousComplaintsCount > 1 && (
                        <button
                          type="button"
                          onClick={() =>
                            setShowPreviousComplaintsTable(
                              !showPreviousComplaintsTable
                            )
                          }
                          style={{
                            background: showPreviousComplaintsTable
                              ? "linear-gradient(135deg, #4caf50 0%, #45a049 100%)"
                              : "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                            border: "none",
                            color: "white",
                            fontWeight: 500,
                            fontSize: "14px",
                            borderRadius: "4px",
                            padding: "6px 16px",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            transition: "all 0.3s ease",
                            boxShadow: "0 2px 8px rgba(0, 123, 255, 0.3)",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = "translateY(-2px)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 12px rgba(0, 123, 255, 0.5)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 2px 8px rgba(0, 123, 255, 0.3)";
                          }}
                        >
                          <TableChartIcon fontSize="small" />
                          {showPreviousComplaintsTable
                            ? "Hide SRs"
                            : "View All SRs"}
                        </button>
                      )}
                    </div>
                    <div
                      style={{
                        maxWidth: "100%",
                        overflowX: "auto",
                        display:
                          previousComplaintsCount === 1 ||
                          showPreviousComplaintsTable
                            ? "block"
                            : "none",
                      }}
                    >
                      <div className="table-responsive">
                        <table
                          ref={historyTableRef}
                          className="table table-striped table-bordered complaint-table srTablewrpr"
                          style={{ width: "100%" }}
                        >
                          <thead>
                            <tr>
                              <th className="text-center">SR No</th>
                              <th className="text-center">SR Date</th>
                              <th className="text-start">Product(s) involved</th>
                              <th className="text-end">SR QTY</th>
                              <th className="text-end">SR VALUE</th>
                              <th className="text-center">Resolution status</th>
                              <th className="text-center">Action</th>
                            </tr>
                          </thead>
                          <tbody></tbody>
                        </table>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Details - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseCustomerDetails"
                aria-expanded="false"
                aria-controls="collapseCustomerDetails"
              >
                <span>Customer Details</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse viewDetCardPnlOutr"
            id="collapseCustomerDetails"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0 }}>
                <div className="p-3">
                  <div className="row g-3">
                    {/* Customer Type - View Only (match /raise-complaint look) */}
                    <div className="col-12 mb-4">
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          flexWrap: "wrap",
                        }}
                      >
                        <div
                          className="detail-label"
                          style={{
                            margin: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#333",
                            whiteSpace: "nowrap",
                          }}
                        >
                          Customer Type <span className="text-danger">*</span>
                        </div>

                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "default",
                            margin: 0,
                            padding: "4px 8px",
                            backgroundColor:
                              (srData.addCustomerType || srData.customerType) === "B2B"
                                ? "#e3f2fd"
                                : "transparent",
                            borderRadius: "6px",
                            border:
                              (srData.addCustomerType || srData.customerType) === "B2B"
                                ? "1px solid #0b6bff"
                                : "1px solid #dee2e6",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <input
                            type="radio"
                            disabled
                            checked={
                              (srData.addCustomerType || srData.customerType) === "B2B"
                            }
                            readOnly
                            style={{
                              marginRight: "8px",
                              width: "14px",
                              height: "14px",
                              accentColor: "#0b6bff",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight:
                                (srData.addCustomerType || srData.customerType) === "B2B"
                                  ? "600"
                                  : "500",
                              color:
                                (srData.addCustomerType || srData.customerType) === "B2B"
                                  ? "#0b6bff"
                                  : "#666",
                            }}
                          >
                            <strong>Channel Partner</strong>
                          </span>
                        </label>

                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            cursor: "default",
                            margin: 0,
                            padding: "4px 8px",
                            backgroundColor:
                              (srData.addCustomerType || srData.customerType) === "B2C"
                                ? "#e3f2fd"
                                : "transparent",
                            borderRadius: "6px",
                            border:
                              (srData.addCustomerType || srData.customerType) === "B2C"
                                ? "1px solid #0b6bff"
                                : "1px solid #dee2e6",
                            transition: "all 0.2s ease",
                          }}
                        >
                          <input
                            type="radio"
                            disabled
                            checked={
                              (srData.addCustomerType || srData.customerType) === "B2C"
                            }
                            readOnly
                            style={{
                              marginRight: "8px",
                              width: "14px",
                              height: "14px",
                              accentColor: "#0b6bff",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "13px",
                              fontWeight:
                                (srData.addCustomerType || srData.customerType) === "B2C"
                                  ? "600"
                                  : "500",
                              color:
                                (srData.addCustomerType || srData.customerType) === "B2C"
                                  ? "#0b6bff"
                                  : "#666",
                            }}
                          >
                            <strong>End Consumer</strong>
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer Name</div>
                        <div className="detail-value">
                          {srData.customerName || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer Contact Person</div>
                        <div className="detail-value">
                          {srData.customerContactPerson || 
                           srData.representativeName || 
                           "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer Address</div>
                        <div className="detail-value">
                          {srData.customerAddress || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer City</div>
                        <div className="detail-value">
                          {srData.customerCity || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer State</div>
                        <div className="detail-value">
                          {srData.customerState || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Customer Postal Code</div>
                        <div className="detail-value">
                          {srData.customerPinCode || 
                           srData.customerPostalCode || 
                           "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Mobile Number</div>
                        <div className="detail-value">
                          {srData.customerMobile || 
                           srData.customerMobileNo || 
                           "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Phone Number</div>
                        <div className="detail-value">
                          {srData.customerPhone || "N/A"}
                        </div>
                      </div>
                    </div>

                    {(srData.addCustomerType || srData.customerType) === "B2B" && (
                      <div className="col-lg-6 col-md-12">
                        <div className="mb-2">
                          <div className="detail-label">Dealer/Distributor</div>
                          <div className="detail-value">
                            {srData.dealerDistributor ||
                              srData.DealerDistributor ||
                              "N/A"}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* View End Consumer Button */}
                    <div className="col-12 mt-3">
                      <button
                        type="button"
                        className="btn btn-primary endCustBtn"
                        onClick={() => setShowEndCustomer(!showEndCustomer)}
                        style={{
                          background: "linear-gradient(135deg, #2a9dff 0%, #0b6bff 100%)",
                          borderColor: "transparent",
                          color: "#ffffff",
                          boxShadow: "0 8px 16px rgba(11, 107, 255, 0.22)",
                          padding: "9px 18px",
                          borderRadius: "8px",
                          fontWeight: 700,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "6px",
                          transition: "all 0.2s ease-in-out",
                          cursor: "pointer",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "translateY(-1px)";
                          e.target.style.boxShadow = "0 10px 18px rgba(11, 107, 255, 0.28)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "0 8px 16px rgba(11, 107, 255, 0.22)";
                        }}
                      >
                        {showEndCustomer ? (
                          <>
                            <CloseIcon style={{ fontSize: "16px" }} />
                            HIDE END CONSUMER
                          </>
                        ) : (
                          <>
                            <AddCircleOutlineIcon style={{ fontSize: "16px" }} />
                            VIEW END CONSUMER
                          </>
                        )}
                      </button>
                    </div>

                    {/* End Consumer Details Section */}
                    {showEndCustomer && (
                      <div className="col-12 mt-3">
                        <div
                          className="card"
                          style={{
                            border: "1px solid #dee2e6",
                            borderRadius: "8px",
                          }}
                        >
                          <div
                            className="card-header"
                            style={{
                              backgroundColor: "#f8f9fa",
                              padding: "12px 20px",
                            }}
                          >
                            <h6
                              className="mb-0"
                              style={{ fontSize: "16px", fontWeight: "600" }}
                            >
                              End Consumer Details
                            </h6>
                          </div>
                          <div
                            className="card-body"
                            style={{ padding: "20px" }}
                          >
                            <div className="row g-3">
                              {/* Helper function to get end consumer field value */}
                              {(() => {
                                const endCustomer = srData?.endCustomer || {};
                                const getName = () =>
                                  srData?.consumerName ||
                                  srData?.ConsumerName ||
                                  srData?.endCustomerName ||
                                  srData?.endConsumerName ||
                                  endCustomer?.name ||
                                  "";
                                const getAddress = () =>
                                  srData?.consumerAddress ||
                                  srData?.ConsumerAddress ||
                                  srData?.endCustomerAddress ||
                                  srData?.endConsumerAddress ||
                                  endCustomer?.address ||
                                  "";
                                const getCountry = () =>
                                  srData?.consumerCountryName ||
                                  srData?.ConsumerCountryName ||
                                  srData?.endCustomerCountry ||
                                  srData?.endConsumerCountry ||
                                  endCustomer?.country ||
                                  endCustomer?.countryName ||
                                  "";
                                const getZone = () =>
                                  srData?.consumerZoneName ||
                                  srData?.ConsumerZoneName ||
                                  srData?.endCustomerZone ||
                                  srData?.endConsumerZone ||
                                  endCustomer?.zone ||
                                  "";
                                const getProvince = () =>
                                  srData?.endCustomerProvince ||
                                  srData?.endConsumerProvince ||
                                  endCustomer?.province ||
                                  "";
                                const getZoneProvince = () => {
                                  const zone = getZone();
                                  const province = getProvince();
                                  if (zone && province) return `${zone} / ${province}`;
                                  return zone || province || "";
                                };
                                const getState = () =>
                                  srData?.consumerTerritoryName ||
                                  srData?.ConsumerTerritoryName ||
                                  srData?.endCustomerState ||
                                  srData?.endConsumerState ||
                                  endCustomer?.state ||
                                  "";
                                const getCity = () =>
                                  srData?.endCustomerCity ||
                                  srData?.endConsumerCity ||
                                  endCustomer?.city ||
                                  "";
                                const getStateCity = () => {
                                  const state = getState();
                                  const city = getCity();
                                  if (state && city) return `${state} / ${city}`;
                                  return state || city || "";
                                };
                                const getPinCode = () =>
                                  srData?.consumerPostalCode ||
                                  srData?.ConsumerPostalCode ||
                                  srData?.endCustomerPinCode ||
                                  srData?.endConsumerPinCode ||
                                  srData?.endCustomerPostalCode ||
                                  srData?.endConsumerPostalCode ||
                                  endCustomer?.pinCode ||
                                  endCustomer?.postalCode ||
                                  "";
                                const getMobileNumber = () =>
                                  srData?.consumerContactNumber ||
                                  srData?.ConsumerContactNumber ||
                                  srData?.endCustomerMobileNumber ||
                                  srData?.endConsumerMobileNumber ||
                                  srData?.endCustomerContactNumber ||
                                  srData?.endConsumerContactNumber ||
                                  endCustomer?.mobileNumber ||
                                  endCustomer?.contactNumber ||
                                  "";

                                const consumerName = getName();
                                const consumerAddress = getAddress();
                                const country = getCountry();
                                const zoneProvince = getZoneProvince();
                                const stateCity = getStateCity();
                                const postalCode = getPinCode();
                                const contactNumber = getMobileNumber();

                                return (
                                  <>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        Consumer Name
                                        </div>
                                        <div className="detail-value">
                                          {consumerName || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        Consumer Address
                                        </div>
                                        <div className="detail-value">
                                          {consumerAddress || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">Country</div>
                                        <div className="detail-value">
                                          {country || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        Zone/Province
                                        </div>
                                        <div className="detail-value">
                                          {zoneProvince || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        State/City
                                        </div>
                                        <div className="detail-value">
                                          {stateCity || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        Postal Code
                                        </div>
                                        <div className="detail-value">
                                          {postalCode || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                        Contact Number
                                    </div>
                                        <div className="detail-value">
                                          {contactNumber || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">Consumer Country Name</div>
                                        <div className="detail-value">
                                          {srData.consumerCountryName || srData.ConsumerCountryName || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">Consumer Zone Name</div>
                                        <div className="detail-value">
                                          {srData.consumerZoneName || srData.ConsumerZoneName || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">Consumer Territory Name</div>
                                        <div className="detail-value">
                                          {srData.consumerTerritoryName || srData.ConsumerTerritoryName || "N/A"}
                                        </div>
                                      </div>
                                    </div>
                                    <div className="col-lg-4">
                                      <div className="mb-2">
                                        <div className="detail-label">
                                          Project/Site Details
                                        </div>
                                        <div className="detail-value">
                                          {srData.projectSiteDetails ||
                                            srData.ProjectSiteDetails ||
                                            "N/A"}
                                        </div>
                                      </div>
                                    </div>

                                    {/* Secondary Invoice Attachments (CopyFor = "Secondary Invoice") - Customer Details card */}
                                    <div className="col-12 mt-3">
                                      <div className="detail-label mb-2">Secondary Invoice Attachments</div>
                                      <div className="upload-card">
                                        {srData.secondaryInvoiceFiles && srData.secondaryInvoiceFiles.length > 0 ? (
<UploadedFileList
                                            files={srData.secondaryInvoiceFiles}
                                            noTopMargin
                                            onOpen={(fileItem) => {
                                              if (fileItem.url) window.open(fileItem.url, "_blank");
                                            }}
                                          />
                        ) : (
                                          <div className="upload-helper" style={{ color: "#666", fontStyle: "italic" }}>
                                            No secondary invoice attachments
                                          </div>
                                        )}
                                      </div>
                                    </div>

                                    {/* Legacy Secondary Invoice Upload block - kept for any other use; same data as above */}
                                    <div className="col-12 mt-3" style={{ display: "none" }}>
                                      <div className="upload-card">
                                        {srData.secondaryInvoiceFiles && srData.secondaryInvoiceFiles.length > 0 ? (
                                          <div
                                  style={{
                                              display: "grid",
                                              gridTemplateColumns: "repeat(4, 1fr)",
                                              gap: "15px",
                                              marginTop: "20px",
                                            }}
                                          >
                                            {srData.secondaryInvoiceFiles.map((fileItem, index) => (
                                              <div
                                                key={fileItem.id || index}
                                  style={{
                                                  position: "relative",
                                                  border: "1px solid #ddd",
                                                  borderRadius: "8px",
                                                  padding: "10px",
                                                  backgroundColor: "#f9f9f9",
                                                }}
                                              >
                                                <div
                                      style={{
                                                    width: "100%",
                                                    height: "150px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    marginBottom: "8px",
                                                    backgroundColor: "white",
                                                    borderRadius: "4px",
                                        overflow: "hidden",
                                                    cursor: fileItem.url ? "pointer" : "default",
                                                  }}
                                                  onClick={() => {
                                                    if (fileItem.url) {
                                                      window.open(fileItem.url, "_blank");
                                                    }
                                                  }}
                                                >
                                                  {fileItem.preview || fileItem.url ? (
                                                    <img
                                                      src={fileItem.preview || fileItem.url}
                                                      alt={fileItem.name || `Secondary Invoice ${index + 1}`}
                                        style={{
                                                        maxWidth: "100%",
                                                        maxHeight: "100%",
                                                        objectFit: "contain",
                                                      }}
                                                    />
                                                  ) : (
                                      <div
                                        style={{
                                          textAlign: "center",
                                                        color: "#666",
                                                      }}
                                                    >
                                                      <svg
                                                        width="48"
                                                        height="48"
                                                        viewBox="0 0 24 24"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                      >
                                                        <path
                                                          d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                                                          stroke="#666"
                                                          strokeWidth="2"
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                        />
                                                        <path
                                                          d="M14 2V8H20"
                                                          stroke="#666"
                                                          strokeWidth="2"
                                                          strokeLinecap="round"
                                                          strokeLinejoin="round"
                                                        />
                                                      </svg>
                                                      <div
                                                        style={{
                                                          fontSize: "10px",
                                                          marginTop: "4px",
                                                        }}
                                                      >
                                                        PDF
                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                                <div
                                                  style={{
                                                    fontSize: "0.75rem",
                                                    color: "#333",
                                                    wordBreak: "break-word",
                                                    textAlign: "center",
                                                    paddingTop: "4px",
                                                  }}
                                                  title={fileItem.name || `Secondary Invoice ${index + 1}`}
                                                >
                                                  {fileItem.name && fileItem.name.length > 20
                                                    ? fileItem.name.substring(0, 20) + "..."
                                                    : fileItem.name || `Secondary Invoice ${index + 1}`}
                                                </div>
                                              </div>
                                  ))}
                                </div>
                                        ) : (
                                          <div className="upload-helper" style={{ color: "#666", fontStyle: "italic" }}>
                                            No secondary invoice files uploaded
                              </div>
                                        )}
                                      </div>
                                    </div>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Customer Billing Details - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseCustomerBillingDetails"
                aria-expanded="false"
                aria-controls="collapseCustomerBillingDetails"
              >
                <span>Customer Billing Details</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse viewDetCardPnlOutr"
            id="collapseCustomerBillingDetails"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0 }}>
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Billing Invoice No.</div>
                        <div className="detail-value">
                          {srData.billingInvoiceNo || srData.invoiceNumber || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">Invoice Date</div>
                        <div className="detail-value">
                          {formatDate(srData.billingInvoiceDate) || formatDate(srData.invoiceDate) || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">PO / PI / SO No.</div>
                        <div className="detail-value">
                          {srData.billingPONo || srData.orderNo || srData.poNo || "N/A"}
                        </div>
                      </div>
                    </div>
                    <div className="col-lg-3 col-md-6">
                      <div className="mb-2">
                        <div className="detail-label">PO / PI / SO No. Date</div>
                        <div className="detail-value">
                          {formatDate(srData.billingPODate) || formatDate(srData.orderDate) || formatDate(srData.poDate) || "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product Details - Collapsible */}
        {srData.products && srData.products.length > 0 && (
          <div className="viewDetwrpr mb-3 material-section-panel">
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ margin: 0 }}>
                <div
                  className="section-heading d-flex justify-content-between align-items-center"
                  style={{ cursor: "pointer" }}
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseProductDetails"
                  aria-expanded="false"
                  aria-controls="collapseProductDetails"
                >
                  <span>Material Section</span>
                  <i className="bi bi-chevron-down"></i>
                </div>
              </div>
            </div>
            {materialSectionError && (
              <div className="alert alert-warning mb-2" role="alert" style={{ marginLeft: "0.5rem", marginRight: "0.5rem" }}>
                {materialSectionError}
              </div>
            )}
            <div
              className="collapse viewDetCardPnlOutr"
              id="collapseProductDetails"
            >
              <div className="viewDetCardPnlWrpr contPdTop">
                <div className="card-panel" style={{ paddingTop: 0 }}>
                  <div style={{ maxWidth: "100%", overflowX: "auto" }}>
                    <div className="table-responsive">
                      <table
                        ref={productTableRef}
                        className="table table-striped table-bordered complaint-table"
                        style={{ width: "100%" }}
                      >
                        <thead>
                          <tr>
                            {/* Must match DataTables column order (incl. hidden cols) — wrong counts misalign headers */}
                            <th style={{ display: "none" }} aria-hidden="true" />
                            <th style={{ display: "none" }} aria-hidden="true">
                              SNo.
                            </th>
                            <th>Business Vertical</th>
                            <th>PlantName</th>
                            <th>Item Description</th>
                            <th>Product Details</th>
                            <th>Invoice Quantity</th>
                            <th>UOM</th>
                            <th>Invoice Unit Price</th>
                            <th>Total Price</th>
                            <th style={{ display: showMaterialComponentDetailsColumn ? undefined : "none" }}>
                              Component Details
                            </th>
                            <th style={{ display: "none" }} aria-hidden="true">
                              SR Type Code
                            </th>
                            <th>SR Category</th>
                            <th style={{ display: "none" }} aria-hidden="true">
                              SR Category (dropdown)
                            </th>
                            <th style={{ display: showMaterialSrQtyValueApproveColumns ? undefined : "none" }}>
                              SR Qty
                            </th>
                            <th style={{ display: showMaterialSrQtyValueApproveColumns ? undefined : "none" }}>
                              SR Value
                            </th>
                            <th style={{ display: showMaterialSrQtyValueApproveColumns ? undefined : "none" }}>
                              Approve Qty
                            </th>
                            <th style={{ display: showMaterialSrQtyValueApproveColumns ? undefined : "none" }}>
                              Cal. Approved Value
                            </th>
                            {!isSeUserType() && <th>Action</th>}
                          </tr>
                        </thead>
                        <tbody></tbody>
                        <tfoot>
                          <tr style={{ backgroundColor: "#f8f9fa", fontWeight: 600, border: "none" }}>
                            {(() => {
                              const products = srData?.products || [];
                              const formatNum2 = (n) =>
                                formatViewSrMoney(n).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                              const invoiceQtyTotal = products.reduce(
                                (sum, p) => sum + parseInvoiceNumberLoose(pickInvoiceQuantityRaw(p)),
                                0
                              );
                              const invoiceValueTotal = products.reduce((sum, p) => {
                                const qty = parseInvoiceNumberLoose(pickInvoiceQuantityRaw(p));
                                const price = parseInvoiceNumberLoose(p?.Price ?? p?.price ?? p?.UnitPrice ?? p?.unitPrice ?? 0);
                                const lineVal = parseInvoiceNumberLoose(pickInvoiceValueRaw(p));
                                return sum + (lineVal > 0 ? lineVal : qty * price);
                              }, 0);
                              const unitPriceAvg = invoiceQtyTotal > 0 ? products.reduce((sum, p) => {
                                const qty = parseInvoiceNumberLoose(pickInvoiceQuantityRaw(p));
                                const lineVal = parseInvoiceNumberLoose(pickInvoiceValueRaw(p));
                                const price = parseInvoiceNumberLoose(p?.Price ?? p?.price ?? p?.UnitPrice ?? p?.unitPrice ?? 0);
                                return sum + (qty > 0 && lineVal > 0 ? lineVal / qty : price);
                              }, 0) / products.length : 0;
                              const compQtyTotal = products.reduce((sum, p) => sum + (parseFloat(p?.ComplaintQty ?? p?.complaintQty ?? p?.SRQuantity ?? p?.srQuantity ?? 0) || 0), 0);
                              const srValueTotal = products.reduce((sum, p) => {
                                const srVal = parseFloat(p?.SRValue ?? p?.srValue ?? 0) || 0;
                                if (srVal > 0) return sum + srVal;
                                const cq = parseFloat(p?.ComplaintQty ?? p?.complaintQty ?? p?.SRQuantity ?? p?.srQuantity ?? 0) || 0;
                                const price = parseFloat(p?.Price ?? p?.price ?? 0) || 0;
                                return sum + cq * price;
                              }, 0);
                              const approvedQtyTotal = products.reduce((sum, p) => {
                                const srQty = parseFloat(p?.ComplaintQty ?? p?.complaintQty ?? p?.SRQuantity ?? p?.srQuantity ?? 0) || 0;
                                const price = parseFloat(p?.Price ?? p?.price ?? 0) || 0;
                                const qtyRaw = parseFloat(p?.ApprovedQty ?? p?.approvedQty ?? p?.ApprovedQuantity ?? p?.approvedQuantity ?? p?.ApprvQuantity ?? p?.apprvQuantity ?? 0) || 0;
                                const valRaw = parseFloat(p?.ApprvValue ?? p?.apprvValue ?? p?.ApprovedValue ?? p?.approvedValue ?? 0) || 0;
                                let qty = qtyRaw > 0 ? qtyRaw : (price > 0 && valRaw > 0 ? valRaw / price : 0);
                                if (srQty > 0 && qty > srQty) qty = srQty;
                                return sum + qty;
                              }, 0);
                              const approvedValueTotal = products.reduce((sum, p) => {
                                const srQty = parseFloat(p?.ComplaintQty ?? p?.complaintQty ?? p?.SRQuantity ?? p?.srQuantity ?? 0) || 0;
                                const price = parseFloat(p?.Price ?? p?.price ?? 0) || 0;
                                const qtyRaw = parseFloat(p?.ApprovedQty ?? p?.approvedQty ?? p?.ApprovedQuantity ?? p?.approvedQuantity ?? p?.ApprvQuantity ?? p?.apprvQuantity ?? 0) || 0;
                                const valRaw = parseFloat(p?.ApprvValue ?? p?.apprvValue ?? p?.ApprovedValue ?? p?.approvedValue ?? 0) || 0;
                                let qty = qtyRaw > 0 ? qtyRaw : (price > 0 && valRaw > 0 ? valRaw / price : 0);
                                if (srQty > 0 && qty > srQty) qty = srQty;
                                const val = valRaw > 0 ? valRaw : qty * price;
                                return sum + val;
                              }, 0);
                              const footCell = { border: "none" };
                              const isSE = isSeUserType();
                              return (
                                <>
                                  {/* hidden SLNo */}<td style={{ ...footCell, display: "none" }}></td>
                                  {/* SNo (hidden) */}<td style={{ ...footCell, display: "none" }}></td>
                                  {/* BV */}<td style={footCell}></td>
                                  {/* Plant */}<td style={footCell}></td>
                                  {/* Item */}<td style={footCell}></td>
                                  {/* Product Details */}<td style={footCell}></td>
                                  {/* Invoice Qty */}<td className="text-end" style={footCell}>{formatViewSrQty(invoiceQtyTotal)}</td>
                                  {/* UOM */}<td style={footCell}></td>
                                  {/* Unit Price */}<td className="text-end" style={footCell}>{formatViewSrMoney(unitPriceAvg)}</td>
                                  {/* Total Price */}<td className="text-end" style={footCell}>{formatNum2(invoiceValueTotal)}</td>
                                  {/* Component Details */}<td style={{ ...footCell, display: showMaterialComponentDetailsColumn ? undefined : "none" }}></td>
                                  {/* SR Type Code (hidden col) */}<td style={{ ...footCell, display: "none" }}></td>
                                  {/* SR Category */}<td style={footCell}></td>
                                  {/* SR Category dropdown (hidden col) */}<td style={{ ...footCell, display: "none" }}></td>
                                  {/* SR Qty — total SR / complaint qty across lines */}
                                  <td
                                    className="text-end"
                                    style={{
                                      ...footCell,
                                      display: showMaterialSrQtyValueApproveColumns ? undefined : "none",
                                    }}
                                  >
                                    {formatViewSrQty(compQtyTotal)}
                                  </td>
                                  {/* SR Value */}
                                  <td
                                    className="text-end"
                                    style={{
                                      ...footCell,
                                      display: showMaterialSrQtyValueApproveColumns ? undefined : "none",
                                    }}
                                  >
                                    {formatViewSrMoney(srValueTotal)}
                                  </td>
                                  {/* Approved Qty */}
                                  <td
                                    className="text-end foot-approved-qty"
                                    style={{
                                      ...footCell,
                                      display: showMaterialSrQtyValueApproveColumns ? undefined : "none",
                                    }}
                                  >
                                    {formatViewSrQty(approvedQtyTotal)}
                                  </td>
                                  {/* Approved Value */}
                                  <td
                                    className="text-end foot-approved-value"
                                    style={{
                                      ...footCell,
                                      display: showMaterialSrQtyValueApproveColumns ? undefined : "none",
                                    }}
                                  >
                                    {formatViewSrMoney(approvedValueTotal)}
                                  </td>
                                  {!isSE && <td style={footCell}></td>}
                                </>
                              );
                            })()}
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>
                  
                  {/* Invoice Total Section - all values dynamic from srData.products and header */}
                  <div
                    className="mt-3 p-3"
                    style={{
                      borderTop: "1px solid #dee2e6",
                      backgroundColor: "#f8f9fa",
                      borderRadius: "0 0 8px 8px",
                    }}
                  >
                    {(() => {
                      const products = srData?.products || [];
                      const marketCode = srData?.MarketCode || srData?.marketCode || "";
                      const marketName = srData?.MarketName || srData?.marketName || "";
                      let isExport = false;
                      if (marketCode || marketName) {
                        const codeUpper = marketCode.toUpperCase();
                        const nameUpper = marketName.toUpperCase();
                        if (
                          codeUpper === "M002" ||
                          codeUpper.includes("EXP") ||
                          nameUpper.includes("EXPORT") ||
                          codeUpper === "EXPORT" ||
                          nameUpper === "EXPORT"
                        ) {
                          isExport = true;
                        }
                      }
                      const summaryLabelStyle = {
                        fontSize: "13px",
                        fontWeight: 700,
                        color: "#343a40",
                        marginBottom: "6px",
                        letterSpacing: "0.2px",
                      };
                      const summaryValueStyle = {
                        fontWeight: 700,
                        fontSize: "18px",
                        color: "#111827",
                        lineHeight: 1.25,
                      };
                      const summaryTileStyle = {
                        backgroundColor: "#ffffff",
                        border: "1px solid #dee2e6",
                        borderRadius: "8px",
                        padding: "12px 14px",
                        height: "100%",
                        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                      };

                      // Three currencies (Export commercial) + related amounts
                      const invoiceDocCurrency = (
                        srData?.invoiceCurrency ||
                        srData?.InvoiceCurrency ||
                        srData?.currency ||
                        srData?.Currency ||
                        ""
                      )
                        .toString()
                        .trim();
                      const localCurrency = (
                        srData?.localCurrency ||
                        srData?.documentCurrency ||
                        srData?.DocumentCurrency ||
                        "INR"
                      )
                        .toString()
                        .trim() || "INR";
                      const reportingCurrency = (
                        srData?.reportingCurrency ||
                        srData?.ReportingCurrency ||
                        "USD"
                      )
                        .toString()
                        .trim() || "USD";
                      const exchangeRateNum =
                        parseFloat(
                          srData?.exchangeRate ?? srData?.ExchangeRate ?? 0
                        ) || 0;
                      const exchangeRateDisplay =
                        (
                          srData?.exchangeRate ??
                          srData?.ExchangeRate ??
                          ""
                        ).toString().trim() ||
                        (exchangeRateNum > 0 ? String(exchangeRateNum) : "");

                      const conversionRateText = (() => {
                        const fromApi =
                          products[0]?.ConversionRate ??
                          products[0]?.conversionRate ??
                          products[0]?.Conversion_Rate ??
                          srData?.conversionRate ??
                          srData?.ConversionRate ??
                          "";
                        if (fromApi && String(fromApi).trim()) return String(fromApi).trim();
                        if (invoiceDocCurrency && localCurrency && exchangeRateDisplay) {
                          return `${invoiceDocCurrency} to ${localCurrency} - ${exchangeRateDisplay}`;
                        }
                        return exchangeRateDisplay || "N/A";
                      })();

                      // SR Value Total = sum of SRValue or (ComplaintQty * Price) — Invoice / Doc. Currency amount
                      const srValueTotal = products.reduce((sum, p) => {
                        const srVal = parseFloat(p?.SRValue ?? p?.srValue ?? 0) || 0;
                        if (srVal > 0) return sum + srVal;
                        const complaintQty = parseFloat(p?.ComplaintQty ?? p?.complaintQty ?? p?.SRQuantity ?? p?.srQuantity ?? 0) || 0;
                        const price = parseFloat(p?.Price ?? p?.price ?? 0) || 0;
                        return sum + complaintQty * price;
                      }, 0);

                      // Approved Value Total = sum of ApprvValue / ApprovedValue
                      const approvedValueTotal = products.reduce((sum, p) => {
                        const v = parseFloat(p?.ApprvValue ?? p?.apprvValue ?? p?.ApprovedValue ?? p?.approvedValue ?? 0) || 0;
                        return sum + v;
                      }, 0);

                      const formatNum = (n) => Number(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
                      // Local Currency Amount = Invoice/Doc amount × Exchange Rate (existing export INR calc)
                      const srValueLocal =
                        exchangeRateNum > 0 ? srValueTotal * exchangeRateNum : null;
                      const approvedValueLocal =
                        exchangeRateNum > 0 ? approvedValueTotal * exchangeRateNum : null;
                      const finalApprovedValueTotal = resolveFinalApprovedValueTotal(
                        products,
                        srData?.finalApprovedValue ?? srData?.FinalApprovedValue
                      );
                      const finalApprovedValueLocal =
                        exchangeRateNum > 0 ? finalApprovedValueTotal * exchangeRateNum : null;
                      // Reporting Currency Amount = Exchange Rate × Invoice/Doc. Currency Amount
                      // Invoice/Doc. Currency Amount on raise = Local Currency Amount field (base ÷ rate);
                      // so Reporting ≈ base. Prefer stored ReportingAmount when present.
                      const storedReportingAmount = parseFloat(
                        srData?.reportingAmount ?? srData?.ReportingAmount ?? NaN
                      );
                      const srValueReporting = Number.isFinite(storedReportingAmount)
                        ? storedReportingAmount
                        : exchangeRateNum > 0
                          ? srValueTotal
                          : null;
                      const reportingRatio =
                        srValueTotal > 0 && srValueReporting != null
                          ? srValueReporting / srValueTotal
                          : exchangeRateNum > 0
                            ? 1
                            : 0;
                      const approvedValueReporting =
                        reportingRatio > 0 ? approvedValueTotal * reportingRatio : null;
                      const finalApprovedValueReporting =
                        reportingRatio > 0 ? finalApprovedValueTotal * reportingRatio : null;
                      const rcaDisplay = resolveRcaFromSr(srData, products);
                      const rcaGivenVal = (rcaDisplay.given || "").trim();
                      const showRcaSummary =
                        Boolean(rcaGivenVal) || Boolean(rcaDisplay.remarks);
                      const visitRequiredVal = String(
                        srData?.physicalVisitRequired ??
                          srData?.PhysicalVisitRequired ??
                          products?.[0]?.physicalVisitRequired ??
                          products?.[0]?.PhysicalVisitRequired ??
                          ""
                      ).trim();
                      const visitTravelerVal = String(
                        srData?.siteVisitTraveler ??
                          srData?.SiteVisitTraveler ??
                          products?.[0]?.siteVisitTraveler ??
                          products?.[0]?.SiteVisitTraveler ??
                          ""
                      ).trim();
                      const visitTypeVal = String(
                        srData?.technicalVisitType ??
                          srData?.TechnicalVisitType ??
                          products?.[0]?.technicalVisitType ??
                          products?.[0]?.TechnicalVisitType ??
                          ""
                      ).trim();
                      const showVisitSummary =
                        isDomesticMarketSr(srData) &&
                        hasProductQualitySrCategory(srData, products) &&
                        (Boolean(visitRequiredVal) ||
                          Boolean(visitTravelerVal) ||
                          Boolean(visitTypeVal));
                      const chKittyVal = resolveDeductFromChKitty(srData, products);
                      const showChKittySummary = Boolean(chKittyVal);

                      return (
                        <>
                        <div className="row g-3">
                          {isExport && (
                            <>
                              <div className="col-md-3">
                                <div style={summaryTileStyle}>
                                  <div className="detail-label" style={summaryLabelStyle}>Exchange Rate</div>
                                  <div className="detail-value" style={summaryValueStyle}>
                                    {conversionRateText}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div style={summaryTileStyle}>
                                  <div className="detail-label" style={summaryLabelStyle}>Invoice / Doc. Currency</div>
                                  <div className="detail-value" style={summaryValueStyle}>
                                    {invoiceDocCurrency || "N/A"}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div style={summaryTileStyle}>
                                  <div className="detail-label" style={summaryLabelStyle}>Local Currency</div>
                                  <div className="detail-value" style={summaryValueStyle}>
                                    {localCurrency || "N/A"}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div style={summaryTileStyle}>
                                  <div className="detail-label" style={summaryLabelStyle}>Reporting Currency</div>
                                  <div className="detail-value" style={summaryValueStyle}>
                                    {reportingCurrency || "N/A"}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          {showMaterialSrQtyValueApproveColumns && (
                          <div className="col-md-3">
                            <div style={summaryTileStyle}>
                              <div className="detail-label" style={summaryLabelStyle}>
                                {isExport && invoiceDocCurrency
                                  ? `SR Value Total (${invoiceDocCurrency})`
                                  : "SR Value Total"}
                              </div>
                              <div className="detail-value" style={summaryValueStyle}>
                                {formatNum(srValueTotal)}
                              </div>
                            </div>
                          </div>
                          )}
                          {showMaterialSrQtyValueApproveColumns && isExport && (
                            <>
                              <div className="col-md-3">
                                <div style={summaryTileStyle}>
                                  <div className="detail-label" style={summaryLabelStyle}>
                                    {`SR Value Total (${localCurrency})`}
                                  </div>
                                  <div className="detail-value" style={summaryValueStyle}>
                                    {srValueLocal != null ? formatNum(srValueLocal) : "N/A"}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div style={summaryTileStyle}>
                                  <div className="detail-label" style={summaryLabelStyle}>
                                    {`Reporting Currency Amount (${reportingCurrency})`}
                                  </div>
                                  <div className="detail-value" style={summaryValueStyle}>
                                    {srValueReporting != null
                                      ? formatNum(srValueReporting)
                                      : "N/A"}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          {showMaterialSrQtyValueApproveColumns && (
                          <div className="col-md-3">
                            <div style={summaryTileStyle}>
                              <div className="detail-label" style={summaryLabelStyle}>
                                {isExport && invoiceDocCurrency
                                  ? `Cal. Approved Value (${invoiceDocCurrency})`
                                  : "Cal. Approved Value"}
                              </div>
                              <div className="detail-value" style={summaryValueStyle}>
                                {formatNum(approvedValueTotal)}
                              </div>
                            </div>
                          </div>
                          )}
                          {showMaterialSrQtyValueApproveColumns && isExport && (
                            <>
                              <div className="col-md-3">
                                <div style={summaryTileStyle}>
                                  <div className="detail-label" style={summaryLabelStyle}>
                                    {`Cal. Approved Value (${localCurrency})`}
                                  </div>
                                  <div className="detail-value" style={summaryValueStyle}>
                                    {approvedValueLocal != null ? formatNum(approvedValueLocal) : "N/A"}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div style={summaryTileStyle}>
                                  <div className="detail-label" style={summaryLabelStyle}>
                                    {`Cal. Approved Reporting Currency Amount (${reportingCurrency})`}
                                  </div>
                                  <div className="detail-value" style={summaryValueStyle}>
                                    {approvedValueReporting != null
                                      ? formatNum(approvedValueReporting)
                                      : "N/A"}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                          <div className="col-md-3">
                            <div style={summaryTileStyle}>
                              <div className="detail-label" style={summaryLabelStyle}>
                                {isExport && invoiceDocCurrency
                                  ? `Final Approved Value Total (${invoiceDocCurrency})`
                                  : "Final Approved Value Total"}
                              </div>
                              <div className="detail-value" style={summaryValueStyle}>
                                {formatNum(finalApprovedValueTotal)}
                              </div>
                            </div>
                          </div>
                          {isExport && (
                            <>
                              <div className="col-md-3">
                                <div style={summaryTileStyle}>
                                  <div className="detail-label" style={summaryLabelStyle}>
                                    {`Final Approved Value Total (${localCurrency})`}
                                  </div>
                                  <div className="detail-value" style={summaryValueStyle}>
                                    {finalApprovedValueLocal != null
                                      ? formatNum(finalApprovedValueLocal)
                                      : "N/A"}
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-3">
                                <div style={summaryTileStyle}>
                                  <div className="detail-label" style={summaryLabelStyle}>
                                    {`Final Approved Reporting Currency Amount (${reportingCurrency})`}
                                  </div>
                                  <div className="detail-value" style={summaryValueStyle}>
                                    {finalApprovedValueReporting != null
                                      ? formatNum(finalApprovedValueReporting)
                                      : "N/A"}
                                  </div>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                        {showRcaSummary && (
                          <div
                            className="mt-3"
                            style={{
                              padding: "16px",
                              backgroundColor: "#f8fafc",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <div className="row g-3">
                              <div className="col-md-4">
                                <label
                                  className="form-label mb-0"
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "#111827",
                                    marginBottom: "6px",
                                    display: "block",
                                  }}
                                >
                                  Is RCA Required?
                                </label>
                                <div className="d-flex gap-3 align-items-center" style={{ minHeight: "44px" }}>
                                  {["Yes", "No"].map((opt) => (
                                    <label
                                      key={opt}
                                      className="d-flex align-items-center gap-1 mb-0"
                                      style={{ fontSize: "14px", fontWeight: 500 }}
                                    >
                                      <input
                                        type="radio"
                                        name="viewRcaRequired"
                                        value={opt}
                                        checked={rcaGivenVal === opt}
                                        disabled
                                        readOnly
                                      />
                                      {opt}
                                    </label>
                                  ))}
                                  {!rcaGivenVal && (
                                    <span style={{ fontSize: "14px", color: "#6b7280" }}>—</span>
                                  )}
                                </div>
                              </div>
                              {rcaGivenVal === "Yes" && (
                                <div className="col-md-8">
                                  <label
                                    className="form-label mb-0"
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: 500,
                                      color: "#111827",
                                      marginBottom: "6px",
                                      display: "block",
                                    }}
                                  >
                                    RCA Remarks
                                  </label>
                                  <textarea
                                    className="form-control"
                                    rows={2}
                                    readOnly
                                    disabled
                                    value={rcaDisplay.remarks || ""}
                                    style={{
                                      fontSize: "14px",
                                      resize: "vertical",
                                      minHeight: "44px",
                                      backgroundColor: "#ffffff",
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                        {showChKittySummary && (
                          <div
                            className="mt-3"
                            style={{
                              padding: "16px",
                              backgroundColor: "#f8fafc",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <div className="row g-3">
                              <div className="col-md-4">
                                <label
                                  className="form-label mb-0"
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "#111827",
                                    marginBottom: "6px",
                                    display: "block",
                                  }}
                                >
                                  Money to be deducted from CH Kitty?
                                </label>
                                <div className="d-flex gap-3 align-items-center" style={{ minHeight: "44px" }}>
                                  {["Yes", "No"].map((opt) => (
                                    <label
                                      key={opt}
                                      className="d-flex align-items-center gap-1 mb-0"
                                      style={{ fontSize: "14px", fontWeight: 500 }}
                                    >
                                      <input
                                        type="radio"
                                        name="viewDeductFromChKitty"
                                        value={opt}
                                        checked={chKittyVal === opt}
                                        disabled
                                        readOnly
                                      />
                                      {opt}
                                    </label>
                                  ))}
                                  {!chKittyVal && (
                                    <span style={{ fontSize: "14px", color: "#6b7280" }}>—</span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                        {showVisitSummary && (
                          <div
                            className="mt-3"
                            style={{
                              padding: "16px",
                              backgroundColor: "#f8fafc",
                              borderRadius: "8px",
                              border: "1px solid #e2e8f0",
                            }}
                          >
                            <div className="row g-3">
                              <div className="col-md-4">
                                <label
                                  className="form-label mb-0"
                                  style={{
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "#111827",
                                    marginBottom: "6px",
                                    display: "block",
                                  }}
                                >
                                  If a physical visit is required by the technical person to the complaint site?
                                </label>
                                <div className="d-flex gap-3 align-items-center" style={{ minHeight: "44px" }}>
                                  {["Yes", "No"].map((opt) => (
                                    <label
                                      key={opt}
                                      className="d-flex align-items-center gap-1 mb-0"
                                      style={{ fontSize: "14px", fontWeight: 500 }}
                                    >
                                      <input
                                        type="radio"
                                        name="viewPhysicalVisitRequired"
                                        value={opt}
                                        checked={visitRequiredVal === opt}
                                        disabled
                                        readOnly
                                      />
                                      {opt}
                                    </label>
                                  ))}
                                  {!visitRequiredVal && (
                                    <span style={{ fontSize: "14px", color: "#6b7280" }}>—</span>
                                  )}
                                </div>
                              </div>
                              {visitRequiredVal === "Yes" && (
                                <>
                                  <div className="col-md-4">
                                    <label
                                      className="form-label mb-0"
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        color: "#111827",
                                        marginBottom: "6px",
                                        display: "block",
                                      }}
                                    >
                                      Who would be traveling to the site?
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      readOnly
                                      disabled
                                      value={visitTravelerVal || "—"}
                                      style={{
                                        fontSize: "14px",
                                        minHeight: "44px",
                                        backgroundColor: "#ffffff",
                                      }}
                                    />
                                  </div>
                                  <div className="col-md-4">
                                    <label
                                      className="form-label mb-0"
                                      style={{
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        color: "#111827",
                                        marginBottom: "6px",
                                        display: "block",
                                      }}
                                    >
                                      Is it a local visit or outstation visit by the technical person?
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      readOnly
                                      disabled
                                      value={visitTypeVal || "—"}
                                      style={{
                                        fontSize: "14px",
                                        minHeight: "44px",
                                        backgroundColor: "#ffffff",
                                      }}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sample Information - Collapsible */}
        <div className="viewDetwrpr mb-3">
          <div className="viewDetCardPnlWrpr">
            <div className="card-panel" style={{ margin: 0 }}>
              <div
                className="section-heading d-flex justify-content-between align-items-center"
                style={{ cursor: "pointer" }}
                data-bs-toggle="collapse"
                data-bs-target="#collapseSampleInformation"
                aria-expanded="false"
                aria-controls="collapseSampleInformation"
              >
                <span>Sample Information</span>
                <i className="bi bi-chevron-down"></i>
              </div>
            </div>
          </div>
          <div
            className="collapse viewDetCardPnlOutr"
            id="collapseSampleInformation"
          >
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ paddingTop: 0 }}>
                <div className="p-3">
                  <div className="row g-3">
                    <div className="col-lg-3 col-md-4 col-sm-6">
                      <div className="mb-2">
                        <div className="detail-label">Sample Sent</div>
                        <div className="detail-value">
                          {(() => {
                            const st = (srData.sampleInfo?.sampleType ?? srData.sampleType ?? "").toString().trim().toLowerCase();
                            if (st === "no") return "No";
                            const fallbackHasAny = !!(
                              (srData.sampleType && st !== "no") ||
                              srData.courierAgency ||
                              srData.docketNo ||
                              srData.sentTo ||
                              srData.sampleDetails
                            );
                            return srData.sampleInfo?.sampleSent ?? (fallbackHasAny ? "Yes" : "No");
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>

                  {(() => {
                    const st = (srData.sampleInfo?.sampleType ?? srData.sampleType ?? "").toString().trim().toLowerCase();
                    const sent = (srData.sampleInfo?.sampleSent ?? "").toString().trim().toLowerCase();
                    const isYes = sent === "yes";
                    if (st === "no") return false;
                    return (
                      isYes ||
                      srData.courierAgency ||
                      srData.courierDate ||
                      srData.docketNo ||
                      srData.sentTo ||
                      srData.sampleDetails
                    );
                  })() && (
                    <div className="row g-3 mt-1">
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Courier Agency</div>
                          <div className="detail-value">
                            {srData.sampleInfo?.courierAgency || srData.courierAgency || "N/A"}
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Courier Date</div>
                          <div className="detail-value">
                            {formatDate(srData.sampleInfo?.courierDate ?? srData.courierDate) || "N/A"}
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Docket No.</div>
                          <div className="detail-value">
                            {srData.sampleInfo?.docketNo || srData.docketNo || "N/A"}
                          </div>
                        </div>
                      </div>

                      <div className="col-lg-3 col-md-6">
                        <div className="mb-2">
                          <div className="detail-label">Sent To</div>
                          <div className="detail-value">
                            {srData.sampleInfo?.sentTo || srData.sentTo || "N/A"}
                          </div>
                        </div>
                      </div>

                      <div className="col-12">
                        <div className="mb-2">
                          <div className="detail-label">Sample Details</div>
                          <div className="detail-value" style={{ whiteSpace: "pre-wrap" }}>
                            {srData.sampleInfo?.sampleDetails || srData.sampleDetails || "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

{/* Checklist Information - Collapsible */}
{Array.isArray(srData.checklistResponses) &&
  srData.checklistResponses.length > 0 && (
  <div className="viewDetwrpr mb-3">
    <div className="viewDetCardPnlWrpr">
      <div className="card-panel" style={{ margin: 0 }}>
        <div
          className="section-heading d-flex justify-content-between align-items-center"
          style={{ cursor: "pointer" }}
          data-bs-toggle="collapse"
          data-bs-target="#collapseChecklistInfo"
          aria-expanded="false"
          aria-controls="collapseChecklistInfo"
        >
          <span>Checklist Responses</span>
          <i className="bi bi-chevron-down"></i>
        </div>
      </div>
    </div>
    <div
      className="collapse viewDetCardPnlOutr"
      id="collapseChecklistInfo"
    >
      <div className="viewDetCardPnlWrpr">
        <div className="card-panel" style={{ paddingTop: 15 }}>
          {groupChecklistByTemplate(srData.checklistResponses).map(
            (template) => (
              <div
                className="mb-3"
                key={template.templateCode || template.templateName}
              >
                <div
                  className="section-heading d-flex justify-content-between align-items-center"
                  style={{
                    cursor: "pointer",
                    backgroundColor: "#eee",
                    padding: "12px 16px",
                    borderRadius: "4px",
                  }}
                  data-bs-toggle="collapse"
                  data-bs-target={`#collapseChecklist_${template.templateCode || template.templateName}`}
                  aria-expanded="false"
                  aria-controls={`collapseChecklist_${template.templateCode || template.templateName}`}
                >
                  <span
                    className="innerColHdng"
                    style={{ fontSize: "16px", fontWeight: 500 }}
                  >
                    {template.templateName}
                  </span>
                  <i className="bi bi-chevron-down innerColArrow"></i>
                </div>
                <div
                  className="collapse mt-2"
                  id={`collapseChecklist_${template.templateCode || template.templateName}`}
                >
                  <div
                    className="p-3"
                    style={{ backgroundColor: "#fafbfc" }}
                  >
                    <SrCategoryTabbedChecklistReadOnly
                      templateCode={String(template.templateCode || "").trim()}
                      responseRows={template.responseRows || []}
                      materialRows={srData.products || []}
                      childSrCode={
                        childSrCodeFromQuery ||
                        srData.childSRNumber ||
                        srData.ChildSRNumber ||
                        null
                      }
                      fallbackQuestions={template.questions || []}
                    />

                    {(() => {
                      const tcKey = String(template.templateCode || "")
                        .trim()
                        .toUpperCase();

                      const media =
                        checklistAttachmentsByTemplate[tcKey] ||
                        checklistAttachmentsByTemplate["__DEFAULT__"] ||
                        (() => {
                          const values = Object.values(
                            checklistAttachmentsByTemplate || {}
                          );
                          return values.length === 1 ? values[0] : null;
                        })();

                      const hasDamage =
                        Array.isArray(media?.damageFiles) && media.damageFiles.length > 0;
                      const hasVideo = !!media?.videoUrl;
                      const hasMeta =
                        !!media?.complaintReceivedDate || !!media?.visitReportComments;

                      if (!media || (!hasDamage && !hasVideo && !hasMeta)) return null;

                      return (
                        <div style={{ marginTop: 18 }}>
                          {(media?.complaintReceivedDate || media?.visitReportComments) && (
                            <div className="row g-3 mb-2">
                              <div className="col-lg-4 col-md-6">
                                <div className="mb-2">
                                  <div className="detail-label" style={{ fontWeight: 800 }}>
                                    SR Received Date
                                  </div>
                                  <div className="compReceivedDte">
                                    <div
                                      className="detail-value"
                                      style={{
                                        fontSize: "16px",
                                        fontWeight: 800,
                                        color: "#0b3b66",
                                        whiteSpace: "pre-wrap",
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                        lineHeight: "1.35",
                                      }}
                                    >
                                      {media?.complaintReceivedDate
                                        ? formatDate(media.complaintReceivedDate)
                                        : "N/A"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="col-lg-8 col-md-6">
                                <div className="mb-2">
                                  <div className="detail-label" style={{ fontWeight: 800 }}>
                                    Visit Report Comments
                                  </div>
                                  <div className="viewRepCommnt">
                                    <div
                                      className="detail-value"
                                      style={{
                                        whiteSpace: "pre-wrap",
                                        fontSize: "16px",
                                        fontWeight: 800,
                                        color: "#0b3b66",
                                        overflowWrap: "anywhere",
                                        wordBreak: "break-word",
                                        lineHeight: "1.35",
                                      }}
                                    >
                                      {media?.visitReportComments
                                        ? renderSafeText(
                                            media.visitReportComments,
                                            "N/A"
                                          )
                                        : "N/A"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}

                          {hasDamage && (
                            <div className="mt-3">
                              <div className="detail-label mb-2">Damage Images</div>
                              <UploadedFileList
                                files={media.damageFiles}
                                onOpen={(fileItem) => {
                                  if (fileItem.url) window.open(fileItem.url, "_blank");
                                }}
                              />
                            </div>
                          )}

                          {hasVideo && (
                            <div className="mt-3">
                              <div className="detail-label mb-2">Video</div>
                              <a
                                href={media.videoUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                  color: "#ff8c00",
                                  fontWeight: 600,
                                  textDecoration: "none",
                                }}
                              >
                                Open video
                              </a>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  </div>
)}

        {/* Service Request Query History - Collapsible */}
        <div className="viewDetwrpr sr-query-history-root mb-3">
            <div className="viewDetCardPnlWrpr">
              <div className="card-panel" style={{ margin: 0 }}>
                <div
                  className="section-heading sr-query-history-section-toggle d-flex justify-content-between align-items-center"
                  data-bs-toggle="collapse"
                  data-bs-target="#collapseQueryHistory"
                  aria-expanded="false"
                  aria-controls="collapseQueryHistory"
                >
                  <span>Service Request Query History</span>
                  <i className="bi bi-chevron-down"></i>
                </div>
              </div>
            </div>
            <div
              className="collapse viewDetCardPnlOutr"
              id="collapseQueryHistory"
            >
              <div className="viewDetCardPnlWrpr">
                <div className="card-panel" style={{ paddingTop: 15 }}>
                  {queryHistoryFilter ? (
                    <div
                      className="d-flex justify-content-between align-items-center mb-3"
                      style={{
                        background: "#eef5ff",
                        border: "1px solid #cfe2ff",
                        borderRadius: 6,
                        padding: "8px 12px",
                      }}
                    >
                      <span style={{ fontSize: 13 }}>
                        Showing queries raised by{" "}
                        <strong>
                          {queryHistoryFilter.userName ||
                            queryHistoryFilter.userId ||
                            "selected user"}
                        </strong>
                      </span>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={clearQueryHistoryFilter}
                      >
                        Show all
                      </button>
                    </div>
                  ) : null}
                  {loadingQueries ? (
                    <div className="sr-query-history-loading">
                      <p>Loading queries...</p>
                    </div>
                  ) : (
                    <div className="sr-query-history-list">
                      {visibleQueryHistory.length === 0 ? (
                        <div className="text-muted" style={{ padding: "8px 0" }}>
                          No queries found
                          {queryHistoryFilter ? " for this user." : "."}
                        </div>
                      ) : null}
                      {visibleQueryHistory.map((display, index) => {
                        if (!display || typeof display !== "object") return null;
                        const isPending = display.status === "Pending";
                        const isResponded = display.status === "Responded";
                        const canRespond =
                          isSeUserType() &&
                          isPending &&
                          (String(display.raisedToUserId || "") ===
                            String(getCurrentUserId() ?? "") ||
                            String(display.raisedToUserId || "") ===
                              String(currentUserIdCode ?? ""));
                        const collapseId = `collapseQuery${display.queryId != null ? display.queryId : index}`;
                        const cardHeaderClass = isPending
                          ? "sr-query-history-card-header sr-query-history-card-header--pending"
                          : isResponded
                          ? "sr-query-history-card-header sr-query-history-card-header--responded"
                          : "sr-query-history-card-header sr-query-history-card-header--neutral";
                        const railNodeClass = isResponded
                          ? "sr-query-history-rail-node sr-query-history-rail-node--ok"
                          : isPending
                          ? "sr-query-history-rail-node sr-query-history-rail-node--pending"
                          : "sr-query-history-rail-node sr-query-history-rail-node--neutral";
                        const badgeMode = isPending
                          ? "sr-query-history-badge sr-query-history-badge--pending"
                          : isResponded
                          ? "sr-query-history-badge sr-query-history-badge--responded"
                          : "sr-query-history-badge sr-query-history-badge--neutral";
                        const respDateShown =
                          safeChild(display?.formattedResponseDate) ||
                          (isPending ? safeChild(display?.formattedQueryDate) : "");
                        const respTimeShown =
                          safeChild(display?.formattedResponseTime) ||
                          (isPending ? safeChild(display?.formattedQueryTime) : "");

                        return (
                          <div
                            key={display.queryId != null ? display.queryId : index}
                            className="sr-query-history-item"
                          >
                            <div className="sr-query-history-rail" aria-hidden="true">
                              {index < visibleQueryHistory.length - 1 ? (
                                <div className="sr-query-history-rail-line" />
                              ) : null}
                              <div className={railNodeClass}>
                                <i
                                  className={isResponded ? "bi bi-check" : "bi bi-question-circle"}
                                  aria-hidden="true"
                                />
                              </div>
                            </div>
                            <div className="sr-query-history-card">
                              <div
                                className={cardHeaderClass}
                                data-bs-toggle="collapse"
                                data-bs-target={`#${collapseId}`}
                                aria-expanded="true"
                                aria-controls={collapseId}
                              >
                              {isPending ? (
                                <div className="sr-query-history-header-main">
                                  <div className="sr-query-history-kicker">QUERY RAISED</div>
                                  <div className="sr-query-history-by-line">
                                    <span className="sr-query-history-meta-label sr-query-history-meta-label--upper">BY:</span>{" "}
                                    <span className="sr-query-history-strong-name">
                                      {safeChild(display?.raisedByName) || "Approver"}
                                      {safeChild(display?.raisedByRoleName)
                                        ? ` (${safeChild(display?.raisedByRoleName)})`
                                        : ""}
                                    </span>
                                    <span className="sr-query-history-sep"> | </span>
                                    <span>
                                      {safeChild(display?.raisedToUserName) || ""}
                                      {safeChild(display?.raisedToRoleName)
                                        ? ` (${safeChild(display?.raisedToRoleName)})`
                                        : ""}
                                    </span>
                                    <span className="sr-query-history-sep"> | </span>
                                    <span className="sr-query-history-date">
                                      {safeChild(display?.formattedQueryDate)}
                                    </span>
                                    <span className="sr-query-history-time">
                                      {" "}
                                      @ {safeChild(display?.formattedQueryTime)}
                                    </span>
                                  </div>
                                </div>
                              ) : (
                                <div className="sr-query-history-header-main">
                                  <span className="sr-query-history-meta-label">
                                    Query raised by:
                                  </span>{" "}
                                  <span className="sr-query-history-strong-name">
                                    {safeChild(display?.raisedByName) || "Approver"}
                                    {safeChild(display?.raisedByRoleName)
                                      ? ` (${safeChild(display?.raisedByRoleName)})`
                                      : ""}
                                  </span>
                                  {safeChild(display?.raisedToUserName) ? (
                                    <>
                                      <span className="sr-query-history-sep"> | </span>
                                      <span className="sr-query-history-meta-muted">
                                        {safeChild(display?.raisedToUserName)}
                                        {safeChild(display?.raisedToRoleName)
                                          ? ` (${safeChild(display?.raisedToRoleName)})`
                                          : ""}
                                      </span>
                                    </>
                                  ) : null}
                                  <span className="sr-query-history-sep"> | </span>
                                  <span className="sr-query-history-date">
                                    {safeChild(display?.formattedQueryDate)}
                                  </span>
                                  <span className="sr-query-history-time">
                                    {" "}
                                    @ {safeChild(display?.formattedQueryTime)}
                                  </span>
                                </div>
                              )}
                              <span className={badgeMode}>
                                {safeChild(display?.status) || "Pending"}
                              </span>
                              <i className="bi bi-chevron-down sr-query-history-chevron" aria-hidden="true" />
                            </div>

                            <div className="collapse show" id={collapseId}>
                              <div className="sr-query-history-card-body">
                                <div className="sr-query-history-field-block sr-query-history-field-block--query">
                                  <label
                                    className="sr-query-history-field-label sr-query-history-field-label--query"
                                  >
                                    Query:
                                    <span className="sr-query-history-field-sub">
                                      Query raised to{" "}
                                      <span className="sr-query-history-field-sub-em">
                                        {safeChild(display?.raisedToUserName) ||
                                          safeChild(display?.raisedToRoleName) ||
                                          "Sales Executive"}
                                      </span>
                                      {safeChild(display?.raisedToRoleName)
                                        ? ` (${safeChild(display?.raisedToRoleName)})`
                                        : ""}
                                    </span>
                                  </label>
                                  <textarea
                                    className="form-control sr-query-history-textarea-query"
                                    value={safeChild(display?.queryText)}
                                    readOnly
                                    disabled
                                    rows={3}
                                  />
                                </div>

                                {safeChild(display?.responseText) ? (
                                  <div className="sr-query-history-field-block sr-query-history-field-block--response">
                                    <label
                                      className="sr-query-history-field-label sr-query-history-field-label--response"
                                    >
                                      Response:
                                      <span className="sr-query-history-field-sub">
                                        Responded by{" "}
                                        <span className="sr-query-history-field-sub-em">
                                          {safeChild(display?.respondedByName) || "N/A"}
                                        </span>
                                        {respDateShown ? (
                                          <>
                                            {" | "}
                                            <span className="sr-query-history-date-inline">
                                              {respDateShown}
                                            </span>
                                            {" @ "}
                                            <span className="sr-query-history-time-inline">
                                              {respTimeShown}
                                            </span>
                                          </>
                                        ) : null}
                                      </span>
                                    </label>
                                    <textarea
                                      className="form-control sr-query-history-textarea-response"
                                      value={safeChild(display?.responseText)}
                                      readOnly
                                      disabled
                                      rows={3}
                                    />

                                    {/* Response Attachments (Query Response) */}
                                    {(() => {
                                      const qid =
                                        display?.queryId != null ? Number(display.queryId) : null;
                                      const qno = String(display?.queryNo || "").trim();
                                      if (!qid && !qno) return null;

                                      const isQueryResponse = (a) =>
                                        String(a?.copyFor || "")
                                          .toLowerCase()
                                          .includes("query response");

                                      // Server-side filtered fetch by QueryNo (cached).
                                      if (qno) {
                                        // fire-and-forget (do not await inside render)
                                        if (
                                          !(
                                            ticketAttachmentsByQueryNo &&
                                            Array.isArray(ticketAttachmentsByQueryNo[qno])
                                          ) &&
                                          !(
                                            loadingTicketAttachmentsByQueryNo &&
                                            loadingTicketAttachmentsByQueryNo[qno]
                                          )
                                        ) {
                                          ensureQueryAttachmentsLoaded(qno);
                                        }
                                      }

                                      const source = qno
                                        ? (ticketAttachmentsByQueryNo?.[qno] || [])
                                        : (Array.isArray(ticketAttachments) ? ticketAttachments : []);

                                      const items = source
                                        .filter((a) => isQueryResponse(a))
                                        .filter((a) => !!a?.url);

                                      // While fetching per-QueryNo, show a small placeholder
                                      if (qno && loadingTicketAttachmentsByQueryNo?.[qno] && items.length === 0) {
                                        return (
                                          <div className="sr-query-history-attachments-loading mt-3">
                                            Loading attachments...
                                          </div>
                                        );
                                      }

                                      if (items.length === 0) return null;

                                      return (
                                        <div className="sr-query-history-attachments-section mt-4">
                                          <div className="sr-query-history-attachments-title detail-label mb-2">
                                            Response Attachments
                                          </div>
                                          <UploadedFileList
                                            files={items}
                                            onOpen={(fileItem) => {
                                              if (fileItem.url) {
                                                window.open(fileItem.url, "_blank");
                                              }
                                            }}
                                          />
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div className="sr-query-history-field-block sr-query-history-field-block--response">
                                    <label
                                      className="sr-query-history-field-label sr-query-history-field-label--response"
                                    >
                                      Response:
                                      <span className="sr-query-history-field-sub">
                                        Responded by{" "}
                                        <span className="sr-query-history-field-sub-em">N/A</span>
                                        {(respDateShown || respTimeShown) ? (
                                          <>
                                            {" | "}
                                            <span className="sr-query-history-date-inline">
                                              {respDateShown || safeChild(display?.formattedQueryDate)}
                                            </span>
                                            {" @ "}
                                            <span className="sr-query-history-time-inline">
                                              {respTimeShown || safeChild(display?.formattedQueryTime)}
                                            </span>
                                          </>
                                        ) : null}
                                      </span>
                                    </label>
                                    <textarea
                                      className="form-control sr-query-history-textarea-response"
                                      value="N/A"
                                      readOnly
                                      disabled
                                      rows={3}
                                    />
                                  </div>
                                )}

                                {canRespond ? null : null}
                              </div>
                            </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

      {/* Edit modals removed - View only mode for approvers */}
      {/* SR Checklist Modal removed */}
      <div
        className={`modal fade checklist-modal ${checklistOpen ? "show" : ""}`}
        id="checklistModal"
        ref={checklistModalRef}
        tabIndex="-1"
        aria-labelledby="checklistModalLabel"
        aria-hidden={!checklistOpen}
        style={{
          display: checklistOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content checklist-modal-content">
            <div className="modal-header checklist-modal-header">
              <h5
                className="modal-title checklist-modal-title"
                id="checklistModalLabel"
              >
                SR Checklist
              </h5>
              <button
                type="button"
                className="btn-close checklist-modal-close"
                aria-label="Close"
                onClick={handleChecklistClose}
              ></button>
            </div>
            <div className="modal-body checklist-modal-body">
              {/* Product Information Section */}
              <div className="row g-3 mb-4">
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    Item Category
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control"
                    value={checklistData.category}
                    readOnly
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    Item Name
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control"
                    value={checklistData.productName}
                    readOnly
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    Item Code
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control"
                    value={checklistData.productCode}
                    readOnly
                  />
                </div>
                <div className="col-12 col-sm-6 col-md-3">
                  <label className="form-label checklist-form-label">
                    SR Qty
                  </label>
                  <input
                    type="text"
                    className="form-control checklist-form-control text-end"
                    value={checklistData.quantity}
                    readOnly
                  />
                </div>
              </div>

              {/* SR Lines Section */}
              <div className="checklist-sr-lines-section">
                <div className="checklist-sr-lines-content mt-3">
                  <div className="row g-3">
                    {complaintLines.map((line) => (
                      <React.Fragment key={line.id}>
                        <div className="col-12 col-sm-6 col-md-5">
                          <label className="form-label checklist-form-label">
                            SR Category
                          </label>
                          <select
                            className="form-select checklist-form-control"
                            value={line.type}
                            onChange={(e) =>
                              updateComplaintLine(
                                line.id,
                                "type",
                                e.target.value
                              )
                            }
                          >
                            <option value="">Select SR Category</option>
                            {loadingComplaintTypes ? (
                              <option value="" disabled>
                                Loading...
                              </option>
                            ) : complaintTypes.length === 0 ? (
                              <option value="" disabled>
                                No complaint types available
                              </option>
                            ) : (
                              complaintTypes.map((type, index) => {
                                const typeId =
                                  type.ComplaintTypeId ||
                                  type.complaintTypeId ||
                                  type.id ||
                                  index;
                                const typeName =
                                  type.ComplaintTypeName ||
                                  type.complaintTypeName ||
                                  type.name ||
                                  `Type ${index + 1}`;
                                return (
                                  <option key={typeId} value={String(typeId)}>
                                    {typeName}
                                  </option>
                                );
                              })
                            )}
                          </select>
                        </div>
                        <div className="col-12 col-sm-4 col-md-3">
                          <label className="form-label checklist-form-label">
                            Qty
                          </label>
                          <input
                            type="number"
                            className="form-control checklist-form-control text-end"
                            min="0"
                            value={line.qty}
                            onChange={(e) =>
                              updateComplaintLine(
                                line.id,
                                "qty",
                                e.target.value
                              )
                            }
                          />
                        </div>
                        <div className="col-12 col-sm-12 col-md-2 d-flex gap-2 align-items-end justify-content-end">
                          <button
                            type="button"
                            className="btn btn-sm btn-icon-checklist btn-add-checklist"
                            onClick={addComplaintLine}
                            title="Add Line"
                          >
                            <AddCircleOutlineIcon fontSize="medium" />
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-icon-checklist btn-remove-checklist"
                            onClick={() => removeComplaintLine(line.id)}
                            disabled={complaintLines.length === 1}
                            title="Remove Line"
                          >
                            <DeleteOutlineIcon fontSize="medium" />
                          </button>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer checklist-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary checklist-btn-close"
                onClick={handleChecklistClose}
              >
                <CloseIcon fontSize="small" className="me-1" />
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Raise Query Modal - Bootstrap 5 */}
      {raiseQueryOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={raiseQuerySubmitting ? undefined : handleRaiseQueryClose}
          style={{ zIndex: 1040 }}
        ></div>
      )}
      <div
        className={`modal fade raise-query-modal ${
          raiseQueryOpen ? "show" : ""
        }`}
        id="raiseQueryModal"
        ref={raiseQueryModalRef}
        tabIndex="-1"
        aria-labelledby="raiseQueryModalLabel"
        aria-hidden={!raiseQueryOpen}
        style={{
          display: raiseQueryOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div
            className="modal-content"
            style={{
              position: "relative",
              borderRadius: "12px",
              border: "none",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
            }}
          >
            {raiseQuerySubmitting && (
              <div
                role="status"
                aria-live="polite"
                aria-busy="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.88)",
                  zIndex: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "12px",
                  gap: "14px",
                }}
              >
                <div
                  className="spinner-border text-success"
                  style={{ width: "2.75rem", height: "2.75rem" }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: 600,
                  }}
                >
                  Submitting…
                </span>
              </div>
            )}
            {/* Modal Header */}
            <div
              className="modal-header"
              style={{
                borderBottom: "2px solid #f0f0f0",
                padding: "20px 24px",
                backgroundColor: "#fafafa",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <h5
                className="modal-title"
                id="raiseQueryModalLabel"
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#1f1f1f",
                  margin: 0,
                }}
              >
                Raise Query
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() => !raiseQuerySubmitting && handleRaiseQueryClose()}
                disabled={raiseQuerySubmitting}
                style={{
                  fontSize: "14px",
                  opacity: raiseQuerySubmitting ? 0.35 : 0.6,
                  pointerEvents: raiseQuerySubmitting ? "none" : "auto",
                }}
              ></button>
            </div>

            {/* Modal Body */}
            <div
              className="modal-body"
              style={{
                padding: "24px",
              }}
            >
              <style>
                {`
                  #raiseQueryModal .raise-query-select2-container-left .select2-selection__rendered {
                    text-align: left !important;
                    padding-left: 12px !important;
                  }
                  #raiseQueryModal .raise-query-select2-container-left .select2-selection__placeholder {
                    text-align: left !important;
                  }
                  #raiseQueryModal .select2-container--default .select2-selection--single .select2-selection__rendered {
                    text-align: left !important;
                    padding-left: 12px !important;
                  }
                  #raiseQueryModal .raise-query-select2-dropdown-left .select2-search__field,
                  #raiseQueryModal .raise-query-select2-dropdown-left .select2-results__option {
                    text-align: left !important;
                  }
                `}
              </style>
              {/* Raise Query For Section */}
              <div className="row" style={{ marginBottom: "24px" }}>
                <div className="col-12 col-md-8">
                  <label
                    className="form-label"
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#333",
                      marginBottom: "10px",
                      display: "block",
                      textAlign: "left",
                    }}
                  >
                    Raise Query for
                  </label>
                  <select
                    id="raiseQuerySelect"
                    className="form-select raise-query-select"
                    defaultValue={raiseQueryData.name}
                    disabled={raiseQuerySubmitting}
                    style={{
                      fontSize: "14px",
                      padding: "12px 16px",
                      border: "1px solid #d0d0d0",
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      transition: "all 0.2s",
                      width: "100%",
                    }}
                  >
                    <option value="">Search for name...</option>
                    {nameOptions.map((option, index) => (
                      <option
                        key={option.id || index}
                        value={option.id || option.value || option.name}
                      >
                        {option?.name
                          ? option.name
                          : option?.label || option?.id || option?.value || ""}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Query Text Section */}
              <div>
                <label
                  className="form-label"
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: "10px",
                    display: "block",
                    textAlign: "left",
                  }}
                >
                  Type your query <span style={{ color: "#dc3545" }}>*</span>
                </label>
                <textarea
                  className="form-control"
                  rows="5"
                  value={raiseQueryData.query}
                  disabled={raiseQuerySubmitting}
                  onChange={(e) => {
                    setRaiseQueryData((prev) => ({
                      ...prev,
                      query: e.target.value,
                    }));
                  }}
                  placeholder="Enter your query here..."
                  style={{
                    resize: "vertical",
                    fontSize: "14px",
                    padding: "12px 16px",
                    border: "1px solid #d0d0d0",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    transition: "all 0.2s",
                    lineHeight: "1.5",
                    minHeight: "120px",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#007bff";
                    e.target.style.boxShadow =
                      "0 0 0 0.2rem rgba(0, 123, 255, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d0d0d0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div
              className="modal-footer"
              style={{
                borderTop: "2px solid #f0f0f0",
                padding: "16px 24px",
                backgroundColor: "#fafafa",
                borderRadius: "0 0 12px 12px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                type="button"
                className="btn"
                onClick={handleRaiseQueryClose}
                disabled={raiseQuerySubmitting}
                style={{
                  background: "linear-gradient(135deg, #e53935 0%, #c62828 100%)",
                  border: "none",
                  color: "#ffffff",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(229, 57, 53, 0.4)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: raiseQuerySubmitting ? 0.65 : 1,
                  cursor: raiseQuerySubmitting ? "not-allowed" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (raiseQuerySubmitting) return;
                  e.currentTarget.style.background = "linear-gradient(135deg, #ef5350 0%, #d32f2f 100%)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(229, 57, 53, 0.6)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #e53935 0%, #c62828 100%)";
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(229, 57, 53, 0.4)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <CancelIcon style={{ fontSize: "16px" }} />
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleRaiseQuerySubmit}
                disabled={raiseQuerySubmitting}
                style={{
                  background: "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(46, 125, 50, 0.4)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: raiseQuerySubmitting ? 0.85 : 1,
                  cursor: raiseQuerySubmitting ? "wait" : "pointer",
                }}
                onMouseEnter={(e) => {
                  if (raiseQuerySubmitting) return;
                  e.currentTarget.style.background = "linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)";
                  e.currentTarget.style.boxShadow = "0 6px 20px rgba(46, 125, 50, 0.6)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)";
                  e.currentTarget.style.boxShadow = "0 4px 15px rgba(46, 125, 50, 0.4)";
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                <SendIcon style={{ fontSize: "16px" }} />
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Approve/Reject Modal - Matching Previous SRs Design */}
      {approveRejectOpen && (
        <div
          className="approve-reject-modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            overflow: "auto",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              if (!approveRejectSubmitting) {
                handleApproveRejectClose();
              }
            }
          }}
        >
          <div
            className="approve-reject-modal-container"
            style={{
              position: "relative",
              backgroundColor: "white",
              borderRadius: "8px",
              width: "95%",
              maxWidth: "1400px",
              maxHeight: "90vh",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {approveRejectSubmitting && (
              <div
                role="status"
                aria-live="polite"
                aria-busy="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.88)",
                  zIndex: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "8px",
                  gap: "14px",
                }}
              >
                <div
                  className="spinner-border text-success"
                  style={{ width: "2.75rem", height: "2.75rem" }}
                />
                <span style={{ fontSize: "14px", color: "#333", fontWeight: 600 }}>
                  Submitting…
                </span>
              </div>
            )}
            {/* Modal Header */}
            <div
              className="approve-reject-modal-header"
              style={{
                padding: "20px",
                borderBottom: "1px solid #dee2e6",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px 8px 0 0",
                flexShrink: 0,
              }}
            >
              <h3 className="approve-reject-modal-title" style={{ 
                margin: 0, 
                color: "#333", 
                fontWeight: 600,
              }}>
                {wipCloseFlow
                  ? wipCloseModalTitle
                  : canReopenRejectedSr
                    ? "Reopen Service Request"
                    : isAssignFlowApprover
                      ? "Assign Service Request"
                      : "Approve / Reject Service Request"}
              </h3>
              <button
                type="button"
                onClick={() => !approveRejectSubmitting && handleApproveRejectClose()}
                disabled={approveRejectSubmitting}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6c757d",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  opacity: approveRejectSubmitting ? 0.35 : 1,
                  pointerEvents: approveRejectSubmitting ? "none" : "auto",
                }}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Modal Body */}
            <div
              className="approve-reject-modal-body"
              style={{
                padding: "20px",
                overflowY: "auto",
                overflowX: "hidden",
                flex: "1 1 auto",
                minHeight: 0,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {/* Items Table Section */}
              <div style={{ marginBottom: "24px" }}>
                <div className="table-responsive" style={{ maxHeight: "300px", overflowX: "auto", overflowY: "auto" }}>
                  <table
                    className="table table-bordered"
                    style={{
                      width: "100%",
                      marginBottom: 0,
                      borderCollapse: "separate",
                      borderSpacing: 0,
                      fontSize: "13px",
                    }}
                  >
                    <thead style={{ position: "sticky", top: 0, zIndex: 10 }}>
                      <tr>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "center", minWidth: "60px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>SNo.</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "left", minWidth: "120px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Plant Name</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "left", minWidth: "180px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Item Description</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "60px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Invoice Qty</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "center", minWidth: "50px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>UOM</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "130px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Invoice Unit Price</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "120px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Total Price</th>
                        <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "left", minWidth: "140px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>SR Category</th>
                        {showMaterialComponentDetailsColumn && (
                          <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "center", minWidth: "120px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>
                            Component Details
                          </th>
                        )}
                        {showMaterialSrQtyValueApproveColumns && (
                          <>
                            <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "60px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>SR Qty</th>
                            <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "60px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>SR Value</th>
                            <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "120px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Approve Qty</th>
                            <th style={{ backgroundColor: "#f8f9fa", padding: "12px 10px", fontSize: "13px", fontWeight: 600, color: "#495057", textAlign: "right", minWidth: "130px", whiteSpace: "nowrap", borderBottom: "2px solid #dee2e6" }}>Cal. Approved Value</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {approveData.items.length > 0 ? (
                        approveData.items.map((item, index) => (
                          <tr
                            key={item.id}
                            style={{
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                              transition: "background-color 0.2s",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = "#f0f7ff";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = index % 2 === 0 ? "#ffffff" : "#fafafa";
                            }}
                          >
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "center", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.sno}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.plantName}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.itemDescription}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.invoiceQty}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "center", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.uom}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.invoiceUnitPrice}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                              {item.totalPrice}
                            </td>
                            <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", border: "1px solid #dee2e6", fontWeight: 500 }}>
                              {item.srCategory || "N/A"}
                            </td>
                            {showMaterialComponentDetailsColumn && (
                              <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "center", border: "1px solid #dee2e6" }}>
                                {materialRowShowsComponentDetailsIcon(item) ? (
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 component-details-icon"
                                    title="Component Details"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      const key =
                                        item.materialRowKey ||
                                        item.MaterialRowKey ||
                                        getMaterialRowKey(item, index);
                                      if (item.ComponentDetails || item.componentDetails) {
                                        productDataRef.current[key] = {
                                          ...(productDataRef.current[key] || {}),
                                          ...item,
                                          ComponentDetails:
                                            item.ComponentDetails ?? item.componentDetails,
                                          componentDetails:
                                            item.ComponentDetails ?? item.componentDetails,
                                        };
                                        componentDetailsByRowKeyRef.current[key] =
                                          item.ComponentDetails ?? item.componentDetails;
                                      }
                                      handleComponentDetailsOpen(key);
                                    }}
                                    style={{
                                      cursor: "pointer",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      border: "none",
                                      background: "transparent",
                                    }}
                                  >
                                    <svg
                                      xmlns="http://www.w3.org/2000/svg"
                                      width="20"
                                      height="20"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                      stroke="#1976d2"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    >
                                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                  </button>
                                ) : (
                                  "—"
                                )}
                              </td>
                            )}
                            {showMaterialSrQtyValueApproveColumns && (
                              <>
                                <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                                  {item.srQty}
                                </td>
                                <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                                  {item.srValue}
                                </td>
                                <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                                  {item.approvedQty || "0"}
                                </td>
                                <td style={{ padding: "12px 10px", fontSize: "13px", color: "#333", textAlign: "right", fontWeight: 500, border: "1px solid #dee2e6" }}>
                                  {item.approvedValue}
                                </td>
                              </>
                            )}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={
                              8 +
                              (showMaterialComponentDetailsColumn ? 1 : 0) +
                              (showMaterialSrQtyValueApproveColumns ? 4 : 0)
                            }
                            className="text-center"
                            style={{
                              padding: "40px 16px",
                              color: "#999",
                              fontSize: "14px",
                              border: "1px solid #dee2e6",
                              fontWeight: 500,
                            }}
                          >
                            No items found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Factory intent & settlement + Final approval value (two panels; hidden for WIP Close) */}
              {!wipCloseFlow && (
                <div className="row g-3" style={{ marginBottom: "24px" }}>
                  {(showFactoryIntentSettlementSection ||
                    shouldShowSeApproveRoutingControls(srData) ||
                    isRcaUserType()) && (
                  <div className="col-md-8">
                    <div
                      style={{
                        height: "100%",
                        padding: "16px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                  {showFactoryIntentSettlementSection && (
                    <>
                  <div
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: "#334155",
                      marginBottom: "12px",
                    }}
                  >
                    Factory intent &amp; settlement
                    {isBcmUser() && (
                      <span style={{ fontWeight: 400, fontSize: "14px", color: "#c82735", marginLeft: "8px" }}>
                        {canReopenRejectedSr
                          ? "(Required when you Reopen — same rules as Approve; not required for Reject)"
                          : "(Required when you Approve — not required for Reject)"}
                      </span>
                    )}
                  </div>
                  <div className="row g-3">
                    {(() => {
                      const fiLabel = {
                        fontSize: "13px",
                        fontWeight: 500,
                        color: "#111827",
                        marginBottom: "6px",
                        display: "block",
                      };
                      const fiControl = (editable) => ({
                        width: "100%",
                        fontSize: "15px",
                        fontWeight: 500,
                        color: "#111827",
                        padding: "10px 14px",
                        borderRadius: "6px",
                        border: "1px solid #d1d5db",
                        backgroundColor: editable ? "#ffffff" : "#f3f4f6",
                        boxSizing: "border-box",
                        minHeight: "44px",
                      });
                      const bcm = isBcmUser();
                      return (
                        <>
                          <div className="col-md-4">
                            <label className="form-label mb-0" style={fiLabel}>
                              Factory Intent No.
                              {bcm ? <span style={{ color: "#dc3545" }}> *</span> : ""}
                            </label>
                            <input
                              type="text"
                              className="form-control"
                              value={approveData.factoryIntentNo}
                              onChange={(e) =>
                                setApproveData((prev) => ({ ...prev, factoryIntentNo: e.target.value }))
                              }
                              disabled={!bcm}
                              placeholder="—"
                              style={fiControl(bcm)}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label mb-0" style={fiLabel}>
                              Factory Intent Date
                              {bcm ? <span style={{ color: "#dc3545" }}> *</span> : ""}
                            </label>
                            <input
                              type="date"
                              className="form-control"
                              value={approveData.factoryIntentDate}
                              onChange={(e) =>
                                setApproveData((prev) => ({ ...prev, factoryIntentDate: e.target.value }))
                              }
                              disabled={!bcm}
                              style={fiControl(bcm)}
                            />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label mb-0" style={fiLabel}>
                              Settlement Type
                            </label>
                            <select
                              className="form-select"
                              value={approveData.settlementType}
                              onChange={(e) =>
                                setApproveData((prev) => ({ ...prev, settlementType: e.target.value }))
                              }
                              disabled={!bcm}
                              style={fiControl(bcm)}
                            >
                              <option value="">Select…</option>
                              <option value="Replacement">Replacement</option>
                              <option value="Credit Note">Credit Note</option>
                              <option value="STO/CN Details">STO/CN Details</option>
                              <option value="Replacement and Returnable">Replacement & Returnable</option>
                            </select>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                    </>
                  )}

              {shouldShowSeApproveRoutingControls(srData) && (
                  <div style={{ marginTop: "16px" }}>
                  {approveSeDropdowns.loading ? (
                    <div
                      className="d-flex align-items-center gap-2 py-4 justify-content-center"
                      style={{ minHeight: "120px", color: "#64748b" }}
                    >
                      <div className="spinner-border spinner-border-sm text-primary" role="status" aria-label="Loading">
                        <span className="visually-hidden">Loading</span>
                      </div>
                      <span style={{ fontSize: "13px" }}>Loading routing options…</span>
                    </div>
                  ) : (
                    <div
                      className="position-relative"
                      style={{
                        minHeight:
                          seApproveRoutingRoleCodesLoading || seApproveRoutingUsersLoading ? "168px" : undefined,
                      }}
                    >
                      {(seApproveRoutingRoleCodesLoading || seApproveRoutingUsersLoading) && (
                        <div
                          className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center gap-2"
                          style={{
                            zIndex: 4,
                            backgroundColor: "rgba(248, 250, 252, 0.88)",
                            borderRadius: "8px",
                            backdropFilter: "blur(1px)",
                          }}
                        >
                          <div className="spinner-border text-primary" role="status" style={{ width: "2rem", height: "2rem" }} aria-label="Loading">
                            <span className="visually-hidden">Loading</span>
                          </div>
                          <span style={{ fontSize: "13px", color: "#475569", fontWeight: 500 }}>
                            {seApproveRoutingUsersLoading ? "Loading users…" : "Loading roles…"}
                          </span>
                        </div>
                      )}
                    <div className="row g-3">
                      {(() => {
                        const seLabel = {
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#111827",
                          marginBottom: "6px",
                          display: "block",
                        };
                        const srFlowOptions = getSeApproveSrFlowOptions(srData);
                        const allowedFlowIds = new Set(srFlowOptions.map((o) => o.id));
                        const rawSpec = canonicalSeUserSpecificationType(
                          approveData.seUserSpecificationType || ""
                        );
                        const spec =
                          rawSpec && allowedFlowIds.has(rawSpec) ? rawSpec : "";
                        const roleOptionsFiltered =
                          isSeApproveScopedSpec(spec)
                            ? filterSeApproveRoutingRolesBySrFlow(
                                spec,
                                seApproveRoutingRoleOptions || [],
                                getCanonicalSeApproveRoutingMarketKey(srData)
                              ).map((r) => ({
                                id: r.userTypeCode,
                                text: r.userTypeName || r.userTypeCode,
                              }))
                            : (approveSeDropdowns.userTypes || []).map((ut) => ({
                                id: ut.userTypeCode || ut.UserTypeCode || "",
                                text:
                                  ut.userTypeName ||
                                  ut.UserTypeName ||
                                  ut.userTypeCode ||
                                  ut.UserTypeCode ||
                                  "",
                              }));
                        return (
                          <>
                            <div className="col-12 col-md-6 col-lg-4">
                              <div className="select2-wrapper">
                                <label className="form-label mb-0" style={seLabel}>
                                  SR Flow <span style={{ color: "#dc3545" }}>*</span>
                                </label>
                                <CustomMultiSelect
                                  options={srFlowOptions}
                                  value={spec ? [spec] : []}
                                  onChange={(selected) => {
                                    const last =
                                      Array.isArray(selected) && selected.length
                                        ? selected[selected.length - 1]
                                        : "";
                                    setApproveData((prev) => ({
                                      ...prev,
                                      seUserSpecificationType: canonicalSeUserSpecificationType(last || ""),
                                      sePlantIds: [],
                                      seRdcIds: [],
                                      seNextApproverUserTypeCode: "",
                                      seNextApproverRoutingUserId: "",
                                    }));
                                  }}
                                  placeholder="Select SR Flow"
                                  disabled={
                                    approveSeDropdowns.loading ||
                                    seApproveRoutingRoleCodesLoading ||
                                    seApproveRoutingUsersLoading
                                  }
                                  showSelectAll={false}
                                />
                              </div>
                            </div>
                            <div className="col-12 col-md-6 col-lg-4">
                              <div className="select2-wrapper">
                                <label className="form-label mb-0" style={seLabel}>
                                  Role <span style={{ color: "#dc3545" }}>*</span>
                                </label>
                                <CustomMultiSelect
                                  options={roleOptionsFiltered}
                                  value={
                                    approveData.seNextApproverUserTypeCode
                                      ? [approveData.seNextApproverUserTypeCode]
                                      : []
                                  }
                                  onChange={(selected) => {
                                    const last =
                                      Array.isArray(selected) && selected.length
                                        ? selected[selected.length - 1]
                                        : "";
                                    setApproveData((prev) => ({
                                      ...prev,
                                      seNextApproverUserTypeCode: last || "",
                                      seNextApproverRoutingUserId: "",
                                    }));
                                  }}
                                  placeholder="Select Role"
                                  disabled={
                                    approveSeDropdowns.loading ||
                                    seApproveRoutingRoleCodesLoading ||
                                    seApproveRoutingUsersLoading
                                  }
                                  showSelectAll={false}
                                />
                              </div>
                            </div>
                            {isSeApproveScopedSpec(spec) && (
                              <div className="col-12 col-md-6 col-lg-4">
                                <div className="select2-wrapper">
                                  <label className="form-label mb-0" style={seLabel}>
                                    User <span style={{ color: "#dc3545" }}>*</span>
                                  </label>
                                  <CustomMultiSelect
                                    options={(seApproveRoutingUserOptions || []).map((u) => ({
                                      id: u.userIdCode,
                                      text: u.displayText || u.userIdCode,
                                    }))}
                                    value={
                                      approveData.seNextApproverRoutingUserId
                                        ? [approveData.seNextApproverRoutingUserId]
                                        : []
                                    }
                                    onChange={(selected) => {
                                      const last =
                                        Array.isArray(selected) && selected.length
                                          ? selected[selected.length - 1]
                                          : "";
                                      setApproveData((prev) => ({
                                        ...prev,
                                        seNextApproverRoutingUserId: last || "",
                                      }));
                                    }}
                                    placeholder={
                                      !approveData.seNextApproverUserTypeCode
                                        ? "Select Role first"
                                        : getSeApproveRoutingUserSelectPlaceholder(spec)
                                    }
                                    disabled={
                                      !approveData.seNextApproverUserTypeCode ||
                                      approveSeDropdowns.loading ||
                                      seApproveRoutingRoleCodesLoading ||
                                      seApproveRoutingUsersLoading
                                    }
                                    showSelectAll={false}
                                  />
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    </div>
                  )}
                  </div>
              )}
                  {isRcaUserType() && (
                    <div style={{ marginTop: "16px" }}>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label
                            className="form-label mb-0"
                            style={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#111827",
                              marginBottom: "6px",
                              display: "block",
                            }}
                          >
                            Is RCA Required? <span style={{ color: "#dc3545" }}>*</span>
                          </label>
                          <div className="d-flex gap-3 align-items-center" style={{ minHeight: "44px" }}>
                            {["Yes", "No"].map((opt) => (
                              <label
                                key={opt}
                                className="d-flex align-items-center gap-1 mb-0"
                                style={{ fontSize: "14px", fontWeight: 500, cursor: "pointer" }}
                              >
                                <input
                                  type="radio"
                                  name="approveRcaGiven"
                                  value={opt}
                                  checked={(approveData.rcaGiven || "").trim() === opt}
                                  disabled={approveRejectSubmitting}
                                  onChange={() =>
                                    setApproveData((prev) => ({
                                      ...prev,
                                      rcaGiven: opt,
                                      rcaRemarks: opt === "No" ? "" : prev.rcaRemarks,
                                    }))
                                  }
                                />
                                {opt}
                              </label>
                            ))}
                          </div>
                        </div>
                        {(approveData.rcaGiven || "").trim() === "Yes" && (
                        <div className="col-md-8">
                          <label
                            className="form-label mb-0"
                            style={{
                              fontSize: "13px",
                              fontWeight: 500,
                              color: "#111827",
                              marginBottom: "6px",
                              display: "block",
                            }}
                          >
                            RCA Remarks <span style={{ color: "#dc3545" }}>*</span>
                          </label>
                          <textarea
                            className="form-control"
                            rows={2}
                            value={approveData.rcaRemarks}
                            disabled={approveRejectSubmitting}
                            placeholder="Enter RCA remarks..."
                            onChange={(e) =>
                              setApproveData((prev) => ({ ...prev, rcaRemarks: e.target.value }))
                            }
                            style={{
                              fontSize: "14px",
                              resize: "vertical",
                              minHeight: "44px",
                            }}
                          />
                        </div>
                        )}
                      </div>
                    </div>
                  )}
                    </div>
                  </div>
                  )}
                  <div className="col-md-4">
                    <div
                      style={{
                        height: "100%",
                        padding: "16px",
                        backgroundColor: "#f8fafc",
                        borderRadius: "8px",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "#334155",
                          marginBottom: "12px",
                        }}
                      >
                        Final approval Value
                      </div>
                      <div className="row g-3">
                        <div className="col-md-12">
                      <label className="form-label mb-0" style={{ fontSize: "13px", fontWeight: 500, color: "#111827", marginBottom: "6px", display: "block" }}>
                        Amount
                      </label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="form-control text-end"
                        value={approveData.finalApprovedValue}
                        onChange={(e) => {
                          if (isAssignFlowApprover) return;
                          finalApprovedValueTouchedRef.current = true;
                          const v = e.target.value;
                          if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                            setApproveData((prev) => ({ ...prev, finalApprovedValue: v }));
                          }
                        }}
                        onBlur={() => {
                          if (isAssignFlowApprover) return;
                          const allowZeroFinalApproval = businessVerticalRequiresComponentDetails;
                          setApproveData((prev) => {
                            const raw = (prev.finalApprovedValue || "").trim();
                            const maxTotal = toViewSrMoneyNumber(getFinalApprovalMaxTotal(prev.items));
                            if (!raw) {
                              if (allowZeroFinalApproval) {
                                return { ...prev, finalApprovedValue: "0.00" };
                              }
                              if (maxTotal > 0) {
                                return { ...prev, finalApprovedValue: formatViewSrMoney(maxTotal) };
                              }
                              return { ...prev, finalApprovedValue: "" };
                            }
                            let n = toViewSrMoneyNumber(raw);
                            if (n < 0) n = 0;
                            if (maxTotal > 0 && n > maxTotal) n = maxTotal;
                            if (!allowZeroFinalApproval && n <= 0 && maxTotal > 0) n = maxTotal;
                            if (!allowZeroFinalApproval && n <= 0) {
                              return { ...prev, finalApprovedValue: "" };
                            }
                            return { ...prev, finalApprovedValue: formatViewSrMoney(n) };
                          });
                        }}
                        disabled={approveRejectSubmitting || isAssignFlowApprover}
                        readOnly={isAssignFlowApprover}
                        placeholder="0.00"
                        title={
                          isAssignFlowApprover
                            ? "Final approval Value cannot be changed for Assign flow"
                            : approveRejectOpen
                            ? businessVerticalRequiresComponentDetails
                              ? `Allowed 0 to Total Price ${formatViewSrMoney(getFinalApprovalMaxTotal(approveData.items))}`
                              : `Must be greater than 0 and cannot exceed row total ${formatViewSrMoney(getFinalApprovalMaxTotal(approveData.items))}`
                            : undefined
                        }
                        style={{
                          width: "100%",
                          fontSize: "15px",
                          fontWeight: 500,
                          color: "#111827",
                          padding: "10px 14px",
                          borderRadius: "6px",
                          border: "1px solid #d1d5db",
                          backgroundColor: isAssignFlowApprover ? "#e9ecef" : "#ffffff",
                          boxSizing: "border-box",
                          minHeight: "44px",
                          textAlign: "right",
                          cursor: isAssignFlowApprover ? "not-allowed" : undefined,
                        }}
                      />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* STO/CN Details (Close only) — SRN / Credit Note / Replacement */}
              {wipCloseFlow && (
                <div
                  style={{
                    marginBottom: "24px",
                    padding: "16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#334155",
                      marginBottom: "12px",
                    }}
                  >
                    STO/CN Details
                  </div>

                  <div className="row g-3">
                    {(() => {
                      const item = (approveData.items || [])[0];
                      const stoCnValue = String(item?.stoCnValue || "").trim();
                      const isCloseLevel2 = isValidateSrLabelUserType;
                      const level1Locked = isCloseLevel2; // Level 2: view Level-1 keys; edit Qty + Final Closure
                      // HO Commercial (UT0000013) / Exp. Comm. Controller: Approval Amount is read-only (Final Approved Value).
                      const approvalAmountLocked = isCloseLevel2;
                      const fiLabel = { fontWeight: 600 };
                      const ctrl = (editable) => ({
                        backgroundColor: editable ? "#ffffff" : "#e9ecef",
                        cursor: editable ? undefined : "not-allowed",
                      });
                      if (!item) {
                        return (
                          <div className="col-12">
                            <div className="text-muted" style={{ fontSize: "13px" }}>
                              No items found.
                            </div>
                          </div>
                        );
                      }
                      const clearSharedOnTypeChange = (v) => {
                        setApproveData((prev) => ({
                          ...prev,
                          items: (prev.items || []).map((it) => {
                            const aq = String(it?.approvedQty ?? "").trim();
                            const av = String(
                              it?.approvedValue ?? it?.apprvValue ?? ""
                            ).trim();
                            const fav = String(prev.finalApprovedValue || "").trim();
                            const qtyAutofill =
                              aq && aq !== "0" ? aq : it?.qty ?? "";
                            // Approval Amount: prefer Final Approved Value, else Cal. Approved.
                            const amountAutofill =
                              fav && fav !== "0" && fav !== "0.00"
                                ? fav
                                : av && av !== "0" && av !== "0.00"
                                  ? av
                                  : it?.amount ?? "";
                            return {
                              ...it,
                              stoCnValue: v,
                              referenceNo: "",
                              srnOrderNo: "",
                              reBillOrderNo: "",
                              isReturnable: v === "Replacement",
                              isNonReturnable: false,
                              // Qty from Approved Qty; Approval Amount from Final Approved Value.
                              qty: qtyAutofill,
                              amount: amountAutofill,
                              finalClosureQty: qtyAutofill,
                              finalClosureAmount: amountAutofill,
                            };
                          }),
                        }));
                      };
                      return (
                        <div className="col-12">
                          <div
                            style={{
                              background: "#ffffff",
                              border: "1px solid #e5e7eb",
                              borderRadius: "10px",
                              padding: "14px",
                            }}
                          >
                            <div className="row g-3">
                              <div className="col-md-3">
                                <label className="form-label" style={fiLabel}>
                                  STO/CN Details <span style={{ color: "#dc3545" }}>*</span>
                                </label>
                                <select
                                  className="form-select"
                                  value={stoCnValue}
                                  disabled={level1Locked || approveRejectSubmitting}
                                  onChange={(e) => clearSharedOnTypeChange(e.target.value)}
                                  style={ctrl(!level1Locked)}
                                >
                                  <option value="">Select</option>
                                  <option value="SRN">SRN</option>
                                  <option value="Credit Note">Credit Note</option>
                                  <option value="Replacement">Replacement</option>
                                </select>
                              </div>

                              {(stoCnValue === "SRN" || stoCnValue === "Credit Note") && (
                                <>
                                  <div className="col-md-3">
                                    <label className="form-label" style={fiLabel}>
                                      Reference No. <span style={{ color: "#dc3545" }}>*</span>
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={item?.referenceNo || ""}
                                      disabled={level1Locked || approveRejectSubmitting}
                                      onChange={(e) =>
                                        updateStoCnFieldForAll("referenceNo", e.target.value)
                                      }
                                      placeholder="Enter Reference No."
                                      style={ctrl(!level1Locked)}
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <label className="form-label" style={fiLabel}>
                                      Quantity (Qty) <span style={{ color: "#dc3545" }}>*</span>
                                    </label>
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      className="form-control text-end"
                                      value={item?.qty ?? ""}
                                      disabled={approveRejectSubmitting}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === "" || /^\d*$/.test(v)) {
                                          updateStoCnFieldForAll("qty", v);
                                        }
                                      }}
                                      placeholder="0"
                                      style={ctrl(true)}
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <label className="form-label" style={fiLabel}>
                                      Approval Amount <span style={{ color: "#dc3545" }}>*</span>
                                    </label>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="form-control text-end"
                                      value={item?.amount ?? ""}
                                      disabled={approvalAmountLocked || approveRejectSubmitting}
                                      readOnly={approvalAmountLocked}
                                      onChange={(e) => {
                                        if (approvalAmountLocked) return;
                                        const v = e.target.value;
                                        if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                                          updateStoCnFieldForAll("amount", v);
                                        }
                                      }}
                                      placeholder="0.00"
                                      style={ctrl(!approvalAmountLocked)}
                                    />
                                  </div>
                                </>
                              )}

                              {stoCnValue === "Replacement" && (
                                <>
                                  <div className="col-md-3">
                                    <label className="form-label" style={fiLabel}>
                                      Replacement Type <span style={{ color: "#dc3545" }}>*</span>
                                    </label>
                                    <div className="d-flex gap-4 flex-wrap" style={{ marginTop: "6px" }}>
                                      <div className="form-check form-check-inline">
                                        <input
                                          id="replacementTypeReturnableAll"
                                          type="radio"
                                          name="replacementTypeAll"
                                          className="form-check-input"
                                          checked={!!item?.isReturnable}
                                          disabled={level1Locked || approveRejectSubmitting}
                                          onChange={(e) => {
                                            if (!e.target.checked) return;
                                            updateStoCnFieldForAll("isReturnable", true);
                                            updateStoCnFieldForAll("isNonReturnable", false);
                                            updateStoCnFieldForAll("srnOrderNo", item?.srnOrderNo || "");
                                          }}
                                        />
                                        <label className="form-check-label" htmlFor="replacementTypeReturnableAll">
                                          Returnable
                                        </label>
                                      </div>
                                      <div className="form-check form-check-inline">
                                        <input
                                          id="replacementTypeNonReturnableAll"
                                          type="radio"
                                          name="replacementTypeAll"
                                          className="form-check-input"
                                          checked={!!item?.isNonReturnable}
                                          disabled={level1Locked || approveRejectSubmitting}
                                          onChange={(e) => {
                                            if (!e.target.checked) return;
                                            updateStoCnFieldForAll("isReturnable", false);
                                            updateStoCnFieldForAll("isNonReturnable", true);
                                            updateStoCnFieldForAll("srnOrderNo", "");
                                          }}
                                        />
                                        <label className="form-check-label" htmlFor="replacementTypeNonReturnableAll">
                                          Non-Returnable
                                        </label>
                                      </div>
                                    </div>
                                  </div>

                                  {!!item?.isReturnable && (
                                    <div className="col-md-3">
                                      <label className="form-label" style={fiLabel}>
                                        SRN Order No. <span style={{ color: "#dc3545" }}>*</span>
                                      </label>
                                      <input
                                        type="text"
                                        className="form-control"
                                        value={item?.srnOrderNo || ""}
                                        disabled={level1Locked || approveRejectSubmitting}
                                        onChange={(e) =>
                                          updateStoCnFieldForAll("srnOrderNo", e.target.value)
                                        }
                                        placeholder="Enter SRN Order No."
                                        style={ctrl(!level1Locked)}
                                      />
                                    </div>
                                  )}

                                  {(!!item?.isReturnable || !!item?.isNonReturnable) && (
                                    <>
                                      <div className="col-md-3">
                                        <label className="form-label" style={fiLabel}>
                                          Re-Bill Order No. <span style={{ color: "#dc3545" }}>*</span>
                                        </label>
                                        <input
                                          type="text"
                                          className="form-control"
                                          value={item?.reBillOrderNo || ""}
                                          disabled={level1Locked || approveRejectSubmitting}
                                          onChange={(e) =>
                                            updateStoCnFieldForAll("reBillOrderNo", e.target.value)
                                          }
                                          placeholder="Enter Re-Bill Order No."
                                          style={ctrl(!level1Locked)}
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="form-label" style={fiLabel}>
                                          Quantity (Qty) <span style={{ color: "#dc3545" }}>*</span>
                                        </label>
                                        <input
                                          type="text"
                                          inputMode="numeric"
                                          className="form-control text-end"
                                          value={item?.qty ?? ""}
                                          disabled={approveRejectSubmitting}
                                          onChange={(e) => {
                                            const v = e.target.value;
                                            if (v === "" || /^\d*$/.test(v)) {
                                              updateStoCnFieldForAll("qty", v);
                                            }
                                          }}
                                          placeholder="0"
                                          style={ctrl(true)}
                                        />
                                      </div>
                                      <div className="col-md-3">
                                        <label className="form-label" style={fiLabel}>
                                          Approval Amount <span style={{ color: "#dc3545" }}>*</span>
                                        </label>
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          className="form-control text-end"
                                          value={item?.amount ?? ""}
                                          disabled={approvalAmountLocked || approveRejectSubmitting}
                                          readOnly={approvalAmountLocked}
                                          onChange={(e) => {
                                            if (approvalAmountLocked) return;
                                            const v = e.target.value;
                                            if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                                              updateStoCnFieldForAll("amount", v);
                                            }
                                          }}
                                          placeholder="0.00"
                                          style={ctrl(!approvalAmountLocked)}
                                        />
                                      </div>
                                    </>
                                  )}
                                </>
                              )}

                              {isCloseLevel2 && stoCnValue && (
                                <>
                                  <div className="col-md-3">
                                    <label className="form-label" style={fiLabel}>
                                      Final Closure Reference No.{" "}
                                      <span style={{ color: "#dc3545" }}>*</span>
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      value={item?.finalClosureReferenceNo || ""}
                                      disabled={approveRejectSubmitting}
                                      onChange={(e) =>
                                        updateStoCnFieldForAll(
                                          "finalClosureReferenceNo",
                                          e.target.value
                                        )
                                      }
                                      placeholder="Enter Final Closure Reference No."
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <label className="form-label" style={fiLabel}>
                                      Final Closure Quantity (Qty){" "}
                                      <span style={{ color: "#dc3545" }}>*</span>
                                    </label>
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      className="form-control text-end"
                                      value={item?.finalClosureQty ?? ""}
                                      disabled={approveRejectSubmitting}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === "" || /^\d*$/.test(v)) {
                                          updateStoCnFieldForAll("finalClosureQty", v);
                                        }
                                      }}
                                      placeholder="0"
                                      style={ctrl(true)}
                                    />
                                  </div>
                                  <div className="col-md-3">
                                    <label className="form-label" style={fiLabel}>
                                      Final Closure Amount{" "}
                                      <span style={{ color: "#dc3545" }}>*</span>
                                    </label>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="form-control text-end"
                                      value={item?.finalClosureAmount ?? ""}
                                      disabled={approveRejectSubmitting}
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (v === "" || /^\d*\.?\d{0,2}$/.test(v)) {
                                          updateStoCnFieldForAll("finalClosureAmount", v);
                                        }
                                      }}
                                      placeholder="0.00"
                                      style={ctrl(true)}
                                    />
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* CH Kitty (UT0000009) — Special Case / Reopen; same Yes/No pattern as RCA on Close */}
              {(shouldEditChKittyField(srData, canReopenRejectedSr) ||
                shouldShowChKittyReadonly(srData, approveData.items) ||
                (wipCloseFlow &&
                  Boolean(resolveDeductFromChKitty(srData, approveData.items)))) && (
                <div
                  style={{
                    marginTop: "20px",
                    marginBottom: "8px",
                    padding: "16px",
                    backgroundColor: "#f8fafc",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                  }}
                >
                  {(() => {
                    const editable = shouldEditChKittyField(srData, canReopenRejectedSr);
                    const kittyVal = (approveData.deductFromChKitty || "").trim();
                    const labelStyle = {
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#111827",
                      marginBottom: "6px",
                      display: "block",
                    };
                    return (
                      <div className="row g-3">
                        <div className="col-md-12">
                          <label className="form-label mb-0" style={labelStyle}>
                            Money to be deducted from CH Kitty?
                            {editable && <span style={{ color: "#dc3545" }}> *</span>}
                          </label>
                          <div
                            className="d-flex gap-3 align-items-center"
                            style={{ minHeight: "44px" }}
                          >
                            {["Yes", "No"].map((opt) => (
                              <label
                                key={opt}
                                className="d-flex align-items-center gap-1 mb-0"
                                style={{
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  cursor: editable && !approveRejectSubmitting ? "pointer" : "default",
                                }}
                              >
                                <input
                                  type="radio"
                                  name="approveDeductFromChKitty"
                                  value={opt}
                                  checked={kittyVal === opt}
                                  disabled={!editable || approveRejectSubmitting}
                                  readOnly={!editable}
                                  onChange={() => {
                                    if (!editable) return;
                                    setApproveData((prev) => ({
                                      ...prev,
                                      deductFromChKitty: opt,
                                    }));
                                  }}
                                />
                                {opt}
                              </label>
                            ))}
                            {!editable && !kittyVal && (
                              <span style={{ fontSize: "14px", color: "#6b7280" }}>—</span>
                            )}
                          </div>
                          {editable && kittyVal === "No" && (
                            <div
                              style={{
                                marginTop: "8px",
                                fontSize: "12px",
                                color: "#64748b",
                              }}
                            >
                              Remarks are mandatory when No is selected.
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Remarks Section */}
              <div style={{ marginTop: "24px" }}>
                <label
                  className="form-label"
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#333",
                    marginBottom: "10px",
                    display: "block",
                    textAlign: "left",
                  }}
                >
                  Remarks
                  {(() => {
                    const kittyVal = (approveData.deductFromChKitty || "").trim();
                    const chEdit = shouldEditChKittyField(srData, canReopenRejectedSr);
                    const remarksRequired = !(chEdit && kittyVal === "Yes");
                    return remarksRequired ? (
                      <span
                        style={{
                          color: "#dc3545",
                          fontWeight: 600,
                          fontSize: "14px",
                          marginLeft: "4px",
                        }}
                      >
                        *
                      </span>
                    ) : null;
                  })()}
                </label>
                <textarea
                  className="form-control"
                  rows="3"
                  required={!(
                    shouldEditChKittyField(srData, canReopenRejectedSr) &&
                    (approveData.deductFromChKitty || "").trim() === "Yes"
                  )}
                  aria-required={
                    !(
                      shouldEditChKittyField(srData, canReopenRejectedSr) &&
                      (approveData.deductFromChKitty || "").trim() === "Yes"
                    )
                  }
                  value={approveData.remarks}
                  onChange={(e) =>
                    setApproveData((prev) => ({
                      ...prev,
                      remarks: e.target.value,
                    }))
                  }
                  placeholder="Enter your remarks or comments here..."
                  style={{
                    resize: "vertical",
                    fontSize: "14px",
                    padding: "12px 16px",
                    border: "1px solid #d0d0d0",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                    transition: "all 0.2s",
                    lineHeight: "1.5",
                    minHeight: "100px",
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = "#007bff";
                    e.target.style.boxShadow =
                      "0 0 0 0.2rem rgba(0, 123, 255, 0.1)";
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = "#d0d0d0";
                    e.target.style.boxShadow = "none";
                  }}
                />
              </div>

              {(isZonalProductSpecialistUserType() ||
                shouldShowDomesticProductQualityVisitReadonly(
                  srData,
                  approveData.items
                )) && (
                <div style={{ marginTop: "20px" }}>
                  {(() => {
                    const visitQuestionsReadonly =
                      shouldShowDomesticProductQualityVisitReadonly(
                        srData,
                        approveData.items
                      );
                    const visitControlsDisabled =
                      approveRejectSubmitting || visitQuestionsReadonly;
                    const visitLabel = {
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#111827",
                      marginBottom: "6px",
                      display: "block",
                    };
                    const visitControl = {
                      width: "100%",
                      fontSize: "15px",
                      fontWeight: 500,
                      color: "#111827",
                      padding: "10px 14px",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      backgroundColor: visitQuestionsReadonly ? "#e9ecef" : "#ffffff",
                      boxSizing: "border-box",
                      minHeight: "44px",
                      cursor: visitQuestionsReadonly ? "not-allowed" : undefined,
                    };
                    const visitRequired = (approveData.physicalVisitRequired || "").trim();
                    return (
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label mb-0" style={visitLabel}>
                            If a physical visit is required by the technical person to the complaint site?
                            {!visitQuestionsReadonly && (
                              <span style={{ color: "#dc3545" }}> *</span>
                            )}
                          </label>
                          <div
                            className="d-flex gap-3 align-items-center"
                            style={{ minHeight: "44px" }}
                          >
                            {["Yes", "No"].map((opt) => (
                              <label
                                key={opt}
                                className="d-flex align-items-center gap-1 mb-0"
                                style={{
                                  fontSize: "14px",
                                  fontWeight: 500,
                                  cursor: visitControlsDisabled ? "default" : "pointer",
                                }}
                              >
                                <input
                                  type="radio"
                                  name="approvePhysicalVisitRequired"
                                  value={opt}
                                  checked={visitRequired === opt}
                                  disabled={visitControlsDisabled}
                                  readOnly={visitQuestionsReadonly}
                                  onChange={() => {
                                    if (visitQuestionsReadonly) return;
                                    setApproveData((prev) => ({
                                      ...prev,
                                      physicalVisitRequired: opt,
                                      siteVisitTraveler:
                                        opt === "Yes" ? prev.siteVisitTraveler : "",
                                      technicalVisitType:
                                        opt === "Yes" ? prev.technicalVisitType : "",
                                    }));
                                  }}
                                />
                                {opt}
                              </label>
                            ))}
                            {visitQuestionsReadonly && !visitRequired && (
                              <span style={{ fontSize: "14px", color: "#6b7280" }}>—</span>
                            )}
                          </div>
                        </div>
                        {visitRequired === "Yes" && (
                          <>
                            <div className="col-md-4">
                              <label className="form-label mb-0" style={visitLabel}>
                                Who would be traveling to the site?
                                {!visitQuestionsReadonly && (
                                  <span style={{ color: "#dc3545" }}> *</span>
                                )}
                              </label>
                              <select
                                className="form-select"
                                value={approveData.siteVisitTraveler}
                                disabled={visitControlsDisabled}
                                onChange={(e) => {
                                  if (visitQuestionsReadonly) return;
                                  setApproveData((prev) => ({
                                    ...prev,
                                    siteVisitTraveler: e.target.value,
                                  }));
                                }}
                                style={visitControl}
                              >
                                <option value="">Select…</option>
                                <option value="Product Specialist">Product Specialist</option>
                                <option value="Plant QC Manager">Plant QC Manager</option>
                              </select>
                            </div>
                            <div className="col-md-4">
                              <label className="form-label mb-0" style={visitLabel}>
                                Is it a local visit or outstation visit by the technical person?
                                {!visitQuestionsReadonly && (
                                  <span style={{ color: "#dc3545" }}> *</span>
                                )}
                              </label>
                              <select
                                className="form-select"
                                value={approveData.technicalVisitType}
                                disabled={visitControlsDisabled}
                                onChange={(e) => {
                                  if (visitQuestionsReadonly) return;
                                  setApproveData((prev) => ({
                                    ...prev,
                                    technicalVisitType: e.target.value,
                                  }));
                                }}
                                style={visitControl}
                              >
                                <option value="">Select…</option>
                                <option value="Local Visit">Local Visit</option>
                                <option value="Outstation">Outstation</option>
                              </select>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {shouldShowDomesticProductQualityRcaReadonly(
                srData,
                approveData.items
              ) && (
                <div style={{ marginTop: "20px" }}>
                  {(() => {
                    const rcaGivenVal = (approveData.rcaGiven || "").trim();
                    const rcaLabel = {
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#111827",
                      marginBottom: "6px",
                      display: "block",
                    };
                    return (
                      <div className="row g-3">
                        <div className="col-md-4">
                          <label className="form-label mb-0" style={rcaLabel}>
                            Is RCA Required?
                          </label>
                          <div
                            className="d-flex gap-3 align-items-center"
                            style={{ minHeight: "44px" }}
                          >
                            {["Yes", "No"].map((opt) => (
                              <label
                                key={opt}
                                className="d-flex align-items-center gap-1 mb-0"
                                style={{ fontSize: "14px", fontWeight: 500 }}
                              >
                                <input
                                  type="radio"
                                  name="approveRcaGivenReadonly"
                                  value={opt}
                                  checked={rcaGivenVal === opt}
                                  disabled
                                  readOnly
                                />
                                {opt}
                              </label>
                            ))}
                            {!rcaGivenVal && (
                              <span style={{ fontSize: "14px", color: "#6b7280" }}>—</span>
                            )}
                          </div>
                        </div>
                        {rcaGivenVal === "Yes" && (
                          <div className="col-md-8">
                            <label className="form-label mb-0" style={rcaLabel}>
                              RCA Remarks
                            </label>
                            <textarea
                              className="form-control"
                              rows={2}
                              readOnly
                              disabled
                              value={approveData.rcaRemarks || ""}
                              style={{
                                fontSize: "14px",
                                resize: "vertical",
                                minHeight: "44px",
                                backgroundColor: "#e9ecef",
                                cursor: "not-allowed",
                              }}
                            />
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div
              className="approve-reject-modal-footer"
              style={{
                borderTop: "1px solid #dee2e6",
                padding: "20px",
                backgroundColor: "#f8f9fa",
                borderRadius: "0 0 8px 8px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                flexShrink: 0,
              }}
            >
              {wipCloseFlow ? (
                <button
                  type="button"
                  className="btn"
                  onClick={handleCloseWipSubmit}
                  disabled={approveRejectSubmitting}
                  style={{
                    background: "linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)",
                    border: "none",
                    color: "white",
                    fontWeight: 600,
                    letterSpacing: "0.5px",
                    fontSize: "13px",
                    boxShadow: "0 4px 15px rgba(13, 110, 253, 0.45)",
                    borderRadius: "6px",
                    textTransform: "uppercase",
                    padding: "10px 18px",
                    transition: "all 0.3s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    opacity: approveRejectSubmitting ? 0.85 : 1,
                    cursor: approveRejectSubmitting ? "wait" : "pointer",
                  }}
                  onMouseEnter={(e) => {
                    if (approveRejectSubmitting) return;
                    e.currentTarget.style.boxShadow = "0 6px 20px rgba(13, 110, 253, 0.6)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = "0 4px 15px rgba(13, 110, 253, 0.45)";
                    e.currentTarget.style.transform = "translateY(0)";
                  }}
                >
                  <HighlightOffIcon style={{ fontSize: "18px" }} />
                  {wipCloseSubmitButtonLabel}
                </button>
              ) : (
                <>
                  {!canReopenRejectedSr && !isAssignFlowApprover && (
                    <button
                      type="button"
                      className="btn"
                      onClick={handleRejectSubmit}
                      disabled={approveRejectSubmitting}
                      style={{
                        background: "linear-gradient(135deg, #dc3545 0%, #c82333 100%)",
                        border: "none",
                        color: "white",
                        fontWeight: 600,
                        letterSpacing: "0.5px",
                        fontSize: "13px",
                        boxShadow: "0 4px 15px rgba(220, 53, 69, 0.5)",
                        borderRadius: "6px",
                        textTransform: "uppercase",
                        padding: "10px 18px",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        opacity: approveRejectSubmitting ? 0.85 : 1,
                        cursor: approveRejectSubmitting ? "wait" : "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (approveRejectSubmitting) return;
                        e.currentTarget.style.background = "linear-gradient(135deg, #dc3545 0%, #c82333 100%)";
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(220, 53, 69, 0.7)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "linear-gradient(135deg, #dc3545 0%, #c82333 100%)";
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(220, 53, 69, 0.5)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <CloseIcon style={{ fontSize: "18px" }} />
                      Reject
                    </button>
                  )}
                  {canReopenRejectedSr ? (
                    <button
                      type="button"
                      className="btn"
                      onClick={handleReopenSubmit}
                      disabled={approveRejectSubmitting}
                      style={{
                        background: "linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)",
                        border: "none",
                        color: "white",
                        fontWeight: 600,
                        letterSpacing: "0.5px",
                        fontSize: "13px",
                        boxShadow: "0 4px 15px rgba(13, 110, 253, 0.45)",
                        borderRadius: "6px",
                        textTransform: "uppercase",
                        padding: "10px 18px",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        opacity: approveRejectSubmitting ? 0.85 : 1,
                        cursor: approveRejectSubmitting ? "wait" : "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (approveRejectSubmitting) return;
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(13, 110, 253, 0.6)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(13, 110, 253, 0.45)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <RestartAltIcon style={{ fontSize: "18px" }} />
                      Reopen
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn"
                      onClick={handleApproveSubmit}
                      disabled={approveRejectSubmitting}
                      style={{
                        background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                        border: "none",
                        color: "white",
                        fontWeight: 600,
                        letterSpacing: "0.5px",
                        fontSize: "13px",
                        boxShadow: "0 4px 15px rgba(40, 167, 69, 0.5)",
                        borderRadius: "6px",
                        textTransform: "uppercase",
                        padding: "10px 18px",
                        transition: "all 0.3s ease",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        opacity: approveRejectSubmitting ? 0.85 : 1,
                        cursor: approveRejectSubmitting ? "wait" : "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (approveRejectSubmitting) return;
                        e.currentTarget.style.background = "linear-gradient(135deg, #28a745 0%, #20c997 100%)";
                        e.currentTarget.style.boxShadow = "0 6px 20px rgba(40, 167, 69, 0.7)";
                        e.currentTarget.style.transform = "translateY(-2px)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "linear-gradient(135deg, #28a745 0%, #20c997 100%)";
                        e.currentTarget.style.boxShadow = "0 4px 15px rgba(40, 167, 69, 0.5)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }}
                    >
                      <CheckIcon style={{ fontSize: "18px" }} />
                      {isAssignFlowApprover ? "Assign" : "Approve"}
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approve Modal - Bootstrap 5 */}
      {approveOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={handleApproveClose}
          style={{ zIndex: 1040 }}
        ></div>
      )}
      <div
        className={`modal fade raise-query-modal ${approveOpen ? "show" : ""}`}
        id="approveModal"
        ref={approveModalRef}
        tabIndex="-1"
        aria-labelledby="approveModalLabel"
        aria-hidden={!approveOpen}
        style={{
          display: approveOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content checklist-modal-content">
            <div className="modal-header checklist-modal-header">
              <h5
                className="modal-title checklist-modal-title"
                id="approveModalLabel"
                style={{ fontSize: "16px" }}
              >
                Approve
              </h5>
              <button
                type="button"
                className="btn-close checklist-modal-close"
                aria-label="Close"
                onClick={handleApproveClose}
              ></button>
            </div>
            <div className="modal-body checklist-modal-body">
              <div className="table-responsive">
                <table
                  className="table table-bordered complaint-table"
                  style={{ width: "100%" }}
                >
                  <thead>
                    <tr>
                      <th>Item Category</th>
                      <th>Comp Qty</th>
                      <th>Comp Value</th>
                      <th>Change Value</th>
                    </tr>
                  </thead>
                  <tbody style={{ fontSize: "14px" }}>
                    {approveData.items.length > 0 ? (
                      approveData.items.map((item) => (
                        <tr
                          key={item.id}
                          style={{ backgroundColor: "transparent" }}
                        >
                          <td style={{ textAlign: "left" }}>
                            {item.itemCategory}
                          </td>
                          <td className="text-end dt-numeric-col">
                            {item.compQty}
                          </td>
                          <td className="text-end dt-numeric-col">
                            {item.compValue}
                          </td>
                          <td>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={item.changeValue}
                              onChange={(e) =>
                                handleChangeValueUpdate(item.id, e.target.value)
                              }
                              placeholder="Enter value"
                              style={{ textAlign: "right" }}
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr style={{ backgroundColor: "transparent" }}>
                        <td colSpan="4" className="text-center">
                          No items found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="mt-4">
                <label
                  className="form-label checklist-form-label"
                  style={{
                    width: "100%",
                    textAlign: "left",
                    display: "block",
                  }}
                >
                  Remarks
                </label>
                <textarea
                  className="form-control checklist-form-control"
                  rows="5"
                  value={approveData.remarks}
                  onChange={(e) =>
                    setApproveData((prev) => ({
                      ...prev,
                      remarks: e.target.value,
                    }))
                  }
                  placeholder="Enter remarks here..."
                  style={{
                    resize: "vertical",
                    minHeight: "120px",
                  }}
                />
              </div>
            </div>
            <div
              className="modal-footer checklist-modal-footer"
              style={{
                backgroundColor: "transparent",
                borderTop: "none",
                paddingTop: "0",
              }}
            >
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleApproveClose}
                style={{
                  marginRight: "8px",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => {
                  // Add submit logic here
                  console.log("Approve Submit clicked", approveData);
                  handleApproveClose();
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Product Details Modal Backdrop */}
      {productDetailsOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={handleProductDetailsClose}
          style={{ zIndex: 1040 }}
        ></div>
      )}

      {/* Product Details Modal - Bootstrap 5 (same layout as Raise SR, read-only) */}
      <div
        className={`modal fade checklist-modal ${productDetailsOpen ? "show" : ""}`}
        id="productDetailsModal"
        ref={productDetailsModalRef}
        tabIndex="-1"
        aria-labelledby="productDetailsModalLabel"
        aria-hidden={!productDetailsOpen}
        style={{
          display: productDetailsOpen ? "block" : "none",
          zIndex: 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "800px" }}>
          <div className="modal-content checklist-modal-content">
            <div className="modal-header checklist-modal-header">
              <h5
                className="modal-title checklist-modal-title"
                id="productDetailsModalLabel"
              >
                Product Details
              </h5>
              <button
                type="button"
                className="btn-close-checklist checklist-modal-close"
                aria-label="Close"
                onClick={handleProductDetailsClose}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon style={{ fontSize: "24px", color: "#666" }} />
              </button>
            </div>
            <div className="modal-body checklist-modal-body" style={{ padding: "1.5rem" }}>
              {selectedProductData && (
                <>
                  {(() => {
                    const getValue = (key, altKeys = []) => {
                      const value = selectedProductData[key] ||
                        altKeys.map((k) => selectedProductData[k]).find((v) => v) ||
                        "";
                      return value || "";
                    };
                    return (
                      <>
                        {/* Row 1 */}
                        <div className="row g-3 mb-3">
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Code
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductCode", ["productCode", "Productcode", "PRODUCTID"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Name
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductName", ["productName", "Productname", "MaterialDescription", "materialDescription"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Group
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductCategory", ["productCategory", "Productcategory"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                        </div>
                        {/* Row 2 */}
                        <div className="row g-3 mb-3">
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Design Number
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("DesignNo", ["DesignNumber", "designNo", "Designnumber"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Brand
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductBrand", ["productBrand", "Productbrand"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Finish
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductFinish", ["productFinish", "Productfinish"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                        </div>
                        {/* Row 3 */}
                        <div className="row g-3 mb-3">
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Thickness (mm)
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductThickness", ["productThickness", "Productthickness"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Size (mm)
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("Size", ["size"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                          <div className="col-12 col-sm-6 col-md-4">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Material Group Code
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("MaterialGroupCode", ["materialGroupCode", "MaterialGroup", "materialGroup"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                        </div>
                        {/* Row 4 - Full width for Product Description */}
                        <div className="row g-3">
                          <div className="col-12">
                            <label className="form-label checklist-form-label component-details-label" style={{ textAlign: "left", fontWeight: "bold" }}>
                              Product Description
                            </label>
                            <input
                              type="text"
                              className="form-control checklist-form-control component-details-input"
                              value={getValue("ProductDescription", ["productDescription"])}
                              readOnly
                              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
                            />
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </>
              )}
            </div>
            <div className="modal-footer checklist-modal-footer" style={{ justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleProductDetailsClose}
                style={{
                  backgroundColor: "#dc3545",
                  borderColor: "#dc3545",
                  color: "white",
                  fontWeight: "bold",
                  padding: "0.5rem 1.5rem",
                }}
              >
                <CloseIcon fontSize="small" className="me-1" style={{ color: "white" }} />
                CLOSE
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Component Details Modal Backdrop */}
      {componentDetailsOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={handleComponentDetailsClose}
          style={{ zIndex: approveRejectOpen ? 10040 : 1040 }}
        ></div>
      )}

      {/* Component Details Modal - Bootstrap 5 */}
      <div
        className={`modal fade checklist-modal component-details-modal ${componentDetailsOpen ? "show" : ""}`}
        id="componentDetailsModal"
        ref={componentDetailsModalRef}
        tabIndex="-1"
        aria-labelledby="componentDetailsModalLabel"
        aria-hidden={!componentDetailsOpen}
        style={{
          display: componentDetailsOpen ? "flex" : "none",
          zIndex: approveRejectOpen ? 10050 : 1050,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable component-details-modal-dialog">
          <div className="modal-content checklist-modal-content">
            <div className="modal-header checklist-modal-header">
              <h5
                className="modal-title checklist-modal-title"
                id="componentDetailsModalLabel"
              >
                Component Details
              </h5>
              <button
                type="button"
                className="btn-close-checklist checklist-modal-close"
                aria-label="Close"
                onClick={handleComponentDetailsClose}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: "0.5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon style={{ fontSize: "24px", color: "#666" }} />
              </button>
            </div>
            <div className="modal-body checklist-modal-body">
              {/* Table row format (same layout as Raise SR Component Details grid) */}
              <div className="material-section-panel component-details-grid-panel">
                <div className="component-details-table-scroll">
                  <div className="table-responsive">
                    <table className="table table-striped table-bordered complaint-table component-details-inline-grid">
                      <thead>
                        <tr>
                          <th className="col-serial">#</th>
                          <th className="col-material-group">Material Group Desc.</th>
                          <th className="col-category">Component Category</th>
                          <th className="col-component-list">Component List/Name</th>
                          <th className="col-qty">Component Qty.</th>
                          <th className="col-issue">Component Issue</th>
                          <th className="col-material-code">Material Code (Plant Team)</th>
                          <th className="col-remarks">Component Issue Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(componentDetailsRowsList.length
                          ? componentDetailsRowsList
                          : [
                              {
                                materialGroupDesc: componentDetailsData.materialGroupDesc,
                                componentCategory: componentDetailsData.componentCategory,
                                componentList: componentDetailsData.componentList,
                                componentQty: componentDetailsData.componentQty,
                                componentIssue: componentDetailsData.componentIssue,
                                doorMaterialCode: componentDetailsData.doorMaterialCode,
                                componentIssueRemarks: componentDetailsData.componentIssueRemarks,
                              },
                            ]
                        ).map((row, rowIndex) => (
                        <tr key={row._rowId || `comp-row-${rowIndex}`}>
                          <td className="text-center align-middle component-details-grid-cell col-serial">{rowIndex + 1}</td>
                          <td className="component-details-grid-cell col-material-group">
                            <input
                              type="text"
                              className="form-control form-control-sm component-details-input"
                              value={row.materialGroupDesc || ""}
                              readOnly
                              style={{ backgroundColor: "#f8f9fa", cursor: "default" }}
                            />
                          </td>
                          <td className="component-details-grid-cell col-category">
                            <input
                              type="text"
                              className="form-control form-control-sm component-details-input"
                              value={row.componentCategory || ""}
                              readOnly
                              style={{ backgroundColor: "#f8f9fa", cursor: "default" }}
                            />
                          </td>
                          <td className="component-details-grid-cell col-component-list">
                            <input
                              type="text"
                              className="form-control form-control-sm component-details-input"
                              value={row.componentList || ""}
                              readOnly
                              style={{ backgroundColor: "#f8f9fa", cursor: "default" }}
                            />
                          </td>
                          <td className="component-details-grid-cell col-qty">
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              className="form-control form-control-sm component-details-input component-qty-input"
                              value={row.componentQty || ""}
                              readOnly={!canPlantQcEditComponentQty}
                              disabled={componentDetailsSaving}
                              onChange={
                                canPlantQcEditComponentQty
                                  ? (e) => {
                                      const raw = e.target.value.replace(/[^\d]/g, "");
                                      updateComponentDetailsField(
                                        "componentQty",
                                        raw,
                                        rowIndex
                                      );
                                    }
                                  : undefined
                              }
                              style={{
                                backgroundColor: canPlantQcEditComponentQty
                                  ? "#fff"
                                  : "#f8f9fa",
                                cursor: canPlantQcEditComponentQty
                                  ? "text"
                                  : "default",
                              }}
                            />
                          </td>
                          <td className="component-details-grid-cell col-issue">
                            <input
                              type="text"
                              className="form-control form-control-sm component-details-input"
                              value={row.componentIssue || ""}
                              readOnly
                              style={{ backgroundColor: "#f8f9fa", cursor: "default" }}
                            />
                          </td>
                          <td className="component-details-grid-cell col-material-code">
                            <input
                              type="text"
                              className="form-control form-control-sm component-details-input"
                              value={row.doorMaterialCode || ""}
                              onChange={(e) =>
                                updateComponentDetailsField(
                                  "doorMaterialCode",
                                  e.target.value,
                                  rowIndex
                                )
                              }
                              style={{ cursor: "text" }}
                            />
                          </td>
                          <td className="component-details-grid-cell col-remarks">
                            <textarea
                              rows={2}
                              className="form-control form-control-sm component-details-input component-grid-remarks"
                              value={row.componentIssueRemarks || ""}
                              onChange={(e) =>
                                updateComponentDetailsField(
                                  "componentIssueRemarks",
                                  e.target.value,
                                  rowIndex
                                )
                              }
                              style={{ cursor: "text" }}
                            ></textarea>
                          </td>
                        </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="material-section-total-row">
                          <th colSpan="4" className="text-center">
                            Total
                          </th>
                          <th className="text-center dt-numeric-col">
                            {(componentDetailsRowsList.length
                              ? componentDetailsRowsList
                              : [
                                  {
                                    componentQty: componentDetailsData.componentQty,
                                  },
                                ]
                            ).reduce(
                              (sum, r) => sum + (parseFloat(r.componentQty) || 0),
                              0
                            )}
                          </th>
                          <th colSpan="3"></th>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer checklist-modal-footer">
              <button
                type="button"
                className="btn btn-outline-secondary checklist-btn-cancel"
                onClick={handleComponentDetailsClose}
                disabled={componentDetailsSaving}
              >
                <CancelIcon fontSize="small" className="me-1" />
                Close
              </button>
              {canPlantQcEditComponentQty && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleComponentDetailsSave}
                  disabled={componentDetailsSaving}
                  style={{ minWidth: "110px" }}
                >
                  <SaveIcon fontSize="small" className="me-1" />
                  {componentDetailsSaving ? "Saving…" : "Save"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Response Query Modal Backdrop */}
      {responseQueryOpen && (
        <div
          className="modal-backdrop fade show"
          onClick={responseQuerySubmitting ? undefined : handleResponseQueryClose}
          style={{ zIndex: 1200 }}
        ></div>
      )}

      {/* Response Query Modal - Bootstrap 5 */}
      <div
        className={"modal fade" + (responseQueryOpen ? " show" : "")}
        id="responseQueryModal"
        ref={responseQueryModalRef}
        tabIndex="-1"
        aria-labelledby="responseQueryModalLabel"
        aria-hidden={!responseQueryOpen}
        style={{
          display: responseQueryOpen ? "block" : "none",
          zIndex: 1250,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div
            className="modal-content"
            style={{
              position: "relative",
              borderRadius: "12px",
              border: "none",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.15)",
            }}
          >
            {responseQuerySubmitting && (
              <div
                role="status"
                aria-live="polite"
                aria-busy="true"
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "rgba(255, 255, 255, 0.88)",
                  zIndex: 20,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "12px",
                  gap: "14px",
                }}
              >
                <div
                  className="spinner-border text-success"
                  style={{ width: "2.75rem", height: "2.75rem" }}
                />
                <span
                  style={{
                    fontSize: "14px",
                    color: "#333",
                    fontWeight: 600,
                  }}
                >
                  Submitting…
                </span>
              </div>
            )}
            {/* Modal Header */}
            <div
              className="modal-header"
              style={{
                borderBottom: "2px solid #f0f0f0",
                padding: "20px 24px",
                backgroundColor: "#fafafa",
                borderRadius: "12px 12px 0 0",
              }}
            >
              <h5
                className="modal-title"
                id="responseQueryModalLabel"
                style={{
                  fontSize: "18px",
                  fontWeight: 600,
                  color: "#1f1f1f",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <ReplyIcon style={{ fontSize: "20px", color: "#ff8c00" }} />
                Response to Query
              </h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={() =>
                  !responseQuerySubmitting && handleResponseQueryClose()
                }
                disabled={responseQuerySubmitting}
                style={{
                  opacity: responseQuerySubmitting ? 0.35 : undefined,
                  pointerEvents: responseQuerySubmitting ? "none" : "auto",
                }}
              ></button>
            </div>

            {/* Modal Body */}
            <div
              className="modal-body"
              style={{
                padding: "24px",
                maxHeight: "70vh",
                overflowY: "auto",
              }}
            >
              {selectedQuery && (
                <>
                  {/* Query Details */}
                  <div
                    style={{
                      backgroundColor: "#e7e7e7",
                      padding: "16px",
                      borderRadius: "8px",
                      marginBottom: "20px",
                      borderLeft: "4px solid #e6e3df",
                    }}
                  >
                    <div style={{ marginBottom: "12px", color: "#495057" }}>
                      <strong>Query from: </strong>
                      <span style={{ color: "#666" }}>
                        {safeChild(selectedQuery?.raisedByName) || "Approver"}
                      </span>
                      {safeChild(selectedQuery?.raisedByRoleName) ? (
                        <span style={{ color: "#6b7280" }}>
                          {" "}
                          ({safeChild(selectedQuery?.raisedByRoleName)})
                        </span>
                      ) : null}
                      <span style={{ margin: "0 8px", color: "#9ca3af" }}>|</span>
                      <strong>Query Date: </strong>
                      <span style={{ color: "#666" }}>
                        {safeChild(selectedQuery?.formattedRaisedOnForModal) || "N/A"}
                      </span>
                    </div>
                    <div>
                      <label
                        className="form-label"
                        style={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#495057",
                          marginBottom: "8px",
                          display: "block",
                          textAlign: "left",
                        }}
                      >
                        Query:
                      </label>
                      <textarea
                        className="form-control"
                        value={safeChild(selectedQuery?.queryText) || "N/A"}
                        readOnly
                        disabled
                        rows="3"
                        style={{
                          backgroundColor: "#fff",
                          color: "#333",
                          border: "1px solid #d0d0d0",
                          borderRadius: "8px",
                          resize: "none",
                          cursor: "not-allowed",
                        }}
                      />
                    </div>
                  </div>

                  {/* Response Text Section */}
                  <div style={{ marginBottom: "20px" }}>
                    <label
                      className="form-label"
                      style={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#333",
                        marginBottom: "10px",
                        display: "block",
                        textAlign: "left",
                      }}
                    >
                      Your Response <span style={{ color: "red" }}>*</span>
                    </label>
                    <textarea
                      className="form-control"
                      rows="6"
                      value={responseQueryData.responseText}
                      disabled={responseQuerySubmitting}
                      onChange={(e) => {
                        setResponseQueryData((prev) => ({
                          ...prev,
                          responseText: e.target.value,
                        }));
                      }}
                      placeholder="Enter your response here..."
                      style={{
                        resize: "vertical",
                        fontSize: "14px",
                        padding: "12px 16px",
                        border: "1px solid #d0d0d0",
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                        transition: "all 0.2s",
                        lineHeight: "1.5",
                        minHeight: "150px",
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#ff8c00";
                        e.target.style.boxShadow =
                          "0 0 0 0.2rem rgba(255, 140, 0, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = "#d0d0d0";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                  </div>

                  {/* File Attachments Section (same as Raise SR Invoice Upload) */}
                  <div style={{ marginBottom: "20px" }}>
                    <style>
                      {`
                        /* Hide helper line only in Response Query modal upload panel */
                        #responseQueryModal .upload-helper {
                          display: none !important;
                        }
                      `}
                    </style>
                    <InvoiceUpload
                      title="Attachments Upload"
                      value={responseQueryData.attachments}
                      onChange={(files) =>
                        setResponseQueryData((prev) => ({
                          ...prev,
                          attachments: files,
                        }))
                      }
                    />
                  </div>
                </>
              )}
            </div>

            {/* Modal Footer */}
            <div
              className="modal-footer"
              style={{
                borderTop: "2px solid #f0f0f0",
                padding: "16px 24px",
                backgroundColor: "#fafafa",
                borderRadius: "0 0 12px 12px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                type="button"
                className="btn"
                onClick={handleResponseQueryClose}
                disabled={responseQuerySubmitting}
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
                  border: "1px solid #d0d0d0",
                  color: "#495057",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: responseQuerySubmitting ? 0.65 : 1,
                  cursor: responseQuerySubmitting ? "not-allowed" : "pointer",
                }}
              >
                <CancelIcon style={{ fontSize: "16px" }} />
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleResponseQuerySubmit}
                disabled={responseQuerySubmitting}
                style={{
                  background: "linear-gradient(180deg, #34d399 0%, #16a34a 45%, #15803d 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                  fontSize: "11px",
                  boxShadow:
                    "inset 0 1px 0 rgba(255,255,255,0.35), 0 6px 16px rgba(22,163,74,0.35)",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  padding: "8px 24px",
                  transition: "all 0.3s ease",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  opacity: responseQuerySubmitting ? 0.85 : 1,
                  cursor: responseQuerySubmitting ? "wait" : "pointer",
                }}
              >
                <SendIcon style={{ fontSize: "16px" }} />
                Submit Response
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Previous SRs detailed view modal */}
      {showPreviousComplaintsDetailModal && (
        <div
          className="previous-srs-modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
            overflow: "auto",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowPreviousComplaintsDetailModal(false);
              setSelectedComplaintIndex(null);
            }
          }}
        >
          <div
            className="previous-srs-modal-container"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              width: "95%",
              maxWidth: "1100px",
              maxHeight: "90vh",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Modal Header */}
            <div
              className="previous-srs-modal-header"
              style={{
                borderBottom: "1px solid #dee2e6",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                backgroundColor: "#f8f9fa",
                borderRadius: "8px 8px 0 0",
                flexShrink: 0,
              }}
            >
              <h3 className="previous-srs-modal-title" style={{ 
                margin: 0, 
                color: "#333", 
                fontWeight: 600,
              }}>
                Previous SRs - Detailed View
              </h3>
              <button
                type="button"
                onClick={() => {
                  setShowPreviousComplaintsDetailModal(false);
                  setSelectedComplaintIndex(null);
                }}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: "pointer",
                  color: "#6c757d",
                  padding: "0",
                  width: "30px",
                  height: "30px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CloseIcon />
              </button>
            </div>

            {/* Modal Body */}
            <div
              className="previous-srs-modal-body"
              style={{
                overflowY: "auto",
                overflowX: "hidden",
                flex: "1 1 auto",
                minHeight: 0,
                WebkitOverflowScrolling: "touch",
              }}
            >
              {previousComplaintsData.length === 0 || selectedComplaintIndex === null ? (
                <div style={{ textAlign: "center", padding: "24px", color: "#6c757d" }}>
                  No previous SRs data available.
                </div>
              ) : (
                (() => {
                  const complaint = previousComplaintsData[selectedComplaintIndex];
                  if (!complaint) return null;
                  const invoiceDisplay =
                    complaint.invoiceNumber ??
                    complaint.InvoiceNumber ??
                    complaint.invoiceNo ??
                    complaint.InvoiceNo ??
                    srData?.invoiceNumber ??
                    srData?.invoiceNo ??
                    "";
                  const invoiceDisplayStr =
                    invoiceDisplay != null && invoiceDisplay !== undefined
                      ? String(invoiceDisplay).trim()
                      : "";
                  const currentStatus = String(
                    complaint.statusName || complaint.StatusName || ""
                  ).trim();
                  const finalActionTaken =
                    currentStatus.toLowerCase() === "closed" ? "Closed" : "Open";
                  return (
                    <div className="previous-srs-details-card">
                      <h4 className="previous-srs-details-title">SR Details</h4>

                      <div className="row g-2">
                        <div className="col-md-3">
                          <label className="input-label">SR Number</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={complaint.srNumber || complaint.SRNumber || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Invoice Number</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={invoiceDisplayStr || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Date</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.srDate || complaint.SRDate
                                ? (() => {
                                    const date = new Date(
                                      complaint.srDate || complaint.SRDate
                                    );
                                    const day = String(date.getDate()).padStart(2, "0");
                                    const month = String(date.getMonth() + 1).padStart(
                                      2,
                                      "0"
                                    );
                                    const year = date.getFullYear();
                                    return `${day}-${month}-${year}`;
                                  })()
                                : "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Type</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.complaintType ||
                              complaint.ComplaintType ||
                              "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Product Name & Code</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.productsInvolved ||
                              complaint.ProductsInvolved ||
                              "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Quantity</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.srQuantity !== undefined
                                ? complaint.srQuantity.toString()
                                : complaint.SRQuantity !== undefined
                                  ? complaint.SRQuantity.toString()
                                  : "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Approved Quantity</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.approvedQuantity !== undefined &&
                              complaint.approvedQuantity !== null
                                ? complaint.approvedQuantity.toString()
                                : complaint.ApprovedQuantity !== undefined &&
                                    complaint.ApprovedQuantity !== null
                                  ? complaint.ApprovedQuantity.toString()
                                  : "0"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Approved Value</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.approvedValue !== undefined &&
                              complaint.approvedValue !== null
                                ? `₹${parseFloat(complaint.approvedValue).toLocaleString(
                                    "en-IN",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}`
                                : complaint.ApprovedValue !== undefined &&
                                    complaint.ApprovedValue !== null
                                  ? `₹${parseFloat(
                                      complaint.ApprovedValue
                                    ).toLocaleString("en-IN", {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    })}`
                                  : "₹0.00"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">SR Value</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={
                              complaint.srValue !== undefined
                                ? `₹${parseFloat(complaint.srValue).toLocaleString(
                                    "en-IN",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}`
                                : complaint.SRValue !== undefined
                                  ? `₹${parseFloat(complaint.SRValue).toLocaleString(
                                      "en-IN",
                                      {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      }
                                    )}`
                                  : "N/A"
                            }
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Final Action Taken</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={finalActionTaken}
                            readOnly
                          />
                        </div>

                        <div className="col-md-3">
                          <label className="input-label">Current / Final Status</label>
                          <input
                            type="text"
                            className="form-control form-control-light"
                            value={currentStatus || "N/A"}
                            readOnly
                          />
                        </div>

                        <div className="col-12">
                          <label className="input-label">Latest Comment</label>
                          <textarea
                            className="form-control form-control-light"
                            value={
                              String(
                                complaint.latestComment ||
                                  complaint.LatestComment ||
                                  ""
                              ).trim() || "N/A"
                            }
                            readOnly
                            rows="2"
                          />
                        </div>
                      </div>
                    </div>
                  );
                })()
              )}
            </div>

            {/* Modal Footer */}
            <div
              className="previous-srs-modal-footer"
              style={{
                borderTop: "1px solid #dee2e6",
                display: "flex",
                justifyContent: "flex-end",
                backgroundColor: "#f8f9fa",
                borderRadius: "0 0 8px 8px",
                flexShrink: 0,
              }}
            >
              <button
                type="button"
                className="btn-close-prev-sr"
                onClick={() => {
                  setShowPreviousComplaintsDetailModal(false);
                  setSelectedComplaintIndex(null);
                }}
                style={{
                  background: "linear-gradient(135deg, #6c757d 0%, #5a6268 100%)",
                  border: "none",
                  color: "white",
                  fontWeight: 500,
                  borderRadius: "4px",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(108, 117, 125, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewApproverServiceRequest;
